/*
 * AI Image Generator + Wall Calculator — frontend controller
 * ---------------------------------------------------------------------------
 * Acts like a product page with three entry points, all feeding the same
 * preview + measurement-based pricing + cart flow:
 *   A. GENERATE with AI  -> prompt; rendered in the wall's aspect ratio.
 *   B. UPLOAD their own   -> photo/design/another tool's AI image; sent to our
 *      secure upload endpoint which stores it and returns an asset id.
 *   C. CONSULTANT         -> opens a Shopify contact form, prefilled with the
 *      customer's wall size / shape / prompt / current design reference.
 *
 * Pricing mirrors the existing custom-calculator (per-unit price, 1m² minimum,
 * area units). AI previews are watermarked server-side; uploads are the
 * customer's own asset. Add to Cart attaches the clean `_ai_image_id` (+ source,
 * seed, metadata), wall measurements and price snapshot to the line item.
 */
(() => {
  const ROOT_SELECTOR = '[data-ai-generator]';
  const INIT_FLAG = 'aiGeneratorReady';

  // Generation sizing constraints (typical FLUX bounds). We cap the LONGEST edge
  // so renders stay sharp enough to upscale externally (Upscayl/ON1) without
  // being unnecessarily large.
  const GEN_MIN = 512;
  const GEN_MAX = 2048;
  const GEN_STEP = 32;
  const CM_PER_INCH = 2.54;
  const FT2_PER_M2 = 10.7639; // 1 m² in ft²
  const PREVIEW_FIT_HEIGHT_KEY = 'wallprint_preview_fit_h';
  const PROMPT_HEIGHT_STUDIO_KEY = 'wallprint_prompt_height_studio_v1';
  const PROMPT_HEIGHT_WIDE_KEY = 'wallprint_prompt_height_wide_v1';
  const WIDE_PREVIEW_ROW_KEY = 'wallprint_wide_preview_row_h_v2';
  const COACH_HELP_SEEN_KEY = 'wallprint_coach_help_seen';
  const PREVIEW_RESIZE_GAIN = 1.35;
  const PREVIEW_FIT_MIN_H = 120;
  const PREVIEW_WALL_MIN_H = 200;
  /* Print quality guard — small compositional zoom only; deep zoom crops too few
     source pixels for large-format print. Images always start fully zoomed out. */
  const CROP_MAX_ZOOM = 1.5;

  const ALLOWED_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/pjpeg', 'image/webp'];

  const PREVIEW_TRIM_PALETTES = {
    white: { r: 252, g: 252, b: 250, hi: 255, lo: 218, grain: null },
    cream: { r: 248, g: 242, b: 228, hi: 255, lo: 208, grain: null },
    warm: { r: 250, g: 246, b: 236, hi: 255, lo: 214, grain: null },
    sage: { r: 168, g: 178, b: 162, hi: 198, lo: 128, grain: null },
    grey: { r: 175, g: 178, b: 182, hi: 210, lo: 140, grain: null },
    charcoal: { r: 52, g: 56, b: 60, hi: 78, lo: 34, grain: null },
    black: { r: 28, g: 30, b: 32, hi: 48, lo: 12, grain: null },
    navy: { r: 38, g: 52, b: 78, hi: 58, lo: 24, grain: null },
    terracotta: { r: 186, g: 118, b: 88, hi: 220, lo: 140, grain: null },
    oak: { r: 198, g: 162, b: 120, hi: 232, lo: 148, grain: 'oak' },
    walnut: { r: 92, g: 68, b: 52, hi: 128, lo: 58, grain: 'walnut' },
  };
  const PREVIEW_SKIRTING_BAND = 0.048;
  const PREVIEW_CORNICE_BAND = 0.036;

  const MOCKUP_THEATER_QUIPS = [
    'Rolling your wallpaper onto the wall…',
    'Asking the couch to scoot out of frame…',
    'Teaching the AI what counts as a wall…',
    'Persuading the succulents to hold still…',
    'Bribing the pixels with extra teal…',
    'Convincing the shadows to behave…',
    'Matching your room lighting — almost there…',
    'The plants are the easy part, promise…',
  ];

  const MOCKUP_THEATER_STEPS = [
    { key: 'composite', afterSec: 0 },
    { key: 'occlusion', afterSec: 18 },
    { key: 'light', afterSec: 45 },
    { key: 'polish', afterSec: 75 },
  ];

  const STYLE_STARTERS = {
    botanical:
      'Soft botanical watercolor leaves and stems in sage green and cream, gentle organic repeat, flat illustration surface pattern',
    geometric:
      'Crisp geometric line pattern in navy and warm gold, structured rhythmic repeat, flat graphic design',
    marble:
      'Veined marble texture in soft white and grey, subtle organic flow, full-bleed decorative surface pattern',
    abstract:
      'Abstract fluid colour fields in muted teal and sand, soft blending, contemporary flat art pattern',
    heritage:
      'Classic damask floral ornament in deep teal and ivory, elegant symmetrical repeat, flat print artwork',
    minimal:
      'Minimal fine line sketch pattern in charcoal on warm white, sparse airy repeat, flat graphic surface',
    stripes:
      'Classic stripe wallpaper pattern in soft neutral and sand tones, balanced horizontal repeat, flat graphic surface design',
    terrazzo:
      'Speckled terrazzo texture pattern in warm stone chips, subtle organic scatter, full-bleed decorative surface',
    textile:
      'Woven textile-inspired pattern with soft crosshatch threads, tactile fabric look, flat printable wallpaper surface',
    'mural-landscape':
      'Wide panoramic landscape wallpaper artwork, painterly hills and soft sky, full-bleed horizontal scene, illustrative surface design',
    'mural-botanical':
      'Large-scale botanical wallpaper artwork with oversized leaves and branches, dramatic vertical composition, full-bleed illustrative surface',
    'mural-abstract':
      'Bold abstract wallpaper artwork with sweeping colour shapes and gallery-scale energy, full-bleed surface design',
    cityscape:
      'Stylised city skyline graphic wallpaper artwork, simplified architectural silhouettes, modern full-bleed surface design',
    'motif-scatter':
      'Scattered decorative motif wallpaper with small icons evenly spaced, playful balance, flat illustrated surface design',
    'motif-celestial':
      'Celestial motif wallpaper with moons and stars in a soft night-sky palette, elegant scattered layout, flat printable surface',
    'motif-animal':
      'Stylised animal silhouette motif wallpaper, subtle repeat spacing, flat graphic surface design',
    'photo-nature':
      'Photorealistic nature wallpaper surface, lush forest or meadow detail, soft natural light, full-bleed printable texture',
    'photo-coastal':
      'Photorealistic coastal wallpaper surface, calm ocean and sand tones, airy full-bleed printable texture',
    'photo-stone':
      'Photorealistic stone and rock texture wallpaper, natural mineral detail, full-bleed surface artwork',
    'photo-sky':
      'Photorealistic cloudscape wallpaper surface, soft atmospheric sky gradient, full-bleed printable design',
    watercolor:
      'Loose watercolour wash wallpaper artwork, soft pigment bloom and paper texture, artistic full-bleed surface design',
    'vintage-print':
      'Vintage block-print wallpaper artwork, slightly imperfect ink texture, heritage full-bleed surface design',
    illustration:
      'Contemporary flat illustration wallpaper surface, bold shapes and limited palette, full-bleed graphic design',
  };

  const PROMPT_PATTERN_HINT =
    /\b(pattern|repeat|motif|floral|botanical|geometric|abstract|texture|stripe|tile|marble|watercolor|watercolour|illustration|graphic|damask|minimal|line|gradient|design|ornament|surface|mural|landscape|cityscape|photographic|photorealistic|photo|motif|celestial|terrazzo|textile|watercolour|scenery|skyline|wallpaper)\b/i;

  /** Unset swatch colours — no palette sent until the customer picks again. */
  const PALETTE_NEUTRAL_PRIMARY = '#717986';
  const PALETTE_NEUTRAL_ACCENT = '#939aa6';

  const SIDEBAR_WIDTH_KEY = 'wallprint_studio_sidebar_w_v1';
  const SIDEBAR_WIDTH_MIN = 248;
  const SIDEBAR_WIDTH_MAX = 420;
  const SIDEBAR_WIDTH_DEFAULT = 340;

  function trimRgb(pal) {
    return `rgb(${pal.r}, ${pal.g}, ${pal.b})`;
  }

  function drawWoodGrain(ctx, x, y, w, h, grainType) {
    ctx.save();
    ctx.globalAlpha = grainType === 'walnut' ? 0.2 : 0.14;
    ctx.strokeStyle = grainType === 'walnut' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.32)';
    ctx.lineWidth = grainType === 'walnut' ? 1.2 : 1;
    const step = grainType === 'walnut' ? 5 : 7;
    for (let i = 0; i < w; i += step) {
      const wobble = grainType === 'walnut' ? Math.sin(i * 0.08) * 2 : 0;
      ctx.beginPath();
      ctx.moveTo(x + i, y);
      ctx.lineTo(x + i + 3 + wobble, y + h);
      ctx.stroke();
    }
    ctx.globalAlpha = 0.06;
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    for (let i = step; i < w; i += step * 2.5) {
      ctx.fillRect(x + i, y, 1, h);
    }
    ctx.restore();
  }

  function drawTrimContactShadow(ctx, x, y, w, strength = 0.16) {
    const grad = ctx.createLinearGradient(0, y, 0, y + 6);
    grad.addColorStop(0, `rgba(0,0,0,${strength})`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, 6);
  }

  function drawPreviewSkirting(ctx, vw, vh, style, pal) {
    const sh = vh * PREVIEW_SKIRTING_BAND;
    const y0 = vh - sh;
    drawTrimContactShadow(ctx, 0, y0 - 1, vw, 0.14);

    if (style === 'recessed') {
      ctx.fillStyle = `rgb(${Math.round(pal.lo * 0.78)}, ${Math.round(pal.lo * 0.78)}, ${Math.round(pal.lo * 0.78)})`;
      ctx.fillRect(0, y0, vw, sh * 0.08);
      const grad = ctx.createLinearGradient(0, y0 + sh * 0.08, 0, vh);
      grad.addColorStop(0, `rgb(${pal.lo}, ${pal.lo}, ${pal.lo})`);
      grad.addColorStop(0.1, trimRgb(pal));
      grad.addColorStop(0.82, trimRgb(pal));
      grad.addColorStop(1, `rgb(${Math.round(pal.lo * 0.92)}, ${Math.round(pal.lo * 0.92)}, ${Math.round(pal.lo * 0.92)})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, y0 + sh * 0.08, vw, sh * 0.92);
      ctx.fillStyle = 'rgba(0,0,0,0.22)';
      ctx.fillRect(0, y0 + sh * 0.075, vw, 1);
    } else {
      const grad = ctx.createLinearGradient(0, y0, 0, vh);
      grad.addColorStop(0, `rgb(${pal.hi}, ${pal.hi}, ${pal.hi})`);
      grad.addColorStop(0.05, trimRgb(pal));
      grad.addColorStop(0.78, trimRgb(pal));
      grad.addColorStop(0.94, `rgb(${Math.round(pal.lo * 0.96)}, ${Math.round(pal.lo * 0.96)}, ${Math.round(pal.lo * 0.96)})`);
      grad.addColorStop(1, `rgb(${pal.lo}, ${pal.lo}, ${pal.lo})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, y0, vw, sh);
      ctx.strokeStyle = 'rgba(255,255,255,0.42)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y0 + 0.5);
      ctx.lineTo(vw, y0 + 0.5);
      ctx.stroke();
    }

    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.fillRect(0, vh - 2, vw, 2);
  }

  function drawPreviewCornice(ctx, vw, ch, style, pal) {
    if (style === 'stepped') {
      const stepH = ch * 0.44;
      ctx.fillStyle = `rgb(${pal.hi}, ${pal.hi}, ${pal.hi})`;
      ctx.fillRect(0, 0, vw, stepH * 0.32);
      const midGrad = ctx.createLinearGradient(0, stepH * 0.32, 0, stepH);
      midGrad.addColorStop(0, trimRgb(pal));
      midGrad.addColorStop(1, `rgb(${pal.lo}, ${pal.lo}, ${pal.lo})`);
      ctx.fillStyle = midGrad;
      ctx.fillRect(0, stepH * 0.32, vw, stepH * 0.68);
      const lowerGrad = ctx.createLinearGradient(0, stepH, 0, ch);
      lowerGrad.addColorStop(0, trimRgb(pal));
      lowerGrad.addColorStop(1, `rgb(${Math.round(pal.r * 0.9)}, ${Math.round(pal.g * 0.9)}, ${Math.round(pal.b * 0.9)})`);
      ctx.fillStyle = lowerGrad;
      ctx.fillRect(0, stepH, vw, ch - stepH);
      ctx.fillStyle = 'rgba(0,0,0,0.14)';
      ctx.fillRect(0, stepH - 1, vw, 1);
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.fillRect(0, 0, vw, 1);
    } else {
      const grad = ctx.createLinearGradient(0, 0, 0, ch);
      grad.addColorStop(0, `rgb(${pal.hi}, ${pal.hi}, ${pal.hi})`);
      grad.addColorStop(0.22, trimRgb(pal));
      grad.addColorStop(0.72, trimRgb(pal));
      grad.addColorStop(1, `rgb(${Math.round(pal.r * 1.04)}, ${Math.round(pal.g * 1.04)}, ${Math.round(pal.b * 1.04)})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, vw, ch);
      ctx.fillStyle = 'rgba(255,255,255,0.24)';
      ctx.fillRect(0, 0, vw, 1);
      drawTrimContactShadow(ctx, 0, ch - 1, vw, 0.1);
    }
  }

  function drawPreviewTrimOverlay(ctx, vw, vh, trim) {
    if (!trim || (trim.skirting === 'none' && trim.cornice === 'none')) return;
    const pal = PREVIEW_TRIM_PALETTES[trim.color] || PREVIEW_TRIM_PALETTES.white;
    if (trim.cornice !== 'none') {
      drawPreviewCornice(ctx, vw, vh * PREVIEW_CORNICE_BAND, trim.cornice, pal);
    }
    if (trim.skirting !== 'none') {
      drawPreviewSkirting(ctx, vw, vh, trim.skirting, pal);
    }
    if (pal.grain) {
      if (trim.cornice !== 'none') {
        drawWoodGrain(ctx, 0, 0, vw, vh * PREVIEW_CORNICE_BAND, pal.grain);
      }
      if (trim.skirting !== 'none') {
        drawWoodGrain(ctx, 0, vh * (1 - PREVIEW_SKIRTING_BAND), vw, vh * PREVIEW_SKIRTING_BAND, pal.grain);
      }
    }
  }

  /**
   * Print-tuning sliders — keep preview, zoom dialog, and cart metadata in sync.
   * To add a tool: extend this list, add the slider in liquid, and map it in
   * services/wallprint-ai/src/adjustments.js for fulfillment.
   */
  const ADJUSTMENT_DEFS = [
    {
      prop: 'adjustBrightness',
      metaKey: 'adjust_brightness',
      cssVar: '--ai-adjust-brightness',
      inputKey: 'adjustBrightnessInput',
      valueKey: 'adjustBrightnessValue',
    },
    {
      prop: 'adjustContrast',
      metaKey: 'adjust_contrast',
      cssVar: '--ai-adjust-contrast',
      inputKey: 'adjustContrastInput',
      valueKey: 'adjustContrastValue',
    },
    {
      prop: 'adjustVibrance',
      metaKey: 'adjust_vibrance',
      cssVar: '--ai-adjust-vibrance',
      inputKey: 'adjustVibranceInput',
      valueKey: 'adjustVibranceValue',
    },
    {
      prop: 'adjustShadow',
      metaKey: 'adjust_shadow',
      cssVar: '--ai-adjust-shadow',
      inputKey: 'adjustShadowInput',
      valueKey: 'adjustShadowValue',
    },
  ];

  /** Optional CMYK channel tuning — preview via SVG matrix; fulfillment in adjustments.js */
  const CMYK_ADJUSTMENT_DEFS = [
    {
      prop: 'adjustCyan',
      metaKey: 'adjust_cyan',
      inputKey: 'adjustCyanInput',
      valueKey: 'adjustCyanValue',
      channel: 'cyan',
      label: 'Cyan',
    },
    {
      prop: 'adjustMagenta',
      metaKey: 'adjust_magenta',
      inputKey: 'adjustMagentaInput',
      valueKey: 'adjustMagentaValue',
      channel: 'magenta',
      label: 'Magenta',
    },
    {
      prop: 'adjustYellow',
      metaKey: 'adjust_yellow',
      inputKey: 'adjustYellowInput',
      valueKey: 'adjustYellowValue',
      channel: 'yellow',
      label: 'Yellow',
    },
    {
      prop: 'adjustBlack',
      metaKey: 'adjust_black',
      inputKey: 'adjustBlackInput',
      valueKey: 'adjustBlackValue',
      channel: 'black',
      label: 'Black',
    },
  ];

  const ALL_ADJUSTMENT_DEFS = [...ADJUSTMENT_DEFS, ...CMYK_ADJUSTMENT_DEFS];

  /** @param {File | null | undefined} file */
  const isAllowedUploadFile = (file) => {
    if (!file) return false;
    if (file.type && ALLOWED_UPLOAD_TYPES.includes(file.type)) return true;
    return /\.(png|jpe?g|webp)$/i.test(file.name || '');
  };

  const ref = (root, selector) => root.querySelector(selector);
  const toNum = (value, fallback = 0) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  };

  const snap = (value) => {
    const clamped = Math.min(GEN_MAX, Math.max(GEN_MIN, value));
    return Math.round(clamped / GEN_STEP) * GEN_STEP;
  };

  /**
   * Build generation dimensions whose LONGEST edge ≈ longEdge, preserving ratio.
   * @param {number} ratio - width / height
   * @param {number} longEdge - target longest edge in px
   * @returns {{ width: number, height: number }}
   */
  const dimensionsForRatio = (ratio, longEdge) => {
    const safeRatio = ratio > 0 ? ratio : 1;
    let width;
    let height;
    if (safeRatio >= 1) {
      width = longEdge;
      height = longEdge / safeRatio;
    } else {
      height = longEdge;
      width = longEdge * safeRatio;
    }
    return { width: snap(width), height: snap(height) };
  };

  const WALLPAPER_LAYOUT_LABELS = {
    single: 'Single image / mural',
    horizontal: 'Horizontal band repeat',
    full: 'Full repeat across wall',
    vertical: 'Vertical repeat',
  };

  function resolveWatermarkLogoUrl(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';
    try {
      if (/^https?:\/\//i.test(s)) return s;
      if (s.startsWith('//')) return `https:${s}`;
      return new URL(s, window.location.origin).href;
    } catch {
      return s;
    }
  }

  function isStudioGuestMode(gen) {
    return gen.studioMode === 'standalone' || gen.studioMode === 'embed';
  }

  function ensureStudioGuestSession(gen) {
    if (!isStudioGuestMode(gen)) return;
    gen.customerLoggedIn = true;
    if (!gen.customerId) {
      gen.customerId = 'studio_wallprint_ai';
    }
  }

  class AIImageGenerator {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;

      // Measurement + calculator refs
      this.widthInput = ref(root, '[data-ai-width]');
      this.heightInput = ref(root, '[data-ai-height]');
      this.toolbarWidthInput = ref(root, '[data-ai-toolbar-width]');
      this.toolbarHeightInput = ref(root, '[data-ai-toolbar-height]');
      this.toolbarWallMeasureBtn = ref(root, '[data-ai-toolbar-wall-measure-btn]');
      this.toolbarWallMeasureSummary = ref(root, '[data-ai-toolbar-wall-measure-summary]');
      this.toolbarWallMeasurePop = ref(root, '[data-ai-toolbar-wall-measure-pop]');
      this.toolbarWallMeasurePopWidth = ref(root, '[data-ai-wall-measure-pop-width]');
      this.toolbarWallMeasurePopHeight = ref(root, '[data-ai-wall-measure-pop-height]');
      this.toolbarWallMeasureWrap = ref(root, '[data-ai-toolbar-wall-measure]');
      this.widthTotal = ref(root, '[data-ai-width-total]');
      this.heightTotal = ref(root, '[data-ai-height-total]');
      this.widthMarginText = ref(root, '[data-ai-width-margin-text]');
      this.heightMarginText = ref(root, '[data-ai-height-margin-text]');
      this.removeMargin = ref(root, '[data-ai-remove-margin]');
      this.customBleedInput = ref(root, '[data-ai-custom-bleed]');
      this.marginDiagramTotal = ref(root, '[data-ai-margin-diagram-total]');
      this.marginBandLabels = Array.from(root.querySelectorAll('[data-ai-margin-band-label]'));
      this.wallBleedExplain = ref(root, '[data-ai-wall-bleed-explain]');
      this.regionSelect = ref(root, '[data-ai-region]');

      this.wallSpecsCompact = ref(root, '[data-ai-wall-specs]');
      this.wallReadyEl = ref(root, '[data-ai-wall-ready]');
      this.wallReadyDismiss = ref(root, '[data-ai-wall-ready-dismiss]');
      this.previewMeasurements = ref(root, '[data-ai-preview-measurements]');
      this.previewHud = ref(root, '[data-ai-preview-hud]');
      this.previewHudSource = ref(root, '[data-ai-preview-hud-source]');
      this.previewHudDims = ref(root, '[data-ai-preview-hud-dims]');
      this.previewHudRatio = ref(root, '[data-ai-preview-hud-ratio]');
      this.previewHudPanels = ref(root, '[data-ai-preview-hud-panels]');
      this.previewHudPanelsSep = ref(root, '[data-ai-preview-hud-panels-sep]');
      this.previewHudBleed = ref(root, '[data-ai-preview-hud-bleed]');
      this.previewHudEdgeW = ref(root, '[data-ai-preview-hud-edge-w]');
      this.previewHudEdgeH = ref(root, '[data-ai-preview-hud-edge-h]');

      // Live wall-shape preview
      this.wallPreview = ref(root, '[data-ai-wall-preview]');
      this.wallBox = ref(root, '[data-ai-wall-box]');
      this.wallPanels = ref(root, '[data-ai-wall-panels]');
      this.wallCaption = ref(root, '[data-ai-wall-caption]');
      this.wallGuide = ref(root, '[data-ai-wall-guide]');
      this.wallRatio = ref(root, '[data-ai-wall-ratio]');
      this.wallRatioLabel = ref(root, '[data-ai-wall-ratio-label]');
      this.wallW = ref(root, '[data-ai-wall-w]');
      this.wallH = ref(root, '[data-ai-wall-h]');
      this.wallInnerOutline = ref(root, '[data-ai-wall-inner-outline]');
      this.wallMeasurePrintW = ref(root, '[data-ai-wall-measure-print-w]');
      this.wallMeasurePrintH = ref(root, '[data-ai-wall-measure-print-h]');
      this.wallMeasurePrintWKind = ref(root, '[data-ai-wall-measure-print-w-kind]');
      this.wallMeasurePrintHKind = ref(root, '[data-ai-wall-measure-print-h-kind]');
      this.wallMeasureBleeds = Array.from(root.querySelectorAll('[data-ai-wall-measure-bleed]'));
      this.wallMeasureWallW = ref(root, '[data-ai-wall-measure-wall-w]');
      this.wallMeasureWallH = ref(root, '[data-ai-wall-measure-wall-h]');
      this.wallMeasureWallWValue = ref(root, '[data-ai-wall-measure-wall-w-value]');
      this.wallMeasureWallHValue = ref(root, '[data-ai-wall-measure-wall-h-value]');
      this.wallStage =
        root.querySelector('.ai-generator__wall-stage') ||
        this.wallBox?.closest('.ai-generator__wall-stage') ||
        null;

      // Units + quality badge
      this.unitButtons = Array.from(root.querySelectorAll('[data-ai-unit]'));
      this.unitLabels = Array.from(root.querySelectorAll('[data-ai-unit-label]'));
      this.areaUnitEls = Array.from(root.querySelectorAll('[data-ai-area-unit]'));
      this.previewMeta = null;
      this.toolbarContext = null;
      this.previewStatus = ref(root, '[data-ai-preview-status]');
      this.qualityRow = ref(root, '[data-ai-quality-row]');
      this.qualityBadge = ref(root, '[data-ai-quality-badge]');
      this.qualityBadgeText = ref(root, '[data-ai-quality-badge-text]');
      this.qualityUpscaleNote = ref(root, '[data-ai-quality-upscale-note]');

      // Header / in-page scroll links + design panels
      this.scrollButtons = Array.from(root.querySelectorAll('[data-ai-scroll]'));
      this.generatePanel = root.querySelector('[data-ai-panel="generate"]');
      this.uploadPanel = root.querySelector('[data-ai-panel="upload"]');

      // Generator refs
      this.form = ref(root, '[data-ai-form]');
      this.promptInput = ref(root, '[data-ai-prompt]');
      this.aspectSelect = ref(root, '[data-ai-aspect]');
      this.ratioNote = ref(root, '[data-ai-ratio-note]');
      this.generateButton = ref(root, '[data-ai-generate]');
      this.errorEl = ref(root, '[data-ai-error]');
      this.cartSuccessEl = ref(root, '[data-ai-cart-success]');
      this.preview = ref(root, '[data-ai-preview]');
      this.previewMount = ref(root, '[data-ai-preview-mount]');
      this.previewSizeResizeHandles = Array.from(root.querySelectorAll('[data-ai-preview-size-resize]'));
      this.previewSizeResize = this.previewSizeResizeHandles[0] || null;
      this.designPreviewCol = root.querySelector('.ai-generator__design-preview');
      this.previewChrome = ref(root, '[data-ai-preview-chrome]');
      this.previewStage = root.querySelector('.ai-generator__preview-stage');
      this.designGrid = root.querySelector('.ai-generator__design-grid');
      this.designControls = root.querySelector('.ai-generator__design-controls');
      this.designWorkspace = root.querySelector('.ai-generator__design-workspace');
      this.exploreBar = root.querySelector('.ai-generator__preview-explore[data-ai-explore-bar]');
      this.designHub = root.querySelector('[data-ai-inspiration-gallery]');
      this.promptCollapseBtn = ref(root, '[data-ai-prompt-collapse]');
      this.promptPanel = ref(root, '[data-ai-prompt-panel]');
      this.promptHeadToggle = ref(root, '[data-ai-prompt-head-toggle]');
      this.promptReopenBtn = ref(root, '[data-ai-prompt-reopen]');
      this.promptTextarea = ref(root, '[data-ai-prompt]');
      this.promptField = root.querySelector('.ai-generator__field--prompt');
      this.promptSizeResizeHandles = Array.from(root.querySelectorAll('[data-ai-prompt-size-resize]'));
      this.sidebarWidthResize = ref(root, '[data-ai-sidebar-width-resize]');
      this.promptCoachToggle = ref(root, '[data-ai-prompt-coach-toggle]');
      this.promptCoachPanel = ref(root, '[data-ai-prompt-coach-panel]');
      this.promptCoachMessages = ref(root, '[data-ai-prompt-coach-messages]');
      this.promptCoachInput = ref(root, '[data-ai-prompt-coach-input]');
      this.promptCoachSend = ref(root, '[data-ai-prompt-coach-send]');
      this.promptCoachApply = ref(root, '[data-ai-prompt-coach-apply]');
      this.promptCoachInfo = ref(root, '[data-ai-prompt-coach-info]');
      this.promptCoachClose = ref(root, '[data-ai-prompt-coach-close]');
      this.promptCoachHelpPop = ref(root, '[data-ai-prompt-coach-help-pop]');
      this.promptCoachChips = Array.from(root.querySelectorAll('[data-ai-prompt-coach-chip]'));
      this.palettePrimaryInput = ref(root, '[data-ai-palette-primary]');
      this.paletteAccentInput = ref(root, '[data-ai-palette-accent]');
      this.palettePrimaryPreview = ref(root, '[data-ai-palette-primary-preview]');
      this.paletteAccentPreview = ref(root, '[data-ai-palette-accent-preview]');
      this.palettePrimaryName = ref(root, '[data-ai-palette-primary-name]');
      this.paletteAccentName = ref(root, '[data-ai-palette-accent-name]');
      this.paletteClearBtn = ref(root, '[data-ai-palette-clear]');
      this.paletteHintEl = ref(root, '[data-ai-palette-hint]');
      this.palettePresetsToggle = ref(root, '[data-ai-palette-presets-toggle]');
      this.palettePresetsPanel = ref(root, '[data-ai-palette-presets-panel]');
      this.styleStarterSelect = ref(root, '[data-ai-style-starter-select]');
      this._designPalette = null;
      this._paletteTouched = false;
      this._activePalettePresetId = '';
      this._palettePresetsOpen = false;
      this._palettePresetsDocBound = false;
      this._activeStyleStarter = '';
      this.promptCoachHistory = [];
      this.promptCoachSuggested = null;
      this.promptCoachBusy = false;
      this._promptCoachTyping = null;
      this.designToolbar = root.querySelector('[data-ai-design-toolbar]');
      this.designToolsTray = root.querySelector('[data-ai-design-tools-tray]');
      this.wallOverlayToggle = ref(root, '[data-ai-wall-overlay-toggle]');
      this.wallGuidesToggles = Array.from(root.querySelectorAll('[data-ai-wall-guides-toggle]'));
      this.panelsControl = ref(root, '[data-ai-panels-control]');
      this.panelsWidthEl = ref(root, '[data-ai-panels-width]');
      this.wallOverlay = ref(root, '[data-ai-wall-overlay]');
      this.wallOverlayStage = ref(root, '[data-ai-wall-overlay-stage]');
      this.wallOverlayBox = ref(root, '[data-ai-wall-overlay-box]');
      this.wallOverlayPanels = ref(root, '[data-ai-wall-overlay-panels]');
      this.wallOverlayRatio = ref(root, '[data-ai-wall-overlay-ratio]');
      this.wallOverlayW = ref(root, '[data-ai-wall-overlay-w]');
      this.wallOverlayH = ref(root, '[data-ai-wall-overlay-h]');
      this.panelLinesEl = ref(root, '[data-ai-panel-lines]');
      this.alignGridEl = ref(root, '[data-ai-preview-align-grid]');
      this.alignGridToggle = ref(root, '[data-ai-align-grid-toggle]');
      this.showPanelLinesOnPreview = true;
      this.showAlignGridOnPreview = true;
      this.showWallOverlayOnPreview = true;
      this.skeleton = ref(root, '[data-ai-skeleton]');
      this.skeletonText = ref(root, '[data-ai-skeleton-text]');
      this.placeholder = ref(root, '[data-ai-placeholder]');
      this.placeholderText = ref(root, '[data-ai-placeholder-text]');
      this.placeholderWallJump = ref(root, '[data-ai-scroll="wall"].ai-generator__placeholder-wall-jump');
      this.image = ref(root, '[data-ai-image]');
      this.resultFooter = ref(root, '[data-ai-result]');
      this.scrollCartBtn = root.querySelector('[data-ai-scroll-cart]');
      this.uploadNoteEl = ref(root, '[data-ai-upload-note]');
      this.watermarkNoteEl = ref(root, '[data-ai-watermark-note]');
      this.watermarkNoteText = ref(root, '[data-ai-watermark-note-text]');
      this.uploadPreviewNote =
        root.dataset.uploadPreviewNote ||
        'Your uploaded image will be printed to your wall size. We review every file and will contact you if there are any major changes after the upscaling process, or if we need you to confirm anything else.';
      this.generatedPreviewNote =
        root.dataset.generatedPreviewNote ||
        'Preview is watermarked to protect the design. Your purchased print is delivered clean and high-resolution.';
      this.ownershipPreviewNote =
        root.dataset.ownershipPreviewNote ||
        'AI-generated design previews are proprietary to WallPrint, watermarked, and may not be downloaded or used outside WallPrint.';
      this.watermarkLogoUrl = resolveWatermarkLogoUrl(root.dataset.watermarkLogoUrl || '');
      this.previewOwnershipEl = ref(root, '[data-ai-preview-ownership]');
      this.previewOwnershipText = ref(root, '[data-ai-preview-ownership-text]');
      this.customerLoggedIn = root.dataset.customerLoggedIn === 'true';
      this.customerId = String(root.dataset.customerId || '').trim();
      this.accountLoginUrl = root.dataset.accountLoginUrl || '/account/login';
      this.installationUnavailable = root.dataset.installationUnavailable === 'true';
      this.previewIdleText =
        root.dataset.previewIdleText || 'Generate or upload a design to see your preview here.';
      this.previewWallHint =
        root.dataset.previewWallHint ||
        'Add your wall width and height above so the preview, panel guides, and AI image match your wall shape.';
      this.previewGeneratingText =
        root.dataset.previewGeneratingText || 'Designing your wallpaper…';
      this.previewTilingText =
        root.dataset.previewTilingText || 'Making your design a seamless repeat…';
      this.previewUpscalingText =
        root.dataset.previewUpscalingText || 'Upscaling your design for print…';
      this.refineBlock = ref(root, '[data-ai-refine-block]');
      this.refinePromptInput = ref(root, '[data-ai-refine-prompt]');
      this.refineLoginEl = ref(root, '[data-ai-refine-login]');
      this.refineLoginLink = ref(root, '[data-ai-refine-login-link]');
      this.refineBtn = ref(root, '[data-ai-refine]');
      this.tilePatternBtn = ref(root, '[data-ai-tile-pattern]');
      this.upscaleBtn = ref(root, '[data-ai-upscale]') || ref(root, '[data-ai-upscale-pro]');
      this.tileLoginEl = ref(root, '[data-ai-tile-login]');
      this.tileLoginLink = ref(root, '[data-ai-tile-login-link]');
      this.seamlessRepeatInput = ref(root, '[data-ai-seamless-repeat]');
      this.seamlessHintEl = ref(root, '[data-ai-seamless-hint]');
      this.wallpaperLayoutWrap = ref(root, '[data-ai-wallpaper-layout-wrap]');
      this.wallpaperLayoutSelect = ref(root, '[data-ai-wallpaper-layout]');
      this.patternRepeatInput = ref(root, '[data-ai-pattern-repeat]');
      this.patternRepeatLabel = ref(root, '[data-ai-pattern-repeat-label]');
      this.repeatCountHint = ref(root, '[data-ai-repeat-count-hint]');
      this.repeatPreviewBtn = ref(root, '[data-ai-repeat-preview]');
      this.repeatDialog = ref(root, '[data-ai-repeat-dialog]');
      this.repeatCanvas = ref(root, '[data-ai-repeat-canvas]');
      this.repeatThumbWrap = ref(root, '[data-ai-repeat-thumb-wrap]');
      this.repeatThumbPlaceholder = ref(root, '[data-ai-repeat-thumb-placeholder]');
      this.repeatThumbCanvas = ref(root, '[data-ai-repeat-thumb]');
      this.roomMockupBtn = ref(root, '[data-ai-room-mockup]');
      this.roomDialog = ref(root, '[data-ai-room-dialog]');
      this.roomDialogClose = ref(root, '[data-ai-room-dialog-close]');
      this.roomDialogResize = ref(root, '[data-ai-room-dialog-resize]');
      this.roomMockupTheater = ref(root, '[data-ai-room-mockup-theater]');
      this.roomMockupTheaterRoom = ref(root, '[data-ai-room-mockup-theater-room]');
      this.roomMockupTheaterDesign = ref(root, '[data-ai-room-mockup-theater-design]');
      this.roomMockupTheaterWall = ref(root, '[data-ai-room-mockup-theater-wall]');
      this.roomMockupTheaterQuip = ref(root, '[data-ai-room-mockup-theater-quip]');
      this.roomMockupTheaterProgress = ref(root, '[data-ai-room-mockup-theater-progress]');
      this.roomMockupTheaterSteps = Array.from(root.querySelectorAll('[data-ai-room-mockup-step]'));
      this._mockupTheaterTimers = { quip: 0, step: 0, progress: 0 };
      this._mockupTheaterQuipIdx = 0;
      this.roomFileInput = ref(root, '[data-ai-room-file]');
      this.roomRunBtn = ref(root, '[data-ai-room-run]');
      this.roomRunLabel = ref(root, '[data-ai-room-run-label]');
      this.roomRunSpinner = ref(root, '[data-ai-room-run-spinner]');
      this.roomFastPreviewWrap = ref(root, '[data-ai-room-fast-preview-wrap]');
      this.roomFastPreviewInput = ref(root, '[data-ai-room-fast-preview]');
      this.roomStatusEl = ref(root, '[data-ai-room-status]');
      this.roomResultImg = ref(root, '[data-ai-room-result]');
      this.previewTrimToggle = ref(root, '[data-ai-preview-trim-toggle]');
      this.previewFitOutBtn = ref(root, '[data-ai-preview-fit-out]');
      this.previewTrimPop = ref(root, '[data-ai-preview-trim-pop]');
      this.previewTrimSkirtingBtns = Array.from(root.querySelectorAll('[data-ai-preview-trim-skirting]'));
      this.previewTrimCorniceBtns = Array.from(root.querySelectorAll('[data-ai-preview-trim-cornice]'));
      this.previewTrimColorBtns = Array.from(root.querySelectorAll('[data-ai-preview-trim-color]'));
      this.previewTrimCanvas = ref(root, '[data-ai-preview-trim-overlay]');
      this.roomPremiumInput = null;
      this.roomEditorWrap = ref(root, '[data-ai-room-editor-wrap]');
      this.roomCornerWrap = this.roomEditorWrap || ref(root, '[data-ai-room-cornerpin-wrap]');
      this.roomFileNameEl = ref(root, '[data-ai-room-file-name]');
      this.roomEditorHint = ref(root, '[data-ai-room-editor-hint]');
      this.roomCornerToggle = ref(root, '[data-ai-room-cornerpin]');
      this.roomCornerStage = ref(root, '[data-ai-room-cornerpin-stage]');
      this.roomCornerImg = ref(root, '[data-ai-room-cornerpin-img]');
      this.roomCornerPoly = ref(root, '[data-ai-room-cornerpin-poly]');
      this.roomExclusionSvg = ref(root, '[data-ai-room-exclusion-svg]');
      this.roomExclusionHandles = ref(root, '[data-ai-room-exclusion-handles]');
      this.roomExclusionAddBtn = ref(root, '[data-ai-room-exclusion-add]');
      this.roomExclusionList = ref(root, '[data-ai-room-exclusion-list]');
      this.roomEditorLoading = ref(root, '[data-ai-room-editor-loading]');
      this.roomEditorLoadingText = ref(root, '[data-ai-room-editor-loading-text]');
      this.roomUploadPreview = ref(root, '[data-ai-room-upload-preview]');
      this.roomUploadPreviewImg = ref(root, '[data-ai-room-upload-preview-img]');
      this.roomStockWrap = ref(root, '[data-ai-room-stock]');
      this.roomStockList = ref(root, '[data-ai-room-stock-list]');
      this.roomStockNote = ref(root, '[data-ai-room-stock-note]');
      this.roomFinishToggle = ref(root, '[data-ai-room-finish-toggle]');
      this.roomFinishStandardBtn = ref(root, '[data-ai-room-finish-standard]');
      this.roomFinishPhotorealBtn = ref(root, '[data-ai-room-finish-photoreal]');
      this.roomEnhanceBtn = ref(root, '[data-ai-room-enhance]');
      this.roomMockupThumbWrap = ref(root, '[data-ai-room-mockup-thumb-wrap]');
      this.roomMockupThumb = ref(root, '[data-ai-room-mockup-thumb]');
      this.roomCornerHandles = Array.from(root.querySelectorAll('[data-ai-room-corner]'));
      /** Normalized wall quad corners — TL, TR, BR, BL. */
      this._roomCorners = [
        { x: 0.15, y: 0.15 },
        { x: 0.85, y: 0.15 },
        { x: 0.85, y: 0.85 },
        { x: 0.15, y: 0.85 },
      ];
      /** Normalized exclusion quads — { corners: [{x,y}, …] } TL→TR→BR→BL. */
      this._roomExclusions = [];
      /** User moved wall pins — do not overwrite with auto-detect or aspect fit. */
      this._roomCornersUserEdited = false;
      this._roomCornerImgUrl = '';
      this._roomCornerActive = -1;
      this._roomExclusionActive = null;
      /** Last successful room mockup URL (already watermarked on the server). */
      this.lastMockupUrl = '';
      this.lastCompositeUrl = '';
      this.lastHasPhotoreal = false;
      this._mockupFinishMode = 'photoreal';
      this.lastMockupRoomId = '';
      /** Fallback when DataTransfer cannot mirror files onto the dialog input. */
      this._roomPhotoFile = null;
      /** Selected stock room from free gallery — { id, name, corners, image_url }. */
      this._stockRoomSelection = null;
      this._stockRoomsLoaded = false;
      this._stockRoomsLoading = false;
      this._roomLayoutSessionId = '';
      this._roomUploadPreviewUrl = '';
      this._roomSuggestLoading = false;
      this._roomSuggestSeq = 0;
      this._roomPrefetchSeq = 0;
      this._roomPhotoCompressed = null;
      this._previewTrim = { skirting: 'none', cornice: 'none', color: 'white' };
      this._previewTrimRaf = 0;
      this.wallPhotoWrap = ref(root, '[data-ai-wall-photo]');
      this.wallPhotoInput = ref(root, '[data-ai-wall-photo-input]');
      this.wallPhotoTrigger = ref(root, '[data-ai-wall-photo-trigger]');
      this.wallPhotoThumb = ref(root, '[data-ai-wall-photo-thumb]');
      this.wallPhotoTitle = ref(root, '[data-ai-wall-photo-title]');
      this.wallPhotoSub = ref(root, '[data-ai-wall-photo-sub]');
      this.wallPhotoRemove = ref(root, '[data-ai-wall-photo-remove]');
      this.wallPhotoInfo = ref(root, '[data-ai-wall-photo-info]');
      this.wallPhotoPop = ref(root, '[data-ai-wall-photo-pop]');
      this.wallBleedInfo = ref(root, '[data-ai-wall-bleed-info]');
      this.wallBleedPop = ref(root, '[data-ai-wall-bleed-pop]');
      this.wallBleedWrap = ref(root, '[data-ai-wall-bleed]');
      this.wallPhotoThumbUrl = '';
      this.repeatDialogClose = ref(root, '[data-ai-repeat-dialog-close]');
      this.addToCartButton = ref(root, '[data-ai-add-to-cart]');

      // Upload refs
      this.fileInput = ref(root, '[data-ai-file], [data-ai-upload-input]');
      this.dropzone = ref(root, '[data-ai-dropzone]');
      this.uploadTrigger = ref(root, '[data-ai-upload-trigger]');
      this.uploadStatus = ref(root, '[data-ai-upload-status]');
      this.cropViewport = ref(root, '[data-ai-crop-viewport]');
      this.cropImage = ref(root, '[data-ai-crop-image]');
      this.cropHint = ref(root, '[data-ai-crop-hint]');
      this.adjustWrap = ref(root, '[data-ai-adjust-wrap]');
      this.adjustDropdown = ref(root, '[data-ai-adjust-dropdown]');
      this.adjustPanel = ref(root, '[data-ai-adjust-panel]');
      this.adjustBrightnessInput = ref(root, '[data-ai-adjust-brightness]');
      this.adjustContrastInput = ref(root, '[data-ai-adjust-contrast]');
      this.adjustVibranceInput = ref(root, '[data-ai-adjust-vibrance]');
      this.adjustShadowInput = ref(root, '[data-ai-adjust-shadow]');
      this.adjustBrightnessValue = ref(root, '[data-ai-adjust-brightness-value]');
      this.adjustContrastValue = ref(root, '[data-ai-adjust-contrast-value]');
      this.adjustVibranceValue = ref(root, '[data-ai-adjust-vibrance-value]');
      this.adjustShadowValue = ref(root, '[data-ai-adjust-shadow-value]');
      this.adjustCyanInput = ref(root, '[data-ai-adjust-cyan]');
      this.adjustMagentaInput = ref(root, '[data-ai-adjust-magenta]');
      this.adjustYellowInput = ref(root, '[data-ai-adjust-yellow]');
      this.adjustBlackInput = ref(root, '[data-ai-adjust-black]');
      this.adjustCyanValue = ref(root, '[data-ai-adjust-cyan-value]');
      this.adjustMagentaValue = ref(root, '[data-ai-adjust-magenta-value]');
      this.adjustYellowValue = ref(root, '[data-ai-adjust-yellow-value]');
      this.adjustBlackValue = ref(root, '[data-ai-adjust-black-value]');
      this.cmykDropdown = ref(root, '[data-ai-cmyk-dropdown]');
      this.cmykPanel = ref(root, '[data-ai-cmyk-panel]');
      this.cmykMatrix = ref(root, '[data-ai-cmyk-matrix]');
      this.cmykLockButtons = Array.from(root.querySelectorAll('[data-ai-cmyk-lock]'));
      this._cmykChannelLocked = {
        cyan: true,
        magenta: true,
        yellow: true,
        black: true,
      };
      this.adjustResetBtn = ref(root, '[data-ai-adjust-reset]');
      this.adjustTeaser = ref(root, '[data-ai-adjust-teaser]');
      this.adjustNote = ref(root, '[data-ai-adjust-note]');
      this.tilingDropdown = ref(root, '[data-ai-tiling-dropdown]');
      this.tilingPanel = ref(root, '[data-ai-tiling-panel]');
      this.tilingTeaser = ref(root, '[data-ai-tiling-teaser]');
      this.tilingSummary = ref(root, '[data-ai-tiling-summary]');
      this.repeatOverlayCanvas = ref(root, '[data-ai-repeat-overlay]');
      this._repeatOverlayRaf = 0;
      this._repeatThumbRaf = 0;
      this._repeatThumbToken = 0;
      this.previewStack = ref(root, '[data-ai-preview-stack]');
      this.cropWatermarkCanvas = ref(root, '[data-ai-crop-watermark]');
      for (const def of ALL_ADJUSTMENT_DEFS) {
        this[def.prop] = 1;
      }
      this._adjustPersistTimer = null;
      this.previewZoomOpens = Array.from(root.querySelectorAll('[data-ai-preview-zoom-open]'));
      this.previewZoomOpen = this.previewZoomOpens[0] || null;
      this.previewZoomDialog = ref(root, '[data-ai-preview-zoom]');
      this.previewZoomClose = ref(root, '[data-ai-preview-zoom-close]');
      this.previewZoomImg = ref(root, '[data-ai-preview-zoom-img]');
      this.zoomFrame = ref(root, '[data-ai-zoom-frame]');
      this.zoomCrop = ref(root, '[data-ai-zoom-crop]');
      this.zoomPanels = ref(root, '[data-ai-zoom-panels]');
      this.zoomPanelsToggle = ref(root, '[data-ai-zoom-panels-toggle]');
      this.zoomLegend = ref(root, '[data-ai-zoom-legend]');
      this._zoomPanelsOn = false;
      this.previewExpandToggles = Array.from(root.querySelectorAll('[data-ai-preview-expand-toggle]'));
      this._previewExpanded = false;
      this._layoutPrefKey = 'wallprint:studio-wide-view';

      this.cropNaturalW = 0;
      this.cropNaturalH = 0;
      this.cropScale = 1;
      this.cropMinScale = 1;
      this.cropPanX = 0;
      this.cropPanY = 0;
      this.cropDrag = null;
      this.cropEventsBound = false;
      this.cropResizeObserver = null;
      this._cropAspectKey = '';
      this._previewFitKey = '';
      this._previewUserFitH = 0;
      this._previewUserAspectKey = '';
      this._previewResizing = false;
      this._previewLayoutRaf = 0;
      this._previewLayoutLock = false;
      this._previewColWidth = 0;
      this._previewColSizeKey = '';
      this._measureInputTimer = null;

      // Consultant refs (dialog + inline page form)
      this.consultDialog = ref(root, '[data-ai-consultant]');
      this.consultInline = ref(root, '[data-ai-consult-inline]');
      this.consultOpenButton = ref(root, '[data-ai-consultant-open]');
      this.consultCloseButton = ref(root, '[data-ai-consultant-close]');
      this.consultForms = Array.from(root.querySelectorAll('[data-ai-consult-form]'));
      this.consultUnitLabels = Array.from(root.querySelectorAll('[data-ai-consult-unit-label]'));

      // Config from data attributes
      this.generateEndpoint = root.dataset.generateEndpoint || '/apps/wallprint-ai/generate';
      this.uploadEndpoint = root.dataset.uploadEndpoint || '/apps/wallprint-ai/upload';
      this.tilePatternEndpoint =
        root.dataset.tilePatternEndpoint || '/apps/wallprint-ai/tile-pattern';
      this.upscaleEndpoint = root.dataset.upscaleEndpoint || '/apps/wallprint-ai/upscale';
      this.roomMockupEndpoint =
        root.dataset.roomMockupEndpoint || '/apps/wallprint-ai/room-mockup';
      this.stockRoomsEndpoint = root.dataset.stockRoomsEndpoint || '';
      this.stockRoomsPreviewEndpoint = root.dataset.stockRoomsPreviewEndpoint || '';
      this.ipScanEndpoint = root.dataset.ipScanEndpoint || '/apps/wallprint-ai/ip-scan';
      this.promptAssistantEndpoint =
        root.dataset.promptAssistantEndpoint || '/apps/wallprint-ai/prompt-assistant';
      this.storefrontApiKey = root.dataset.storefrontApiKey || '';
      this.studioMode = root.dataset.studioMode || 'standalone';
      ensureStudioGuestSession(this);
      this.designLibraryCustomerId = this.#resolveDesignLibraryCustomerId();
      this.variantId = root.dataset.variantId || '';
      this.redirectToCart = root.dataset.redirectToCart === 'true';
      const perSqm = toNum(root.dataset.pricePerSqm, 0);
      this.baseUnitPrice = toNum(root.dataset.baseUnitPrice, 0);
      if (perSqm > 0) this.baseUnitPrice = perSqm / 100;
      this.retailUnitPrice = toNum(root.dataset.retailUnitPrice, this.baseUnitPrice);
      this.memberActive =
        root.dataset.memberActive === 'true' || root.dataset.tradeActive === 'true';
      this.tradeActive = this.memberActive;
      this.memberDiscountPercent = toNum(root.dataset.memberDiscountPercent, 0);
      this.memberPricingLabel =
        root.dataset.memberPricingLabel || 'Appstle member pricing';
      this.tradePricePerSqm = toNum(root.dataset.tradePricePerSqm, 0);
      this.productId = root.dataset.productId || '';
      this.productTitle = root.dataset.productTitle || '';
      this.defaultMargin = toNum(root.dataset.defaultMargin, 5);
      this.panelWidth = toNum(root.dataset.panelWidth, 106);
      this.minArea = toNum(root.dataset.minArea, 1);
      this.currencyIso = (root.dataset.currencyIso || 'ZAR').toUpperCase();
      this.locale = root.dataset.locale || document.documentElement.lang || 'en';
      this.currencyFallback = root.dataset.currencyFallback || 'R';
      this.installEnabled = root.dataset.installEnabled === 'true';
      this.installRate = toNum(root.dataset.installRate, 0);
      this.installVariantId = root.dataset.installVariantId || '';
      this.maxUploadBytes = toNum(root.dataset.maxUploadMb, 45) * 1024 * 1024;
      this.genLongEdge = toNum(root.dataset.genLongEdge, 1280);
      this.minUploadDimension = toNum(root.dataset.minUploadDimension, 1000);
      this.minPrintDpi = toNum(root.dataset.minPrintDpi, 30);
      this.recommendedPrintDpi = toNum(root.dataset.recommendedPrintDpi, 100);

      this.unit = root.dataset.defaultUnit === 'imperial' ? 'imperial' : 'metric';

      /** @type {null | object} */
      this.currentAsset = null;
      this.mode = 'generate';
      this.isGenerating = false;
      this.isTiling = false;
      this.isUpscaling = false;
      this.isMockup = false;
      this.isUploading = false;
      this.isAdding = false;
      this.objectUrl = null;

      this.checkoutAckEl = ref(root, '[data-ai-checkout-ack]');
      this.ackGeneratedInput = ref(root, '[data-ai-ack-generated]');
      this.ackUploadInput = ref(root, '[data-ai-ack-upload]');
      this.ackUpscaleInput = ref(root, '[data-ai-ack-upscale]');
      this.checkoutNotesEl = ref(root, '[data-ai-checkout-notes]');
      this.checkoutUpscaleBlock = ref(root, '[data-ai-ack-block="upscale"]');
      this.ipScanPanel = ref(root, '[data-ai-ip-scan-panel]');
      this.ipScanRunBtn = ref(root, '[data-ai-ip-scan-run]');
      this.ipScanRunLabel = ref(root, '[data-ai-ip-scan-run-label]');
      this.ipScanSpinner = ref(root, '[data-ai-ip-scan-spinner]');
      this.ipScanBadge = ref(root, '[data-ai-ip-scan-badge]');
      this.ipScanSummary = ref(root, '[data-ai-ip-scan-summary]');
      this.ipScanFindings = ref(root, '[data-ai-ip-scan-findings]');
      this.ipScanReportLink = ref(root, '[data-ai-ip-scan-report]');
      this.ipScanStage = ref(root, '[data-ai-ip-scan-stage]');
      this.ipScanThumb = ref(root, '[data-ai-ip-scan-thumb]');
      this.ipScanStatus = ref(root, '[data-ai-ip-scan-status]');
      this.ipScanProgress = ref(root, '[data-ai-ip-scan-progress]');
      this._ipScanRunning = false;
      this._ipScanTheaterTimer = null;
      this._ipScanPhaseIndex = 0;
      this.#cacheTextTargets();

      this.root.wallprintAiGenerator = this;
      this.reportError = (message) => this.#showError(message);
      this.scrollToStudioSection = (section) => this.#scrollToSection(section);
      this.hasPreviewShapeContext = () => this.#hasPreviewShapeContext();
      this.promptPreviewShapeSetup = (options) => this.#promptPreviewShapeSetup(options);
      document.dispatchEvent(
        new CustomEvent('wallprint:ai-generator-ready', { detail: { root: this.root } })
      );

      this.#bindEvents();
      this.#bindAcknowledgements();
      this.#initLayoutPreference();
      this.#syncPreviewExpandToggles();
      this.#syncPromptPanelToggleUi();
      this.#initStyledSelects();
      this.#upgradeLegacyDesignToolsChrome();
      this.#bindToolbarOverflowMenu();
      this.#initDesignToolsTray();
      this.#initInstallationUnavailable();
      this.#syncPreviewPlaceholder(this.#computeMeasureState());
      this.#syncRefineUi();
      this.#syncPreviewActions();
      this.#syncDesignEmphasis();
      if (this.skeleton) this.skeleton.hidden = true;
      this.preview?.classList.remove('is-preview-busy');
      this.root.classList.remove('is-loading', 'is-tiling');
      if (this.refineLoginLink && this.accountLoginUrl) {
        this.refineLoginLink.href = this.accountLoginUrl;
      }
      if (this.tileLoginLink && this.accountLoginUrl) {
        this.tileLoginLink.href = this.accountLoginUrl;
      }
      this.#syncUnitUI();
      if (this.unit === 'imperial' && this.customBleedInput && this.customBleedInput.value !== '') {
        this.customBleedInput.value = String(
          this.#roundTrimDisp(this.#fromCm(toNum(this.customBleedInput.value, this.defaultMargin)))
        );
      }
      this.#syncCustomBleedConstraints();
      this.#updateMeasureFast();
      this.calculate({ layout: false });
      this.#updateAckApplicability();
      this.#setNoteForSource('generated');

      this.#initHeaderSlider();
      this.#initHeroScrollPerf();
      this.#schedulePreviewLayout(true);

      const runDeferredInit = () => {
        this.#bindScrollCues();
        this.#initPortfolioThumbSliders();
        this.calculate({ layout: true });
        if (this.#isWideLayoutView()) {
          this.bootstrapWidePreviewLayout();
        } else {
          this._previewFitKey = '';
          this.#schedulePreviewLayout(true);
        }
        this.#markPreviewLayoutReady();
        // API health is checked on first generate/upload — not on page load (avoids
        // iOS/Android "local network" prompts when dev_api points at localhost/ngrok).
      };

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(runDeferredInit, { timeout: 400 });
      } else {
        window.setTimeout(runDeferredInit, 0);
      }

      // Re-open the dialog after a successful contact-form POST. The success
      // element is only server-rendered for the form that actually posted, so
      // its presence is a precise signal (works with multiple sections too).
      const consultSuccess = this.root.querySelector('.ai-generator__consult-success');
      if (consultSuccess) {
        if (this.consultInline?.contains(consultSuccess)) {
          this.consultInline.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          this.openConsultant();
        }
      }
    }

    /** Tiny explore-link thumbnails — crossfade only while visible. */
    #initPortfolioThumbSliders() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.root.querySelectorAll('[data-ai-explore-thumb-slider]').forEach((slider) => {
        const slides = Array.from(slider.querySelectorAll('[data-ai-explore-thumb]'));
        if (slides.length <= 1 || reducedMotion) return;

        let index = slides.findIndex((s) => s.classList.contains('is-active'));
        if (index < 0) index = 0;
        let timer = null;

        const tick = () => {
          slides[index]?.classList.remove('is-active');
          index = (index + 1) % slides.length;
          slides[index]?.classList.add('is-active');
        };

        const start = () => {
          if (timer) return;
          timer = window.setInterval(tick, 2800);
        };
        const stop = () => {
          if (!timer) return;
          clearInterval(timer);
          timer = null;
        };

        if (typeof IntersectionObserver === 'undefined') {
          start();
          return;
        }

        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) start();
            else stop();
          },
          { threshold: 0.05 }
        );
        observer.observe(slider);
      });
    }

    /** Background hero slider — images/videos behind the header copy and nav buttons. */
    #initHeaderSlider() {
      const header = this.root.querySelector('[data-ai-header-slider]');
      if (!header) return;

      const slides = Array.from(header.querySelectorAll('.ai-generator__header-slide'));
      const dots = Array.from(header.querySelectorAll('[data-ai-header-dot]'));
      const autoplay = header.dataset.sliderAutoplay !== 'false';
      const intervalSec = Math.max(3, parseInt(header.dataset.sliderInterval || '6', 10) || 6);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let index = slides.findIndex((s) => s.classList.contains('is-active'));
      if (index < 0) index = 0;

      const syncVideos = () => {
        if (this._heroMediaPaused) return;
        slides.forEach((slide, i) => {
          const video = slide.querySelector('video');
          if (!video) return;
          if (i === index) {
            if (video.preload === 'none') video.preload = 'metadata';
            video.play().catch(() => {});
          } else {
            video.pause();
            try {
              video.currentTime = 0;
            } catch {
              /* ignore */
            }
          }
        });
      };

      let timer = null;
      const goTo = (nextIndex) => {
        slides[index]?.classList.remove('is-active');
        dots[index]?.classList.remove('is-active');
        dots[index]?.setAttribute('aria-selected', 'false');

        index = (nextIndex + slides.length) % slides.length;

        slides[index]?.classList.add('is-active');
        dots[index]?.classList.add('is-active');
        dots[index]?.setAttribute('aria-selected', 'true');
        syncVideos();
      };

      const stopAutoplay = () => {
        if (!timer) return;
        clearInterval(timer);
        timer = null;
      };
      const restartAutoplay = () => {
        stopAutoplay();
        if (!autoplay || reducedMotion || this._heroMediaPaused || slides.length <= 1) return;
        timer = setInterval(() => goTo(index + 1), intervalSec * 1000);
      };

      const pauseAllVideos = () => {
        header.querySelectorAll('video').forEach((video) => {
          video.pause();
        });
      };

      this._heroMediaPaused = false;
      this._heroHeaderMedia = {
        pause: () => {
          this._heroMediaPaused = true;
          stopAutoplay();
          pauseAllVideos();
        },
        resume: () => {
          this._heroMediaPaused = false;
          syncVideos();
          restartAutoplay();
        },
      };

      if (slides.length <= 1) {
        syncVideos();
        restartAutoplay();
        return;
      }

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const target = parseInt(dot.dataset.aiHeaderDot || '0', 10);
          if (target === index) return;
          goTo(target);
          restartAutoplay();
        });
      });

      syncVideos();
      restartAutoplay();

      header.addEventListener('mouseenter', stopAutoplay);
      header.addEventListener('mouseleave', restartAutoplay);
    }

    /**
     * Hero rolls up and collapses when scrolling toward the design studio.
     */
    #initHeroScrollPerf() {
      const inner = this.root.querySelector('.ai-generator__inner--hero-scroll');
      const header =
        this.root.querySelector('.ai-generator__header--has-slider') ||
        this.root.querySelector('[data-ai-header-slider]');
      const anchor =
        this.root.querySelector('[data-ai-section="design"]') ||
        this.root.querySelector('.ai-generator__main');
      if (!inner || !header) return;

      let fullHeaderHeight = 0;
      let rafPending = false;

      const measureHeader = () => {
        inner.classList.remove('ai-generator__inner--hero-rolled');
        header.style.removeProperty('max-height');
        const h = Math.round(header.getBoundingClientRect().height);
        if (h > 80) fullHeaderHeight = h;
        if (fullHeaderHeight > 0) {
          const px = `${fullHeaderHeight}px`;
          inner.style.setProperty('--hero-header-h', px);
          header.style.setProperty('--hero-header-h', px);
        }
      };

      const getHeroRollProgress = () => {
        if (fullHeaderHeight < 80) return 0;

        const innerTop = inner.getBoundingClientRect().top;
        const scrollPast = Math.max(0, -innerTop);
        // Banner scrolls away with the page; complete the fold over ~72% of its
        // height so it's fully folded shortly before it leaves the viewport.
        const span = Math.max(120, fullHeaderHeight * 0.72);
        let roll = Math.min(1, scrollPast / span);

        if (anchor) {
          const anchorTop = anchor.getBoundingClientRect().top;
          const anchorTrigger = Math.min(180, fullHeaderHeight * 0.48);
          const anchorProgress = Math.max(0, anchorTrigger - anchorTop);
          roll = Math.max(roll, Math.min(1, anchorProgress / span));
        }

        return roll;
      };

      const applyRoll = (roll) => {
        const value = roll.toFixed(3);
        inner.style.setProperty('--hero-roll', value);
        header.style.setProperty('--hero-roll', value);
        inner.classList.toggle('ai-generator__inner--hero-rolled', roll >= 0.97);
        inner.classList.toggle('ai-generator__inner--hero-at-rest', roll < 0.04);
        if (this._heroHeaderMedia) {
          if (roll < 0.04) this._heroHeaderMedia.resume();
          else this._heroHeaderMedia.pause();
        }
      };

      const update = () => {
        rafPending = false;
        applyRoll(getHeroRollProgress());
      };

      const scheduleUpdate = () => {
        if (rafPending) return;
        rafPending = true;
        requestAnimationFrame(update);
      };

      window.addEventListener('scroll', scheduleUpdate, { passive: true });
      document.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true });
      window.addEventListener('resize', () => {
        measureHeader();
        scheduleUpdate();
      }, { passive: true });
      inner.style.setProperty('--hero-roll', '0');
      header.style.setProperty('--hero-roll', '0');
      measureHeader();
      scheduleUpdate();
      // Resume after layout changes below the header. Do NOT call measureHeader()
      // here: it strips the rolled class / max-height to re-measure, which makes a
      // scrolled-away banner flash back to full size for a frame. The header's full
      // height only changes on resize, which has its own measureHeader() call.
      this._heroScrollResume = () => {
        scheduleUpdate();
      };
    }

    #bindEvents() {
      this.generateButton?.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.mode === 'generate') this.generate();
      });

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        if (this.mode === 'generate') this.generate();
      });

      [this.widthInput, this.heightInput].forEach((el) => {
        el?.addEventListener('input', () => this.#onMeasureInput());
        el?.addEventListener('focusin', () => this._heroHeaderMedia?.pause());
        el?.addEventListener('blur', () => this.#onMeasureBlur());
      });
      this.#bindToolbarWallMeasure();
      this.removeMargin?.addEventListener('change', () => this.calculate());
      this.customBleedInput?.addEventListener('input', () => this.#onMeasureInput());
      this.customBleedInput?.addEventListener('change', () => {
        if (this.memberActive && this.customBleedInput) {
          const fallbackCm = this.defaultMargin;
          const rawCm = this.#toCm(toNum(this.customBleedInput.value, this.#fromCm(fallbackCm)));
          const clampedCm = Math.max(0, Math.min(20, rawCm));
          this.customBleedInput.value = String(this.#roundTrimDisp(this.#fromCm(clampedCm)));
        }
        this.calculate();
      });
      this.wallReadyDismiss?.addEventListener('click', () => {
        this._wallReadyDismissed = true;
        this.wallReadyEl?.classList.remove('is-visible');
        if (this.wallReadyEl) this.wallReadyEl.hidden = true;
      });
      this.refineBtn?.addEventListener('click', () => this.refine());
      this.#bindPaletteAndStyleStarters();
      this.roomDialogClose?.addEventListener('click', () => this.roomDialog?.close());
      this.#bindRoomDialogResize();
      this.roomDialog?.addEventListener('click', (e) => {
        if (e.target === this.roomDialog) this.roomDialog.close();
      });
      this.roomFileInput?.addEventListener('change', () => {
        this._roomPhotoFile = this.roomFileInput?.files?.[0] || null;
        this._roomPhotoCompressed = null;
        if (this._roomPhotoFile) this.#clearStockRoomSelection();
        this.#syncRoomDialogUi();
        this.#syncWallPhotoUi();
        this.#syncRoomUploadPreview();
      });
      this.roomFinishStandardBtn?.addEventListener('click', () => this.#setMockupFinishMode('standard'));
      this.roomFinishPhotorealBtn?.addEventListener('click', () => this.#setMockupFinishMode('photoreal'));
      this.roomEnhanceBtn?.addEventListener('click', () => this.runRoomEnhance());
      this.roomExclusionAddBtn?.addEventListener('click', () => this.#addRoomExclusion());
      this.roomCornerToggle?.addEventListener('change', () => this.#syncCornerPinUi());
      this.#bindCornerPinDrag();
      this.#ensureRoomEditorLayoutObserver();
      this.roomRunBtn?.addEventListener('click', () => {
        if (this.isMockup) return;
        if (!this.#canRunRoomMockup()) {
          if (this.roomStatusEl) {
            this.roomStatusEl.hidden = false;
            this.roomStatusEl.textContent = 'Choose a room photo or pick a stock room below.';
          }
          if (!this._stockRoomSelection) this.roomFileInput?.click();
          return;
        }
        this.runRoomMockup();
      });
      this.roomMockupThumbWrap?.addEventListener('click', () => this.#openRoomDialog());
      this.root?.addEventListener('wallprint:hub-placement-changed', () => {
        this._previewFitKey = '';
        this.#schedulePreviewLayout(true);
      });
      this.#bindPreviewSizeResize();
      this.#syncPreviewSizeResizeUi(true);
      this.#bindPromptSizeResize();
      this.#initSidebarWidth();
      this.#bindSidebarWidthResize();
      this.#initPromptHeight();
      this.root?.addEventListener('wallprint:layout-mode-changed', () => {
        this.#initPromptHeight();
        this.#clampSidebarWidth();
        this.#initStyledSelects();
        this.#syncPromptPanelToggleUi();
        if (this.#isWideLayoutView()) {
          window.requestAnimationFrame(() => {
            this.#ensureWidePreviewRowMinimum();
            this.bootstrapWidePreviewLayout();
            this.#syncWideViewPreviewRowResizeUi();
            this.#syncPreviewSizeResizeUi(true);
          });
        } else {
          this.#syncPreviewSizeResizeUi(true);
        }
      });
      window.addEventListener('resize', () => {
        window.clearTimeout(this._sidebarClampTimer);
        this._sidebarClampTimer = window.setTimeout(() => this.#clampSidebarWidth(), 120);
      });
      this.previewTrimToggle?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#togglePreviewTrimPop();
      });
      this.previewFitOutBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#fitPreviewCropOut();
      });
      for (const btn of this.previewTrimSkirtingBtns) {
        btn.addEventListener('click', () => {
          this._previewTrim.skirting = btn.dataset.aiPreviewTrimSkirting || 'none';
          this.#syncPreviewTrimUi();
          this.#schedulePreviewTrimUpdate();
        });
      }
      for (const btn of this.previewTrimCorniceBtns) {
        btn.addEventListener('click', () => {
          this._previewTrim.cornice = btn.dataset.aiPreviewTrimCornice || 'none';
          this.#syncPreviewTrimUi();
          this.#schedulePreviewTrimUpdate();
        });
      }
      for (const btn of this.previewTrimColorBtns) {
        btn.addEventListener('click', () => {
          this._previewTrim.color = btn.dataset.aiPreviewTrimColor || 'white';
          this.#syncPreviewTrimUi();
          this.#schedulePreviewTrimUpdate();
        });
      }
      this.wallPhotoTrigger?.addEventListener('click', () => this.wallPhotoInput?.click());
      this.wallPhotoInput?.addEventListener('change', () => this.#onWallPhotoSelected());
      this.wallPhotoRemove?.addEventListener('click', () => this.#clearWallPhoto());
      this.wallPhotoInfo?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#toggleWallPhotoPop();
      });
      this.wallBleedInfo?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#toggleWallBleedPop();
      });
      document.addEventListener('click', (e) => {
        if (this.wallPhotoPop && !this.wallPhotoPop.hidden && !this.wallPhotoWrap?.contains(e.target)) {
          this.#toggleWallPhotoPop(false);
        }
        if (this.wallBleedPop && !this.wallBleedPop.hidden && !this.wallBleedWrap?.contains(e.target)) {
          this.#toggleWallBleedPop(false);
        }
        if (
          this.promptCoachHelpPop &&
          !this.promptCoachHelpPop.hidden &&
          !this.promptCoachHelpPop.contains(e.target) &&
          !this.promptCoachInfo?.contains(e.target)
        ) {
          this.promptCoachHelpPop.hidden = true;
          this.promptCoachInfo?.setAttribute('aria-expanded', 'false');
        }
        if (
          this.previewTrimPop &&
          !this.previewTrimPop.hidden &&
          !this.previewTrimPop.contains(e.target) &&
          !this.previewTrimToggle?.contains(e.target)
        ) {
          this.#togglePreviewTrimPop(false);
        }
        if (
          this.toolbarWallMeasurePop &&
          !this.toolbarWallMeasurePop.hidden &&
          !this.toolbarWallMeasureWrap?.contains(e.target)
        ) {
          this.#toggleToolbarWallMeasurePop(false);
        }
      });
      this.repeatPreviewBtn?.addEventListener('click', () => this.#openRepeatPreview());
      this.repeatThumbWrap?.addEventListener('click', () => {
        if (this.repeatThumbWrap?.classList.contains('is-placeholder')) return;
        if (!this.repeatPreviewBtn?.disabled) this.#openRepeatPreview();
      });
      this.repeatDialogClose?.addEventListener('click', () => this.repeatDialog?.close());
      this.wallpaperLayoutSelect?.addEventListener('change', () => {
        this.#syncWallpaperLayoutUi();
        this.#persistCropToAsset();
      });
      this.patternRepeatInput?.addEventListener('input', () => {
        this.#syncWallpaperLayoutUi();
        this.#persistCropToAsset();
      });
      this.seamlessRepeatInput?.addEventListener('change', () => {
        if (this.seamlessRepeatInput?.checked && this.wallpaperLayoutSelect?.value === 'single') {
          this.wallpaperLayoutSelect.value = 'full';
        }
        this.#syncWallpaperLayoutUi();
        this.#syncSeamlessHint();
        this.#syncRepeatToggleLabel();
      });
      this.regionSelect?.addEventListener('change', () => this.calculate());

      this.aspectSelect?.addEventListener('change', () => {
        this._wallLayoutKey = '';
        this._previewFitKey = '';
        this.calculate();
        this.#resetResult();
      });

      this.scrollButtons.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const section = btn.dataset.aiScroll;
          if (section) {
            this.#scrollToSection(section);
            if (btn.dataset.aiScrollFocus === 'prompt' && this.promptInput instanceof HTMLElement) {
              window.setTimeout(() => {
                this.promptInput.focus({ preventScroll: true });
              }, 420);
            }
          }
        });
      });

      Array.from(this.root.querySelectorAll('[data-ai-wall-focus]')).forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const targetId = btn.dataset.aiWallFocus;
          const target = targetId ? document.getElementById(targetId) : null;
          if (!target) return;
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          const focusable = target.querySelector(
            'input:not([type="hidden"]):not([disabled]), select:not([disabled]), button:not([disabled]), textarea:not([disabled])'
          );
          focusable?.focus({ preventScroll: true });
        });
      });

      this.unitButtons.forEach((btn) =>
        btn.addEventListener('click', () => this.setUnit(btn.dataset.aiUnit))
      );

      // Upload interactions
      this.uploadTrigger?.addEventListener('click', () => this.#openUploadPicker());

      this.fileInput?.addEventListener('change', () => {
        const file = this.fileInput.files?.[0];
        if (file) this.handleFile(file);
      });

      if (this.dropzone) {
        ['dragenter', 'dragover'].forEach((evt) =>
          this.dropzone.addEventListener(evt, (e) => {
            e.preventDefault();
            this.dropzone.classList.add('is-dragover');
          })
        );
        ['dragleave', 'drop'].forEach((evt) =>
          this.dropzone.addEventListener(evt, (e) => {
            e.preventDefault();
            this.dropzone.classList.remove('is-dragover');
          })
        );
        this.dropzone.addEventListener('drop', (e) => {
          const file = e.dataTransfer?.files?.[0];
          if (file) this.handleFile(file);
        });
      }

      this.addToCartButton?.addEventListener('click', () => this.addToCart());

      this.root.querySelectorAll('[data-ai-pro-teaser-select]').forEach((select) => {
        const defaultValue = select.value;
        select.addEventListener('change', () => {
          select.value = defaultValue;
        });
      });

      this.root.querySelectorAll('[data-ai-pro-teaser-input]').forEach((input) => {
        const blockEdit = (event) => {
          event.preventDefault();
          input.blur();
        };
        input.addEventListener('keydown', blockEdit);
        input.addEventListener('paste', blockEdit);
        input.addEventListener('focus', () => input.blur());
      });

      this.alignGridToggle?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.#handleLockedAlignGrid(event, this.alignGridToggle)) return;
        this.#toggleAlignGrid();
      });
      this.wallOverlayToggle?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.#handleLockedPanelGuides(event, this.wallOverlayToggle)) return;
        this.#togglePanelGuides();
      });
      this.wallGuidesToggles.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.#handleLockedPanelGuides(event, btn)) return;
          this.#togglePanelGuides();
        });
      });

      this.#bindToolbarCapture();
      this.roomMockupBtn?.addEventListener('click', (event) => {
        if (!(this.roomMockupBtn instanceof HTMLButtonElement)) return;
        event.preventDefault();
        event.stopPropagation();
        if (this.#handleLockedToolbarAction(event, this.roomMockupBtn)) return;
        this.#openRoomDialog();
      });

      this.previewZoomClose?.addEventListener('click', () => this.#closePreviewZoom());
      this.previewZoomDialog?.addEventListener('click', (event) => {
        if (event.target === this.previewZoomDialog) this.#closePreviewZoom();
      });
      this.previewZoomDialog?.addEventListener('cancel', () => this.#closePreviewZoom());
      this.zoomPanelsToggle?.addEventListener('click', () => {
        this._zoomPanelsOn = !this._zoomPanelsOn;
        this.#syncZoomPanels();
      });
      this.previewZoomImg?.addEventListener('load', () => this.#renderZoomOverlay());

      this.root.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-ai-preview-expand-toggle]');
        if (!toggle || !this.root.contains(toggle)) return;
        event.preventDefault();
        this.#setPreviewExpanded(!this._previewExpanded);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this._previewExpanded) {
          this.#setPreviewExpanded(false);
        }
      });

      for (const def of ALL_ADJUSTMENT_DEFS) {
        this[def.inputKey]?.addEventListener('input', () => this.#onAdjustInput());
      }
      this.#bindCmykChannelLocks();
      this.adjustResetBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#resetAdjustments(true);
      });
      this.root.querySelectorAll('[data-ai-panel-info]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
      });
      this.adjustDropdown?.addEventListener('toggle', () => {
        if (this.adjustDropdown?.open && this.tilingDropdown?.open) {
          this.tilingDropdown.open = false;
        }
        this.calculate({ layout: true });
        this._previewFitKey = '';
        this.#schedulePreviewLayout(true);
      });
      this.cmykDropdown?.addEventListener('toggle', () => {
        this.calculate({ layout: true });
      });
      this.tilingDropdown?.addEventListener('toggle', () => {
        if (this.tilingDropdown?.open && this.adjustDropdown?.open) {
          this.adjustDropdown.open = false;
        }
        this.calculate({ layout: true });
        this.#scheduleRepeatThumbUpdate();
        this._previewFitKey = '';
        this.#schedulePreviewLayout(true);
      });
      this.promptCollapseBtn?.addEventListener('click', (event) => {
        event.stopPropagation();
        if (!this.#isWideLayoutView()) {
          this.#setPreviewExpanded(true);
        }
      });
      this.promptReopenBtn?.addEventListener('click', () => this.#setPromptCollapsed(false, { manual: true }));
      this.#syncAdjustControlUi();
      this.#syncPreviewStatusBar();
      this.#syncToolbarDensity();
      this.#bindPreviewProtection();
      this.#syncRepeatToggleLabel();

      // Consultant dialog
      this.consultOpenButton?.addEventListener('click', () => this.openConsultant());
      this.consultCloseButton?.addEventListener('click', () => this.consultDialog?.close());
      this.consultDialog?.addEventListener('click', (event) => {
        // Close when the backdrop (the dialog element itself) is clicked.
        if (event.target === this.consultDialog) this.consultDialog.close();
      });
      this.consultForms.forEach((form) => {
        form.addEventListener('submit', (event) => this.#handleConsultSubmit(event));
        form.querySelectorAll('[data-ai-consult-file]').forEach((input) => {
          input.addEventListener('change', () => this.#onConsultFileChange(input));
        });
      });

      let resizeTimer;
      const onWindowLayoutChange = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const state = this.calculate({ layout: true });
          if (!this.cropViewport?.hidden) this.#refreshUploadCropLayout(true);
          this.#syncToolbarDensity();
          this._heroScrollResume?.();
        }, 200);
      };
      window.addEventListener('resize', onWindowLayoutChange, { passive: true });

      if (typeof ResizeObserver !== 'undefined' && this.designPreviewCol) {
        this.previewResizeObserver = new ResizeObserver((entries) => {
          const colEntry =
            entries.find((e) => e.target === this.designPreviewCol) || entries[0];
          const width = Math.round(colEntry?.contentRect?.width || 0);
          const height = Math.round(colEntry?.contentRect?.height || 0);
          const sizeKey = `${width}x${height}`;
          if (width < 80 && height < 80) return;
          if (sizeKey === this._previewColSizeKey) return;
          this._previewColSizeKey = sizeKey;
          this._previewColWidth = width;
          this._previewFitKey = '';
          this.#schedulePreviewLayout();
          if (entries.some((e) => e.target === this.wallStage || e.target === this.wallPreview)) {
            this._wallLayoutKey = '';
            this.#scheduleWallLayoutUpdate();
          }
          if (entries.some((e) => e.target === this.repeatThumbWrap)) {
            this.#scheduleRepeatThumbUpdate();
          }
          this.#syncToolbarDensity();
        });
        this.previewResizeObserver.observe(this.designPreviewCol);
        if (this.designGrid) this.previewResizeObserver.observe(this.designGrid);
        if (this.designControls) this.previewResizeObserver.observe(this.designControls);
        const rail = this.root.querySelector('.ai-generator__design-preview-rail');
        const studio = this.root.querySelector('.ai-generator__design-studio');
        const previewBody = this.root.querySelector('.ai-generator__design-preview-body');
        if (rail) this.previewResizeObserver.observe(rail);
        if (studio) this.previewResizeObserver.observe(studio);
        if (previewBody) this.previewResizeObserver.observe(previewBody);
        if (this.wallStage) this.previewResizeObserver.observe(this.wallStage);
        if (this.wallPreview) this.previewResizeObserver.observe(this.wallPreview);
        if (this.repeatThumbWrap) {
          this.previewResizeObserver.observe(this.repeatThumbWrap);
        }
        if (this.designHub) {
          this.previewResizeObserver.observe(this.designHub);
        }
      }

      this.#bindPromptVoice();
      this.#bindPromptCoach();
    }

    #bindPromptVoice() {
      if (this.root.dataset.wpPromptVoiceBound === '1') return;

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

      const hideIfUnsupported = () => {
        const buttons = Array.from(this.root.querySelectorAll('[data-ai-prompt-voice]'));
        if (!SpeechRecognition) {
          buttons.forEach((btn) => {
            btn.hidden = true;
          });
          return false;
        }
        return true;
      };

      if (!hideIfUnsupported()) return;

      this.root.dataset.wpPromptVoiceBound = '1';

      let recognition = null;
      let listeningBtn = null;
      let voiceBase = '';
      let voiceInterim = '';
      let activeInput = null;

      const getComposerParts = (btn) => {
        const composer = btn.closest('.ai-generator__prompt-composer');
        return {
          input: composer?.querySelector('textarea') || null,
          hint: composer?.querySelector('[data-ai-prompt-voice-hint]') || null,
        };
      };

      const applyValue = () => {
        if (!activeInput) return;
        const max = Number(activeInput.maxLength) || 800;
        const spoken = voiceInterim ? `${voiceBase} ${voiceInterim}`.trim() : voiceBase.trim();
        activeInput.value = spoken.slice(0, max);
        activeInput.dispatchEvent(new Event('input', { bubbles: true }));
      };

      const setListening = (btn, on) => {
        btn.classList.toggle('is-listening', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = btn.querySelector('[data-ai-prompt-voice-label]');
        if (label) label.textContent = on ? 'Stop' : 'Talk';
        btn.setAttribute('aria-label', on ? 'Stop voice input' : 'Talk instead of type');
        btn.title = on ? 'Stop listening' : 'Talk instead of type';
        const { hint } = getComposerParts(btn);
        if (hint) hint.hidden = !on;
        this.root.classList.toggle('is-prompt-voice-active', on);
      };

      const stopListening = () => {
        try {
          recognition?.stop();
        } catch (_) {
          /* ignore */
        }
      };

      const reportVoiceError = (event) => {
        if (event?.error === 'aborted' || event?.error === 'no-speech') return;
        if (listeningBtn) setListening(listeningBtn, false);
        listeningBtn = null;
        activeInput = null;
        recognition = null;
        this.root.classList.remove('is-prompt-voice-active');
        if (event?.error === 'not-allowed') {
          this.#showError(
            'Microphone access was blocked. Allow it in your browser settings to talk your prompt.'
          );
        } else if (event?.error === 'network') {
          this.#showError(
            'Voice input needs an internet connection (browser speech uses online recognition). Type your prompt instead, or check your connection.'
          );
        } else if (event?.error) {
          this.#showError('Voice input hit a snag — try again or type your prompt.');
        }
      };

      const startListening = (btn) => {
        const { input } = getComposerParts(btn);
        if (!input || input.disabled) {
          if (input === this.refinePromptInput) {
            this.#showError('Log in to refine with voice, or use the main prompt above.');
          }
          return;
        }

        try {
          recognition = new SpeechRecognition();
          recognition.continuous = true;
          recognition.interimResults = true;
          const pageLang = (document.documentElement.lang || '').trim();
          recognition.lang = pageLang && pageLang.startsWith('en') ? pageLang : 'en-GB';

          voiceBase = input.value;
          voiceInterim = '';
          activeInput = input;
          listeningBtn = btn;

          recognition.onresult = (event) => {
            let finalChunk = '';
            let interimChunk = '';
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
              const result = event.results[i];
              const text = result[0]?.transcript || '';
              if (result.isFinal) finalChunk += text;
              else interimChunk += text;
            }
            if (finalChunk) {
              voiceBase = `${voiceBase} ${finalChunk}`.trim();
              voiceInterim = '';
            } else {
              voiceInterim = interimChunk;
            }
            applyValue();
          };

          recognition.onerror = reportVoiceError;

          recognition.onend = () => {
            if (listeningBtn) {
              if (voiceInterim) voiceBase = `${voiceBase} ${voiceInterim}`.trim();
              voiceInterim = '';
              applyValue();
              setListening(listeningBtn, false);
              listeningBtn = null;
            }
            activeInput = null;
            recognition = null;
            this.root.classList.remove('is-prompt-voice-active');
          };

          recognition.start();
          setListening(btn, true);
          input.focus();
        } catch (_) {
          this.#showError('Voice input is not supported in this browser.');
        }
      };

      this.root.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-ai-prompt-voice]');
        if (!btn || !this.root.contains(btn) || btn.hidden || btn.disabled) return;
        event.preventDefault();
        if (listeningBtn === btn) {
          stopListening();
          return;
        }
        if (listeningBtn) stopListening();
        startListening(btn);
      });

      this.generateButton?.addEventListener('click', () => {
        if (listeningBtn) stopListening();
      });
      this.root.querySelector('[data-ai-refine]')?.addEventListener('click', () => {
        if (listeningBtn) stopListening();
      });

      requestAnimationFrame(() => hideIfUnsupported());
    }

    #bindPromptCoach() {
      if (!this.promptCoachToggle || !this.promptCoachPanel) return;

      const welcomeText =
        'Hi — describe your wallpaper idea, or say how to change your current prompt. I will ask a quick question or two, then polish it for Generate.';

      const setCoachHelpOpen = (open) => {
        if (!this.promptCoachHelpPop) return;
        this.promptCoachHelpPop.hidden = !open;
        this.promptCoachInfo?.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      this.promptCoachInfo?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setCoachHelpOpen(this.promptCoachHelpPop?.hidden);
      });

      const setPanelOpen = (open) => {
        this.promptCoachPanel.hidden = !open;
        this.promptCoachToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.promptCoachToggle.classList.toggle('is-open', open);
        this.promptCoachToggle
          .closest('[data-ai-prompt-coach-wrap]')
          ?.classList.toggle('is-open', open);
        this.generatePanel?.classList.toggle('is-coach-open', open);
        this.root.classList.toggle('is-coach-open', open);
        if (open && !this.promptCoachMessages?.childElementCount) {
          this.#appendPromptCoachMessage('assistant', welcomeText, { type: true });
        }
        if (open) {
          try {
            if (!localStorage.getItem(COACH_HELP_SEEN_KEY)) {
              localStorage.setItem(COACH_HELP_SEEN_KEY, '1');
              setCoachHelpOpen(true);
            }
          } catch {
            /* ignore */
          }
          this.promptCoachInput?.focus();
        } else {
          setCoachHelpOpen(false);
          this.#clearPromptCoachTyping({ complete: true });
        }
        this.#clearDesignGridHeight();
        requestAnimationFrame(() => {
          try {
            const bounds = this.#computePreviewBounds();
            this.#syncDesignGridHeight(bounds);
          } catch {
            /* layout optional */
          }
          this.#schedulePreviewLayout(true);
        });
      };

      this.promptCoachToggle.addEventListener('click', () => {
        setPanelOpen(this.promptCoachPanel.hidden);
      });

      this.promptCoachClose?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setPanelOpen(false);
      });

      if (!this.promptCoachPanel.hidden) {
        setPanelOpen(true);
      }

      const sendCoachMessage = async (text) => {
        const content = String(text || '').trim();
        if (!content || this.promptCoachBusy) return;

        this.promptCoachBusy = true;
        if (this.promptCoachSend) this.promptCoachSend.disabled = true;
        if (this.promptCoachInput) this.promptCoachInput.disabled = true;
        this.promptCoachChips.forEach((chip) => {
          chip.disabled = true;
        });

        this.#appendPromptCoachMessage('user', content);
        this.promptCoachHistory.push({ role: 'user', content });
        if (this.promptCoachInput) this.promptCoachInput.value = '';
        this.#appendPromptCoachMessage('assistant', '…', { pending: true });

        try {
          const url = this.#customerGatedEndpointUrl(this.promptAssistantEndpoint);
          const res = await fetch(url, {
            method: 'POST',
            headers: this.#apiHeaders({ json: true, url: this.promptAssistantEndpoint }),
            body: JSON.stringify({
              messages: this.promptCoachHistory,
              current_prompt: String(this.promptInput?.value || '').trim(),
              seamless: Boolean(this.seamlessRepeatInput?.checked),
            }),
          });
          const data = await res.json().catch(() => ({}));
          this.#removePromptCoachPending();

          if (!res.ok) {
            const msg =
              data?.message ||
              (res.status === 429
                ? 'Daily assistant limit reached — type your prompt directly for now.'
                : 'Assistant unavailable — try again or type your prompt.');
            this.#appendPromptCoachMessage('assistant', msg);
            this.promptCoachHistory.push({ role: 'assistant', content: msg });
            return;
          }

          const reply = String(data.reply || '').trim() || 'Tell me more about mood or colours.';
          this.#appendPromptCoachMessage('assistant', reply);
          this.promptCoachHistory.push({ role: 'assistant', content: reply });

          this.promptCoachSuggested = data.suggested_prompt || null;
          if (this.promptCoachApply) {
            const ready = Boolean(data.ready_to_generate && this.promptCoachSuggested);
            this.promptCoachApply.hidden = !ready;
          }
        } catch (_) {
          this.#removePromptCoachPending();
          const msg = 'Connection issue — try again in a moment.';
          this.#appendPromptCoachMessage('assistant', msg);
          this.promptCoachHistory.push({ role: 'assistant', content: msg });
        } finally {
          this.promptCoachBusy = false;
          if (this.promptCoachSend) this.promptCoachSend.disabled = false;
          if (this.promptCoachInput) this.promptCoachInput.disabled = false;
          this.promptCoachChips.forEach((chip) => {
            chip.disabled = false;
          });
        }
      };

      this.promptCoachSend?.addEventListener('click', () => {
        sendCoachMessage(this.promptCoachInput?.value || '');
      });

      this.promptCoachInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          sendCoachMessage(this.promptCoachInput?.value || '');
        }
      });

      this.promptCoachChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          sendCoachMessage(chip.textContent || '');
        });
      });

      this.promptCoachApply?.addEventListener('click', () => {
        const next = String(this.promptCoachSuggested || '').trim();
        if (!next || !this.promptInput) return;
        const max = Number(this.promptInput.maxLength) || 800;
        this.promptInput.value = next.slice(0, max);
        this.promptInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.promptInput.focus();
        this.promptCoachApply.hidden = true;
      });
    }

    #appendPromptCoachMessage(role, text, { pending = false, type = false } = {}) {
      if (!this.promptCoachMessages) return;
      const bubble = document.createElement('div');
      bubble.className = `ai-generator__prompt-coach-msg ai-generator__prompt-coach-msg--${role}`;
      if (pending) bubble.dataset.aiPromptCoachPending = 'true';
      this.promptCoachMessages.appendChild(bubble);
      if (type && role === 'assistant' && !pending) {
        this.#typePromptCoachText(bubble, text);
      } else {
        bubble.textContent = text;
      }
      this.promptCoachMessages.scrollTop = this.promptCoachMessages.scrollHeight;
    }

    #clearPromptCoachTyping({ complete = false } = {}) {
      const state = this._promptCoachTyping;
      if (!state) return;
      state.cancelled = true;
      if (state.timer) {
        window.clearTimeout(state.timer);
      }
      const bubble = state.bubble;
      if (bubble) {
        if (complete && bubble.dataset.aiPromptCoachFullText) {
          bubble.textContent = bubble.dataset.aiPromptCoachFullText;
          delete bubble.dataset.aiPromptCoachFullText;
        }
        bubble.classList.remove('is-typing');
        bubble.removeAttribute('aria-busy');
      }
      this._promptCoachTyping = null;
    }

    #typePromptCoachText(bubble, text) {
      if (!bubble) return;
      this.#clearPromptCoachTyping();
      bubble.dataset.aiPromptCoachFullText = text;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        bubble.textContent = text;
        return;
      }

      bubble.textContent = '';
      bubble.classList.add('is-typing');
      bubble.setAttribute('aria-busy', 'true');

      const state = { bubble, cancelled: false, timer: 0, index: 0 };
      this._promptCoachTyping = state;

      const tick = () => {
        if (state.cancelled || !bubble.isConnected) {
          bubble.classList.remove('is-typing');
          bubble.removeAttribute('aria-busy');
          if (bubble.isConnected) bubble.textContent = text;
          return;
        }
        if (state.index >= text.length) {
          bubble.classList.remove('is-typing');
          bubble.removeAttribute('aria-busy');
          delete bubble.dataset.aiPromptCoachFullText;
          this._promptCoachTyping = null;
          return;
        }

        state.index += 1;
        bubble.textContent = text.slice(0, state.index);
        this.promptCoachMessages.scrollTop = this.promptCoachMessages.scrollHeight;

        const char = text[state.index - 1];
        let delay = 32;
        if (char === ' ') delay = 44;
        if (char === ',') delay = 120;
        if (char === '—') delay = 160;
        if (/[.!?]/.test(char)) delay = 420;

        state.timer = window.setTimeout(tick, delay);
      };

      state.timer = window.setTimeout(tick, 280);
    }

    #removePromptCoachPending() {
      this.promptCoachMessages
        ?.querySelector('[data-ai-prompt-coach-pending]')
        ?.remove();
    }

    #bindAcknowledgements() {
      this.ackGeneratedInput?.addEventListener('change', () => this.#syncCheckoutAck());
      this.ackUploadInput?.addEventListener('change', () => this.#syncCheckoutAck());
      this.ackUpscaleInput?.addEventListener('change', () => this.#syncCheckoutAck());
      this.ipScanRunBtn?.addEventListener('click', () => this.#runIpScan());
      this.#syncCheckoutAck();
    }

    #resetAcknowledgements() {
      if (this.ackGeneratedInput) this.ackGeneratedInput.checked = false;
      if (this.ackUploadInput) this.ackUploadInput.checked = false;
      if (this.ackUpscaleInput) this.ackUpscaleInput.checked = false;
      this.#resetIpScan();
      this.checkoutAckEl?.querySelectorAll('[data-ai-ack-row]').forEach((row) => {
        row.classList.remove('is-inactive', 'is-active');
      });
      this.#updateAckApplicability();
    }

    #resetIpScan() {
      if (this.currentAsset) {
        this.currentAsset.ipScan = null;
      }
      this.#setIpScanScanning(false);
      if (this.ipScanRunBtn) this.ipScanRunBtn.disabled = !this.currentAsset;
      if (this.ipScanBadge) {
        this.ipScanBadge.hidden = true;
        this.ipScanBadge.textContent = '';
        this.ipScanBadge.className = 'ai-generator__ip-scan-badge';
      }
      if (this.ipScanSummary) {
        this.ipScanSummary.hidden = true;
        this.ipScanSummary.textContent = '';
      }
      if (this.ipScanFindings) {
        this.ipScanFindings.hidden = true;
        this.ipScanFindings.replaceChildren();
      }
      if (this.ipScanReportLink) {
        this.ipScanReportLink.hidden = true;
        this.ipScanReportLink.removeAttribute('href');
      }
      if (this.ipScanRunLabel) {
        this.ipScanRunLabel.textContent =
          this.root.dataset.ipScanButtonLabel || 'Run IP safety check';
      }
      this.#syncCheckoutAck();
    }

    /**
     * AI-generated vs upload acknowledgement — one active row by design source.
     * @param {ReturnType<AIImageGenerator['calculate']>} [state]
     */
    #updateAckApplicability(state = this.calculate()) {
      if (!this.checkoutAckEl) return;

      const source = this.currentAsset?.source;
      const hasDesign = Boolean(this.currentAsset);
      const active = {
        generated: hasDesign && source === 'generated',
        upload: hasDesign && source === 'uploaded',
      };

      this.checkoutAckEl.querySelectorAll('[data-ai-ack-row]').forEach((row) => {
        const key = row.getAttribute('data-ai-ack-row');
        const isActive = key ? Boolean(active[key]) : false;
        row.classList.toggle('is-inactive', !isActive);
        row.classList.toggle('is-active', isActive);
      });

      this.#syncCheckoutAck();
    }

    /** Show IP terms, NOTE block, and Add to Cart inside the order summary card. */
    #setCheckoutVisible(visible) {
      if (this.resultFooter) this.resultFooter.hidden = !visible;
      if (this.scrollCartBtn) this.scrollCartBtn.hidden = !visible;
      if (visible) {
        this.#updateAckApplicability();
        this.#syncCheckoutAck();
      }
    }

    /** Enable add to cart when design + IP ack (+ upscale ack if needed) are complete. */
    #syncCheckoutAck() {
      const hasDesign = Boolean(this.currentAsset);
      const complete = this.#checkoutAcksComplete();

      if (this.ipScanPanel) {
        this.ipScanPanel.hidden = false;
        this.ipScanPanel.classList.toggle('is-awaiting-design', !hasDesign);
      }
      if (this.checkoutAckEl) {
        this.checkoutAckEl.classList.toggle('is-awaiting-design', !hasDesign);
      }
      if (this.ipScanRunBtn) {
        this.ipScanRunBtn.disabled = !hasDesign || this._ipScanRunning;
      }

      if (this.addToCartButton) {
        this.addToCartButton.disabled = this.isAdding || !complete;
        this.addToCartButton.classList.toggle('is-ack-pending', hasDesign && !complete);
      }

      const prompt = this.checkoutAckEl?.querySelector('[data-ai-ack-prompt]');
      if (prompt) {
        if (!hasDesign) {
          prompt.textContent =
            'Generate or upload a design above, run the IP safety check, and tick the acknowledgement to enable Add to Cart.';
        } else if (!this.#ipScanReady()) {
          prompt.textContent = 'Run the IP safety check, then tick the acknowledgement below.';
        } else if (!this.#activeAckChecked()) {
          prompt.textContent = 'Tick the intellectual property acknowledgement to enable Add to Cart.';
        } else if (this.#needsUpscaleAck() && !this.ackUpscaleInput?.checked) {
          prompt.textContent = 'Confirm the upscaling note below to enable Add to Cart.';
        } else {
          prompt.hidden = true;
          return;
        }
        prompt.hidden = false;
      }
    }

    #activeAckChecked() {
      const source = this.currentAsset?.source;
      if (source === 'generated') return Boolean(this.ackGeneratedInput?.checked);
      if (source === 'uploaded') return Boolean(this.ackUploadInput?.checked);
      return false;
    }

    #needsUpscaleAck() {
      return Boolean(this.checkoutUpscaleBlock && !this.checkoutUpscaleBlock.hidden);
    }

    #ipScanReady() {
      const scan = this.currentAsset?.ipScan;
      if (!scan) return false;
      return scan.status === 'safe' || scan.status === 'warning';
    }

    #checkoutAcksComplete() {
      if (!this.currentAsset) return false;
      if (!this.#ipScanReady()) return false;
      if (this.currentAsset.ipScan?.status === 'unsafe') return false;
      if (!this.#activeAckChecked()) return false;
      if (this.#needsUpscaleAck() && !this.ackUpscaleInput?.checked) return false;
      return true;
    }

    #syncUpscaleAckVisibility(isUpscale = false) {
      if (!this.checkoutUpscaleBlock) return;
      this.checkoutUpscaleBlock.hidden = !isUpscale;
      if (!isUpscale && this.ackUpscaleInput) {
        this.ackUpscaleInput.checked = false;
      }
      this.#syncCheckoutAck();
    }

    #syncIpScanUi() {
      const scan = this.currentAsset?.ipScan;
      if (!scan) {
        if (this.ipScanBadge) {
          this.ipScanBadge.hidden = true;
          this.ipScanBadge.textContent = '';
          this.ipScanBadge.className = 'ai-generator__ip-scan-badge';
        }
        if (this.ipScanSummary) {
          this.ipScanSummary.hidden = true;
          this.ipScanSummary.textContent = '';
        }
        if (this.ipScanFindings) {
          this.ipScanFindings.hidden = true;
          this.ipScanFindings.replaceChildren();
        }
        if (this.ipScanReportLink) {
          this.ipScanReportLink.hidden = true;
          this.ipScanReportLink.removeAttribute('href');
        }
        this.#syncCheckoutAck();
        return;
      }

      const labels = {
        safe: 'Safe',
        warning: 'Review',
        unsafe: 'High risk',
        error: 'Scan error',
      };

      if (this.ipScanBadge) {
        this.ipScanBadge.hidden = false;
        this.ipScanBadge.textContent = labels[scan.status] || scan.status;
        this.ipScanBadge.className = `ai-generator__ip-scan-badge is-${scan.status || 'error'}`;
      }

      if (this.ipScanSummary) {
        this.ipScanSummary.hidden = !scan.summary;
        this.ipScanSummary.textContent = scan.summary || '';
      }

      if (this.ipScanFindings) {
        this.ipScanFindings.replaceChildren();
        const findings = Array.isArray(scan.findings) ? scan.findings : [];
        if (findings.length) {
          this.ipScanFindings.hidden = false;
          for (const item of findings) {
            const li = document.createElement('li');
            li.textContent = `${item.label || 'Finding'} (${item.category || 'other'}) — ${item.detail || ''}`;
            this.ipScanFindings.appendChild(li);
          }
        } else {
          this.ipScanFindings.hidden = true;
        }
      }

      if (this.ipScanReportLink && scan.report_url) {
        this.ipScanReportLink.href = this.#resolveApiAssetUrl(scan.report_url);
        this.ipScanReportLink.hidden = false;
      }

      if (this.ipScanRunLabel) {
        this.ipScanRunLabel.textContent =
          scan.status === 'error' ? 'Re-run IP safety check' : 'Re-run IP safety check';
      }

      this.#syncCheckoutAck();
    }

    #setIpScanProgress(value) {
      if (!this.ipScanProgress) return;
      const clamped = Math.min(96, Math.max(0, value));
      this.ipScanProgress.setAttribute('aria-valuenow', String(Math.round(clamped)));
      const bar = this.ipScanProgress.querySelector('.ai-generator__ip-scan-progress-bar');
      if (bar) bar.style.width = `${clamped}%`;
    }

    #stopIpScanTheater() {
      if (this._ipScanTheaterTimer) {
        clearInterval(this._ipScanTheaterTimer);
        this._ipScanTheaterTimer = null;
      }
      if (this.ipScanStage) this.ipScanStage.hidden = true;
      this.#setIpScanProgress(0);
    }

    #startIpScanTheater() {
      this.#stopIpScanTheater();
      const phases = [
        'Analysing your design…',
        'Checking copyright signals…',
        'Scanning trademarks & brands…',
        'Reviewing likeness matches…',
      ];
      this._ipScanPhaseIndex = 0;
      if (this.ipScanStage) this.ipScanStage.hidden = false;
      if (this.ipScanStatus) this.ipScanStatus.textContent = phases[0];
      if (this.ipScanThumb) {
        const previewUrl =
          this.currentAsset?.previewUrl ||
          this.currentAsset?.thumbUrl ||
          this.preview?.querySelector('img')?.currentSrc ||
          this.preview?.querySelector('img')?.src ||
          '';
        if (previewUrl) {
          this.ipScanThumb.style.backgroundImage = `url("${String(previewUrl).replace(/"/g, '\\"')}")`;
        } else {
          this.ipScanThumb.style.backgroundImage = '';
        }
      }

      let progress = 8;
      this.#setIpScanProgress(progress);
      this._ipScanTheaterTimer = window.setInterval(() => {
        this._ipScanPhaseIndex = (this._ipScanPhaseIndex + 1) % phases.length;
        if (this.ipScanStatus) this.ipScanStatus.textContent = phases[this._ipScanPhaseIndex];
        progress = Math.min(96, progress + 4 + Math.random() * 6);
        this.#setIpScanProgress(progress);
      }, 1400);
    }

    #setIpScanScanning(on) {
      this._ipScanRunning = on;
      this.ipScanPanel?.classList.toggle('is-scanning', on);
      if (this.ipScanSpinner) this.ipScanSpinner.hidden = !on;
      if (this.ipScanBadge) {
        if (on) {
          this.ipScanBadge.hidden = false;
          this.ipScanBadge.textContent = 'Scanning';
          this.ipScanBadge.className = 'ai-generator__ip-scan-badge is-scanning';
        } else if (!this.currentAsset?.ipScan) {
          this.ipScanBadge.hidden = true;
          this.ipScanBadge.textContent = '';
          this.ipScanBadge.className = 'ai-generator__ip-scan-badge';
        }
      }
      if (this.ipScanRunLabel) {
        this.ipScanRunLabel.textContent = on
          ? 'Scanning…'
          : this.root.dataset.ipScanButtonLabel || 'Run IP safety check';
      }
      if (on) this.#startIpScanTheater();
      else this.#stopIpScanTheater();
    }

    async #runIpScan() {
      if (this._ipScanRunning || !this.currentAsset) return;

      this.#setIpScanScanning(true);
      if (this.ipScanRunBtn) this.ipScanRunBtn.disabled = true;
      this.#clearError();

      try {
        const asset = this.currentAsset;
        const res = await fetch(this.#apiEndpointUrl(this.ipScanEndpoint), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            ...this.#apiHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ai_image_id: asset.aiImageId,
            source: asset.source,
            metadata: {
              prompt: asset.prompt,
              ...asset.metadata,
            },
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || data.error || `IP scan failed (${res.status})`);
        }

        asset.ipScan = data;
        this.#syncIpScanUi();

        if (data.status === 'unsafe') {
          this.#showError(
            'This design flagged a high IP risk. Please revise your design or contact us before ordering.'
          );
        } else if (data.status === 'warning') {
          this.#showError(
            'IP scan found items worth reviewing. Read the findings and confirm the acknowledgement before checkout.'
          );
        }
      } catch (err) {
        console.error('[ai-generator] IP scan', err);
        this.currentAsset.ipScan = {
          status: 'error',
          summary: err instanceof Error ? err.message : 'IP scan could not complete',
          findings: [],
        };
        this.#syncIpScanUi();
        this.#showError(
          'IP safety check could not complete. Try again, or contact us if this keeps happening.'
        );
      } finally {
        this.#setIpScanScanning(false);
        if (this.ipScanRunBtn) this.ipScanRunBtn.disabled = !this.currentAsset;
        this.#syncCheckoutAck();
      }
    }

    get isWallShape() {
      return (this.aspectSelect?.value || 'wall') === 'wall';
    }

    get lengthUnit() {
      return this.unit === 'imperial' ? 'in' : 'cm';
    }

    get areaUnit() {
      return this.unit === 'imperial' ? 'ft²' : 'm²';
    }

    /** CSS aspect-ratio value for the design preview mount. */
    #previewAspectCssValue(printWidthCm, printHeightCm) {
      if (printWidthCm > 0 && printHeightCm > 0) {
        return `${Math.round(printWidthCm)} / ${Math.round(printHeightCm)}`;
      }
      const option = this.aspectSelect?.selectedOptions[0];
      const ratioStr = option?.dataset.ratio || '';
      if (ratioStr.includes('/')) return ratioStr.trim();
      const map = { '1:1': '1 / 1', '16:9': '16 / 9', '4:5': '4 / 5' };
      return map[this.aspectSelect?.value || ''] || '16 / 9';
    }

    /** Numeric width/height ratio for layout math. */
    #previewAspectRatio(printWidthCm, printHeightCm) {
      if (printWidthCm > 0 && printHeightCm > 0) {
        return printWidthCm / printHeightCm;
      }
      const option = this.aspectSelect?.selectedOptions[0];
      const ratioStr = option?.dataset.ratio || '16 / 9';
      const parts = ratioStr.split('/').map((s) => parseFloat(s.trim()));
      if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
        return parts[0] / parts[1];
      }
      const map = { '1:1': 1, '16:9': 16 / 9, '4:5': 4 / 5 };
      return map[this.aspectSelect?.value || ''] || 16 / 9;
    }

    #isServerBackedAsset(asset = this.currentAsset) {
      const id = String(asset?.aiImageId || '');
      return id.startsWith('gen_') || id.startsWith('upload_');
    }

    isServerBackedAsset(asset = this.currentAsset) {
      return this.#isServerBackedAsset(asset);
    }

    /** Display unit -> cm (internal math is always metric). */
    #toCm(value) {
      return this.unit === 'imperial' ? value * CM_PER_INCH : value;
    }

    /** cm -> display unit. */
    #fromCm(cm) {
      return this.unit === 'imperial' ? cm / CM_PER_INCH : cm;
    }

    /** Round a display value: whole cm, or 0.1 in. */
    #roundDisp(value) {
      return this.unit === 'imperial' ? Math.round(value * 10) / 10 : Math.round(value);
    }

    /** Round trim values for display: up to 2 decimals in metric, 0.1 in imperial. */
    #roundTrimDisp(value) {
      if (!Number.isFinite(value)) return 0;
      if (this.unit === 'imperial') return Math.round(value * 10) / 10;
      return Number(value.toFixed(2));
    }

    /** m² -> display area unit (m² or ft²). */
    #areaDisp(m2) {
      return this.unit === 'imperial' ? m2 * FT2_PER_M2 : m2;
    }

    /**
     * Switches the measurement unit, converting any entered values so the real
     * wall size is preserved.
     * @param {string} unit - 'metric' | 'imperial'
     */
    setUnit(unit) {
      if ((unit !== 'metric' && unit !== 'imperial') || unit === this.unit) return;
      const prev = this.unit;
      this.unit = unit;

      [this.widthInput, this.heightInput].forEach((input) => {
        if (!input || input.value === '') return;
        const value = toNum(input.value, 0);
        if (!value) return;
        const cm = prev === 'imperial' ? value * CM_PER_INCH : value;
        input.value = String(this.#roundDisp(this.#fromCm(cm)));
      });

      if (this.customBleedInput && this.customBleedInput.value !== '') {
        const value = toNum(this.customBleedInput.value, 0);
        const cm = prev === 'imperial' ? value * CM_PER_INCH : value;
        this.customBleedInput.value = String(this.#roundTrimDisp(this.#fromCm(cm)));
      }
      this.#syncCustomBleedConstraints();

      this.#syncUnitUI();
      this.calculate();
    }

    #syncUnitUI() {
      this.unitButtons.forEach((btn) => {
        const active = btn.dataset.aiUnit === this.unit;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
      this.unitLabels.forEach((el) => (el.textContent = this.lengthUnit));
      this.consultUnitLabels.forEach((el) => (el.textContent = this.lengthUnit));
      this.#syncConsultMeasureFields();
    }

    /** Keep the custom-trim input's min/max/step sane for the active unit (max = 20 cm). */
    #syncCustomBleedConstraints() {
      if (!this.customBleedInput) return;
      const imperial = this.unit === 'imperial';
      this.customBleedInput.max = String(this.#roundTrimDisp(this.#fromCm(20)));
      this.customBleedInput.step = imperial ? '0.25' : '0.5';
    }

    /**
     * Tracks whether the active design came from AI or upload (for cart metadata).
     * @param {string} mode - 'generate' | 'upload'
     */
    setMode(mode) {
      this.mode = mode === 'upload' ? 'upload' : 'generate';
    }

    #cacheTextTargets() {
      const selectors = [
        '[data-ai-area]',
        '[data-ai-chargeable]',
        '[data-ai-print-size]',
        '[data-ai-panels]',
        '[data-ai-price]',
        '[data-ai-min-note]',
      ];
      this._textTargets = Object.fromEntries(
        selectors.map((selector) => [selector, Array.from(this.root.querySelectorAll(selector))])
      );
    }

    /** @param {HTMLElement | null | undefined} el @param {string} text */
    #setTextIfChanged(el, text) {
      if (el && el.textContent !== text) el.textContent = text;
    }

    /** @param {string} selector @param {string} text */
    #setTextAll(selector, text) {
      const els = this._textTargets?.[selector];
      if (els) {
        els.forEach((el) => {
          if (el.textContent !== text) el.textContent = text;
        });
        return;
      }
      this.root.querySelectorAll(selector).forEach((el) => {
        if (el.textContent !== text) el.textContent = text;
      });
    }

    /** Active trim allowance (total cm per dimension); 0 when removed. */
    #getTrimAllowanceCm() {
      if (this.removeMargin?.checked) return 0;
      if (this.memberActive && this.customBleedInput) {
        const raw = toNum(this.customBleedInput.value, NaN);
        if (Number.isFinite(raw) && raw >= 0) return Math.min(20, this.#toCm(raw));
      }
      return this.defaultMargin;
    }

    /** Trim allowance per side (half of total added per dimension). @param {number} bleedCm */
    #trimSideCm(bleedCm) {
      return bleedCm > 0 ? bleedCm / 2 : 0;
    }

    /** Short bleed copy for the wall panel trim column. @param {number} bleedCm @param {string} lu */
    #formatBleedExplanation(bleedCm, lu) {
      if (bleedCm <= 0) {
        return 'Trim removed — your print matches your wall size exactly.';
      }
      const total = this.#roundTrimDisp(this.#fromCm(bleedCm));
      const side = this.#roundTrimDisp(this.#fromCm(this.#trimSideCm(bleedCm)));
      return `We add ${total} ${lu} total per dimension (${side} ${lu} each side) for trimming during install.`;
    }

    /** Margin-card SVG diagram — sync total + per-side band labels. @param {number} bleedCm @param {string} lu */
    #syncMarginDiagramBleed(bleedCm, lu) {
      const removed = !!this.removeMargin?.checked || bleedCm <= 0;
      const activeCm = removed ? this.defaultMargin : bleedCm;
      const sideDisp = this.#roundTrimDisp(this.#fromCm(this.#trimSideCm(activeCm)));
      const sideLabel = `${sideDisp} ${lu}`;

      if (this.marginDiagramTotal) {
        const totalDisp = this.#roundTrimDisp(this.#fromCm(activeCm));
        this.marginDiagramTotal.textContent = removed
          ? 'Print (trim removed)'
          : `Print (+${totalDisp} ${lu} total)`;
      }
      this.marginBandLabels.forEach((el) => {
        el.textContent = removed ? '—' : sideLabel;
      });
      if (this.wallBleedExplain) {
        this.wallBleedExplain.textContent = this.#formatBleedExplanation(bleedCm, lu);
      }
    }

    /** Custom bleed field — lock when trim removed; reset value for non-Pro. @param {object} state */
    #syncCustomBleedUi(state) {
      const wrap = this.root.querySelector('[data-ai-wall-trim-custom]');
      if (!wrap) return;
      const removed = !!this.removeMargin?.checked;
      wrap.classList.toggle('is-trim-removed', removed);
      if (!this.customBleedInput) return;
      if (!this.memberActive) {
        this.customBleedInput.value = String(this.#roundTrimDisp(this.#fromCm(this.defaultMargin)));
        return;
      }
      this.customBleedInput.disabled = removed;
    }

    /** Label under width/height inputs. @param {number} bleedCm @param {string} lu */
    #formatTrimInputLabel(bleedCm, lu) {
      if (bleedCm <= 0) return 'Trim allowance removed';
      const total = this.#roundTrimDisp(this.#fromCm(bleedCm));
      const side = this.#roundTrimDisp(this.#fromCm(this.#trimSideCm(bleedCm)));
      return `${total} ${lu} trim allowance (${side} ${lu}/side)`;
    }

    /** Orange band label on the live wall diagram. @param {number} bleedCm @param {string} lu */
    #formatTrimSideBandLabel(bleedCm, lu) {
      if (bleedCm <= 0) return '';
      const side = this.#roundTrimDisp(this.#fromCm(this.#trimSideCm(bleedCm)));
      return `+${side} ${lu}`;
    }

    /** Core wall + pricing numbers (no DOM). */
    #computeMeasureState() {
      const widthDisp = toNum(this.widthInput?.value, 0);
      const heightDisp = toNum(this.heightInput?.value, 0);
      const widthCm = widthDisp > 0 ? this.#toCm(widthDisp) : 0;
      const heightCm = heightDisp > 0 ? this.#toCm(heightDisp) : 0;
      const bleedCm = this.#getTrimAllowanceCm();

      const printWidthCm = widthCm > 0 ? widthCm + bleedCm : 0;
      const printHeightCm = heightCm > 0 ? heightCm + bleedCm : 0;

      const actualAreaM2 = (printWidthCm / 100) * (printHeightCm / 100);
      const units = Math.round(Math.max(this.minArea, actualAreaM2) * 100);
      const chargeableAreaM2 = units / 100;
      const panels = printWidthCm > 0 ? Math.ceil(printWidthCm / this.panelWidth) : 0;

      const install =
        this.installEnabled && !this.installationUnavailable && this.#isInstallSelected();
      let price = 0;
      if (this.baseUnitPrice > 0) {
        price = chargeableAreaM2 * (this.baseUnitPrice * 100);
        if (install) price += chargeableAreaM2 * this.installRate;
      }

      return {
        unit: this.unit,
        lengthUnit: this.lengthUnit,
        widthDisp,
        heightDisp,
        widthCm,
        heightCm,
        bleedCm,
        printWidthCm,
        printHeightCm,
        actualAreaM2,
        chargeableAreaM2,
        units,
        panels,
        install,
        installRegion: this.regionSelect?.value || '',
        price: Math.round(price),
        minApplied: actualAreaM2 > 0 && actualAreaM2 < this.minArea,
      };
    }

    /** Step 1 — totals + aspect ratio text immediately; box sizing debounced. */
    #updateMeasureFast() {
      const state = this.#computeMeasureState();
      const lu = state.lengthUnit;
      const fmtLen = (cm) => `${this.#roundDisp(this.#fromCm(cm))} ${lu}`;

      this.#setTextIfChanged(this.widthTotal, fmtLen(state.printWidthCm));
      this.#setTextIfChanged(this.heightTotal, fmtLen(state.printHeightCm));

      const marginLabel = this.#formatTrimInputLabel(state.bleedCm, lu);
      this.#setTextIfChanged(this.widthMarginText, marginLabel);
      this.#setTextIfChanged(this.heightMarginText, marginLabel);

      if (this.wallSpecsCompact) {
        const hide = state.printWidthCm <= 0 || state.printHeightCm <= 0;
        if (this.wallSpecsCompact.hidden !== hide) this.wallSpecsCompact.hidden = hide;
      }

      const { widthDisp, heightDisp, printWidthCm, printHeightCm, panels } = state;
      this.root.classList.toggle('is-fixed-aspect-mode', !this.isWallShape);
      this.root.classList.toggle(
        'is-ratio-only-preview',
        !this.isWallShape && !(printWidthCm > 0 && printHeightCm > 0)
      );
      this.#syncPreviewShape(printWidthCm, printHeightCm, { layout: false });
      this.#renderWallShape(widthDisp, heightDisp, printWidthCm, printHeightCm, panels, {
        layout: false,
      });
      this.#syncWallPreviewCopy(state);
      this.#syncMarginDiagramBleed(state.bleedCm, lu);
      this.#syncCustomBleedUi(state);
      this.#syncPreviewMeasurements(state);
      this.#syncPreviewPlaceholder(state);
      this.#syncStartHere(state);
      if (widthDisp > 0 && heightDisp > 0) {
        this.#scheduleWallLayoutUpdate();
      } else if (!this.isWallShape) {
        this._wallLayoutKey = '';
        this.#renderWallShape(widthDisp, heightDisp, printWidthCm, printHeightCm, panels, {
          layout: true,
        });
        this.#schedulePreviewLayout(true);
      }
      this.#syncWallpaperLayoutUi();
    }

    /** One layout pass per frame for the Step 1 wall box only (avoid shifting Step 2 preview). */
    #scheduleWallLayoutUpdate() {
      if (this._wallLayoutRaf) cancelAnimationFrame(this._wallLayoutRaf);
      this._wallLayoutRaf = requestAnimationFrame(() => {
        this._wallLayoutRaf = requestAnimationFrame(() => {
          this._wallLayoutRaf = 0;
          const state = this.#computeMeasureState();
          const { widthDisp, heightDisp, printWidthCm, printHeightCm, panels } = state;
          if (!(widthDisp > 0 && heightDisp > 0)) return;
          this._wallLayoutKey = '';
          this.#renderWallShape(widthDisp, heightDisp, printWidthCm, printHeightCm, panels, {
            layout: true,
          });
        });
      });
    }

    /**
     * Format a whole-currency total for the active Shopify market (e.g. ZAR, USD).
     * @param {number} amountMajor
     */
    #formatPrice(amountMajor) {
      if (!Number.isFinite(amountMajor) || amountMajor <= 0) return '—';
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyIso,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amountMajor);
      } catch {
        return `${this.currencyFallback}${Math.round(amountMajor).toLocaleString(this.locale)}`;
      }
    }

    /** @param {boolean} visible @param {string} text */
    #setMinNote(visible, text) {
      const els = this._textTargets?.['[data-ai-min-note]'];
      if (els) {
        els.forEach((el) => {
          el.hidden = !visible;
          if (visible && el.textContent !== text) el.textContent = text;
        });
        return;
      }
      this.root.querySelectorAll('[data-ai-min-note]').forEach((el) => {
        el.hidden = !visible;
        if (visible && el.textContent !== text) el.textContent = text;
      });
    }

    /** Re-run hero scroll-under stacking after layout or smooth-scroll. */
    #scheduleHeroScrollSync() {
      const sync = () => this._heroScrollResume?.();
      sync();
      requestAnimationFrame(sync);
      for (const delay of [80, 350, 700, 1100]) {
        window.setTimeout(sync, delay);
      }
      if ('onscrollend' in window) {
        window.addEventListener('scrollend', sync, { once: true, passive: true });
      }
    }

    /** Smooth-scroll to wall / design / upload / summary. */
    #scrollToSection(section) {
      const target = this.root.querySelector(`[data-ai-section="${section}"]`);
      if (!target) return;

      const inner = this.root.querySelector('.ai-generator__inner--hero-scroll');
      // Do not force at-rest here — that raises the sticky hero (z-index 5) over the form/preview.
      if (inner) inner.classList.remove('ai-generator__inner--hero-at-rest');
      this._heroScrollResume?.();

      const shopHeader =
        document.querySelector('.header-section, #header-group, header-component') ||
        document.querySelector('header');
      const offset = Math.max(72, (shopHeader?.getBoundingClientRect().height || 0) + 12);

      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

      if (section === 'upload') this.setMode('upload');
      else if (section === 'design') this.setMode('generate');

      this.#scheduleHeroScrollSync();
    }

    /** Step 1 totals only while typing; wall preview debounced; full sync on blur. */
    #onMeasureInput() {
      this.#updateMeasureFast();
      this._heroHeaderMedia?.pause();
      clearTimeout(this._measureInputTimer);
      // Shortly after typing pauses, run a full layout pass so the preview box
      // auto-fits the wall aspect ratio — no click/blur required.
      this._measureInputTimer = window.setTimeout(() => {
        this.calculate({ layout: true, sync: false });
      }, 350);
    }

    #onMeasureBlur() {
      clearTimeout(this._measureInputTimer);
      this._heroScrollResume?.();
      this.calculate({ layout: true, sync: true });
    }

    #bindToolbarWallMeasure() {
      const syncMeasureField = (field, value, source) => {
        const targets =
          field === 'width'
            ? [
                this.widthInput,
                this.toolbarWidthInput,
                this.toolbarWallMeasurePopWidth,
              ]
            : [
                this.heightInput,
                this.toolbarHeightInput,
                this.toolbarWallMeasurePopHeight,
              ];
        for (const el of targets) {
          if (!el || el === source) continue;
          if (el.value !== value) el.value = value;
        }
      };

      const bindMeasureInput = (el, field) => {
        el?.addEventListener('input', () => {
          syncMeasureField(field, el.value, el);
          this.#onMeasureInput();
        });
        el?.addEventListener('focusin', () => this._heroHeaderMedia?.pause());
        el?.addEventListener('blur', () => {
          syncMeasureField(field, el.value, el);
          this.#onMeasureBlur();
        });
      };

      bindMeasureInput(this.toolbarWidthInput, 'width');
      bindMeasureInput(this.toolbarHeightInput, 'height');
      bindMeasureInput(this.toolbarWallMeasurePopWidth, 'width');
      bindMeasureInput(this.toolbarWallMeasurePopHeight, 'height');

      this.toolbarWallMeasureBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.#isToolbarWallMeasureCompact()) {
          this.#toggleToolbarWallMeasurePop();
          return;
        }
        this.toolbarWidthInput?.focus();
      });

      this.toolbarWallMeasurePop?.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.#toggleToolbarWallMeasurePop(false);
      });
    }

    #isToolbarWallMeasureCompact(chip = this.root.querySelector('.ai-generator__toolbar-chip--wall-measure')) {
      if (!chip || chip.classList.contains('is-wall-measure-force-expanded')) return false;
      if (chip.classList.contains('is-wall-measure-compact')) return true;

      const expanded = chip.querySelector('[data-ai-toolbar-wall-measure-expanded]');
      const btn = this.toolbarWallMeasureBtn;
      if (!expanded || !btn) return false;

      const expandedStyle = window.getComputedStyle(expanded);
      const btnStyle = window.getComputedStyle(btn);
      const expandedHidden = expandedStyle.display === 'none' || expandedStyle.visibility === 'hidden';
      const btnVisible = btnStyle.display !== 'none' && btnStyle.visibility !== 'hidden';
      return expandedHidden && btnVisible;
    }

    #toggleToolbarWallMeasurePop(force) {
      if (!this.toolbarWallMeasurePop || !this.toolbarWallMeasureBtn) return;
      const open =
        typeof force === 'boolean' ? force : this.toolbarWallMeasurePop.hidden;
      this.toolbarWallMeasurePop.hidden = !open;
      this.toolbarWallMeasureBtn.classList.toggle('is-open', open);
      this.toolbarWallMeasureBtn.setAttribute('aria-expanded', String(open));
      this.toolbarWallMeasureWrap?.classList.toggle('is-wall-measure-pop-open', open);

      if (open) {
        this.#syncToolbarWallMeasureValues();
        window.requestAnimationFrame(() => {
          const target =
            this.toolbarWallMeasurePopWidth?.value === '' &&
            this.toolbarWallMeasurePopHeight?.value !== ''
              ? this.toolbarWallMeasurePopHeight
              : this.toolbarWallMeasurePopWidth;
          target?.focus();
          target?.select?.();
        });
      } else {
        clearTimeout(this._measureInputTimer);
        this.calculate({ layout: true, sync: true });
      }
    }

    #syncToolbarWallMeasureValues(state = this.#computeMeasureState()) {
      const w = this.widthInput?.value ?? '';
      const h = this.heightInput?.value ?? '';
      if (this.toolbarWidthInput && document.activeElement !== this.toolbarWidthInput) {
        this.toolbarWidthInput.value = w;
      }
      if (this.toolbarHeightInput && document.activeElement !== this.toolbarHeightInput) {
        this.toolbarHeightInput.value = h;
      }
      if (
        this.toolbarWallMeasurePopWidth &&
        document.activeElement !== this.toolbarWallMeasurePopWidth
      ) {
        this.toolbarWallMeasurePopWidth.value = w;
      }
      if (
        this.toolbarWallMeasurePopHeight &&
        document.activeElement !== this.toolbarWallMeasurePopHeight
      ) {
        this.toolbarWallMeasurePopHeight.value = h;
      }

      const lu = state.lengthUnit || this.lengthUnit;
      this.root.querySelectorAll('[data-ai-toolbar-unit-label], [data-ai-toolbar-pop-unit]').forEach((el) => {
        if (el.textContent !== lu) el.textContent = lu;
      });

      if (this.toolbarWallMeasureSummary) {
        const wd = state.widthDisp ?? toNum(w, 0);
        const hd = state.heightDisp ?? toNum(h, 0);
        let summary = 'Wall';
        if (wd > 0 && hd > 0) {
          summary = `${this.#roundDisp(wd)}×${this.#roundDisp(hd)}`;
        } else if (wd > 0) {
          summary = `${this.#roundDisp(wd)}×…`;
        } else if (hd > 0) {
          summary = `…×${this.#roundDisp(hd)}`;
        }
        if (this.toolbarWallMeasureSummary.textContent !== summary) {
          this.toolbarWallMeasureSummary.textContent = summary;
        }
        const btnLabel =
          wd > 0 && hd > 0
            ? `Wall size ${this.#roundDisp(wd)} by ${this.#roundDisp(hd)} ${lu}`
            : 'Edit wall measurements';
        this.toolbarWallMeasureBtn?.setAttribute('aria-label', btnLabel);
        this.toolbarWallMeasureBtn?.setAttribute('title', btnLabel);
      }
    }

    #syncToolbarWallMeasureLayout() {
      const chip = this.root.querySelector('.ai-generator__toolbar-chip--wall-measure');
      const wrap = this.root.querySelector('[data-ai-toolbar-wrap]');
      if (!chip || !wrap) return;

      const wrapW = wrap.clientWidth;
      const compactBreakpoint = 1080;
      const onPreviewRail = Boolean(wrap.closest('.ai-generator__design-preview-rail'));

      if (onPreviewRail) {
        chip.classList.toggle('is-wall-measure-compact', wrapW > 0 && wrapW < compactBreakpoint);
        return;
      }

      const toolbar = this.designToolbar || wrap.querySelector('[data-ai-design-toolbar]');
      const overflows = Boolean(toolbar && toolbar.scrollWidth > wrapW + 8);
      const useCompact = wrapW > 0 && (wrapW < compactBreakpoint || overflows);
      chip.classList.toggle('is-wall-measure-compact', useCompact);
    }

    #bindScrollCues() {
      const cues = Array.from(this.root.querySelectorAll('[data-ai-scroll-cue]'));
      if (!cues.length) return;

      cues.forEach((cue) => {
        const targetId = cue.dataset.aiScrollTarget;
        const target = targetId
          ? this.root.querySelector(`[data-ai-section="${targetId}"]`)
          : null;
        if (!target) return;

        const observer = new IntersectionObserver(
          ([entry]) => {
            cue.hidden = entry.isIntersecting;
          },
          { threshold: 0.1, rootMargin: '-72px 0px -40% 0px' }
        );
        observer.observe(target);
      });
    }

    /**
     * @param {{ layout?: boolean; sync?: boolean }} [options]
     * layout:false skips getBoundingClientRect / preview resize.
     */
    /**
     * Side-effect-free measurement snapshot. Used by external controllers
     * (e.g. design-save) so reading state never re-triggers calculate()/onCalculate,
     * which would otherwise recurse infinitely.
     */
    measureState() {
      return this.#computeMeasureState();
    }

    /** Minimum wide-view preview grid row (toolbar chrome + usable viewport). */
    widePreviewRowMinHeight(printWidthCm = 0, printHeightCm = 0) {
      return this.#widePreviewRowMinUsable(printWidthCm, printHeightCm);
    }

    calculate(options = {}) {
      const layout = options.layout !== false;
      const sync = options.sync !== false;
      const state = this.#computeMeasureState();
      const { widthDisp, heightDisp, printWidthCm, printHeightCm, panels } = state;
      const lu = state.lengthUnit;
      const fmtLen = (cm) => `${this.#roundDisp(this.#fromCm(cm))} ${lu}`;

      this.#setTextIfChanged(this.widthTotal, fmtLen(printWidthCm));
      this.#setTextIfChanged(this.heightTotal, fmtLen(printHeightCm));
      this.#syncToolbarWallMeasureValues(state);

      const marginLabel = this.#formatTrimInputLabel(state.bleedCm, lu);
      this.#setTextIfChanged(this.widthMarginText, marginLabel);
      this.#setTextIfChanged(this.heightMarginText, marginLabel);

      this.#setTextAll('[data-ai-area]', this.#areaDisp(state.actualAreaM2).toFixed(2));
      this.#setTextAll('[data-ai-chargeable]', this.#areaDisp(state.chargeableAreaM2).toFixed(2));
      this.areaUnitEls.forEach((el) => {
        if (el.textContent !== this.areaUnit) el.textContent = this.areaUnit;
      });
      const printSizeText =
        printWidthCm > 0
          ? `${this.#roundDisp(this.#fromCm(printWidthCm))} × ${this.#roundDisp(this.#fromCm(printHeightCm))} ${lu}`
          : '—';
      this.#setTextAll('[data-ai-print-size]', printSizeText);
      this.#setTextAll('[data-ai-panels]', String(panels));

      const minNoteText = state.minApplied
        ? `Minimum ${this.#areaDisp(this.minArea).toFixed(2)} ${this.areaUnit} charge applied`
        : '';
      this.#setMinNote(state.minApplied, minNoteText);

      if (this.wallSpecsCompact) {
        const hide = printWidthCm <= 0 || printHeightCm <= 0;
        if (this.wallSpecsCompact.hidden !== hide) this.wallSpecsCompact.hidden = hide;
      }

      this.#setTextAll(
        '[data-ai-price]',
        this.baseUnitPrice > 0 ? this.#formatPrice(state.price) : '—'
      );

      this.#syncPreviewShape(printWidthCm, printHeightCm, { layout });
      this.#renderWallShape(widthDisp, heightDisp, printWidthCm, printHeightCm, panels, { layout });

      this.root.classList.toggle('is-fixed-aspect-mode', !this.isWallShape);
      this.root.classList.toggle(
        'is-ratio-only-preview',
        !this.isWallShape && !(printWidthCm > 0 && printHeightCm > 0)
      );

      this.#syncWallPreviewCopy(state);
      this.#syncMarginDiagramBleed(state.bleedCm, lu);
      this.#syncCustomBleedUi(state);

      if (sync) {
        this.#revalidateUpload(state);
        this.#updateQualityBadge(state);
        this.#updateAckApplicability(state);
        this.#syncCheckoutAck();
        this.#syncConsultMeasureFields();
        this.#syncPreviewMeasurements(state);
        this.#syncPreviewPlaceholder(state);
        this.root.wallprintAiDesignSave?.onCalculate(state);
      }
      this.#syncPanelLinesOnPreview(state);
      if (layout) {
        this.#syncPreviewWallOverlay(state);
        this.#syncUploadCropAfterMeasureChange(state);
      } else {
        this.#syncPanelGuideControls(state);
      }
      this.#syncStartHere(state);
      return state;
    }

    /** Compact print size + ratio chips above the live preview. */
    #syncPreviewMeasurements(state) {
      const ratioOnly = !this.isWallShape && !(state.printWidthCm > 0 && state.printHeightCm > 0);
      const hide = (state.printWidthCm <= 0 || state.printHeightCm <= 0) && !ratioOnly;
      if (this.previewMeasurements) this.previewMeasurements.hidden = hide;

      const lu = state.lengthUnit;
      const printW = state.printWidthCm > 0 ? this.#roundDisp(this.#fromCm(state.printWidthCm)) : null;
      const printH = state.printHeightCm > 0 ? this.#roundDisp(this.#fromCm(state.printHeightCm)) : null;
      const wallW = state.widthDisp > 0 ? this.#roundDisp(state.widthDisp) : null;
      const wallH = state.heightDisp > 0 ? this.#roundDisp(state.heightDisp) : null;

      const dims =
        printW != null ? `${printW} × ${printH} ${lu} print` : ratioOnly ? 'No wall size' : '—';
      this.#setTextAll('[data-ai-preview-wall-dims]', dims);

      const ratioText =
        state.printWidthCm > 0
          ? this.#formatAspectRatio(state.printWidthCm / state.printHeightCm)
          : ratioOnly
            ? this.#formatAspectRatio(this.#previewAspectRatio(0, 0))
            : '—';
      const shapeSuffix = this.isWallShape ? '' : ` · ${this.aspectSelect?.value || 'Custom'}`;
      this.#setTextAll('[data-ai-preview-wall-ratio]', `${ratioText}${shapeSuffix}`);

      const panelsText =
        state.panels > 0 ? `${state.panels} panel${state.panels === 1 ? '' : 's'}` : ratioOnly ? '—' : '—';
      this.#setTextAll('[data-ai-preview-wall-panels]', panelsText);

      this.#syncPreviewHud(state, { lu, printW, printH, wallW, wallH, ratioText, panelsText, hide });
    }

    #syncPreviewHud(state, ctx) {
      if (this.previewHud) this.previewHud.hidden = true;
    }

    /** Hide Start here prompt once wall width and height are entered. */
    #syncStartHere(state) {
      const started = state.widthCm > 0 && state.heightCm > 0;
      this.root.querySelectorAll('[data-ai-start-badge]').forEach((el) => {
        el.hidden = started;
      });
      const wallStep = this.root.querySelector('[data-ai-section="wall"]');
      if (wallStep) wallStep.classList.toggle('ai-generator__step--start-pending', !started);
      const heroStep = this.root.querySelector('[data-ai-hero-step-start]');
      if (heroStep) heroStep.classList.toggle('ai-generator__hero-steps-item--active', !started);
      this.#syncWallReadyMessage(started);
    }

    /** Thank-you banner once width and height are both entered. */
    #syncWallReadyMessage(started) {
      if (!this.wallReadyEl) return;

      if (!started) {
        this.wallReadyEl.hidden = true;
        this.wallReadyEl.classList.remove('is-visible');
        this._wallMeasurementsReady = false;
        this._wallReadyDismissed = false;
        this.root.classList.remove('is-wall-ready');
        return;
      }

      this.root.classList.add('is-wall-ready');

      if (this._wallReadyDismissed) return;

      const wasReady = this._wallMeasurementsReady;
      this._wallMeasurementsReady = true;
      this.wallReadyEl.hidden = false;

      if (!wasReady) {
        this.wallReadyEl.classList.remove('is-visible');
        requestAnimationFrame(() => {
          this.wallReadyEl?.classList.add('is-visible');
        });
      } else if (!this.wallReadyEl.classList.contains('is-visible')) {
        this.wallReadyEl.classList.add('is-visible');
      }
    }

    /** Refit upload crop when wall aspect changes; preserve pan on minor recalculations. */
    #syncUploadCropAfterMeasureChange(state) {
      if (!this.cropViewport || this.cropViewport.hidden || !this.cropNaturalW) return;
      const aspectKey = this.isWallShape
        ? `${Math.round(state.printWidthCm)}:${Math.round(state.printHeightCm)}`
        : this.aspectSelect?.value || '1:1';
      const aspectChanged = aspectKey !== this._cropAspectKey;
      this._cropAspectKey = aspectKey;
      this.#refreshUploadCropLayout(!aspectChanged);
    }

    /**
     * Customer-facing print-quality badge based on effective DPI at the wall.
     * @param {ReturnType<AIImageGenerator['calculate']>} state
     */
    #setPreviewLive(active) {
      this.preview?.classList.toggle('has-content', active);
      this.preview?.classList.toggle('is-awaiting-design', !active);
      this.previewStatus?.classList.toggle('is-live', active);
      this.uploadNoteEl?.classList.toggle('is-live', active);
      if (this.previewHud && !active) this.previewHud.hidden = true;
      this.#syncDesignEmphasis();
      this.#syncPreviewStatusBar();
      this.#syncPreviewActions();
    }

    /** When empty, widen and highlight the preview; compact controls slightly. */
    #syncDesignEmphasis() {
      const hadDesign = this._hadDesign;
      const hasDesign = Boolean(this.currentAsset);
      this.designGrid?.classList.toggle('ai-generator__design-grid--empty', !hasDesign);
      this.root.classList.toggle('ai-generator--no-design', !hasDesign);
      this.root.classList.toggle('ai-generator--has-design', hasDesign);

      if (
        hasDesign &&
        !hadDesign &&
        !this._promptManuallyOpen &&
        !this._previewExpanded &&
        !this.#isWideLayoutView()
      ) {
        this.#setPromptCollapsed(true);
      }
      if (!hasDesign) {
        this.#setPromptCollapsed(false);
        this._promptManuallyOpen = false;
      }
      this._hadDesign = hasDesign;
    }

    #syncPromptPanelToggleUi() {
      const wide = this.#isWideLayoutView();
      const collapsed = this.designGrid?.classList.contains('is-prompt-collapsed') ?? false;

      if (this.promptCollapseBtn) {
        this.promptCollapseBtn.hidden = wide;
        if (!wide) {
          this.promptCollapseBtn.setAttribute('aria-expanded', 'true');
        }
      }

      const headHint = this.root.querySelector('[data-ai-prompt-head-hint]');
      if (headHint instanceof HTMLElement) {
        headHint.hidden = true;
      }

      this.promptHeadToggle?.setAttribute('aria-expanded', 'true');
      this.promptPanel?.classList.remove('is-prompt-dock-minimized');

      if (this.promptReopenBtn) {
        this.promptReopenBtn.hidden = !collapsed || !this.currentAsset || wide;
      }
    }

    #setPromptCollapsed(collapsed, { manual = false } = {}) {
      if (this.#isWideLayoutView()) return;

      if (manual) {
        this._promptManuallyOpen = !collapsed;
      }

      this.designGrid?.classList.toggle('is-prompt-collapsed', collapsed);
      this.#syncPromptPanelToggleUi();
      this._previewFitKey = '';
      this.#schedulePreviewLayout();
    }

    /** Design library lives in the design grid (full width below prompt + preview). */
    #isLibraryGridDocked() {
      const grid = this.designGrid;
      const hub =
        this.designHub || this.root?.querySelector('[data-ai-inspiration-gallery]');
      if (!grid || !hub) return false;
      return hub.parentElement === grid;
    }

    /** In wide view: preview/tools → prompt → library → store links. Studio: prompt|preview row, library below. */
    #syncWideViewExploreDock(expanded) {
      const bar = this.exploreBar;
      const hub = this.designHub || this.root.querySelector('[data-ai-inspiration-gallery]');
      const grid = this.designGrid;
      const workspace = this.designWorkspace;
      const controls = this.designControls;
      const studio = grid?.querySelector('.ai-generator__design-studio');
      const previewRowResize =
        this.previewRowResizeHandle || this.root.querySelector('[data-ai-preview-row-resize]');
      this.previewRowResizeHandle = previewRowResize;
      const splitter =
        this.previewHubSplitter || this.root.querySelector('[data-ai-preview-hub-splitter]');
      this.previewHubSplitter = splitter;
      if (!grid) return;
      if (hub && hub !== this.designHub) this.designHub = hub;

      if (expanded) {
        hub?.classList.add('is-wideview-docked');
        bar?.classList.add('is-wideview-docked');
        splitter?.classList.add('is-wideview-docked');

        const stack = [studio, previewRowResize, controls, splitter, hub, bar].filter(Boolean);
        for (const el of stack) {
          if (el.parentElement !== grid) grid.appendChild(el);
        }
        for (const el of stack) grid.appendChild(el);

        hub?.wallprintInspirationInstance?.refreshDockRefs?.();
        this.#syncWideViewPreviewRowResizeUi();
        return;
      }

      hub?.classList.remove('is-wideview-docked');
      bar?.classList.remove('is-wideview-docked');
      splitter?.classList.remove('is-wideview-docked');
      const studioStack = [controls, studio, splitter, hub, bar].filter(Boolean);
      for (const el of studioStack) {
        if (el.parentElement !== grid) grid.appendChild(el);
      }
      for (const el of studioStack) grid.appendChild(el);
      hub?.wallprintInspirationInstance?.refreshDockRefs?.();
    }

    /** Re-run wide-view dock after inspiration gallery mounts (script order). */
    syncWideViewDock() {
      this.designHub = this.root.querySelector('[data-ai-inspiration-gallery]');
      this.exploreBar = this.root.querySelector('.ai-generator__preview-explore[data-ai-explore-bar]');
      if (!this._previewExpanded) return;
      this.#syncWideViewExploreDock(true);
      this.#syncWideViewPreviewRowResizeUi();
      this.designHub?.wallprintInspirationInstance?.refreshDockRefs?.();
      this._previewFitKey = '';
      this.#schedulePreviewLayout(true);
    }

    /** Re-query preview resize handles after wide-view dock moves DOM. */
    #refreshPreviewSizeResizeHandles() {
      this.previewSizeResizeHandles = Array.from(
        this.root.querySelectorAll('[data-ai-preview-size-resize]')
      );
      this.previewSizeResize =
        this.previewSizeResizeHandles.find((el) => !el.hidden) ||
        this.previewSizeResizeHandles[0] ||
        null;
      this.#bindPreviewSizeResize();
    }

    /** Wide view — show grid row resize bar; studio foot stays as fallback inside preview. */
    #syncWideViewPreviewRowResizeUi() {
      this.previewRowResizeHandle =
        this.previewRowResizeHandle || this.root.querySelector('[data-ai-preview-row-resize]');
      this.#syncPreviewSizeResizeUi(true);
      if (this.#isWideLayoutView()) this.#refreshPreviewSizeResizeHandles();
    }

    /** Size wide-view preview grid row after gallery init (no stored preference). */
    syncWideViewPreviewRow() {
      const state = this.#computeMeasureState();
      this.#syncWideViewPreviewRow(state.printWidthCm, state.printHeightCm, { force: true });
    }

    /** Wide view — lock preview row to px and reflow the live viewport. */
    bootstrapWidePreviewLayout() {
      if (!this.#isWideLayoutView()) return;
      this.#ensureWidePreviewRowMinimum();
      const gallery = this.designHub?.wallprintInspirationInstance;
      const state = this.#computeMeasureState();
      if (!gallery?._widePreviewRowUserSet) {
        this.#syncWideViewPreviewRow(state.printWidthCm, state.printHeightCm, { force: true });
      }
      this._previewFitKey = '';
      this.#schedulePreviewLayout(true);
    }

    /** Wide view by default; preference stored in localStorage. */
    #initLayoutPreference() {
      let expanded = false;
      try {
        const stored = localStorage.getItem(this._layoutPrefKey);
        if (stored !== null) expanded = stored === '1';
      } catch {
        /* ignore storage errors */
      }
      this.#setPreviewExpanded(expanded, { persist: false });
    }

    #persistLayoutPreference(expanded) {
      try {
        localStorage.setItem(this._layoutPrefKey, expanded ? '1' : '0');
      } catch {
        /* ignore storage errors */
      }
    }

    /** Expand preview full-width; prompt/upload moves to a bottom dock. */
    #setPreviewExpanded(expanded, { persist = true } = {}) {
      if (this._previewExpanded === expanded) {
        this.#syncPreviewExpandToggles();
        return;
      }

      this._previewExpanded = expanded;
      if (persist) this.#persistLayoutPreference(expanded);
      this.root.classList.toggle('is-preview-expanded', expanded);
      this.root.classList.toggle('is-wide-view', expanded);
      if (this.designGrid) {
        this.designGrid.classList.toggle('is-preview-expanded', expanded);
        this.designGrid.classList.toggle('is-wide-view', expanded);
      }
      this.previewExpandToggles = Array.from(
        this.root.querySelectorAll('[data-ai-preview-expand-toggle]')
      );
      this.#bindPreviewExpandToggles();
      this.#syncPreviewExpandToggles();
      this.#syncWideViewExploreDock(expanded);
      this.#syncWideViewPreviewRowResizeUi();
      this.root.dispatchEvent(new CustomEvent('wallprint:layout-mode-changed'));

      if (expanded) {
        this.#clearDesignGridHeight();
        if (this.designControls) this.designControls.style.minHeight = '';
        this.#setPromptCollapsed(false);
      }

      this._previewFitKey = '';
      this.#schedulePreviewLayout(true);
      this.#syncToolbarDensity();
      window.requestAnimationFrame(() => this._toolbarOverflowSync?.());

      if (expanded) {
        requestAnimationFrame(() => {
          const gallery = this.designHub?.wallprintInspirationInstance;
          const state = this.#computeMeasureState();
          if (!gallery?._widePreviewRowUserSet) {
            this.#syncWideViewPreviewRow(state.printWidthCm, state.printHeightCm, { force: true });
          }
          requestAnimationFrame(() => {
            this.bootstrapWidePreviewLayout();
            this.previewStage?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        });
      } else {
        this.designGrid?.style.removeProperty('--ai-wide-preview-row-h');
      }
    }

    #syncPreviewExpandToggles() {
      const expanded = this._previewExpanded;

      for (const btn of this.previewExpandToggles) {
        const target = btn.dataset.aiLayoutTarget;
        if (target === 'wide') {
          btn.setAttribute('aria-pressed', String(expanded));
          btn.setAttribute(
            'aria-label',
            expanded
              ? 'Wide view (current) — larger preview with prompt dock below'
              : 'Switch to wide view — larger preview with prompt dock below'
          );
          btn.setAttribute(
            'title',
            expanded
              ? 'Wide view (current) — larger preview with prompt dock below'
              : 'Switch to wide view — larger preview with prompt dock below'
          );
        } else if (target === 'studio') {
          btn.setAttribute('aria-pressed', String(!expanded));
          btn.setAttribute(
            'aria-label',
            !expanded
              ? 'Studio layout (current) — side-by-side preview and prompt'
              : 'Switch to studio layout — side-by-side preview and prompt'
          );
          btn.setAttribute(
            'title',
            !expanded
              ? 'Studio layout (current) — side-by-side preview and prompt'
              : 'Switch to studio layout — side-by-side preview and prompt'
          );
        } else {
          const label = expanded ? 'Studio' : 'Wide view';
          const ariaLabel = expanded ? 'Switch to studio layout' : 'Switch to wide view layout';
          const title = expanded
            ? 'Studio layout — side-by-side preview and prompt'
            : 'Wide view — larger preview with prompt dock below';
          btn.setAttribute('aria-pressed', String(expanded));
          btn.setAttribute('aria-label', ariaLabel);
          btn.setAttribute('title', title);
          const text =
            btn.querySelector('[data-ai-preview-expand-label]') ||
            btn.querySelector('.ai-generator__layout-toggle-label');
          if (text) text.textContent = label;
        }
      }
    }

    /** Toolbar uses flex-wrap — no density collapse needed. */
    #syncToolbarDensity() {
      this.designToolbar?.classList.remove('is-toolbar-icons-only', 'is-toolbar-compact');
      this.#syncToolbarWallMeasureLayout();
    }

    /**
     * Preview toolbar: always visible; disabled until the action applies (shows what’s possible).
     * @param {ReturnType<AIImageGenerator['calculate']>} [state]
     */
    #syncPreviewActions(state = this.calculate()) {
      const hasDesign = Boolean(this.currentAsset);
      const hasVisual = this.#previewHasVisual();
      const hasSrc = Boolean(this.#getPreviewZoomSrc());
      const busy = this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling || this.isMockup;
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      const canTileBase = hasDesign && this.#isServerBackedAsset(this.currentAsset);

      if (this.tilePatternBtn) {
        this.tilePatternBtn.hidden = false;
        const needsLogin = !this.customerLoggedIn && !isStudioGuestMode(this);
        const tileLocked = !canTileBase || needsLogin;
        this.tilePatternBtn.disabled = busy;
        this.tilePatternBtn.setAttribute('aria-disabled', String(tileLocked || busy));
        this.tilePatternBtn.classList.toggle('is-action-locked', tileLocked && !busy);
        this.tilePatternBtn.classList.toggle('is-preview-teaser', tileLocked);
        this.tilePatternBtn.title = !hasDesign
          ? 'Add a design first'
            : !this.#isServerBackedAsset(this.currentAsset)
            ? 'Upload or generate must finish on the server before Make seamless'
            : needsLogin
              ? 'Log in to adapt your design for wallpaper repeat'
              : 'Adapt your design so it repeats cleanly across your wall';
        const label =
          this.tilePatternBtn.querySelector('[data-ai-tile-pattern-label]') ||
          this.tilePatternBtn.querySelector('.ai-generator__button-label');
        if (label) {
          label.textContent = this.currentAsset?.tiled ? 'Re-make seamless' : 'Make seamless';
        }
      }
      if (this.roomMockupBtn) {
        const canMockup = canTileBase && hasDims;
        const needsLogin = !this.customerLoggedIn && !isStudioGuestMode(this);
        const mockupLocked = !canMockup || needsLogin;
        this.roomMockupBtn.hidden = false;
        this.roomMockupBtn.disabled = busy;
        this.roomMockupBtn.setAttribute('aria-disabled', String(mockupLocked || busy));
        this.roomMockupBtn.classList.toggle('is-action-locked', mockupLocked && !busy);
        this.roomMockupBtn.classList.toggle('is-preview-teaser', mockupLocked);
        this.roomMockupBtn.title = !canMockup
          ? 'Add your wall size and a design first'
          : needsLogin
            ? 'Log in to create a wall mockup from a room photo'
            : 'Upload a room photo to preview your design on a wall';
      }
      if (this.upscaleBtn) {
        const studio = isStudioGuestMode(this);
        const needsLogin = !this.customerLoggedIn && !studio;
        const canUpscale = canTileBase && hasDims;
        const upscaled = Boolean(this.currentAsset?.upscaled || this.currentAsset?.metadata?.upscaled);
        const effDpi = this.#currentEffectiveDpi(state);
        const wantsUpscale = effDpi > 0 && effDpi < this.recommendedPrintDpi;
        const upscaleLocked = !canUpscale || needsLogin;
        this.upscaleBtn.hidden = false;
        this.upscaleBtn.disabled = busy;
        this.upscaleBtn.removeAttribute('disabled');
        this.upscaleBtn.setAttribute('aria-disabled', String(upscaleLocked || busy));
        this.upscaleBtn.classList.toggle('is-action-locked', upscaleLocked && !busy);
        this.upscaleBtn.classList.toggle('is-preview-teaser', upscaleLocked);
        this.upscaleBtn.classList.toggle('ai-generator__pro-teaser-btn', !studio);
        if (studio) {
          this.upscaleBtn.classList.remove('ai-generator__pro-teaser-btn');
        }
        const label = this.upscaleBtn.querySelector('.ai-generator__toolbar-label');
        if (label) label.textContent = upscaled ? 'Re-upscale' : 'Upscale';
        this.upscaleBtn.title = !hasDesign
          ? 'Add a design first'
          : !hasDims
            ? 'Enter wall width and height first'
            : !this.#isServerBackedAsset(this.currentAsset)
              ? 'Upload or generate must finish on the server before upscaling'
              : needsLogin
                ? 'Log in to upscale your design for print'
                : upscaled
                  ? 'Run print upscale again at your wall size'
                  : wantsUpscale
                    ? `Upscale for print (~${effDpi} DPI now — target ${this.recommendedPrintDpi}+)`
                    : 'Upscale your design to print resolution';
      }
      if (this.tileLoginEl) {
        this.tileLoginEl.hidden = this.customerLoggedIn || !canTileBase;
      }
      this.#syncSeamlessHint();
      this.#syncRepeatToggleLabel();
      this.#syncWallpaperLayoutUi();

      if (this.previewZoomOpens.length) {
        this.#syncPreviewZoomButtons();
      }
      if (this.previewTrimToggle) {
        const showTrim = hasDesign && !busy && this.cropViewport && !this.cropViewport.hidden;
        this.previewTrimToggle.hidden = !showTrim;
        this.previewTrimToggle.classList.toggle('is-preview-teaser', !showTrim);
        if (!showTrim) this.#togglePreviewTrimPop(false);
      }
      if (this.previewFitOutBtn) {
        const showFitOut = hasDesign && !busy && this.cropViewport && !this.cropViewport.hidden;
        this.previewFitOutBtn.hidden = !showFitOut;
        this.previewFitOutBtn.classList.toggle('is-preview-teaser', !showFitOut);
        this.previewFitOutBtn.disabled = !showFitOut;
      }

      if (this.wallOverlayToggle || this.wallGuidesToggles.length || this.panelsControl) {
        if (this.panelsWidthEl) {
          const panelCm = String(Math.round(this.panelWidth));
          if (this.panelsWidthEl instanceof HTMLSelectElement) {
            const match = Array.from(this.panelsWidthEl.options).find(
              (opt) => opt.value === panelCm && !opt.disabled
            );
            if (match) this.panelsWidthEl.value = panelCm;
            this.#syncStyledSelect(this.panelsWidthEl);
          } else {
            this.panelsWidthEl.textContent = `${panelCm} cm`;
          }
        }
        this.#syncPanelGuideControls(state);
      }

      this.#syncAlignGridControls(state);

      if (this.scrollCartBtn && !this.scrollCartBtn.hidden) {
        this.scrollCartBtn.disabled = busy;
        this.scrollCartBtn.setAttribute('aria-disabled', String(!hasDims || busy));
        this.scrollCartBtn.classList.toggle('is-action-locked', !hasDims && !busy);
        this.scrollCartBtn.classList.toggle('is-preview-teaser', !hasDims);
        this.scrollCartBtn.title = !hasDims
          ? 'Enter wall width and height first'
          : 'Add to cart';
      }

      this.root.wallprintAiDesignShare?.syncButton(state);
      this.root.wallprintAiCommunityGalleryShare?.syncButton(state);
      this.#ensureToolbarClickable();
    }

    /** Toolbar buttons stay clickable when locked; native disabled only while busy. */
    #ensureToolbarClickable() {
      const busy = this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling || this.isMockup;
      const selectors =
        '[data-ai-preview-zoom-open], [data-ai-room-mockup], [data-ai-tile-pattern], [data-ai-upscale], [data-ai-upscale-pro], [data-ai-share-open], [data-ai-gallery-share-open]';
      for (const btn of this.root.querySelectorAll(selectors)) {
        if (btn instanceof HTMLButtonElement) btn.disabled = busy;
      }
    }

    #bindToolbarCapture() {
      if (this.root.dataset.aiToolbarCapture === 'true') return;
      this.root.dataset.aiToolbarCapture = 'true';

      this.root.addEventListener(
        'click',
        (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;

          const cropBtn = target.closest('[data-ai-preview-zoom-open]');
          if (cropBtn && this.root.contains(cropBtn)) {
            event.preventDefault();
            event.stopPropagation();
            if (this.#handleLockedViewCrop(event, cropBtn)) return;
            this.#openPreviewZoom();
            return;
          }

          const mockupBtn = target.closest('[data-ai-room-mockup]');
          if (mockupBtn && this.root.contains(mockupBtn)) {
            event.preventDefault();
            event.stopPropagation();
            if (this.#handleLockedToolbarAction(event, mockupBtn)) return;
            this.#openRoomDialog();
            return;
          }

          const tileBtn = target.closest('[data-ai-tile-pattern]');
          if (tileBtn && this.root.contains(tileBtn)) {
            event.preventDefault();
            event.stopPropagation();
            if (this.#handleLockedToolbarAction(event, tileBtn)) return;
            this.tilePattern();
            return;
          }

          const upscaleBtn = target.closest('[data-ai-upscale], [data-ai-upscale-pro]');
          if (upscaleBtn && this.root.contains(upscaleBtn)) {
            event.preventDefault();
            event.stopPropagation();
            if (this.#handleLockedToolbarAction(event, upscaleBtn)) return;
            this.upscaleDesign();
            return;
          }

          const alignGridBtn = target.closest('[data-ai-align-grid-toggle]');
          if (alignGridBtn && this.root.contains(alignGridBtn)) {
            event.preventDefault();
            event.stopPropagation();
            if (this.#handleLockedAlignGrid(event, alignGridBtn)) return;
            this.#toggleAlignGrid();
            return;
          }

          const guidesBtn = target.closest('[data-ai-wall-overlay-toggle]');
          if (guidesBtn && this.root.contains(guidesBtn)) {
            event.preventDefault();
            event.stopPropagation();
            if (this.#handleLockedPanelGuides(event, guidesBtn)) return;
            this.#togglePanelGuides();
          }
        },
        true
      );
    }

    #syncPreviewZoomButtons() {
      this.previewZoomOpens = Array.from(this.root.querySelectorAll('[data-ai-preview-zoom-open]'));
      const hasVisual = this.#previewHasVisual();
      const hasSrc = Boolean(this.#getPreviewZoomSrc());
      const busy = this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling || this.isMockup;
      const zoomReady = hasVisual && hasSrc;

      for (const btn of this.previewZoomOpens) {
        btn.hidden = false;
        btn.disabled = busy;
        btn.setAttribute('aria-disabled', String(!zoomReady || busy));
        btn.classList.toggle('is-preview-teaser', !zoomReady);
        btn.classList.toggle('is-zoom-ready', zoomReady && !busy);
        btn.title = zoomReady
          ? 'Zoom and view print crop area'
          : 'Available after you generate or upload a design';
        btn.setAttribute(
          'aria-label',
          zoomReady ? 'Zoom and view print crop area' : 'View crop — add a design first'
        );
      }
    }

    #handleLockedViewCrop(event, btn) {
      if (!(btn instanceof HTMLButtonElement)) return false;
      const busy = this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling || this.isMockup;
      if (busy) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      if (btn.getAttribute('aria-disabled') !== 'true') return false;
      event.preventDefault();
      event.stopPropagation();
      if (!this.#previewHasVisual() || !this.#getPreviewZoomSrc()) {
        this.#showError('Generate or upload a design first, then view the print crop area.');
      }
      this.#scrollToSection('design');
      return true;
    }

    #getDesignPreviewSrc() {
      const raw =
        this.cropImage?.src ||
        this.image?.src ||
        this.currentAsset?.previewUrl ||
        this.currentAsset?.originalUrl ||
        '';
      return raw ? this.#resolveApiAssetUrl(raw) : '';
    }

    #bindRoomDialogResize() {
      const dialog = this.roomDialog;
      const handle = this.roomDialogResize;
      if (!dialog || !handle) return;

      handle.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const rect = dialog.getBoundingClientRect();
        const startX = event.clientX;
        const startY = event.clientY;
        const startW = rect.width;
        const startH = rect.height;
        handle.setPointerCapture?.(event.pointerId);

        const onMove = (ev) => {
          const maxW = window.innerWidth * 0.98;
          const maxH = window.innerHeight * 0.96;
          const nextW = Math.min(maxW, Math.max(360, startW + ev.clientX - startX));
          const nextH = Math.min(maxH, Math.max(420, startH + ev.clientY - startY));
          dialog.style.width = `${Math.round(nextW)}px`;
          dialog.style.height = `${Math.round(nextH)}px`;
        };

        const onUp = () => {
          handle.releasePointerCapture?.(event.pointerId);
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
      });
    }

    #syncMockupTheaterWallClip() {
      const wall = this.roomMockupTheaterWall;
      if (!wall || !Array.isArray(this._roomCorners) || this._roomCorners.length < 4) return;
      const pts = this._roomCorners
        .map((c) => `${Math.round(c.x * 1000) / 10}% ${Math.round(c.y * 1000) / 10}%`)
        .join(', ');
      wall.style.clipPath = `polygon(${pts})`;
    }

    #stopMockupTheater() {
      const timers = this._mockupTheaterTimers || {};
      if (timers.quip) window.clearInterval(timers.quip);
      if (timers.step) window.clearInterval(timers.step);
      if (timers.progress) window.clearInterval(timers.progress);
      this._mockupTheaterTimers = { quip: 0, step: 0, progress: 0 };
      for (const step of this.roomMockupTheaterSteps || []) {
        step.classList.remove('is-active', 'is-done');
      }
      if (this.roomMockupTheater) {
        this.roomMockupTheater.hidden = true;
        this.roomMockupTheater.classList.remove(
          'is-running',
          'is-complete',
          'is-phase-composite',
          'is-phase-occlusion',
          'is-phase-light',
          'is-phase-polish'
        );
        this.roomMockupTheater.style.removeProperty('--mockup-roll');
      }
      if (this.roomMockupTheaterProgress) {
        this.roomMockupTheaterProgress.style.width = '0%';
      }
    }

    #setMockupTheaterPhase(phaseKey) {
      if (!this.roomMockupTheater) return;
      for (const key of ['composite', 'occlusion', 'light', 'polish']) {
        this.roomMockupTheater.classList.toggle(`is-phase-${key}`, key === phaseKey);
      }
    }

    #syncMockupTheaterProgress(elapsedMs) {
      const pct = Math.min(94, (1 - Math.exp(-elapsedMs / 52000)) * 100);
      const roll = Math.min(96, pct * 1.04);
      if (this.roomMockupTheater) {
        this.roomMockupTheater.style.setProperty('--mockup-roll', roll.toFixed(2));
      }
      if (this.roomMockupTheaterProgress) {
        this.roomMockupTheaterProgress.style.width = `${pct.toFixed(1)}%`;
      }
      return pct;
    }

    #startMockupTheater() {
      this.#stopMockupTheater();
      if (!this.roomMockupTheater) return;

      const roomSrc = this.roomCornerImg?.src || this._roomCornerImgUrl || '';
      const designSrc = this.#getDesignPreviewSrc();
      if (this.roomMockupTheaterRoom && roomSrc) this.roomMockupTheaterRoom.src = roomSrc;
      if (this.roomMockupTheaterDesign && designSrc) this.roomMockupTheaterDesign.src = designSrc;
      this.#syncMockupTheaterWallClip();

      this.roomMockupTheater.hidden = false;
      this.roomMockupTheater.classList.add('is-running');
      this._mockupTheaterQuipIdx = 0;

      const showQuip = () => {
        if (!this.roomMockupTheaterQuip) return;
        this.roomMockupTheaterQuip.classList.add('is-changing');
        window.setTimeout(() => {
          if (!this.roomMockupTheaterQuip) return;
          this.roomMockupTheaterQuip.textContent =
            MOCKUP_THEATER_QUIPS[this._mockupTheaterQuipIdx % MOCKUP_THEATER_QUIPS.length];
          this._mockupTheaterQuipIdx += 1;
          this.roomMockupTheaterQuip.classList.remove('is-changing');
        }, 220);
      };
      showQuip();

      const syncSteps = (elapsedSec) => {
        const steps = this.roomMockupTheaterSteps || [];
        let activeIdx = 0;
        for (let i = MOCKUP_THEATER_STEPS.length - 1; i >= 0; i--) {
          if (elapsedSec >= MOCKUP_THEATER_STEPS[i].afterSec) {
            activeIdx = i;
            break;
          }
        }
        steps.forEach((el, idx) => {
          el.classList.toggle('is-active', idx === activeIdx);
          el.classList.toggle('is-done', idx < activeIdx);
        });
        this.#setMockupTheaterPhase(MOCKUP_THEATER_STEPS[activeIdx]?.key || 'composite');
      };

      const started = Date.now();
      syncSteps(0);
      this.#syncMockupTheaterProgress(0);

      this._mockupTheaterTimers.quip = window.setInterval(showQuip, 4800);
      this._mockupTheaterTimers.step = window.setInterval(() => {
        const elapsedSec = Math.floor((Date.now() - started) / 1000);
        syncSteps(elapsedSec);
      }, 900);
      this._mockupTheaterTimers.progress = window.setInterval(() => {
        this.#syncMockupTheaterProgress(Date.now() - started);
      }, 120);
    }

    #getPreviewZoomSrc() {
      if (this.cropViewport && !this.cropViewport.hidden && this.cropImage?.src) {
        return this.cropImage.src;
      }
      if (this.image && !this.image.hidden && this.image.src) {
        return this.image.src;
      }
      return '';
    }

    #openPreviewZoom() {
      const rawSrc = this.#getPreviewZoomSrc();
      const src = this.#resolveApiAssetUrl(rawSrc);
      if (!src || !this.previewZoomDialog || !this.previewZoomImg) {
        if (!src) {
          this.#showError('Generate or upload a design first, then you can view the print crop area.');
        }
        return;
      }
      this.previewZoomImg.src = src;
      this.previewZoomImg.alt = 'Wallpaper design preview';
      try {
        if (!this.previewZoomDialog.open) {
          if (typeof this.previewZoomDialog.showModal === 'function') {
            this.previewZoomDialog.showModal();
          } else {
            this.previewZoomDialog.setAttribute('open', '');
          }
        }
      } catch (err) {
        console.warn('[ai-generator] preview zoom dialog', err);
        this.previewZoomDialog.setAttribute('open', '');
      }
      this.#applyAdjustmentPreview();
      this.#applyAdjustmentCssVars(this.zoomFrame);
      this.#renderZoomOverlay();
      this.previewZoomImg.addEventListener('load', () => this.#renderZoomOverlay(), { once: true });
    }

    #closePreviewZoom() {
      if (this.previewZoomDialog?.open) {
        this.previewZoomDialog.close();
      }
      if (this.previewZoomImg) {
        this.previewZoomImg.removeAttribute('src');
      }
    }

    /**
     * Draws the crop rectangle (the part of the design that prints to the wall,
     * based on the wall aspect ratio + the customer's drag/zoom framing) and the
     * optional panel guides inside it, in the zoom dialog.
     */
    #renderZoomOverlay() {
      if (!this.zoomCrop) return;
      const cropActive = this.cropViewport && !this.cropViewport.hidden && this.cropNaturalW > 0;
      const crop = cropActive ? this.#getUploadCropMetadata() : null;
      const hasBox =
        crop &&
        crop.crop_norm_width > 0 &&
        crop.crop_norm_height > 0;

      if (!hasBox) {
        this.zoomCrop.hidden = true;
        if (this.zoomPanelsToggle) this.zoomPanelsToggle.hidden = true;
        if (this.zoomLegend) this.zoomLegend.textContent = '';
        return;
      }

      this.zoomCrop.hidden = false;
      this.zoomCrop.style.left = `${crop.crop_norm_left * 100}%`;
      this.zoomCrop.style.top = `${crop.crop_norm_top * 100}%`;
      this.zoomCrop.style.width = `${crop.crop_norm_width * 100}%`;
      this.zoomCrop.style.height = `${crop.crop_norm_height * 100}%`;

      const state = this.measureState();
      const panels = state.panels || 0;
      const canPanels = state.printWidthCm > 0 && panels > 1;
      if (this.zoomPanelsToggle) {
        this.zoomPanelsToggle.hidden = !canPanels;
        if (!canPanels) this._zoomPanelsOn = false;
      }
      this.#syncZoomPanels(state);

      if (this.zoomLegend) {
        const lu = state.lengthUnit;
        const size =
          state.printWidthCm > 0
            ? `${this.#roundDisp(this.#fromCm(state.printWidthCm))}×${this.#roundDisp(
                this.#fromCm(state.printHeightCm)
              )} ${lu}`
            : '';
        const parts = ['Highlighted area is what prints to your wall'];
        if (size) parts.push(size);
        if (canPanels) parts.push(`${panels} panels`);
        this.zoomLegend.textContent = parts.join(' · ');
      }
    }

    #syncZoomPanels(state = this.measureState()) {
      if (this.zoomPanelsToggle) {
        this.zoomPanelsToggle.setAttribute('aria-pressed', String(this._zoomPanelsOn));
        const zoomPanelsState = this.zoomPanelsToggle.querySelector('[data-ai-zoom-panels-state]');
        if (zoomPanelsState) {
          zoomPanelsState.textContent = this._zoomPanelsOn ? 'On' : 'Off';
        }
        this.zoomPanelsToggle.setAttribute(
          'aria-label',
          `Panels, ${this._zoomPanelsOn ? 'on' : 'off'}`
        );
      }
      if (!this.zoomPanels) return;

      const panels = state.panels || 0;
      const show = this._zoomPanelsOn && state.printWidthCm > 0 && panels > 1;
      this.zoomPanels.hidden = !show;
      this.zoomPanels.replaceChildren();
      if (!show) return;

      // Panel boundaries map across the FULL wall, i.e. across the crop rectangle.
      for (let i = 1; i < panels; i += 1) {
        const pct = ((i * this.panelWidth) / state.printWidthCm) * 100;
        if (pct >= 100) break;
        const line = document.createElement('span');
        line.className = 'ai-generator__zoom-panel-line';
        line.style.left = `${pct}%`;
        this.zoomPanels.appendChild(line);
      }
    }

    #syncPreviewStatusBar() {
      const qualityVisible = this.qualityRow && !this.qualityRow.hidden;
      const hasVisual = this.#previewHasVisual();

      if (this.previewStatus) {
        this.previewStatus.hidden = !qualityVisible;
      }

      this.#syncToolbarContext();

      if (this.adjustWrap) {
        this.adjustWrap.hidden = false;
        this.adjustWrap.classList.toggle('is-adjust-teaser', !hasVisual);
      }
      if (this.adjustPanel) {
        this.adjustPanel.classList.toggle('is-adjust-teaser', !hasVisual);
      }
      const adjustDisabled = !hasVisual;
      for (const def of ADJUSTMENT_DEFS) {
        const input = this[def.inputKey];
        if (input) input.disabled = adjustDisabled;
      }
      this.#syncCmykChannelLocks(adjustDisabled);
      if (this.cmykPanel) {
        this.cmykPanel.classList.toggle('is-adjust-teaser', !hasVisual);
      }
      if (this.adjustResetBtn) this.adjustResetBtn.disabled = adjustDisabled;
      if (this.adjustTeaser) this.adjustTeaser.hidden = hasVisual;
      if (this.adjustNote) this.adjustNote.hidden = !hasVisual;
      if (this.tilingPanel) {
        this.tilingPanel.classList.toggle('is-adjust-teaser', !hasVisual);
      }
      if (this.tilingTeaser) this.tilingTeaser.hidden = hasVisual;
    }

    #syncToolbarContext() {
      /* Upload note lives under the preview deck — no toolbar context strip. */
    }

    #bindCmykChannelLocks() {
      for (const btn of this.cmykLockButtons) {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const channel = btn.getAttribute('data-ai-cmyk-lock');
          if (!channel || !(channel in this._cmykChannelLocked)) return;
          this._cmykChannelLocked[channel] = !this._cmykChannelLocked[channel];
          this.#syncCmykChannelLocks(!this.#previewHasVisual());
        });
      }
      this.#syncCmykChannelLocks(!this.#previewHasVisual());
    }

    #syncCmykChannelLocks(adjustDisabled = !this.#previewHasVisual()) {
      for (const def of CMYK_ADJUSTMENT_DEFS) {
        const channel = def.channel;
        const locked = Boolean(this._cmykChannelLocked[channel]);
        const input = this[def.inputKey];
        const col = this.root.querySelector(`[data-ai-cmyk-col="${channel}"]`);
        const btn = this.root.querySelector(`[data-ai-cmyk-lock="${channel}"]`);
        const label = (def.label || channel).toLowerCase();

        if (input) input.disabled = adjustDisabled || locked;
        col?.classList.toggle('is-cmyk-channel-locked', locked);
        if (btn) {
          btn.classList.toggle('is-locked', locked);
          btn.setAttribute('aria-pressed', locked ? 'true' : 'false');
          btn.setAttribute(
            'aria-label',
            locked ? `Unlock ${label} channel` : `Lock ${label} channel`,
          );
          btn.title = locked ? `Unlock to adjust ${label}` : `Lock ${label} channel`;
        }
      }
    }

    #sliderToMultiplier(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return 1;
      return Math.min(1.5, Math.max(0.5, n / 100));
    }

    #onAdjustInput() {
      for (const def of ALL_ADJUSTMENT_DEFS) {
        this[def.prop] = this.#sliderToMultiplier(this[def.inputKey]?.value);
      }
      this.#syncAdjustControlUi();
      this.#applyAdjustmentPreview();
      window.clearTimeout(this._adjustPersistTimer);
      this._adjustPersistTimer = window.setTimeout(() => this.#persistCropToAsset(), 120);
    }

    #resetAdjustments(persist = false) {
      for (const def of ALL_ADJUSTMENT_DEFS) {
        this[def.prop] = 1;
        const input = this[def.inputKey];
        if (input) input.value = '100';
      }
      this.#syncAdjustControlUi();
      this.#applyAdjustmentPreview();
      if (persist) this.#persistCropToAsset();
    }

    #syncAdjustControlUi() {
      for (const def of ALL_ADJUSTMENT_DEFS) {
        const input = this[def.inputKey];
        const valueEl = this[def.valueKey];
        const multiplier = this[def.prop];
        const pct = Math.round(multiplier * 100);
        if (valueEl) valueEl.textContent = String(pct);
        if (input) {
          const fill = `${Math.max(0, Math.min(100, Number(input.value) - 50))}%`;
          input.style.setProperty('--ai-adjust-pct', fill);
        }
      }
    }

    #cmykChannelScale(multiplier) {
      return 2 - multiplier;
    }

    #syncCmykFilterMatrix() {
      const matrixEl = this.cmykMatrix;
      if (!matrixEl) return;
      const black = this.#cmykChannelScale(this.adjustBlack);
      const r = this.#cmykChannelScale(this.adjustCyan) * black;
      const g = this.#cmykChannelScale(this.adjustMagenta) * black;
      const b = this.#cmykChannelScale(this.adjustYellow) * black;
      matrixEl.setAttribute(
        'values',
        `${r} 0 0 0 0  0 ${g} 0 0 0  0 0 ${b} 0 0  0 0 0 1 0`,
      );
    }

    /** CSS custom properties consumed by preview + zoom image filters. */
    #getAdjustmentCssVars() {
      const vars = {};
      for (const def of ADJUSTMENT_DEFS) {
        if (def.prop === 'adjustShadow') {
          vars[def.cssVar] = String(Math.pow(this[def.prop], 0.4));
        } else {
          vars[def.cssVar] = String(this[def.prop]);
        }
      }
      return vars;
    }

    #applyAdjustmentCssVars(target) {
      if (!target) return;
      const vars = this.#getAdjustmentCssVars();
      for (const [name, value] of Object.entries(vars)) {
        target.style.setProperty(name, value);
      }
    }

    #applyAdjustmentPreview() {
      this.#syncCmykFilterMatrix();
      for (const target of [this.root, this.cropViewport, this.zoomFrame]) {
        this.#applyAdjustmentCssVars(target);
      }
    }

    #shouldShowCropWatermark() {
      return false;
    }

    async #syncCropWatermarkOverlay() {
      const canvas = this.cropWatermarkCanvas;
      const viewport = this.cropViewport;
      if (!canvas || !viewport || viewport.hidden) return;

      const show = this.#shouldShowCropWatermark();
      canvas.hidden = !show;
      if (!show) return;

      const w = Math.max(1, Math.floor(viewport.clientWidth));
      const h = Math.max(1, Math.floor(viewport.clientHeight));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      await this.#drawCanvasWatermark(ctx, w, h);
    }

    #getAdjustmentMetadata() {
      const meta = {};
      for (const def of ALL_ADJUSTMENT_DEFS) {
        meta[def.metaKey] = Number(this[def.prop].toFixed(3));
      }
      return meta;
    }

    #getWallpaperLayout() {
      const fromAsset = this.currentAsset?.metadata?.wallpaper_layout;
      const fromSelect = this.wallpaperLayoutSelect?.value;
      const layout = fromSelect || fromAsset || 'single';
      return WALLPAPER_LAYOUT_LABELS[layout] ? layout : 'single';
    }

    /** Room mockup — one mural on the wall unless the design is an explicit seamless repeat. */
    #getMockupWallpaperLayout(asset = this.currentAsset) {
      const tiled = Boolean(
        this.seamlessRepeatInput?.checked ||
          asset?.tiled ||
          asset?.metadata?.tiled ||
          asset?.metadata?.seamless_repeat
      );
      if (!tiled) return 'single';
      const layout = this.#getWallpaperLayout();
      return layout === 'single' ? 'full' : layout;
    }

    #getRoomEditorMetrics() {
      const stage = this.roomCornerStage;
      const img = this.roomCornerImg;
      if (!stage || !img) return null;
      const stageRect = stage.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();
      if (!stageRect.width || !stageRect.height || !imgRect.width || !imgRect.height) return null;
      return {
        stageRect,
        imgRect,
        offsetX: imgRect.left - stageRect.left,
        offsetY: imgRect.top - stageRect.top,
        imgW: imgRect.width,
        imgH: imgRect.height,
        stageW: stageRect.width,
        stageH: stageRect.height,
      };
    }

    #clientToRoomNorm(clientX, clientY) {
      const m = this.#getRoomEditorMetrics();
      if (!m) return null;
      const clamp01 = (n) => Math.max(0, Math.min(1, n));
      return {
        x: clamp01((clientX - m.stageRect.left - m.offsetX) / m.imgW),
        y: clamp01((clientY - m.stageRect.top - m.offsetY) / m.imgH),
      };
    }

    #roomNormToStagePct(x, y) {
      const m = this.#getRoomEditorMetrics();
      if (!m) return { left: x * 100, top: y * 100 };
      return {
        left: ((m.offsetX + x * m.imgW) / m.stageW) * 100,
        top: ((m.offsetY + y * m.imgH) / m.stageH) * 100,
      };
    }

    #getPatternRepeatCm() {
      const n = Number(this.patternRepeatInput?.value || this.currentAsset?.metadata?.pattern_repeat_cm);
      if (!Number.isFinite(n)) return 52;
      return Math.min(120, Math.max(30, Math.round(n)));
    }

    #getWallpaperPrefsMetadata() {
      return {
        wallpaper_layout: this.#getWallpaperLayout(),
        pattern_repeat_cm: this.#getPatternRepeatCm(),
        seamless_repeat: Boolean(this.seamlessRepeatInput?.checked),
      };
    }

    /** Seamless switch label stays static beside the prompt. */
    #syncRepeatToggleLabel() {
      const title = this.root.querySelector('[data-ai-repeat-title]');
      if (title) title.textContent = 'Seamless';
    }

    /** Discourage save-as and right-click on watermarked preview pixels. */
    #bindPreviewProtection() {
      const blockContext = (event) => {
        const t = event.target;
        if (t?.closest?.('button, input, textarea, select, a, [contenteditable="true"], label')) return;
        if (t?.closest?.('[data-ai-crop-viewport]')) return;
        event.preventDefault();
      };

      for (const zone of [this.previewMount, this.preview, this.previewStage].filter(Boolean)) {
        zone.addEventListener('contextmenu', blockContext);
        zone.addEventListener('dragstart', (event) => {
          if (event.target?.matches?.('img, canvas')) event.preventDefault();
        });
      }

      const protectImage = (el) => {
        if (!el) return;
        el.setAttribute('draggable', 'false');
        el.addEventListener('dragstart', (e) => e.preventDefault());
      };
      protectImage(this.image);
      protectImage(this.roomResultImg);
      protectImage(this.previewZoomImg);

      this.root.addEventListener(
        'keydown',
        (event) => {
          if (!(event.ctrlKey || event.metaKey)) return;
          const key = event.key?.toLowerCase();
          if (key === 's' || key === 'p') {
            if (this.preview?.classList.contains('has-content')) event.preventDefault();
          }
        },
        true
      );
    }

    #syncSeamlessHint() {
      if (!this.seamlessHintEl) return;

      const hasDesign = Boolean(this.currentAsset);
      const canAdaptCurrent =
        hasDesign &&
        (String(this.currentAsset?.aiImageId || '').startsWith('gen_') ||
          String(this.currentAsset?.aiImageId || '').startsWith('upload_'));

      let text;
      if (!hasDesign) {
        text =
          'Optimised for wallpaper repeat. Creates a repeat-ready pattern when you Generate — it may look different from the same prompt with this turned off.';
      } else if (canAdaptCurrent) {
        text =
          'Optimised for wallpaper repeat. Applies to your next Generate or Refine only — it will not change the design you have. To adapt your current design, use Make seamless on the toolbar.';
      } else {
        text =
          'Optimised for wallpaper repeat. Applies to your next Generate only — it will not change the design or image you already have.';
      }

      this.seamlessHintEl.textContent = text;
    }

    #syncWallpaperLayoutUi() {
      const layout = this.#getWallpaperLayout();
      const repeatCm = this.#getPatternRepeatCm();
      if (this.wallpaperLayoutSelect && this.wallpaperLayoutSelect.value !== layout) {
        this.wallpaperLayoutSelect.value = layout;
      }
      if (this.patternRepeatLabel) {
        this.patternRepeatLabel.textContent = `${repeatCm} cm`;
      }
      if (this.patternRepeatInput) {
        this.patternRepeatInput.value = String(repeatCm);
      }

      const isRepeatLayout = layout !== 'single';
      const hasVisual = this.#previewHasVisual();
      if (this.patternRepeatInput) {
        this.patternRepeatInput.disabled = !hasVisual || !isRepeatLayout;
      }
      if (this.repeatPreviewBtn) {
        this.repeatPreviewBtn.disabled = !hasVisual || !this.currentAsset || !isRepeatLayout;
      }
      if (this.wallpaperLayoutSelect) {
        this.wallpaperLayoutSelect.disabled = !hasVisual;
      }

      const state = this.#computeMeasureState();
      if (this.repeatCountHint) {
        if (!isRepeatLayout || !state.printWidthCm) {
          this.repeatCountHint.textContent = isRepeatLayout
            ? 'Enter wall width to see how many repeats fit across your wall.'
            : 'Single image — no repeat. Your design scales to your wall size.';
        } else {
          const repeats = Math.max(1, Math.round(state.printWidthCm / repeatCm));
          this.repeatCountHint.textContent =
            layout === 'horizontal'
              ? `About ${repeats} repeats across your wall (one tall band).`
              : layout === 'full'
                ? `About ${repeats} repeats across · pattern tiles down the wall too.`
                : `Pattern repeats vertically on your wall.`;
        }
      }

      if (this.tilePatternBtn) {
        const label =
          this.tilePatternBtn.querySelector('[data-ai-tile-pattern-label]') ||
          this.tilePatternBtn.querySelector('.ai-generator__button-label');
        if (label) {
          label.textContent = this.currentAsset?.tiled ? 'Re-make seamless' : 'Make seamless';
        }
        this.tilePatternBtn.title = isRepeatLayout
          ? 'Adapt your current design into a seamless repeat for printing'
          : 'Switch wallpaper style to a repeat option first, or preview how repeat would look';
      }

      const hasDesign = Boolean(this.currentAsset);
      if (this.tilingSummary) {
        if (hasDesign) {
          const short = {
            single: 'Single mural',
            horizontal: 'Band repeat',
            full: 'Full repeat',
            vertical: 'Vertical repeat',
          };
          this.tilingSummary.hidden = false;
          this.tilingSummary.textContent = isRepeatLayout
            ? `${repeatCm} cm · ${short[layout] || layout}`
            : short.single;
        } else {
          this.tilingSummary.hidden = true;
        }
      }

      this.#scheduleRepeatOverlayUpdate();
    }

    #scheduleRepeatOverlayUpdate() {
      if (this._repeatOverlayRaf) cancelAnimationFrame(this._repeatOverlayRaf);
      this._repeatOverlayRaf = requestAnimationFrame(() => {
        this._repeatOverlayRaf = 0;
        this.#updateRepeatOverlay();
        this.#scheduleRepeatThumbUpdate();
      });
    }

    #scheduleRepeatThumbUpdate() {
      if (this._repeatThumbRaf) cancelAnimationFrame(this._repeatThumbRaf);
      this._repeatThumbRaf = requestAnimationFrame(() => {
        this._repeatThumbRaf = 0;
        this.#updateRepeatThumb();
      });
    }

    async #updateRepeatThumb() {
      const canvas = this.repeatThumbCanvas;
      const wrap = this.repeatThumbWrap;
      const placeholder = this.repeatThumbPlaceholder;
      if (!canvas || !wrap) return;

      const layout = this.#getWallpaperLayout();
      const isRepeat = layout !== 'single';
      const hasVisual = this.#previewHasVisual();

      const showPlaceholder = () => {
        wrap.hidden = false;
        wrap.classList.add('is-placeholder');
        wrap.classList.remove('has-preview');
        placeholder?.removeAttribute('hidden');
        canvas.hidden = true;
      };

      if (!isRepeat || !hasVisual || !this.currentAsset) {
        showPlaceholder();
        return;
      }

      const src =
        this.cropImage?.src ||
        this.image?.src ||
        this.currentAsset.originalUrl ||
        this.currentAsset.previewUrl;
      if (!src) {
        showPlaceholder();
        return;
      }

      const displayW = Math.max(48, Math.floor(wrap.clientWidth || 0));
      if (displayW < 24) return;

      wrap.hidden = false;
      const displayH = Math.max(28, Math.floor(wrap.clientHeight || 36));
      const token = ++this._repeatThumbToken;

      try {
        await this.#drawRepeatPreviewOnCanvas(canvas, src, layout, {
          displayWidth: displayW,
          displayHeight: displayH,
        });
        if (token !== this._repeatThumbToken) return;
        wrap.classList.remove('is-placeholder');
        wrap.classList.add('has-preview');
        placeholder?.setAttribute('hidden', '');
        canvas.hidden = false;
      } catch {
        showPlaceholder();
      }
    }

    #updateRepeatOverlay() {
      const canvas = this.repeatOverlayCanvas;
      const viewport = this.cropViewport;
      if (!canvas || !viewport || viewport.hidden) return;

      const layout = this.#getWallpaperLayout();
      const isRepeat = layout !== 'single';
      canvas.hidden = !isRepeat;
      this.root.classList.toggle('is-repeat-layout', isRepeat);
      if (!isRepeat) return;

      const state = this.#computeMeasureState();
      if (!(state.printWidthCm > 0 && state.printHeightCm > 0)) {
        canvas.hidden = true;
        return;
      }

      const { w: vw, h: vh } = this.#getCropViewportSize();
      if (!vw || !vh) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = Math.max(1, Math.round(vw * dpr));
      const ch = Math.max(1, Math.round(vh * dpr));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }
      canvas.style.width = `${vw}px`;
      canvas.style.height = `${vh}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, vw, vh);

      const repeatCm = this.#getPatternRepeatCm();
      const tileW = vw * (repeatCm / state.printWidthCm);
      const aspect =
        this.cropNaturalW > 0 && this.cropNaturalH > 0
          ? this.cropNaturalH / this.cropNaturalW
          : state.printHeightCm / state.printWidthCm;
      let tileH = tileW * aspect;
      if (layout === 'horizontal') tileH = vh;

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 5]);

      for (let x = tileW; x < vw - 0.5; x += tileW) {
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, vh);
        ctx.stroke();
      }

      if (layout !== 'horizontal') {
        for (let y = tileH; y < vh - 0.5; y += tileH) {
          ctx.beginPath();
          ctx.moveTo(0, y + 0.5);
          ctx.lineTo(vw, y + 0.5);
          ctx.stroke();
        }
      }

      ctx.setLineDash([]);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.strokeRect(0.5, 0.5, tileW - 1, tileH - 1);
      this.#schedulePreviewTrimUpdate();
    }

    async #openRepeatPreview() {
      if (!this.currentAsset || !this.repeatCanvas) return;
      const layout = this.#getWallpaperLayout();
      if (layout === 'single') {
        this.#showError('Choose a repeat wallpaper style to preview tiling.');
        return;
      }
      const src =
        this.cropImage?.src ||
        this.image?.src ||
        this.currentAsset.originalUrl ||
        this.currentAsset.previewUrl;
      if (!src) return;

      await this.#drawRepeatPreviewOnCanvas(this.repeatCanvas, src, layout);
      this.repeatDialog?.showModal?.();
    }

    async #drawRepeatPreviewOnCanvas(canvas, src, layout, opts = {}) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const state = this.#computeMeasureState();
      const printWcm = state.printWidthCm || 300;
      const printHcm = state.printHeightCm || 240;
      const repeatCm = this.#getPatternRepeatCm();

      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Could not load design for repeat preview'));
        el.src = src;
      });

      let canvasW;
      let canvasH;
      if (opts.displayWidth && opts.displayHeight) {
        canvasW = Math.max(1, Math.round(opts.displayWidth));
        canvasH = Math.max(1, Math.round(opts.displayHeight));
        canvas.style.width = `${canvasW}px`;
        canvas.style.height = `${canvasH}px`;
      } else {
        const maxDim = opts.maxDim ?? 720;
        const fitScale = Math.min(maxDim / printWcm, maxDim / printHcm);
        canvasW = Math.max(1, Math.round(printWcm * fitScale));
        canvasH = Math.max(1, Math.round(printHcm * fitScale));
        canvas.style.width = '';
        canvas.style.height = '';
      }

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(canvasW * dpr));
      canvas.height = Math.max(1, Math.round(canvasH * dpr));

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, canvasW, canvasH);

      const tileW = canvasW * (repeatCm / printWcm);
      const aspect = img.height / img.width;
      let tileH = tileW * aspect;
      if (layout === 'horizontal') tileH = canvasH;

      const cols = Math.ceil(canvasW / tileW) + 1;
      const rows = layout === 'horizontal' ? 1 : Math.ceil(canvasH / tileH) + 1;

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const dx = col * tileW;
          const dy = row * tileH;
          if (layout === 'horizontal') {
            ctx.drawImage(img, dx, 0, tileW, canvasH);
          } else {
            ctx.drawImage(img, dx, dy, tileW, tileH);
          }
        }
      }
    }

    #loadAdjustmentField(meta, def) {
      const n = Number(meta[def.metaKey]);
      if (Number.isFinite(n) && n >= 0.5 && n <= 1.5) {
        this[def.prop] = n;
        const input = this[def.inputKey];
        if (input) input.value = String(Math.round(n * 100));
        return;
      }
      this[def.prop] = 1;
      const input = this[def.inputKey];
      if (input) input.value = '100';
    }

    #loadAdjustmentsFromAsset() {
      const meta = this.currentAsset?.metadata || {};
      for (const def of ALL_ADJUSTMENT_DEFS) {
        this.#loadAdjustmentField(meta, def);
      }
      this.#syncAdjustControlUi();
      this.#applyAdjustmentPreview();
      this.#loadWallpaperPrefsFromAsset();
    }

    #loadWallpaperPrefsFromAsset() {
      const meta = this.currentAsset?.metadata || {};
      if (this.wallpaperLayoutSelect && meta.wallpaper_layout) {
        const layout = String(meta.wallpaper_layout);
        if (WALLPAPER_LAYOUT_LABELS[layout]) {
          this.wallpaperLayoutSelect.value = layout;
        }
      }
      if (this.patternRepeatInput && meta.pattern_repeat_cm != null) {
        const n = Number(meta.pattern_repeat_cm);
        if (Number.isFinite(n)) this.patternRepeatInput.value = String(n);
      }
      if (this.seamlessRepeatInput && typeof meta.seamless_repeat === 'boolean') {
        this.seamlessRepeatInput.checked = meta.seamless_repeat;
      }
      this.#syncWallpaperLayoutUi();
      this.#syncSeamlessHint();
      this.#syncRepeatToggleLabel();
    }

    #currentEffectiveDpi(state = this.calculate()) {
      const asset = this.currentAsset;
      if (asset?.metadata?.upscale_width_px && state.printWidthCm > 0 && state.printHeightCm > 0) {
        const dpiW = asset.metadata.upscale_width_px / (state.printWidthCm / CM_PER_INCH);
        const dpiH = asset.metadata.upscale_height_px / (state.printHeightCm / CM_PER_INCH);
        return Math.floor(Math.min(dpiW, dpiH));
      }
      const w = asset?.source === 'uploaded' ? asset.uploadWidth : asset?.genWidth;
      const h = asset?.source === 'uploaded' ? asset.uploadHeight : asset?.genHeight;
      if (!asset || !w || !h || !(state.printWidthCm > 0 && state.printHeightCm > 0)) return 0;
      const dpiW = w / (state.printWidthCm / CM_PER_INCH);
      const dpiH = h / (state.printHeightCm / CM_PER_INCH);
      return Math.floor(Math.min(dpiW, dpiH));
    }

    #updateQualityBadge(state) {
      if (!this.qualityBadge) return;
      const asset = this.currentAsset;
      const w =
        asset?.metadata?.upscale_width_px ||
        (asset?.source === 'uploaded' ? asset.uploadWidth : asset?.genWidth);
      const h =
        asset?.metadata?.upscale_height_px ||
        (asset?.source === 'uploaded' ? asset.uploadHeight : asset?.genHeight);

      if (!asset || !w || !h || !(state.printWidthCm > 0 && state.printHeightCm > 0)) {
        this.qualityBadge.hidden = true;
        if (this.qualityRow) this.qualityRow.hidden = true;
        if (this.qualityUpscaleNote) this.qualityUpscaleNote.hidden = true;
        if (this.qualityRow) this.qualityRow.classList.remove('is-upscale');
        this.#syncUpscaleAckVisibility(false);
        this.#syncPreviewOwnershipNote(null);
        this.#syncPreviewStatusBar();
        return;
      }

      const dpiW = w / (state.printWidthCm / CM_PER_INCH);
      const dpiH = h / (state.printHeightCm / CM_PER_INCH);
      const effDpi = Math.floor(Math.min(dpiW, dpiH));

      let label;
      let cls;
      if (effDpi >= this.recommendedPrintDpi) {
        label = 'Print ready · excellent';
        cls = 'is-excellent';
      } else if (effDpi >= this.minPrintDpi) {
        label = 'Print ready · good';
        cls = 'is-good';
      } else {
        label = 'Enhanced for print';
        cls = 'is-upscale';
      }
      this.qualityBadge.className = `ai-generator__badge ${cls}`;
      if (this.qualityBadgeText) this.qualityBadgeText.textContent = label;
      this.qualityBadge.hidden = false;
      if (this.qualityRow) {
        this.qualityRow.hidden = false;
        this.qualityRow.classList.toggle('is-upscale', cls === 'is-upscale');
      }
      if (this.qualityUpscaleNote) {
        this.qualityUpscaleNote.hidden = true;
      }
      this.#syncUpscaleAckVisibility(cls === 'is-upscale');
      this.#syncPreviewOwnershipNote(asset);
      this.#syncPreviewStatusBar();
    }

    /**
     * Re-checks an already-uploaded image when the wall size changes. Enlarging
     * the wall lowers effective DPI, so a previously-fine upload can become too
     * low-res — in which case we clear it and warn.
     * @param {ReturnType<AIImageGenerator['calculate']>} state
     */
    #revalidateUpload(state) {
      const asset = this.currentAsset;
      if (!asset || asset.source !== 'uploaded' || !asset.uploadWidth) return;

      const quality = this.#assessUploadQuality({ w: asset.uploadWidth, h: asset.uploadHeight }, state);
      if (quality.block) {
        this.#resetResult();
        this.#setUploadStatus('', '');
        this.#showError(quality.message);
        if (this.fileInput) this.fileInput.value = '';
        return;
      }
      this.#setUploadStatus(`${asset.fileName} — ${quality.message}`, quality.status);
    }

    /** @param {number} ratio width ÷ height */
    #formatAspectRatio(ratio) {
      const presets = [
        [1, '1∶1'],
        [16 / 9, '16∶9'],
        [9 / 16, '9∶16'],
        [4 / 5, '4∶5'],
        [5 / 4, '5∶4'],
      ];
      for (const [target, label] of presets) {
        if (Math.abs(ratio - target) < 0.02) return label;
      }
      return ratio >= 1 ? `${ratio.toFixed(2)}∶1` : `1∶${(1 / ratio).toFixed(2)}`;
    }

    /** Color-coded span matching the wall diagram legend (print / wall / trim). */
    #wallLegendHtml(kind, text) {
      const cls = {
        print: 'ai-generator__wall-legend--print',
        wall: 'ai-generator__wall-legend--wall',
        trim: 'ai-generator__wall-legend--trim',
      }[kind];
      if (!cls) return String(text);
      return `<span class="${cls}">${text}</span>`;
    }

    #syncWallPreviewCopy(state) {
      const lu = state.lengthUnit;
      const hasDims = state.widthDisp > 0 && state.heightDisp > 0;
      const trimCm = state.bleedCm;
      const trimTotal = this.#roundTrimDisp(this.#fromCm(trimCm));
      const trimSide = this.#roundTrimDisp(this.#fromCm(this.#trimSideCm(trimCm)));

      if (this.ratioNote) {
        if (!hasDims && !this.isWallShape) {
          const ratioText = this.#formatAspectRatio(this.#previewAspectRatio(0, 0));
          this.ratioNote.hidden = false;
          this.ratioNote.classList.add('is-reminder');
          this.ratioNote.textContent = `Fixed ${this.aspectSelect?.value || 'shape'} (${ratioText}) — preview uses this ratio only. Enter wall size above for measurements, pricing, and print tools.`;
        } else if (!hasDims) {
          this.ratioNote.hidden = true;
          this.ratioNote.classList.remove('is-reminder');
          this.ratioNote.textContent = '';
        } else if (this.isWallShape) {
          this.ratioNote.hidden = false;
          const ratioLabel = this.#formatAspectRatio(state.printWidthCm / state.printHeightCm);
          const bleedNote =
            state.bleedCm > 0
              ? ` Includes ${trimTotal} ${lu} trim allowance on width and height (${trimSide} ${lu} per side) — print size ${this.#roundDisp(this.#fromCm(state.printWidthCm))} × ${this.#roundDisp(this.#fromCm(state.printHeightCm))} ${lu}.`
              : ' Trim allowance removed — print matches your wall measurements exactly.';
          const panelNote =
            state.panels > 1
              ? ` Panel guides show ${state.panels} cuts at ${this.#roundDisp(this.#fromCm(this.panelWidth))} ${lu} wide.`
              : '';
          this.ratioNote.classList.add('is-reminder');
          this.ratioNote.textContent = `Wall shape ${ratioLabel} · ${this.#roundDisp(state.widthDisp)} × ${this.#roundDisp(state.heightDisp)} ${lu}.${bleedNote}${panelNote} Step 2 preview and AI image match this ratio.`;
        } else {
          this.ratioNote.hidden = false;
          this.ratioNote.classList.add('is-reminder');
          this.ratioNote.textContent = `Fixed shape ${this.aspectSelect?.value || 'custom'} — enter wall size above for accurate pricing. Panel guides use your print width once dimensions are set.`;
        }
      }

      if (!hasDims || !(state.printWidthCm > 0)) {
        if (!this.isWallShape) {
          const ratioText = this.#formatAspectRatio(this.#previewAspectRatio(0, 0));
          const shapeLabel = this.aspectSelect?.selectedOptions[0]?.textContent?.trim() || this.aspectSelect?.value || 'Fixed shape';
          if (this.wallRatioLabel) {
            this.wallRatioLabel.textContent = `Aspect ratio ${ratioText} · ${shapeLabel}`;
          }
          if (this.wallCaption) {
            this.wallCaption.textContent =
              'No wall measurements — diagram shows the selected shape only. Enter width and height for print size and panel guides.';
          }
          if (this.wallGuide) {
            this.wallGuide.textContent =
              'Tools that need your wall size (mockup, panels, cart) stay unavailable until dimensions are entered.';
          }
          return;
        }
        if (this.wallRatioLabel) {
          this.wallRatioLabel.textContent = 'Live aspect-ratio preview';
        }
        if (this.wallCaption) {
          this.wallCaption.textContent =
            'Enter your wall width and height — this diagram scales to your print size with panel guides.';
        }
        if (this.wallGuide) {
          this.wallGuide.innerHTML =
            trimCm > 0
              ? `${this.#wallLegendHtml('print', 'Teal lines')} = print size. ${this.#wallLegendHtml('wall', 'White lines')} = your wall. ${this.#wallLegendHtml('trim', 'Orange labels')} = ${trimSide} ${lu} trim allowance per side (${trimTotal} ${lu} total per dimension).`
              : `${this.#wallLegendHtml('print', 'Teal lines')} match your wall exactly (trim allowance removed). Panel guides appear when print width exceeds one panel.`;
        }
        return;
      }

      const ratioText = this.#formatAspectRatio(state.printWidthCm / state.printHeightCm);
      const printW = this.#roundDisp(this.#fromCm(state.printWidthCm));
      const printH = this.#roundDisp(this.#fromCm(state.printHeightCm));
      const wallW = this.#roundDisp(state.widthDisp);
      const wallH = this.#roundDisp(state.heightDisp);
      const panelSize = this.#roundDisp(this.#fromCm(this.panelWidth));
      const printDims = `${printW} × ${printH} ${lu}`;
      const wallDims = `${wallW} × ${wallH} ${lu}`;

      if (this.wallRatioLabel) {
        this.wallRatioLabel.innerHTML = `Aspect ratio ${ratioText} · ${this.#wallLegendHtml('print', `Print ${printDims}`)}`;
      }
      if (this.wallCaption) {
        this.wallCaption.innerHTML =
          state.bleedCm > 0
            ? `${this.#wallLegendHtml('wall', `Wall ${wallDims}`)} + ${this.#wallLegendHtml('trim', `${trimTotal} ${lu} trim allowance (${trimSide} ${lu}/side)`)} → ${this.#wallLegendHtml('print', `print ${printDims}`)}.`
            : `${this.#wallLegendHtml('print', `Print ${printDims}`)} (trim allowance removed — matches your wall exactly).`;
      }
      if (this.wallGuide) {
        const guidesHiddenNote = !this.showPanelLinesOnPreview ? ' Panel guides are hidden.' : '';
        const panelsLine =
          state.panels > 1
            ? `Dashed panel guides: ${state.panels} panels at ${panelSize} ${lu} wide across the ${this.#wallLegendHtml('print', 'print width')}.${guidesHiddenNote}`
            : `Panel guides appear when your ${this.#wallLegendHtml('print', 'print width')} exceeds one panel.`;
        this.wallGuide.innerHTML = `${panelsLine} ${this.#wallLegendHtml('print', 'Teal')} = print, ${this.#wallLegendHtml('wall', 'white')} = wall, ${this.#wallLegendHtml('trim', 'orange')} = ${trimSide} ${lu} trim per side.`;
      }
      this.#syncPanelGuideControls(state);
    }

    #bindPreviewExpandToggles() {
      for (const btn of this.previewExpandToggles) {
        if (btn.dataset.aiExpandBound === 'true') continue;
        btn.dataset.aiExpandBound = 'true';
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const target = btn.dataset.aiLayoutTarget;
          if (target === 'wide') {
            if (!this._previewExpanded) this.#setPreviewExpanded(true);
          } else if (target === 'studio') {
            if (this._previewExpanded) this.#setPreviewExpanded(false);
          } else {
            this.#setPreviewExpanded(!this._previewExpanded);
          }
        });
      }
    }

    #handleLockedAlignGrid(event, btn) {
      if (!(btn instanceof HTMLButtonElement)) return false;
      if (btn.getAttribute('aria-disabled') !== 'true') return false;
      event.preventDefault();
      event.stopPropagation();
      const state = this.#computeMeasureState();
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      if (!hasDims) this.#scrollToSection('wall');
      else if (!this.#previewHasVisual()) this.#scrollToSection('design');
      return true;
    }

    #toggleAlignGrid() {
      if (!this.#previewGridAvailable()) {
        const state = this.#computeMeasureState();
        const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
        if (!hasDims) this.#scrollToSection('wall');
        else this.#scrollToSection('design');
        return;
      }
      this.showAlignGridOnPreview = !this.showAlignGridOnPreview;
      this._previewFitKey = '';
      this.#syncAlignGridOverlay();
      this.#syncAlignGridControls();
    }

    /** Alignment grid applies on the placeholder wall or when a design image is shown. */
    #previewGridAvailable(state = this.#computeMeasureState()) {
      if (this.#previewHasVisual()) return true;
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      return hasDims && this.preview?.classList.contains('is-awaiting-design');
    }

    #syncAlignGridOverlay(state = this.#computeMeasureState()) {
      const el = this.alignGridEl;
      const available = this.#previewGridAvailable(state);
      const show = available && this.showAlignGridOnPreview;
      const showOverlay = show && this.#previewHasVisual();

      if (el) {
        el.hidden = !showOverlay;
        el.setAttribute('aria-hidden', String(!showOverlay));
      }

      for (const node of [this.preview, this.previewMount]) {
        if (!node) continue;
        if (available) {
          node.classList.toggle('is-align-grid-on', show);
          node.classList.toggle('is-align-grid-off', !show);
        } else {
          node.classList.remove('is-align-grid-on', 'is-align-grid-off');
        }
      }
    }

    #syncAlignGridControls(state = this.#computeMeasureState()) {
      const btn = this.alignGridToggle;
      if (!btn) return;
      const available = this.#previewGridAvailable(state);
      const on = this.showAlignGridOnPreview;
      const locked = !available;

      btn.disabled = false;
      btn.setAttribute('aria-disabled', String(locked));
      btn.classList.toggle('is-action-locked', locked);
      btn.classList.toggle('is-preview-teaser', locked);
      btn.setAttribute('aria-pressed', String(on));
      btn.classList.toggle('is-guides-on', available && on);
      btn.classList.toggle('is-guides-off', available && !on);
      btn.classList.toggle('is-align-grid-on', available && on);
      btn.classList.toggle('is-align-grid-off', available && !on);
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      btn.title = !hasDims
        ? 'Enter wall width and height first'
        : on
          ? 'Turn alignment grid off'
          : 'Turn alignment grid on';
      btn.setAttribute('aria-label', `Alignment grid, ${on ? 'on' : 'off'}`);

      const stateEl = btn.querySelector('[data-ai-align-grid-state]');
      if (stateEl) {
        const showGreen = available && on;
        stateEl.classList.toggle('is-on', showGreen);
        stateEl.classList.toggle('is-off', !showGreen);
      }

      this.#syncAlignGridOverlay(state);
    }

    #handleLockedPanelGuides(event, btn) {
      if (!(btn instanceof HTMLButtonElement)) return false;
      if (btn.getAttribute('aria-disabled') !== 'true') return false;
      event.preventDefault();
      event.stopPropagation();
      const state = this.#computeMeasureState();
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      const multiPanel = state.panels > 1;
      if (!hasDims) {
        this.#scrollToSection('wall');
        return true;
      }
      if (!multiPanel) {
        if (this.wallCaption) {
          this.wallCaption.textContent =
            'Your print fits on one panel — panel cut guides appear when width needs multiple panels.';
        }
        return true;
      }
      return false;
    }

    #handleLockedToolbarAction(event, btn) {
      if (!(btn instanceof HTMLButtonElement)) return false;
      const busy = this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling || this.isMockup;
      if (busy) {
        event.preventDefault();
        event.stopPropagation();
        if (btn.matches('[data-ai-tile-pattern], [data-ai-room-mockup], [data-ai-upscale], [data-ai-upscale-pro]')) {
          this.#showError('Please wait — the studio is still busy with another task.');
        }
        return true;
      }
      if (btn.getAttribute('aria-disabled') !== 'true') return false;
      event.preventDefault();
      event.stopPropagation();
      if (!this.customerLoggedIn && !isStudioGuestMode(this) && btn.matches('[data-ai-room-mockup], [data-ai-tile-pattern], [data-ai-upscale], [data-ai-upscale-pro]')) {
        this.#showError('Log in to use this tool.');
        if (!isStudioGuestMode(this)) {
          window.location.href = this.accountLoginUrl;
        }
        return true;
      }
      if (btn.matches('[data-ai-tile-pattern]')) {
        if (!this.currentAsset) {
          this.#showError('Add a design first, then you can adapt it for wallpaper repeat.');
          this.#scrollToSection('design');
          return true;
        }
        if (!this.#isServerBackedAsset()) {
          this.#showError(
            'Make seamless needs a server-saved design. Upload or generate again while WallPrint AI is connected, then retry.'
          );
          this.#scrollToSection('design');
          return true;
        }
        return true;
      }
      if (!this.currentAsset && btn.matches('[data-ai-room-mockup]')) {
        this.#showError('Add a design first — generate or upload in the panel on the left.');
        this.#scrollToSection('design');
        return true;
      }
      if (btn.matches('[data-ai-room-mockup]')) {
        if (!this.#isServerBackedAsset()) {
          this.#showError(
            'Wall mockup needs a server-saved design. Upload or generate again while WallPrint AI is connected.'
          );
          this.#scrollToSection('design');
        } else {
          this.#showError('Add your wall size and a design first.');
          this.#scrollToSection('wall');
        }
        return true;
      }
      return true;
    }

    #togglePanelGuides() {
      const state = this.#computeMeasureState();
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      const panelsCount = state.panels > 1 ? state.panels : 0;
      if (!hasDims) {
        this.#scrollToSection('wall');
        return;
      }
      if (panelsCount <= 1) {
        if (this.wallCaption) {
          this.wallCaption.textContent =
            'Your print fits on one panel — panel cut guides appear when width needs multiple panels.';
        }
        return;
      }
      this.showPanelLinesOnPreview = !this.showPanelLinesOnPreview;
      this.showWallOverlayOnPreview = this.showPanelLinesOnPreview;
      this._previewFitKey = '';
      const nextState = this.calculate({ layout: false });
      this.#syncPanelLinesOnPreview(nextState);
      this.#refreshWallPanelGuides(nextState);
      this.#syncPreviewWallOverlay(nextState);
      this.#syncWallPreviewCopy(nextState);
      this.#syncPanelGuideControls(nextState);
    }

    #refreshWallPanelGuides(state = this.#computeMeasureState()) {
      if (!(state.widthDisp > 0 && state.heightDisp > 0)) return;
      this.#syncWallShapeAnnotations({
        panelsEl: this.wallPanels,
        ratioEl: this.wallRatio,
        widthDisp: state.widthDisp,
        heightDisp: state.heightDisp,
        widthCm: state.widthCm,
        heightCm: state.heightCm,
        bleedCm: state.bleedCm,
        printWidthCm: state.printWidthCm,
        printHeightCm: state.printHeightCm,
        panels: state.panels,
        boxEl: this.wallBox,
      });
    }

    #syncPanelGuideControls(state = this.#computeMeasureState()) {
      const panelsCount = state.panels > 1 ? state.panels : 0;
      const hasDims = state.printWidthCm > 0 && state.printHeightCm > 0;
      const multiPanel = panelsCount > 1;
      const on = this.showPanelLinesOnPreview;

      if (this.panelsControl) {
        this.panelsControl.hidden = false;
        this.panelsControl.classList.toggle('is-preview-teaser', !hasDims || !multiPanel);
        this.panelsControl.classList.toggle('is-action-locked', !hasDims || !multiPanel);
        this.panelsControl.classList.toggle('is-guides-on', hasDims && multiPanel && on);
        this.panelsControl.classList.toggle('is-guides-off', hasDims && multiPanel && !on);
      }

      const syncToggle = (btn, stateEl, { hideWhenUnavailable = false } = {}) => {
        if (!btn) return;
        const locked = !hasDims || !multiPanel;
        btn.hidden = hideWhenUnavailable && !hasDims;
        btn.disabled = false;
        btn.setAttribute('aria-disabled', String(locked));
        btn.classList.toggle('is-action-locked', locked);
        btn.classList.toggle('is-preview-teaser', !hasDims || !multiPanel);
        btn.setAttribute('aria-pressed', String(on));
        btn.title = !hasDims
          ? 'Enter wall width and height first'
          : !multiPanel
            ? 'Panel guides apply when your print width needs multiple panels'
            : on
              ? 'Turn panel guides off'
              : 'Turn panel guides on';
        btn.setAttribute('aria-label', `Panel guides, ${on ? 'on' : 'off'}`);
        btn.classList.toggle('is-guides-on', multiPanel && on);
        btn.classList.toggle('is-guides-off', multiPanel && !on);
        if (stateEl) {
          const showGreen = multiPanel && on;
          stateEl.classList.toggle('is-on', showGreen);
          stateEl.classList.toggle('is-off', !showGreen);
        }
      };

      syncToggle(
        this.wallOverlayToggle,
        this.wallOverlayToggle?.querySelector('[data-ai-wall-overlay-state]')
      );
      this.wallGuidesToggles.forEach((btn) => {
        syncToggle(btn, btn.querySelector('[data-ai-wall-guides-state]'), {
          hideWhenUnavailable: false,
        });
      });
    }

    /**
     * Draws a to-scale wall rectangle inside a stage element.
     * @param {object} opts
     */
    #renderWallShapeToStage(opts) {
      const {
        stageEl,
        boxEl,
        panelsEl,
        ratioEl,
        wEl,
        hEl,
        widthDisp,
        heightDisp,
        widthCm,
        heightCm,
        bleedCm,
        printWidthCm,
        printHeightCm,
        panels,
        ratioOnly = false,
      } = opts;
      if (!stageEl || !boxEl || !(widthDisp > 0 && heightDisp > 0) || !(printWidthCm > 0 && printHeightCm > 0)) {
        return false;
      }

      const stageRect = stageEl.getBoundingClientRect();
      const hasBleed = bleedCm > 0;
      const FRAME_PAD = {
        top: 28,
        left: 34,
        right: hasBleed ? 36 : 10,
        bottom: hasBleed ? 28 : 10,
      };
      // Use the fixed stage viewport — not the shrink-wrapped shape frame — so
      // only the inner wall box changes size/aspect, not the outer preview panel.
      const STAGE_W = Math.max(
        120,
        Math.floor((stageEl.clientWidth || stageRect?.width || 320) - FRAME_PAD.left - FRAME_PAD.right)
      );
      const STAGE_H = Math.max(
        120,
        Math.floor((stageEl.clientHeight || stageRect?.height || 320) - FRAME_PAD.top - FRAME_PAD.bottom)
      );
      const ratio = printWidthCm / printHeightCm;
      let boxW = STAGE_W;
      let boxH = STAGE_W / ratio;
      if (boxH > STAGE_H) {
        boxH = STAGE_H;
        boxW = STAGE_H * ratio;
      }
      boxEl.style.width = `${Math.round(boxW)}px`;
      boxEl.style.height = `${Math.round(boxH)}px`;
      this.#syncWallBoxOverlays(boxEl);

      const ratioText = this.#formatAspectRatio(ratio);
      if (ratioEl) ratioEl.textContent = ratioText;

      if (!ratioOnly) {
        this.#syncWallShapeAnnotations({
          panelsEl,
          ratioEl,
          widthDisp,
          heightDisp,
          widthCm,
          heightCm,
          bleedCm,
          printWidthCm,
          printHeightCm,
          panels,
          boxEl,
        });
      }
      return true;
    }

    /** Labels, panel guides, bleed badges — without resizing the wall box. */
    #syncWallShapeAnnotations(opts) {
      const {
        panelsEl,
        ratioEl,
        widthDisp,
        heightDisp,
        widthCm,
        heightCm,
        bleedCm,
        printWidthCm,
        printHeightCm,
        panels,
        boxEl,
      } = opts;

      if (!(widthDisp > 0 && heightDisp > 0) || !(printWidthCm > 0 && printHeightCm > 0)) {
        return;
      }

      const ratio = printWidthCm / printHeightCm;
      const lu = this.lengthUnit;
      const printW = this.#roundDisp(this.#fromCm(printWidthCm));
      const printH = this.#roundDisp(this.#fromCm(printHeightCm));
      const wallW = this.#roundDisp(widthDisp);
      const wallH = this.#roundDisp(heightDisp);
      if (ratioEl) ratioEl.textContent = this.#formatAspectRatio(ratio);
      const hasBleed = bleedCm > 0;
      const bleedLabel = this.#formatTrimSideBandLabel(bleedCm, lu);

      if (this.wallMeasurePrintW) this.wallMeasurePrintW.textContent = `${printW} ${lu}`;
      if (this.wallMeasurePrintH) this.wallMeasurePrintH.textContent = `${printH} ${lu}`;
      if (this.wallMeasurePrintWKind) {
        this.wallMeasurePrintWKind.textContent = hasBleed ? 'Print' : 'Size';
        this.wallMeasurePrintWKind.hidden = false;
      }
      if (this.wallMeasurePrintHKind) {
        this.wallMeasurePrintHKind.textContent = hasBleed ? 'Print' : 'Size';
        this.wallMeasurePrintHKind.hidden = false;
      }

      if (this.wallMeasureWallWValue) this.wallMeasureWallWValue.textContent = `${wallW} ${lu}`;
      if (this.wallMeasureWallHValue) this.wallMeasureWallHValue.textContent = `${wallH} ${lu}`;
      this.wallMeasureBleeds.forEach((el) => {
        const valueEl = el.querySelector('[data-ai-wall-measure-bleed-value]');
        if (valueEl) valueEl.textContent = bleedLabel;
      });

      const showBleed = hasBleed && widthCm > 0 && heightCm > 0;
      if (this.wallMeasureWallW) this.wallMeasureWallW.hidden = !showBleed;
      if (this.wallMeasureWallH) this.wallMeasureWallH.hidden = !showBleed;
      this.wallMeasureBleeds.forEach((el) => {
        el.hidden = !showBleed;
      });

      if (this.wallInnerOutline && boxEl) {
        const show = showBleed && printWidthCm > 0 && printHeightCm > 0;
        this.wallInnerOutline.hidden = !show;
        boxEl.classList.toggle('has-wall-bleed', show);
        if (show) {
          const innerW = (widthCm / printWidthCm) * 100;
          const innerH = (heightCm / printHeightCm) * 100;
          const insetX = (100 - innerW) / 2;
          const insetY = (100 - innerH) / 2;
          this.wallInnerOutline.style.width = `${innerW}%`;
          this.wallInnerOutline.style.height = `${innerH}%`;
          boxEl.style.setProperty('--wall-inner-w', `${innerW}%`);
          boxEl.style.setProperty('--wall-inner-h', `${innerH}%`);
          boxEl.style.setProperty('--wall-inset-x', `${insetX}%`);
          boxEl.style.setProperty('--wall-inset-y', `${insetY}%`);
        } else {
          boxEl.classList.remove('has-wall-bleed');
          boxEl.style.removeProperty('--wall-inner-w');
          boxEl.style.removeProperty('--wall-inner-h');
          boxEl.style.removeProperty('--wall-inset-x');
          boxEl.style.removeProperty('--wall-inset-y');
        }
      }

      if (boxEl) {
        boxEl
          .querySelectorAll(':scope > .ai-generator__wall-divider, :scope > .ai-generator__wall-panel-tag')
          .forEach((node) => node.remove());
        if (panelsEl) panelsEl.replaceChildren();
        const panelCount = panels || 0;
        const showGuides = this.showPanelLinesOnPreview && panelCount > 1 && printWidthCm > 0;
        if (this.wallPreview) {
          this.wallPreview.classList.toggle('has-wall-panel-guides', showGuides);
        }
        if (showGuides) {
          const guideH = boxEl.clientHeight || 0;
          for (let i = 1; i < panelCount; i += 1) {
            const pct = ((i * this.panelWidth) / printWidthCm) * 100;
            if (pct >= 100) break;
            const divider = document.createElement('span');
            divider.className = 'ai-generator__wall-divider';
            divider.setAttribute('aria-hidden', 'true');
            divider.style.left = `${pct}%`;
            divider.style.top = '0';
            divider.style.bottom = 'auto';
            divider.style.height = guideH > 0 ? `${guideH}px` : '100%';
            divider.style.transform = 'translateX(-50%)';
            boxEl.appendChild(divider);
          }
          for (let i = 0; i < panelCount; i += 1) {
            const startPct = ((i * this.panelWidth) / printWidthCm) * 100;
            const endPct = Math.min(((i + 1) * this.panelWidth) / printWidthCm * 100, 100);
            const centerPct = (startPct + endPct) / 2;
            const tag = document.createElement('span');
            tag.className = 'ai-generator__wall-panel-tag';
            tag.setAttribute('aria-hidden', 'true');
            tag.style.left = `${centerPct}%`;
            tag.textContent = String(i + 1);
            boxEl.appendChild(tag);
          }
        }
      }

      this.#syncWallBoxOverlays(boxEl);
    }

    /** Panel guides + surface grid must match the wall box in px (inset/100% collapses when all children are absolute). */
    #syncWallBoxOverlays(boxEl) {
      if (!boxEl) return;
      const w = boxEl.clientWidth;
      const h = boxEl.clientHeight;
      const sizeLayer = (el) => {
        if (!el) return;
        if (!(w > 0 && h > 0)) {
          el.style.width = '';
          el.style.height = '';
          return;
        }
        el.style.top = '0';
        el.style.left = '0';
        el.style.width = `${w}px`;
        el.style.height = `${h}px`;
      };
      sizeLayer(this.wallPanels);
      sizeLayer(boxEl.querySelector('.ai-generator__wall-box-surface'));
    }

    /** Step 1 preview when a preset aspect ratio is chosen without wall measurements. */
    #renderRatioOnlyWallPreview(options = {}) {
      const layout = options.layout !== false;
      if (!this.wallPreview || !this.wallBox || this.isWallShape) return;

      const ratio = this.#previewAspectRatio(0, 0);
      const refH = 100;
      const refW = refH * ratio;
      const ratioText = this.#formatAspectRatio(ratio);

      this.wallPreview.classList.remove('is-wall-preview-empty');
      this.wallPreview.classList.remove('has-wall-panel-guides');
      this.wallBox.classList.remove('has-wall-bleed');
      if (this.wallInnerOutline) this.wallInnerOutline.hidden = true;
      if (this.wallMeasureWallW) this.wallMeasureWallW.hidden = true;
      if (this.wallMeasureWallH) this.wallMeasureWallH.hidden = true;
      this.wallMeasureBleeds.forEach((el) => {
        el.hidden = true;
      });
      if (this.wallPanels) this.wallPanels.replaceChildren();
      this.wallBox
        ?.querySelectorAll(':scope > .ai-generator__wall-divider, :scope > .ai-generator__wall-panel-tag')
        .forEach((node) => node.remove());

      this.#syncWallGridAspect(refW, refH);

      if (this.wallRatio) this.wallRatio.textContent = ratioText;
      if (this.wallMeasurePrintW) {
        this.wallMeasurePrintW.textContent = ratioText;
        if (this.wallMeasurePrintWKind) this.wallMeasurePrintWKind.textContent = 'Ratio';
      }
      if (this.wallMeasurePrintH) {
        this.wallMeasurePrintH.textContent = '';
        if (this.wallMeasurePrintHKind) this.wallMeasurePrintHKind.textContent = '';
      }

      if (!layout) return;

      const layoutKey = `ratio-only|${ratio.toFixed(4)}|${this.aspectSelect?.value || ''}`;
      if (this._wallLayoutKey === layoutKey) return;
      this._wallLayoutKey = layoutKey;

      const stage = this.wallStage || this.wallBox?.closest('.ai-generator__wall-stage');
      this.#renderWallShapeToStage({
        stageEl: stage,
        boxEl: this.wallBox,
        panelsEl: this.wallPanels,
        ratioEl: this.wallRatio,
        wEl: this.wallW,
        hEl: this.wallH,
        widthDisp: refW,
        heightDisp: refH,
        widthCm: refW,
        heightCm: refH,
        bleedCm: 0,
        printWidthCm: refW,
        printHeightCm: refH,
        panels: 0,
        ratioOnly: true,
      });
    }

    /** Step 1 grid — preview ~60%; tall walls give controls more width (preview down to ~40%). */
    #syncWallGridAspect(printWidthCm, printHeightCm) {
      const grid = this.root.querySelector('.ai-generator__wall-grid');
      if (!grid) return;
      const tall =
        printWidthCm > 0 && printHeightCm > 0 && printHeightCm / printWidthCm > 1.12;
      grid.classList.toggle('is-wall-aspect-tall', tall);
    }

    /**
     * Draws a to-scale rectangle of the wall, fitted into the preview stage,
     * with measurements and aspect ratio labelled.
     */
    #renderWallShape(widthDisp, heightDisp, printWidthCm, printHeightCm, panels, options = {}) {
      const layout = options.layout !== false;
      if (!this.wallPreview || !this.wallBox) return;

      if (!this.isWallShape && !(widthDisp > 0 && heightDisp > 0)) {
        this.#renderRatioOnlyWallPreview({ layout });
        return;
      }

      if (!(widthDisp > 0 && heightDisp > 0)) {
        this._wallLayoutKey = '';
        this.wallPreview?.classList.add('is-wall-preview-empty');
        this.#syncWallGridAspect(0, 0);
        if (this.wallBox) {
          this.wallBox.style.width = '';
          this.wallBox.style.height = '';
        }
        if (this.wallInnerOutline) this.wallInnerOutline.hidden = true;
        if (this.wallMeasureWallW) this.wallMeasureWallW.hidden = true;
        if (this.wallMeasureWallH) this.wallMeasureWallH.hidden = true;
        this.wallMeasureBleeds.forEach((el) => {
          el.hidden = true;
        });
        this.wallBox?.classList.remove('has-wall-bleed');
        this.wallPreview?.classList.remove('has-wall-panel-guides');
        if (this.wallBox) {
          this.wallBox
            .querySelectorAll(':scope > .ai-generator__wall-divider, :scope > .ai-generator__wall-panel-tag')
            .forEach((node) => node.remove());
        }
        if (this.wallPanels) this.wallPanels.replaceChildren();
        this.#syncWallBoxOverlays(this.wallBox);
        return;
      }
      this.wallPreview?.classList.remove('is-wall-preview-empty');
      this.#syncWallGridAspect(printWidthCm, printHeightCm);

      const state = this.#computeMeasureState();
      const annotationOpts = {
        panelsEl: this.wallPanels,
        ratioEl: this.wallRatio,
        widthDisp,
        heightDisp,
        widthCm: state.widthCm,
        heightCm: state.heightCm,
        bleedCm: state.bleedCm,
        printWidthCm,
        printHeightCm,
        panels,
        boxEl: this.wallBox,
      };

      this.#syncWallShapeAnnotations(annotationOpts);

      if (!layout) {
        this._wallLayoutKey = '';
        return;
      }

      const layoutKey = `${widthDisp}|${heightDisp}|${printWidthCm}|${printHeightCm}|${panels}|${state.bleedCm}`;
      if (this._wallLayoutKey === layoutKey) return;
      this._wallLayoutKey = layoutKey;

      const stage = this.wallStage || this.wallBox?.closest('.ai-generator__wall-stage');
      this.#renderWallShapeToStage({
        stageEl: stage,
        boxEl: this.wallBox,
        panelsEl: this.wallPanels,
        ratioEl: this.wallRatio,
        wEl: this.wallW,
        hEl: this.wallH,
        widthDisp,
        heightDisp,
        widthCm: state.widthCm,
        heightCm: state.heightCm,
        bleedCm: state.bleedCm,
        printWidthCm,
        printHeightCm,
        panels,
      });
    }

    /**
     * Optional wall-shape overlay on the design preview when an image is shown.
     * @param {ReturnType<AIImageGenerator['calculate']>} state
     */
    #previewHasVisual() {
      return (
        (this.cropViewport && !this.cropViewport.hidden) ||
        (this.image && !this.image.hidden && !!this.image.src)
      );
    }

    #syncPanelLinesOnPreview(state) {
      const panelsOk = state.printWidthCm > 0 && state.panels > 1;
      const showGuides = this.showPanelLinesOnPreview && panelsOk;

      this.preview?.classList.toggle('is-panel-guides-hidden', !showGuides);
      this.preview?.classList.toggle('is-panel-guides-on', showGuides);
      this.previewMount?.classList.toggle('is-panel-guides-hidden', !showGuides);
      this.previewMount?.classList.toggle('is-panel-guides-on', showGuides);

      if (!this.panelLinesEl) return;
      this.panelLinesEl.hidden = !showGuides;
      this.panelLinesEl.setAttribute('aria-hidden', String(!showGuides));
      if (!showGuides) {
        this.panelLinesEl.replaceChildren();
        return;
      }
      this.panelLinesEl.replaceChildren();
      for (let i = 1; i < state.panels; i += 1) {
        const pct = ((i * this.panelWidth) / state.printWidthCm) * 100;
        if (pct >= 100) break;
        const line = document.createElement('span');
        line.className = 'ai-generator__panel-line';
        line.style.left = `${pct}%`;
        this.panelLinesEl.appendChild(line);
      }
    }

    #syncPreviewWallOverlay(state) {
      if (this.wallOverlay) this.wallOverlay.hidden = true;
      this.#syncPreviewActions(state);
    }

    #syncPreviewShape(printWidthCm, printHeightCm, options = {}) {
      const layout = options.layout !== false;
      const hasWallDims = printWidthCm > 0 && printHeightCm > 0;
      const ratioKey = `${printWidthCm}:${printHeightCm}:${this.aspectSelect?.value || ''}`;
      if (this._previewWallRatioKey !== ratioKey) {
        this._previewWallRatioKey = ratioKey;
        this._previewFitKey = '';
        this._previewUserFitH = 0;
        this._previewUserAspectKey = '';
      }

      if (this.isWallShape) {
        if (hasWallDims) {
          this.preview?.style.setProperty('--ai-ratio', `${printWidthCm} / ${printHeightCm}`);
        } else {
          this.preview?.style.removeProperty('--ai-ratio');
        }
      } else {
        const option = this.aspectSelect?.selectedOptions[0];
        const ratio = option?.dataset.ratio || '1 / 1';
        this.preview?.style.setProperty('--ai-ratio', ratio);
      }

      this.#syncPreviewPlaceholder({ printWidthCm, printHeightCm });

      if (!layout) return;

      if (this.cropViewport && !this.cropViewport.hidden && hasWallDims) {
        requestAnimationFrame(() => this.#refreshUploadCropLayout(true));
      }

      this.#schedulePreviewLayout();
    }

    /** One preview sizing pass per frame; avoids ResizeObserver ↔ layout feedback loops. */
    #schedulePreviewLayout(force = false) {
      const wideRowResize = this._previewResizing && this.#isWideLayoutView();
      if (this._previewResizing && !wideRowResize) return;
      if (force) {
        if (this._previewLayoutRaf) cancelAnimationFrame(this._previewLayoutRaf);
        this._previewLayoutRaf = 0;
        const rowRecovered = wideRowResize ? false : this.#ensureWidePreviewRowMinimum();
        const state = this.#computeMeasureState();
        this.#fitDesignPreviewBox(state.printWidthCm, state.printHeightCm);
        if (rowRecovered) {
          window.requestAnimationFrame(() => {
            const retryState = this.#computeMeasureState();
            this.#fitDesignPreviewBox(retryState.printWidthCm, retryState.printHeightCm);
          });
        }
        return;
      }
      if (this._previewLayoutRaf) return;
      this._previewLayoutRaf = requestAnimationFrame(() => {
        this._previewLayoutRaf = 0;
        if (this._previewLayoutLock) return;
        this._previewLayoutLock = true;
        const state = this.#computeMeasureState();
        this.#fitDesignPreviewBox(state.printWidthCm, state.printHeightCm);
        this.#syncPanelLinesOnPreview(state);
        if (!this.cropViewport?.hidden) this.#refreshUploadCropLayout(true);
        this._previewLayoutLock = false;
      });
    }

    #markPreviewLayoutReady() {
      if (this.root.classList.contains('is-preview-layout-ready')) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.#isWideLayoutView()) this.bootstrapWidePreviewLayout();
          else this.#schedulePreviewLayout(true);
          this.root.classList.add('is-preview-layout-ready');
          window.requestAnimationFrame(() => {
            const stage = this.previewStage;
            if (stage && Math.floor(stage.getBoundingClientRect().height || 0) < PREVIEW_FIT_MIN_H) {
              this._previewFitKey = '';
              this.#schedulePreviewLayout(true);
            }
          });
        });
      });
    }

    /**
     * Fit the preview inside the live viewport — largest box matching wall aspect ratio.
     */
    #previewAspectStorageKey(printWidthCm, printHeightCm) {
      if (printWidthCm > 0 && printHeightCm > 0) {
        return `${Math.round(printWidthCm)}x${Math.round(printHeightCm)}`;
      }
      return this.isWallShape ? 'wall' : this.aspectSelect?.value || 'ratio';
    }

    #loadPreviewUserHeight(aspectKey) {
      try {
        const raw = localStorage.getItem(`${PREVIEW_FIT_HEIGHT_KEY}_${aspectKey}`);
        const n = Number(raw);
        return Number.isFinite(n) && n >= PREVIEW_FIT_MIN_H ? Math.round(n) : 0;
      } catch {
        return 0;
      }
    }

    #savePreviewUserHeight(aspectKey, heightPx) {
      try {
        if (heightPx >= PREVIEW_FIT_MIN_H) {
          localStorage.setItem(`${PREVIEW_FIT_HEIGHT_KEY}_${aspectKey}`, String(Math.round(heightPx)));
        } else {
          localStorage.removeItem(`${PREVIEW_FIT_HEIGHT_KEY}_${aspectKey}`);
        }
      } catch {
        /* ignore */
      }
    }

    #syncPreviewUserHeight(aspectKey) {
      if (this._previewUserAspectKey !== aspectKey) {
        this._previewUserAspectKey = aspectKey;
        this._previewUserFitH = this.#loadPreviewUserHeight(aspectKey);
      }
      return this._previewUserFitH || 0;
    }

    #previewPreferredHeight(aspectKey, mount, { forceMax = false } = {}) {
      if (forceMax) return 0;
      let preferredH = this.#syncPreviewUserHeight(aspectKey);
      if (!preferredH && mount?.offsetHeight >= PREVIEW_FIT_MIN_H) {
        preferredH = mount.offsetHeight;
      }
      return preferredH;
    }

    #fitPreviewWithinBounds(maxW, maxH, ratio, preferredH = 0) {
      let fitW = maxW;
      let fitH = maxW / ratio;
      if (fitH > maxH) {
        fitH = maxH;
        fitW = maxH * ratio;
      }
      if (preferredH >= PREVIEW_FIT_MIN_H) {
        let targetH = Math.min(maxH, preferredH);
        let targetW = targetH * ratio;
        if (targetW > maxW) {
          targetW = maxW;
          targetH = maxW / ratio;
        }
        fitW = targetW;
        fitH = targetH;
      }
      return { fitW: Math.round(fitW), fitH: Math.round(fitH) };
    }

    #clampPreviewUserHeight(maxW, maxH, ratio, heightPx) {
      const fill = this.#fitPreviewWithinBounds(maxW, maxH, ratio, 0);
      const minH = Math.max(PREVIEW_FIT_MIN_H, Math.round(fill.fitH * 0.55));
      const maxAllowed = Math.max(minH, Math.round(fill.fitH));
      return Math.min(maxAllowed, Math.max(minH, Math.round(heightPx)));
    }

    /** Studio layout — grow the whole preview band (deck) to honour a user-dragged height. */
    #syncStudioPreviewBandMinHeight(fitH) {
      const deck = this.designPreviewCol?.querySelector('.ai-generator__preview-deck');
      if (!deck || !Number.isFinite(fitH) || fitH <= 0) return;
      const deckHead = this.designPreviewCol?.querySelector('.ai-generator__preview-deck-head');
      const uploadNote = this.designPreviewCol?.querySelector('[data-ai-upload-note]');
      const handlePad = this.previewSizeResize && !this.previewSizeResize.hidden ? 18 : 0;
      const uploadFootH =
        uploadNote && !uploadNote.hidden
          ? Math.ceil(uploadNote.getBoundingClientRect().height) + 4
          : 0;
      const headH = (deckHead?.offsetHeight || 0) + 6;
      deck.style.minHeight = `${Math.ceil(fitH + headH + 4 + handlePad + uploadFootH)}px`;
    }

    #setPreviewMountSize(
      mount,
      stage,
      {
        fitW,
        fitH,
        aspectCss,
        bounds,
        printWidthCm,
        printHeightCm,
        hasWallDims,
        hubOpenBelow,
        previewFitCompact,
        previewWallFill = false,
        hasDesign,
        awaitingDesign,
        cropActive,
      }
    ) {
      if (!mount || !stage) return;

      stage.style.setProperty('--ai-rw', String(printWidthCm));
      stage.style.setProperty('--ai-rh', String(printHeightCm));
      stage.style.setProperty('--ai-preview-aspect', aspectCss);
      stage.style.setProperty('--ai-preview-fit-w', `${fitW}px`);
      stage.style.setProperty('--ai-preview-fit-h', `${fitH}px`);
      stage.style.setProperty('--ai-preview-max-h', `${fitH}px`);
      stage.style.height = '';

      mount.style.setProperty('--ai-preview-aspect', aspectCss);
      mount.style.setProperty('--ai-preview-fit-w', `${fitW}px`);
      mount.style.setProperty('--ai-preview-fit-h', `${fitH}px`);
      mount.style.width = `${fitW}px`;
      mount.style.maxWidth = `${fitW}px`;
      mount.style.maxHeight = `${fitH}px`;
      mount.style.flex = '0 0 auto';
      mount.style.flexShrink = '0';
      mount.style.alignSelf = 'center';
      mount.style.height = `${fitH}px`;
      mount.style.minHeight = '';

      if (previewWallFill) {
        // fitH can exceed the precomputed budget when the stack offers more room
        // (e.g. prompt column stretches the row) — raise the stage cap to match,
        // otherwise the CSS min(fit-h, stage-max-h) clamp distorts the wall ratio.
        const stageCap = Math.max(Math.floor(bounds.maxH || 0), fitH);
        stage.style.flex = '1 1 auto';
        stage.style.maxHeight = `${stageCap}px`;
        stage.style.minHeight = '0';
        stage.style.width = '100%';
        stage.style.alignSelf = 'stretch';
        stage.style.setProperty('--ai-preview-stage-max-h', `${stageCap}px`);
      } else if (awaitingDesign && !hasDesign && !cropActive) {
        stage.style.flex = '0 1 auto';
        stage.style.maxHeight = `${Math.min(bounds.maxH, fitH + 14)}px`;
        stage.style.minHeight = '';
      } else if (hasWallDims || hubOpenBelow || previewFitCompact) {
        stage.style.flex = hasDesign ? '1 1 auto' : '0 1 auto';
        const handlePad = this.previewSizeResize && !this.previewSizeResize.hidden ? 18 : 0;
        stage.style.maxHeight = `${fitH + 14 + handlePad}px`;
        stage.style.minHeight = '';
      } else {
        stage.style.flex = '1 1 auto';
        stage.style.maxHeight = `${bounds.maxH}px`;
      }
    }

    /** Wide view — preview budget inside the studio row only. */
    #measureWideViewPreviewBudget({
      studio,
      workspace,
      rail,
      deckHead,
      uploadFootH = 0,
      handlePad = 0,
    } = {}) {
      const workspaceEl =
        workspace || studio?.querySelector('.ai-generator__design-workspace');
      const headH = (deckHead?.offsetHeight || 0) + 6;
      const stagePad = 4;
      const footPad = handlePad || this.#previewResizeHandlePad() || 34;
      if (workspaceEl?.clientHeight > PREVIEW_FIT_MIN_H + headH) {
        return Math.max(
          PREVIEW_WALL_MIN_H,
          Math.floor(workspaceEl.clientHeight - headH - uploadFootH - footPad - stagePad)
        );
      }
      if (!studio) return 0;
      const railH = rail ? rail.offsetHeight : 0;
      const studioGap = parseFloat(getComputedStyle(studio).gap) || 10;
      return Math.max(
        PREVIEW_WALL_MIN_H,
        Math.floor(
          studio.clientHeight - railH - studioGap - headH - uploadFootH - footPad - stagePad - 8
        )
      );
    }

    /** Resolve a CSS length custom property to pixels (vh/vw/px — not parseInt). */
    #readCssLengthPx(el, prop, fallback = 0) {
      if (!el) return fallback;
      const inline = el.style.getPropertyValue(prop);
      const raw = String(inline || getComputedStyle(el).getPropertyValue(prop) || '').trim();
      if (!raw) return fallback;
      const n = parseFloat(raw);
      if (!Number.isFinite(n)) return fallback;
      if (/\bvh\b/i.test(raw)) return Math.round(window.innerHeight * (n / 100));
      if (/\bvw\b/i.test(raw)) return Math.round(window.innerWidth * (n / 100));
      if (/\bpx\b/i.test(raw) || /^\d+(\.\d+)?$/.test(raw)) return Math.round(n);
      if (/%/.test(raw)) {
        const base = el.parentElement?.clientHeight || window.innerHeight;
        return Math.round(base * (n / 100));
      }
      return Math.round(n);
    }

    /** Live preview grid row height — prefer JS-set px over a collapsed studio measure. */
    #readWidePreviewRowHeightPx() {
      const grid = this.designGrid;
      if (!grid) return Math.round(window.innerHeight * 0.72);
      const varH = this.#readCssLengthPx(grid, '--ai-wide-preview-row-h');
      const minVar = this.#readCssLengthPx(grid, '--ai-wide-preview-row-min');
      if (varH > 120) return Math.max(varH, minVar || 0);
      const { min } = this.#widePreviewRowLimits();
      return Math.max(min, Math.round(window.innerHeight * 0.72));
    }

    /** Toolbar stack estimate when the rail has not finished layout yet. */
    #estimateWideViewToolbarChromePx() {
      const rootStyle = getComputedStyle(this.root);
      const stackRaw = rootStyle.getPropertyValue('--ai-toolbar-stack-h').trim() || '6.6rem';
      const remMatch = stackRaw.match(/^([\d.]+)rem$/);
      const rem = remMatch ? parseFloat(remMatch[1]) : 6.6;
      const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const stackPx = Math.round(rem * rootFont);
      return stackPx + 148;
    }

    #widePreviewRowMinUsable(_printWidthCm = 0, _printHeightCm = 0) {
      const chrome = this.#wideViewStudioChrome();
      const vh = window.innerHeight;
      return Math.round(
        Math.max(
          chrome + PREVIEW_FIT_MIN_H + 48,
          Math.min(520, vh * 0.38)
        )
      );
    }

    #widePreviewRowLimits() {
      const vh =
        window.visualViewport?.height ||
        window.innerHeight ||
        document.documentElement?.clientHeight ||
        800;
      const min = this.#widePreviewRowMinUsable();
      return {
        min,
        max: Math.round(Math.min(1400, Math.max(vh * 0.96, min + 280, 720))),
      };
    }

    #ensureWidePreviewRowMinimum() {
      if (!this.#isWideLayoutView() || !this.designGrid) return false;
      const { min } = this.#widePreviewRowLimits();
      const currentPx = this.#readWidePreviewRowHeightPx();
      const gallery = this.designHub?.wallprintInspirationInstance;
      if (currentPx >= min) {
        if (!this.designGrid.style.getPropertyValue('--ai-wide-preview-row-h')) {
          this.#setWidePreviewRowHeight(currentPx);
          return true;
        }
        return false;
      }
      this.#setWidePreviewRowHeight(min);
      if (gallery && currentPx > 0 && currentPx < min) {
        gallery._widePreviewRowUserSet = false;
      }
      return true;
    }

    /** Public — inspiration gallery / row resize bar sync preview grid row height. */
    setWidePreviewRowHeight(px, { userSet = true } = {}) {
      const clamped = this.#setWidePreviewRowHeight(px);
      if (!Number.isFinite(clamped)) return;
      const gallery = this.designHub?.wallprintInspirationInstance;
      if (gallery) {
        gallery._widePreviewRowH = clamped;
        if (userSet) gallery._widePreviewRowUserSet = true;
      }
      return clamped;
    }

    #setWidePreviewRowHeight(px) {
      const grid = this.designGrid;
      const wideView =
        this.root?.classList.contains('is-wide-view') ||
        this.root?.classList.contains('is-preview-expanded');
      if (!grid || !wideView || !Number.isFinite(px)) return;
      const { min, max } = this.#widePreviewRowLimits();
      const clamped = Math.min(max, Math.max(min, Math.round(px)));
      grid.style.setProperty('--ai-wide-preview-row-h', `${clamped}px`);
      grid.style.setProperty('--ai-wide-preview-row-min', `${min}px`);
      return clamped;
    }

    /** Visible preview resize handle height (inline or wide-view foot bar). */
    #previewResizeHandlePad() {
      const handles = this.previewSizeResizeHandles?.length
        ? this.previewSizeResizeHandles
        : this.previewSizeResize
          ? [this.previewSizeResize]
          : [];
      for (const handle of handles) {
        if (!handle || handle.hidden) continue;
        const style = getComputedStyle(handle);
        if (style.display === 'none' || style.visibility === 'hidden') continue;
        const h = Math.ceil(handle.getBoundingClientRect().height);
        if (h > 0) return h + 4;
      }
      return 0;
    }

    /** Rail + deck head + resize bar inside the wide-view studio row. */
    #wideViewStudioChrome() {
      const studio = this.designGrid?.querySelector('.ai-generator__design-studio');
      const rail = this.designGrid?.querySelector('.ai-generator__design-preview-rail');
      const deckHead = this.designPreviewCol?.querySelector('.ai-generator__preview-deck-head');
      let railH = rail?.offsetHeight || 0;
      if (railH < 72) railH = this.#estimateWideViewToolbarChromePx();
      const headH = (deckHead?.offsetHeight || 0) + 6;
      const footH = this.#previewResizeHandlePad() || 34;
      const studioGap = studio ? parseFloat(getComputedStyle(studio).gap) || 0 : 0;
      return railH + headH + footH + studioGap + 8;
    }

    /** Size preview grid row — never auto-shrink (that collapsed the live viewport). */
    #syncWideViewPreviewRow(printWidthCm, printHeightCm, { fitH = 0, force = false, growOnly = false } = {}) {
      const wideView =
        this.root?.classList.contains('is-wide-view') ||
        this.root?.classList.contains('is-preview-expanded');
      if (!wideView || !this.designGrid) return;

      const gallery = this.designHub?.wallprintInspirationInstance;
      if (gallery?._widePreviewRowUserSet && !force && !growOnly) {
        const { min } = this.#widePreviewRowLimits();
        if (this.#readWidePreviewRowHeightPx() >= min) return;
        gallery._widePreviewRowUserSet = false;
      }

      const chrome = this.#wideViewStudioChrome();
      const { min, max } = this.#widePreviewRowLimits();
      const minUsable = chrome + PREVIEW_FIT_MIN_H + 48;

      const currentRaw = this.#readWidePreviewRowHeightPx();

      let targetRow = 0;
      if (growOnly && fitH > 0) {
        targetRow = fitH + chrome;
      } else if (!growOnly) {
        if (printWidthCm > 0 && printHeightCm > 0) {
          const maxW = Math.max(200, (this.designGrid.clientWidth || window.innerWidth) - 32);
          const ratio = printWidthCm / printHeightCm;
          const idealMountH = Math.floor((maxW * 0.96) / ratio);
          targetRow = idealMountH + chrome;
        }
        if (!targetRow) {
          const workspaceTarget = Math.round(window.innerHeight * 0.58);
          targetRow = workspaceTarget + chrome;
        }
        if (!targetRow) targetRow = Math.round(window.innerHeight * 0.74);
      }

      targetRow = Math.max(minUsable, targetRow);
      if (Number.isFinite(currentRaw) && currentRaw > 0 && targetRow < currentRaw && !force && !growOnly) {
        targetRow = currentRaw;
      }
      if (Number.isFinite(currentRaw) && currentRaw > 0 && currentRaw < minUsable && !gallery?._widePreviewRowUserSet) {
        targetRow = Math.max(targetRow, minUsable);
      }

      this.#setWidePreviewRowHeight(Math.min(max, targetRow));
    }

    #ensureWidePreviewRowForMount(fitH, printWidthCm, printHeightCm) {
      if (fitH < PREVIEW_FIT_MIN_H) return;
      const chrome = this.#wideViewStudioChrome();
      const needed = fitH + chrome;
      const studio = this.designGrid?.querySelector('.ai-generator__design-studio');
      const current = studio?.getBoundingClientRect().height || 0;
      if (needed > current - 6) {
        this.#setWidePreviewRowHeight(needed);
        const gallery = this.designHub?.wallprintInspirationInstance;
        if (gallery) {
          gallery._widePreviewRowH = needed;
          gallery._widePreviewRowUserSet = true;
        }
      }
    }

    #syncPreviewSizeResizeUi(show = true) {
      const handles = this.previewSizeResizeHandles?.length
        ? this.previewSizeResizeHandles
        : this.previewSizeResize
          ? [this.previewSizeResize]
          : [];
      if (!handles.length) return;
      const wideView = this.#isWideLayoutView();
      for (const handle of handles) {
        const isFoot = handle.classList.contains('ai-generator__preview-size-resize-handle--studio-foot');
        const isInline = handle.classList.contains('ai-generator__preview-size-resize-handle--inline');
        const isRow = handle.hasAttribute('data-ai-preview-row-resize');
        if (!show) {
          handle.hidden = true;
          continue;
        }
        if (wideView) {
          handle.hidden = isInline || isFoot;
        } else {
          handle.hidden = isFoot || isRow;
        }
      }
    }

    #isWideLayoutView() {
      return (
        this.root?.classList.contains('is-wide-view') ||
        this.root?.classList.contains('is-preview-expanded')
      );
    }

    #promptHeightStorageKey() {
      return this.#isWideLayoutView() ? PROMPT_HEIGHT_WIDE_KEY : PROMPT_HEIGHT_STUDIO_KEY;
    }

    #promptHeightLimits() {
      if (this.#isWideLayoutView()) {
        return { min: 72, max: 192, defaultH: 120 };
      }
      const maxH = Math.round(Math.min(window.innerHeight * 0.42, 400));
      return {
        min: 132,
        max: Math.max(176, maxH),
        defaultH: Math.round(Math.min(window.innerHeight * 0.29, 276)),
      };
    }

    #initPromptHeight() {
      const textarea = this.promptTextarea;
      if (!textarea) return;
      const { min, max, defaultH } = this.#promptHeightLimits();
      let nextH = defaultH;
      try {
        const stored = Number(localStorage.getItem(this.#promptHeightStorageKey()));
        if (Number.isFinite(stored) && stored >= min && stored <= max) {
          nextH = Math.round(stored);
        }
      } catch {
        /* ignore */
      }
      this.#setPromptHeight(nextH, { persist: false });
    }

    #setPromptHeight(heightPx, { persist = true } = {}) {
      const textarea = this.promptTextarea;
      if (!textarea) return;
      const field = this.promptField;
      const { min, max } = this.#promptHeightLimits();
      const inlineHandle = this.root?.querySelector(
        '.ai-generator__prompt-size-resize-handle--inline'
      );
      const footHandle = this.root?.querySelector(
        '.ai-generator__prompt-size-resize-handle--shell-foot'
      );
      const handlePad =
        (inlineHandle && !inlineHandle.hidden && getComputedStyle(inlineHandle).display !== 'none'
          ? Math.ceil(inlineHandle.getBoundingClientRect().height)
          : 0) +
        (footHandle && !footHandle.hidden && getComputedStyle(footHandle).display !== 'none'
          ? Math.ceil(footHandle.getBoundingClientRect().height)
          : 0);
      const clamped = Math.min(max, Math.max(min, Math.round(heightPx)));
      textarea.style.height = `${clamped}px`;
      textarea.style.minHeight = `${clamped}px`;
      textarea.style.maxHeight = `${clamped}px`;
      if (field && !this.#isWideLayoutView()) {
        field.style.minHeight = `${clamped + Math.max(handlePad, 12)}px`;
      }
      this.root?.style.setProperty('--ai-prompt-textarea-h', `${clamped}px`);
      if (this.#isWideLayoutView()) {
        this.root?.style.setProperty('--ai-prompt-row-min-h', `${clamped}px`);
      }
      if (persist) {
        try {
          localStorage.setItem(this.#promptHeightStorageKey(), String(clamped));
        } catch {
          /* ignore */
        }
      }
    }

    #isSidebarLayout() {
      if (!window.matchMedia('(min-width: 750px)').matches) return false;
      if (this.root?.classList.contains('is-wide-view')) return false;
      if (this.root?.classList.contains('is-preview-expanded')) return false;
      if (this.designGrid?.classList.contains('is-prompt-collapsed')) return false;
      return true;
    }

    #sidebarWidthMax() {
      const grid = this.designGrid;
      if (!grid) return SIDEBAR_WIDTH_MAX;
      const gridW = grid.getBoundingClientRect().width;
      if (!Number.isFinite(gridW) || gridW <= 0) return SIDEBAR_WIDTH_MAX;
      const splitW = parseFloat(getComputedStyle(grid).getPropertyValue('--ai-sidebar-split-w')) || 8;
      const previewMin = Math.max(360, Math.floor(gridW * 0.52));
      const maxByPreview = Math.floor(gridW - previewMin - splitW);
      return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN + 24, maxByPreview));
    }

    #readSidebarWidthPx() {
      const inline = this.designGrid?.style.getPropertyValue('--ai-sidebar-w')?.trim();
      const inlinePx = parseInt(String(inline || ''), 10);
      if (Number.isFinite(inlinePx) && inlinePx > 0) return inlinePx;
      if (this.designGrid) {
        const computed = getComputedStyle(this.designGrid).getPropertyValue('--ai-sidebar-w').trim();
        const computedPx = parseInt(computed, 10);
        if (Number.isFinite(computedPx) && computedPx > 0) return computedPx;
      }
      return SIDEBAR_WIDTH_DEFAULT;
    }

    #setSidebarWidth(widthPx, { persist = true } = {}) {
      if (!this.designGrid || !this.#isSidebarLayout()) return;
      const max = this.#sidebarWidthMax();
      const clamped = Math.round(Math.max(SIDEBAR_WIDTH_MIN, Math.min(max, widthPx)));
      this.designGrid.style.setProperty('--ai-sidebar-w', `${clamped}px`);
      if (persist) {
        try {
          localStorage.setItem(SIDEBAR_WIDTH_KEY, String(clamped));
        } catch {
          /* ignore */
        }
      }
      this._previewFitKey = '';
      this.#schedulePreviewLayout(true);
      this.root?.dispatchEvent(new CustomEvent('wallprint:sidebar-width-changed'));
    }

    #initSidebarWidth() {
      if (!this.designGrid) return;
      let width = SIDEBAR_WIDTH_DEFAULT;
      try {
        const stored = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY) || '', 10);
        if (Number.isFinite(stored) && stored >= SIDEBAR_WIDTH_MIN) width = stored;
      } catch {
        /* ignore */
      }
      this.#setSidebarWidth(width, { persist: false });
    }

    #clampSidebarWidth() {
      if (!this.#isSidebarLayout()) return;
      this.#setSidebarWidth(this.#readSidebarWidthPx(), { persist: true });
    }

    #bindSidebarWidthResize() {
      const handle = this.sidebarWidthResize;
      if (!handle || handle.dataset.wpSidebarResizeBound === '1') return;
      handle.dataset.wpSidebarResizeBound = '1';

      handle.addEventListener('pointerdown', (event) => {
        if (handle.hidden || this._sidebarResizing || !this.#isSidebarLayout()) return;
        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startW = this.#readSidebarWidthPx();

        this._sidebarResizing = true;
        this.root?.classList.add('is-sidebar-resizing');
        handle.setPointerCapture?.(event.pointerId);

        const onMove = (ev) => {
          const delta = ev.clientX - startX;
          this.#setSidebarWidth(startW + delta, { persist: false });
        };

        const onUp = () => {
          this._sidebarResizing = false;
          this.root?.classList.remove('is-sidebar-resizing');
          handle.releasePointerCapture?.(event.pointerId);
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
          this.#setSidebarWidth(this.#readSidebarWidthPx(), { persist: true });
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
      });
    }

    #bindPromptSizeResize() {
      const handles = this.promptSizeResizeHandles?.length
        ? this.promptSizeResizeHandles
        : [];
      if (!handles.length || !this.promptTextarea) return;

      for (const handle of handles) {
        if (handle.dataset.wpPromptResizeBound === '1') continue;
        handle.dataset.wpPromptResizeBound = '1';

        handle.addEventListener('pointerdown', (event) => {
          if (handle.hidden || this._promptResizing) return;
          event.preventDefault();
          event.stopPropagation();

          const textarea = this.promptTextarea;
          if (!textarea) return;

          const startY = event.clientY;
          const startH =
            textarea.offsetHeight ||
            parseInt(textarea.style.height, 10) ||
            textarea.getBoundingClientRect().height;

          this._promptResizing = true;
          this.root?.classList.add('is-prompt-resizing');
          handle.setPointerCapture?.(event.pointerId);

          const onMove = (ev) => {
            const delta = ev.clientY - startY;
            this.#setPromptHeight(startH + delta);
          };

          const onUp = () => {
            this._promptResizing = false;
            this.root?.classList.remove('is-prompt-resizing');
            handle.releasePointerCapture?.(event.pointerId);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
          };

          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
          window.addEventListener('pointercancel', onUp);
        });
      }
    }

    #bindPreviewSizeResize() {
      const handles = this.previewSizeResizeHandles?.length
        ? this.previewSizeResizeHandles
        : this.previewSizeResize
          ? [this.previewSizeResize]
          : [];
      if (!handles.length) return;

      for (const handle of handles) {
        if (handle.dataset.wpPreviewResizeBound === '1') continue;
        handle.dataset.wpPreviewResizeBound = '1';

        handle.addEventListener('pointerdown', (event) => {
          if (this._previewResizing) return;
          const style = getComputedStyle(handle);
          if (handle.hidden || style.display === 'none' || style.visibility === 'hidden') return;
          event.preventDefault();
          event.stopPropagation();

        const state = this.#computeMeasureState();
        const aspectKey = this.#previewAspectStorageKey(state.printWidthCm, state.printHeightCm);
        const mount = this.previewMount;
        const stage = this.previewStage;
        if (!mount || !stage) return;

        const ratio = this.#previewAspectRatio(state.printWidthCm, state.printHeightCm);
        const hasWallDims = state.printWidthCm > 0 && state.printHeightCm > 0;
        const wideView =
          this.root?.classList.contains('is-wide-view') ||
          this.root?.classList.contains('is-preview-expanded');
        const fillViewport = !hasWallDims && this.isWallShape;
        const studio = this.designGrid?.querySelector('.ai-generator__design-studio');
        const startY = event.clientY;
        const startRowH = studio?.getBoundingClientRect().height || PREVIEW_FIT_MIN_H;
        const startH =
          mount.offsetHeight ||
          parseInt(mount.style.height, 10) ||
          parseInt(getComputedStyle(mount).height, 10) ||
          PREVIEW_FIT_MIN_H;

        this._previewResizing = true;
        this.root.classList.add('is-preview-resizing');
        handle.setPointerCapture?.(event.pointerId);

        let resizeRaf = 0;

        const onMove = (ev) => {
          if (resizeRaf) return;
          resizeRaf = requestAnimationFrame(() => {
            resizeRaf = 0;
            const wideView =
              this.root?.classList.contains('is-wide-view') ||
              this.root?.classList.contains('is-preview-expanded');
            const liveBounds = this.#computePreviewBounds(state.printWidthCm, state.printHeightCm);
            const stack = this.previewStack || stage;
            const cachedMaxW = Math.max(120, Math.floor(stack?.clientWidth || liveBounds.maxW || 0) - 8);
            let cachedMaxH = Math.max(PREVIEW_FIT_MIN_H, Math.floor(liveBounds.maxH || 0));
            if (wideView && state.printWidthCm > 0 && state.printHeightCm > 0) {
              const studio = this.designGrid?.querySelector('.ai-generator__design-studio');
              const rail = this.designGrid?.querySelector('.ai-generator__design-preview-rail');
              const deckHead = this.designPreviewCol?.querySelector('.ai-generator__preview-deck-head');
              const uploadNote = this.designPreviewCol?.querySelector('[data-ai-upload-note]');
              const handlePad = this.#previewResizeHandlePad() || 34;
              const uploadFootH =
                uploadNote && !uploadNote.hidden
                  ? Math.ceil(uploadNote.getBoundingClientRect().height) + 4
                  : 0;
              const railH = rail?.offsetHeight || 0;
              const headH = (deckHead?.offsetHeight || 0) + 6;
              const { max: rowMax } = this.#widePreviewRowLimits();
              const rowBudget = rowMax - railH - headH - uploadFootH - handlePad - 12;
              const wideBudget = this.#measureWideViewPreviewBudget({
                studio,
                workspace: studio?.querySelector('.ai-generator__design-workspace'),
                rail,
                deckHead,
                uploadFootH,
                handlePad,
              });
              const widthIdeal = Math.floor((cachedMaxW * 0.98) / ratio);
              cachedMaxH = Math.max(cachedMaxH, wideBudget, rowBudget, widthIdeal);
            }
            const libraryGridDocked = this.#isLibraryGridDocked();
            const wallFill =
              state.printWidthCm > 0 && state.printHeightCm > 0 && libraryGridDocked;
            if (wallFill && !wideView) {
              // Studio layout: dragging may grow the whole preview band, not
              // just the mount inside the current stage.
              cachedMaxH = Math.max(cachedMaxH, Math.floor(window.innerHeight * 0.85));
            }
            const delta = (ev.clientY - startY) * 1;
            if (wideView) {
              const { min, max } = this.#widePreviewRowLimits();
              const nextRow = Math.min(max, Math.max(min, Math.round(startRowH + delta)));
              this.setWidePreviewRowHeight(nextRow, { userSet: true });
              this._previewFitKey = '';
              this.#schedulePreviewLayout(true);
              return;
            }
            const clampedH = this.#clampPreviewUserHeight(
              cachedMaxW,
              cachedMaxH,
              ratio,
              startH + delta
            );
            const { fitW, fitH } = this.#fitPreviewWithinBounds(
              cachedMaxW,
              cachedMaxH,
              ratio,
              clampedH
            );
            this._previewUserFitH = fitH;
            this._previewUserAspectKey = aspectKey;
            this.#savePreviewUserHeight(aspectKey, fitH);
            this._previewFitKey = '';
            this.#setPreviewMountSize(mount, stage, {
              fitW,
              fitH,
              aspectCss: this.#previewAspectCssValue(state.printWidthCm, state.printHeightCm),
              bounds: liveBounds,
              printWidthCm: state.printWidthCm,
              printHeightCm: state.printHeightCm,
              hasWallDims: state.printWidthCm > 0 && state.printHeightCm > 0,
              hubOpenBelow:
                !libraryGridDocked &&
                this.root?.classList.contains('is-hub-open') &&
                !this.root?.classList.contains('is-hub-overlay-open'),
              previewFitCompact: false,
              previewWallFill: wallFill && !wideView,
              hasDesign: Boolean(this.currentAsset),
              awaitingDesign: !this.currentAsset,
              cropActive: Boolean(this.cropViewport && !this.cropViewport.hidden),
            });
            if (wallFill && !wideView) {
              this.#syncStudioPreviewBandMinHeight(fitH);
            }
            if (wideView && state.printWidthCm > 0 && state.printHeightCm > 0) {
              this.#ensureWidePreviewRowForMount(fitH, state.printWidthCm, state.printHeightCm);
            }
          });
        };

        const onUp = () => {
          if (resizeRaf) cancelAnimationFrame(resizeRaf);
          resizeRaf = 0;
          this._previewResizing = false;
          this.root.classList.remove('is-preview-resizing');
          handle.releasePointerCapture?.(event.pointerId);
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
          if (wideView) {
            try {
              const studioEl = this.designGrid?.querySelector('.ai-generator__design-studio');
              const rowH = Math.round(studioEl?.getBoundingClientRect().height || 0);
              const { min } = this.#widePreviewRowLimits();
              if (Number.isFinite(rowH) && rowH >= min) {
                localStorage.setItem(WIDE_PREVIEW_ROW_KEY, String(rowH));
              }
            } catch {
              /* ignore */
            }
          }
          this._previewFitKey = '';
          requestAnimationFrame(() => this.#schedulePreviewLayout(true));
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        });
      }
    }

    #fitDesignPreviewBox(printWidthCm, printHeightCm) {
      if (this._previewResizing && !this.#isWideLayoutView()) return;
      if (!(this._previewResizing && this.#isWideLayoutView())) {
        this.#ensureWidePreviewRowMinimum();
      }
      const col = this.designPreviewCol;
      const stage = this.previewStage || col;
      const mount = this.previewMount || this.preview;
      if (!this.preview || !stage || !mount) return;

      const hasWallDims = printWidthCm > 0 && printHeightCm > 0;
      const ratioOnly = !this.isWallShape && !hasWallDims;
      const hasDesign = Boolean(this.currentAsset);
      const cropActive = Boolean(this.cropViewport && !this.cropViewport.hidden);
      const awaitingDesign = !hasDesign;
      const wideView =
        this.root?.classList.contains('is-wide-view') ||
        this.root?.classList.contains('is-preview-expanded');
      const libraryGridDocked = this.#isLibraryGridDocked();
      /* Wide view + no wall dims must fill the row (ratio-only path bails when chrome is 0). */
      const fillViewport =
        (!hasWallDims && !ratioOnly) || (!hasWallDims && awaitingDesign);
      const busy =
        this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling || this.isMockup;

      this.root.classList.toggle('is-no-wall-measure', !hasWallDims);
      this.root.classList.toggle('is-wall-measured', hasWallDims);
      this.root.classList.toggle('is-preview-layout-busy', busy);

      const bounds = this.#computePreviewBounds(printWidthCm, printHeightCm);
      /* Hub open below only applies when library still shares the preview column. */
      const hubOpenBelow =
        !libraryGridDocked &&
        this.root?.classList.contains('is-hub-open') &&
        !this.root?.classList.contains('is-hub-overlay-open');
      /* Wall dimensions: shrink-wrap only when library still shares the preview column. */
      const previewFitCompact = hasWallDims && !libraryGridDocked;
      const previewWallFill = hasWallDims && libraryGridDocked;
      this.root.classList.toggle('is-preview-wall-fill', previewWallFill);

      if (bounds.deck) {
        // User-dragged wall-fill heights own the deck min-height (band growth).
        if (!(previewWallFill && this._previewUserFitH > 0)) {
          bounds.deck.style.minHeight = '0';
        }
        bounds.deck.style.flex = previewFitCompact ? '0 1 auto' : '1 1 auto';
      }

      stage.style.flex = previewFitCompact ? '0 1 auto' : '1 1 auto';
      stage.style.minHeight = '0';
      stage.style.height = '';
      if (!previewWallFill) {
        // Wall-fill mode owns this var in #setPreviewMountSize (raised to the
        // measured fit height); don't reset it here on cached layout passes.
        stage.style.setProperty('--ai-preview-stage-max-h', `${bounds.maxH}px`);
      }
      stage.classList.add('is-preview-fill');

      if (this.previewStack) {
        this.previewStack.style.minHeight = '0';
        if (hubOpenBelow) {
          this.previewStack.style.flex = '0 1 auto';
          this.previewStack.style.alignItems = 'center';
          this.previewStack.style.justifyContent = 'center';
        } else if (previewFitCompact) {
          this.previewStack.style.flex = '0 1 auto';
          this.previewStack.style.alignItems = 'center';
          this.previewStack.style.justifyContent = 'center';
        } else {
          this.previewStack.style.flex = '1 1 auto';
          this.previewStack.style.alignItems = '';
          this.previewStack.style.justifyContent = '';
        }
        this.previewStack.style.height = '';
        this.previewStack.style.width = '100%';
      }

      if (hubOpenBelow || previewFitCompact) {
        stage.style.flex = '0 1 auto';
      }

      mount.style.marginTop = '';
      mount.style.marginBottom = '';
      mount.style.marginInline = '';
      mount.style.flex = previewFitCompact ? '0 0 auto' : '1 1 auto';
      mount.style.flexShrink = previewFitCompact ? '0' : '';
      mount.style.alignSelf = previewFitCompact ? 'center' : 'stretch';
      if (!hasWallDims) {
        mount.style.width = '';
        mount.style.height = '';
        mount.style.maxWidth = '';
        mount.style.maxHeight = '';
        mount.style.minHeight = '';
      }

      if (this.preview && mount !== this.preview) {
        this.preview.style.width = '';
        this.preview.style.height = '';
        this.preview.style.maxWidth = '';
        this.preview.style.flex = '1 1 auto';
        this.preview.style.alignSelf = 'stretch';
      }

      if (fillViewport) {
        const fitKey = `fill|${bounds.maxW}x${bounds.maxH}|${busy ? 'b' : 'i'}`;
        const stageLiveH = Math.floor(stage.getBoundingClientRect().height || 0);
        const mountLiveH = Math.floor(mount.getBoundingClientRect().height || 0);
        const layoutCollapsed = !busy && stageLiveH < PREVIEW_FIT_MIN_H && mountLiveH < PREVIEW_FIT_MIN_H;
        if (this._previewFitKey === fitKey && !layoutCollapsed) return;
        this._previewFitKey = fitKey;

        if (this.previewStack) {
          this.previewStack.style.alignItems = 'stretch';
          this.previewStack.style.justifyContent = 'stretch';
        }
        mount.style.flex = '1 1 auto';
        mount.style.alignSelf = 'stretch';

        stage.style.maxHeight = `${bounds.maxH}px`;
        stage.style.removeProperty('--ai-preview-aspect');
        stage.style.removeProperty('--ai-rw');
        stage.style.removeProperty('--ai-rh');
        stage.style.removeProperty('--ai-preview-fit-w');
        stage.style.removeProperty('--ai-preview-fit-h');
        stage.style.removeProperty('--ai-preview-max-h');

        mount.style.removeProperty('--ai-preview-aspect');
        mount.style.removeProperty('--ai-preview-fit-w');
        mount.style.removeProperty('--ai-preview-fit-h');
        mount.style.width = '';
        mount.style.height = '';
        mount.style.maxWidth = '';
        mount.style.maxHeight = '';

        this.#syncPreviewChromeWidths();
        if (!busy) this.#syncDesignGridHeight(bounds);
        this.#syncPreviewSizeResizeUi(true);
        this._heroScrollResume?.();
        if (
          wideView &&
          !busy &&
          Math.floor(stage.getBoundingClientRect().height || 0) < PREVIEW_FIT_MIN_H
        ) {
          this.#ensureWidePreviewRowMinimum();
          window.requestAnimationFrame(() => {
            this._previewFitKey = '';
            this.#fitDesignPreviewBox(printWidthCm, printHeightCm);
          });
        }
        return;
      }

      if (ratioOnly && !(!hasWallDims && awaitingDesign)) {
        let ratio = this.#previewAspectRatio(0, 0);
        const aspectCss = this.#previewAspectCssValue(0, 0);
        const aspectKey = this.#previewAspectStorageKey(0, 0);
        let preferredH = this.#previewPreferredHeight(aspectKey, mount);
        const maxW = Math.floor(bounds.maxW || 0);
        let maxH = Math.floor(bounds.maxH || 0);
        if (wideView) {
          const studio = this.designGrid?.querySelector('.ai-generator__design-studio');
          const rail = this.designGrid?.querySelector('.ai-generator__design-preview-rail');
          const deckHead = this.designPreviewCol?.querySelector('.ai-generator__preview-deck-head');
          const wideBudget = this.#measureWideViewPreviewBudget({
            studio,
            workspace: studio?.querySelector('.ai-generator__design-workspace'),
            rail,
            deckHead,
          });
          if (wideBudget > maxH) maxH = wideBudget;
          if (!preferredH && wideBudget > PREVIEW_FIT_MIN_H) {
            preferredH = Math.round(wideBudget * 0.86);
          }
        }
        if (maxW < 80 || maxH < 80) {
          if (wideView && this.#ensureWidePreviewRowMinimum()) {
            window.requestAnimationFrame(() => {
              this._previewFitKey = '';
              this.#fitDesignPreviewBox(printWidthCm, printHeightCm);
            });
          }
          return;
        }

        const fitKey = `ratio-only|${maxW}x${maxH}|${this.aspectSelect?.value || ''}|u${preferredH}|${busy ? 'b' : 'i'}`;
        if (!this._previewResizing && this._previewFitKey === fitKey) return;
        this._previewFitKey = fitKey;

        let { fitW, fitH } = this.#fitPreviewWithinBounds(maxW, maxH, ratio, preferredH);
        fitW = Math.round(fitW);
        fitH = Math.round(fitH);

        stage.style.setProperty('--ai-preview-aspect', aspectCss);
        stage.style.setProperty('--ai-preview-fit-w', `${fitW}px`);
        stage.style.setProperty('--ai-preview-fit-h', `${fitH}px`);
        stage.style.setProperty('--ai-preview-max-h', `${fitH}px`);
        stage.style.maxHeight = `${bounds.maxH}px`;
        stage.style.removeProperty('--ai-rw');
        stage.style.removeProperty('--ai-rh');

        mount.style.setProperty('--ai-preview-aspect', aspectCss);
        mount.style.setProperty('--ai-preview-fit-w', `${fitW}px`);
        mount.style.setProperty('--ai-preview-fit-h', `${fitH}px`);
        mount.style.width = `${fitW}px`;
        mount.style.height = `${fitH}px`;
        mount.style.maxWidth = `${fitW}px`;
        mount.style.maxHeight = `${fitH}px`;
        mount.style.flex = '0 1 auto';
        mount.style.alignSelf = 'center';

        this.#syncPreviewChromeWidths();
        if (!busy && hasDesign) this.#syncDesignGridHeight(bounds);
        else this.#clearDesignGridHeight();
        this.#syncPreviewSizeResizeUi(true);
        this._heroScrollResume?.();
        return;
      }

      const applyMeasuredMount = () => {
        const stack = this.previewStack || stage;
        const stackW = Math.floor(stack?.clientWidth || 0);
        let budgetH = Math.floor(bounds.maxH || 0);
        let maxW = Math.max(stackW, Math.floor(bounds.maxW || 0));
        let maxH = budgetH;

        let ratio = this.#previewAspectRatio(printWidthCm, printHeightCm);
        const aspectCss = this.#previewAspectCssValue(printWidthCm, printHeightCm);
        const aspectKey = this.#previewAspectStorageKey(printWidthCm, printHeightCm);
        const libraryGridDocked = this.#isLibraryGridDocked();
        const previewWallFill = hasWallDims && libraryGridDocked;
        const studio = this.designGrid?.querySelector('.ai-generator__design-studio');
        const userSized = this._previewUserFitH > 0;
        let preferredH = this.#previewPreferredHeight(aspectKey, mount, {
          forceMax: previewWallFill && !this._previewResizing && !userSized,
        });
        if (previewWallFill && !this._previewResizing && !userSized) {
          preferredH = 0;
          const handlePad = this.previewSizeResize && !this.previewSizeResize.hidden ? 18 : 0;
          const stackH = Math.floor(stack?.clientHeight || 0);
          if (stackH > handlePad + PREVIEW_FIT_MIN_H) {
            maxH = Math.max(maxH, stackH - handlePad);
          }
          const stackInnerW = Math.max(120, stackW - 8);
          maxW = Math.max(maxW, stackInnerW);
          if (!wideView && studio) {
            const studioRect = studio.getBoundingClientRect();
            const rail = this.designGrid?.querySelector('.ai-generator__design-preview-rail');
            const deckHead = this.designPreviewCol?.querySelector('.ai-generator__preview-deck-head');
            const uploadNote = this.designPreviewCol?.querySelector('[data-ai-upload-note]');
            const railH = rail?.offsetHeight || 0;
            const headH = (deckHead?.offsetHeight || 0) + 6;
            const uploadFootH =
              uploadNote && !uploadNote.hidden
                ? Math.ceil(uploadNote.getBoundingClientRect().height) + 4
                : 0;
            const studioGap = parseFloat(getComputedStyle(studio).gap) || 10;
            const fromStudio = Math.floor(
              studioRect.height - railH - studioGap - headH - 4 - uploadFootH - handlePad
            );
            if (fromStudio > maxH) maxH = fromStudio;
          }
          if (wideView) {
            const uploadNote = this.designPreviewCol?.querySelector('[data-ai-upload-note]');
            maxH = Math.max(
              maxH,
              this.#measureWideViewPreviewBudget({
                studio,
                workspace: studio?.querySelector('.ai-generator__design-workspace'),
                rail: this.designGrid?.querySelector('.ai-generator__design-preview-rail'),
                deckHead: this.designPreviewCol?.querySelector('.ai-generator__preview-deck-head'),
                uploadFootH:
                  uploadNote && !uploadNote.hidden
                    ? Math.ceil(uploadNote.getBoundingClientRect().height) + 4
                    : 0,
                handlePad,
              })
            );
          }
        }

        if (previewWallFill && userSized && !wideView) {
          // Honour user-dragged heights beyond the current band — the band
          // (deck min-height) grows with the preview below.
          const userCap = Math.floor(window.innerHeight * 0.85);
          maxH = Math.max(maxH, Math.min(Math.floor(preferredH || 0), userCap));
        }

        const anchorKey = bounds.controlsAnchorH || 0;
        const hubKey = hubOpenBelow ? 'below' : this.root?.classList.contains('is-hub-open') ? 'open' : 'off';
        const fitKey = `wall|${maxW}x${maxH}x${ratio.toFixed(4)}|u${preferredH || 0}|${hasDesign ? 'd' : 'n'}|${cropActive ? 'c' : 'n'}|${busy ? 'b' : 'i'}|a${anchorKey}|h${hubKey}|w${wideView ? '1' : '0'}`;
        if (!this._previewResizing && this._previewFitKey === fitKey) return;
        if (maxW < 80 || maxH < 80) {
          if (wideView && this.#ensureWidePreviewRowMinimum()) {
            window.requestAnimationFrame(() => {
              this._previewFitKey = '';
              this.#fitDesignPreviewBox(printWidthCm, printHeightCm);
            });
          }
          return;
        }
        this._previewFitKey = fitKey;

        let { fitW, fitH } = this.#fitPreviewWithinBounds(maxW, maxH, ratio, preferredH);
        fitW = Math.round(fitW);
        fitH = Math.round(fitH);

        this.#setPreviewMountSize(mount, stage, {
          fitW,
          fitH,
          aspectCss,
          bounds,
          printWidthCm,
          printHeightCm,
          hasWallDims,
          hubOpenBelow,
          previewFitCompact,
          previewWallFill,
          hasDesign,
          awaitingDesign,
          cropActive,
        });

        if (previewWallFill && !wideView && userSized) {
          this.#syncStudioPreviewBandMinHeight(fitH);
        }

        this.#syncPreviewChromeWidths();
        if (!busy && hasDesign) {
          this.#syncDesignGridHeight(bounds);
        } else {
          this.#clearDesignGridHeight();
        }
        this.#syncPreviewSizeResizeUi(true);
        this._heroScrollResume?.();
      };

      requestAnimationFrame(() => {
        applyMeasuredMount();
        requestAnimationFrame(() => {
          applyMeasuredMount();
          window.setTimeout(applyMeasuredMount, 80);
        });
      });
    }

    /** Studio side-by-side — align preview height to the upload control column. */
    #measureUploadAlignedPreviewMaxH({ deckHead, stage, workspace, headH, stagePad, uploadFootH, handlePad = 0 } = {}) {
      const controls = this.designControls;
      if (!controls || controls.hidden) return 0;
      const uploadBtn = controls.querySelector('[data-ai-upload-trigger]');
      const uploadBottom = uploadBtn
        ? uploadBtn.getBoundingClientRect().bottom
        : controls.getBoundingClientRect().bottom || 0;
      const previewCol = workspace?.querySelector('.ai-generator__design-preview');
      const previewAnchorTop =
        deckHead?.getBoundingClientRect().top ||
        previewCol?.getBoundingClientRect().top ||
        stage?.getBoundingClientRect().top ||
        0;
      if (uploadBottom <= previewAnchorTop + 48) return 0;
      return Math.floor(uploadBottom - previewAnchorTop - headH - stagePad - uploadFootH - handlePad);
    }

    /** Largest preview height that fits wall ratio, workspace, and optional upload-column alignment. */
    #resolveWallPreviewTargetH(
      maxW,
      maxH,
      printWidthCm,
      printHeightCm,
      { uploadAlignedMaxH = 0, workspaceAvailH = 0, libraryBelow = false } = {}
    ) {
      if (printWidthCm <= 0 || printHeightCm <= 0) return maxH;
      const ratio = printWidthCm / printHeightCm;
      const idealH = Math.floor((maxW * 0.98) / ratio);
      let targetH = Math.min(idealH, maxH);
      if (libraryBelow && workspaceAvailH > PREVIEW_WALL_MIN_H) {
        targetH = Math.min(idealH, workspaceAvailH, maxH);
        return Math.max(PREVIEW_WALL_MIN_H, targetH);
      }
      if (uploadAlignedMaxH > PREVIEW_FIT_MIN_H) {
        targetH = Math.min(targetH, uploadAlignedMaxH);
      }
      if (workspaceAvailH > PREVIEW_FIT_MIN_H) {
        targetH = Math.min(targetH, workspaceAvailH);
      }
      return Math.max(PREVIEW_WALL_MIN_H, targetH);
    }

    #previewHeightCaps(printWidthCm, printHeightCm, hasDesign) {
      let ratio = 16 / 9;
      if (printWidthCm > 0 && printHeightCm > 0) {
        ratio = printWidthCm / printHeightCm;
      } else if (!this.isWallShape && this.aspectSelect) {
        const option = this.aspectSelect.selectedOptions[0];
        const ratioStr = option?.dataset.ratio || '16 / 9';
        const parts = ratioStr.split('/').map((s) => parseFloat(s.trim()));
        if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
          ratio = parts[0] / parts[1];
        }
      }

      const hasWallDims = printWidthCm > 0 && printHeightCm > 0;
      const ratioOnly = !this.isWallShape && !hasWallDims;
      const expanded =
        this.root?.classList.contains('is-preview-expanded') ||
        this.root?.classList.contains('is-wide-view');
      const busy =
        this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling || this.isMockup;
      let vhCap = hasWallDims ? 0.72 : 0.78;
      let pxCap = hasWallDims ? 960 : hasDesign ? 820 : 760;

      if (expanded) {
        vhCap = Math.min(0.76, vhCap + 0.08);
        pxCap += 120;
      }

      if (hasWallDims && ratio > 1.2) {
        vhCap = Math.min(0.62, 0.3 + ratio * 0.12);
        pxCap = Math.max(pxCap, Math.round(320 * ratio));
      }

      if (ratio < 1) {
        vhCap = Math.min(0.82, vhCap + Math.min(0.06, (1 - ratio) * 0.1));
        pxCap += Math.round((1 - ratio) * (hasDesign ? 180 : 120));
      }
      if (ratio < 0.75) {
        pxCap = Math.max(pxCap, hasDesign ? 880 : 680);
      }
      if (ratio < 0.55) {
        vhCap = Math.min(0.84, vhCap + 0.04);
        pxCap = Math.max(pxCap, hasDesign ? 920 : 720);
      }

      /* Aspect-locked previews keep their size while generating/uploading. */
      if (busy && !(this._previewUserFitH > 0) && !(hasWallDims || ratioOnly)) {
        vhCap = Math.min(vhCap, expanded ? 0.68 : 0.58);
        pxCap = Math.min(pxCap, expanded ? 640 : 520);
      }

      return { vhCap, pxCap, ratio };
    }

    /** Match preview column height to the prompt/upload column (studio view). */
    #syncDesignGridHeight(_bounds) {
      if (
        this.root?.classList.contains('is-preview-expanded') ||
        this.root?.classList.contains('is-wide-view')
      ) {
        this.#clearDesignGridHeight();
        return;
      }

      /* Wall-measured previews size by aspect ratio — locking column height squashes them. */
      if (this.root?.classList.contains('is-wall-measured')) {
        this.#clearDesignGridHeight();
        return;
      }

      const busy =
        this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling || this.isMockup;
      if (busy) {
        this.#clearDesignGridHeight();
        return;
      }

      const hasDesign = this.preview?.classList.contains('has-content');
      if (!hasDesign) {
        this.#clearDesignGridHeight();
        return;
      }

      if (this.designGrid?.classList.contains('is-prompt-collapsed')) {
        this.#clearDesignGridHeight();
        return;
      }

      /* Library spans full grid width below row 1 — don't lock studio to prompt height. */
      if (this.#isLibraryGridDocked()) {
        this.#clearDesignGridHeight();
        return;
      }

      const grid = this.designGrid;
      const controls = this.designControls;
      if (!grid || !controls) return;

      const controlsH = Math.round(controls.getBoundingClientRect().height);
      if (controlsH < 180) return;

      grid.style.setProperty('--ai-studio-h', `${controlsH}px`);
      grid.classList.add('is-studio-height-synced');
    }

    #clearDesignGridHeight() {
      this.designGrid?.style.removeProperty('--ai-studio-h');
      this.designGrid?.classList.remove('is-studio-height-synced');
      if (this.designControls) {
        this.designControls.style.minHeight = '';
      }
    }

    /** Hub chrome only — expanded panel scrolls separately so preview keeps room. */
    #measureDesignHubChromeH(designHub, { hubOpen = false, hubOverlayOpen = false } = {}) {
      if (!designHub) return 0;
      if (!hubOpen) {
        const head = designHub.querySelector('.ai-generator__design-hub-head');
        const measured = head?.offsetHeight || designHub.offsetHeight || 0;
        return Math.max(Math.ceil(measured), 48) + 10;
      }
      if (hubOverlayOpen) return Math.min(designHub.offsetHeight || 0, 148) + 10;
      const head = designHub.querySelector('.ai-generator__design-hub-head');
      const nav = designHub.querySelector('.ai-generator__design-hub-nav');
      const chrome = (head?.offsetHeight || 48) + (nav?.offsetHeight || 52) + 20;
      return chrome + 10;
    }

    /** Space available for the live viewport inside the design column. */
    #computePreviewBounds(printWidthCm = 0, printHeightCm = 0) {
      const col = this.designPreviewCol;
      const grid = this.designGrid || col?.closest('.ai-generator__design-grid');
      const stage = this.previewStage;
      const deck = col?.querySelector('.ai-generator__preview-deck');
      const studio = grid?.querySelector('.ai-generator__design-studio');
      const workspace = studio?.querySelector('.ai-generator__design-workspace');
      const previewBody = col?.querySelector('.ai-generator__design-preview-body');
      const rail = studio?.querySelector('.ai-generator__design-preview-rail');
      const explore =
        this.exploreBar ||
        col?.querySelector('.ai-generator__preview-explore') ||
        grid?.querySelector(':scope > .ai-generator__preview-explore');
      const designHub =
        this.designHub ||
        workspace?.querySelector('[data-ai-inspiration-gallery]') ||
        grid?.querySelector('[data-ai-inspiration-gallery]');
      const deckHead = col?.querySelector('.ai-generator__preview-deck-head');
      const uploadFoot = col?.querySelector('[data-ai-upload-note]');
      const uploadFootH =
        uploadFoot && !uploadFoot.hidden
          ? Math.ceil(uploadFoot.getBoundingClientRect().height) + 4
          : 0;
      const hasDesign = Boolean(this.currentAsset);
      const awaitingDesign = !hasDesign;
      const expanded =
        this.root?.classList.contains('is-preview-expanded') ||
        this.root?.classList.contains('is-wide-view');
      const promptCollapsed =
        expanded || grid?.classList.contains('is-prompt-collapsed');
      const controlsAnchorH =
        !expanded && !promptCollapsed && this.designControls
          ? Math.round(this.designControls.getBoundingClientRect().height)
          : 0;
      let uploadAlignedMaxH = 0;
      const { vhCap, pxCap } = this.#previewHeightCaps(printWidthCm, printHeightCm, hasDesign);

      const widthRoot =
        previewBody ||
        ((hasDesign && promptCollapsed) || expanded ? grid || workspace || col : workspace || stage || col);
      const stageWidth = Math.floor(widthRoot?.clientWidth || col?.clientWidth || 0);
      let maxW = Math.max(200, stageWidth - 8);

      let maxH = Math.floor(window.innerHeight * (hasDesign ? 0.82 : 0.78));
      let deckMinH = maxH + (deckHead?.offsetHeight || 0) + 12;

      if (studio) {
        const libraryGridDocked = this.#isLibraryGridDocked();
        const hubDocked =
          libraryGridDocked ||
          (expanded && designHub?.classList.contains('is-wideview-docked'));
        const exploreDocked = expanded && explore?.classList.contains('is-wideview-docked');
        const exploreH = exploreDocked ? 0 : explore ? explore.offsetHeight + 10 : 0;
        const hubOpen = Boolean(designHub && !designHub.classList.contains('is-collapsed'));
        const hubOverlayOpen = this.root?.classList.contains('is-hub-overlay-open');
        const hubH =
          hubDocked
            ? 0
            : this.#measureDesignHubChromeH(designHub, { hubOpen, hubOverlayOpen }) +
              (() => {
                if (!hubOpen || hubOverlayOpen || !designHub) return 0;
                const panelInner = designHub.querySelector('.ai-generator__design-hub-panel-inner');
                const resizeHandle = designHub.querySelector('[data-ai-design-hub-resize]');
                const handleH =
                  resizeHandle && !resizeHandle.hidden ? resizeHandle.getBoundingClientRect().height : 0;
                return handleH + (panelInner?.offsetHeight || 0) + 6;
              })();
        const footerH = exploreH + hubH;
        const headH = (deckHead?.offsetHeight || 0) + 6;
        const railH = rail ? rail.offsetHeight : 0;
        const studioGap = parseFloat(getComputedStyle(studio).gap) || 10;
        const stagePad = 4;
        const dockH = 0;
        const chromeH = railH + studioGap + footerH + headH + stagePad + dockH;
        const previewFloor =
          hubOpen && !hubOverlayOpen
            ? 0
            : hubOpen
              ? Math.max(300, Math.floor(window.innerHeight * (expanded ? 0.44 : 0.42)))
              : 0;
        const deckBudget = Math.max(
          awaitingDesign ? 360 : hasDesign ? 340 : 300,
          Math.floor(window.innerHeight * vhCap) - chromeH
        );

        deckMinH = deckBudget;
        maxH = Math.max(
          awaitingDesign ? 360 : hasDesign ? 320 : 280,
          previewFloor || 0,
          deckBudget - headH - stagePad - uploadFootH
        );

        if (
          !libraryGridDocked &&
          (controlsAnchorH > 180 || (printWidthCm > 0 && printHeightCm > 0 && !expanded))
        ) {
          const hasWallDims = printWidthCm > 0 && printHeightCm > 0;
          const handlePad = this.previewSizeResize && !this.previewSizeResize.hidden ? 18 : 0;
          if (!uploadAlignedMaxH) {
            uploadAlignedMaxH = this.#measureUploadAlignedPreviewMaxH({
              deckHead,
              stage,
              workspace,
              headH,
              stagePad,
              uploadFootH,
              handlePad,
            });
          }
          if (uploadAlignedMaxH > PREVIEW_FIT_MIN_H) {
            const ratioVal = printWidthCm > 0 && printHeightCm > 0 ? printWidthCm / printHeightCm : 1;
            const tallBonus = ratioVal < 0.85 ? Math.min(64, Math.round(uploadAlignedMaxH * 0.08)) : 0;
            maxH = Math.max(
              PREVIEW_FIT_MIN_H,
              Math.min(maxH, uploadAlignedMaxH + tallBonus)
            );
            deckMinH = maxH + headH + stagePad + uploadFootH;
          }
          if (hasWallDims) {
            const wsRect = workspace?.getBoundingClientRect();
            const fromWorkspace = wsRect
              ? Math.floor(wsRect.height - headH - stagePad - uploadFootH - (hubOpen && !hubOverlayOpen ? footerH : 0))
              : 0;
            const studioRect = studio?.getBoundingClientRect();
            const fromStudio = studioRect
              ? Math.floor(
                  studioRect.height - railH - studioGap - footerH - headH - stagePad - uploadFootH
                )
              : 0;
            const workspaceAvailH = Math.max(fromWorkspace, fromStudio);
            maxH = this.#resolveWallPreviewTargetH(maxW, maxH, printWidthCm, printHeightCm, {
              uploadAlignedMaxH: libraryGridDocked ? 0 : uploadAlignedMaxH,
              workspaceAvailH,
              libraryBelow: libraryGridDocked,
            });
            deckMinH = maxH + headH + stagePad + uploadFootH;
          } else if (controlsAnchorH > 180) {
            const workspaceBudget = controlsAnchorH - railH - studioGap;
            const previewBudget = workspaceBudget - footerH;
            const stageBudget = previewBudget - headH - stagePad - uploadFootH;
            maxH = Math.max(previewFloor || 240, stageBudget);
            deckMinH = maxH + headH + stagePad + uploadFootH;
          }
        } else if (libraryGridDocked && !expanded) {
          const handlePad = this.previewSizeResize && !this.previewSizeResize.hidden ? 18 : 0;
          const wsRect = workspace?.getBoundingClientRect();
          const fromWorkspace =
            wsRect && wsRect.height > 80
              ? Math.floor(wsRect.height - headH - stagePad - uploadFootH - handlePad)
              : 0;
          const studioRect = studio.getBoundingClientRect();
          const fromStudio =
            studioRect.height > 80
              ? Math.floor(
                  studioRect.height - railH - studioGap - headH - stagePad - uploadFootH - handlePad
                )
              : 0;
          const workspaceAvailH = Math.max(fromWorkspace, fromStudio);
          if (printWidthCm > 0 && printHeightCm > 0) {
            maxH = this.#resolveWallPreviewTargetH(maxW, maxH, printWidthCm, printHeightCm, {
              uploadAlignedMaxH: 0,
              workspaceAvailH,
              libraryBelow: true,
            });
          } else if (workspaceAvailH > PREVIEW_FIT_MIN_H) {
            maxH = Math.max(maxH, workspaceAvailH);
          }
          deckMinH = Math.max(deckMinH, maxH + headH + stagePad + uploadFootH);
        }

        if (expanded && grid && studio) {
          const handlePad = this.previewSizeResize && !this.previewSizeResize.hidden ? 18 : 0;
          const fromWide = this.#measureWideViewPreviewBudget({
            studio,
            workspace: studio.querySelector('.ai-generator__design-workspace'),
            rail,
            deckHead,
            uploadFootH,
            handlePad,
          });
          const wsH = Math.floor(workspace?.clientHeight || 0);
          const rowTarget = this.#readWidePreviewRowHeightPx();
          const fromRow = Math.floor(
            rowTarget - railH - studioGap - headH - stagePad - uploadFootH - handlePad
          );
          const workspaceAvailH = Math.max(
            fromWide,
            fromRow,
            wsH - headH - stagePad - uploadFootH - (hubOpen && !hubOverlayOpen ? footerH : 0)
          );
          if (printWidthCm > 0 && printHeightCm > 0) {
            maxH = this.#resolveWallPreviewTargetH(maxW, maxH, printWidthCm, printHeightCm, {
              uploadAlignedMaxH: 0,
              workspaceAvailH,
              libraryBelow: false,
            });
          } else {
            maxH = Math.max(maxH, workspaceAvailH, Math.round(window.innerHeight * 0.46));
          }
          deckMinH = maxH + headH + stagePad + uploadFootH;
        }
      }

      if (printWidthCm > 0 && printHeightCm > 0) {
        maxH = Math.max(PREVIEW_WALL_MIN_H, maxH);
        deckMinH = Math.max(deckMinH, maxH + (deckHead?.offsetHeight || 0) + 12);
      }

      const libraryGridDockedFinal = this.#isLibraryGridDocked();
      const expandedFinal =
        this.root?.classList.contains('is-preview-expanded') ||
        this.root?.classList.contains('is-wide-view');
      if (printWidthCm > 0 && printHeightCm > 0 && (libraryGridDockedFinal || expandedFinal)) {
        /* Grid-docked / wide view — maximize within workspace; aspect ratio picks width vs height limit. */
        maxH = Math.min(maxH, Math.floor(window.innerHeight * 0.92));
      } else if (libraryGridDockedFinal && !expandedFinal) {
        maxH = Math.max(PREVIEW_FIT_MIN_H, maxH);
      } else if (controlsAnchorH > 180) {
        maxH = Math.min(maxH, Math.floor(window.innerHeight * 0.92));
      } else {
        maxH = Math.min(maxH, Math.floor(window.innerHeight * vhCap), pxCap);
      }

      return { maxW, maxH, deckMinH, deck, vhCap, pxCap, controlsAnchorH, uploadAlignedMaxH };
    }

    /** Keep design chrome full-width; preview sizes inside the live viewport only. */
    #syncPreviewChromeWidths() {
      const frameWidth = '100%';

      if (this.designPreviewCol) {
        this.designPreviewCol.style.setProperty('--ai-preview-frame-width', frameWidth);
      }
      const rail = this.root.querySelector('.ai-generator__design-preview-rail');
      if (rail) {
        rail.style.setProperty('--ai-preview-frame-width', frameWidth);
      }

      if (this.previewChrome) {
        this.previewChrome.style.width = '';
        this.previewChrome.style.maxWidth = '';
        this.previewChrome.style.marginInline = '';
      }
      if (this.previewStack) {
        this.previewStack.style.width = '';
        this.previewStack.style.maxWidth = '';
        this.previewStack.style.marginInline = '';
      }

      if (!this.designToolbar) return;
      this.designToolbar.style.width = '';
      this.designToolbar.style.gridTemplateColumns = '';
    }

    #clearDesignPreviewBoxSize() {
      if (!this.preview || !this.previewStage) return;
      const state = this.#computeMeasureState();
      this.#fitDesignPreviewBox(state.printWidthCm, state.printHeightCm);
    }

    #showError(message) {
      if (!this.errorEl) {
        this.errorEl = this.root.querySelector('[data-ai-error]');
      }
      if (!this.errorEl) {
        console.warn('[ai-generator]', message);
        return;
      }
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    #clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    #showCartSuccess() {
      if (!this.cartSuccessEl) return;
      this.#clearError();
      this.cartSuccessEl.hidden = false;
      this.#scrollToSection('summary');
    }

    #clearCartSuccess() {
      if (!this.cartSuccessEl) return;
      this.cartSuccessEl.hidden = true;
    }

    #resetResult() {
      this.currentAsset = null;
      this.#emitPreviewChanged();
      this.#clearCartSuccess();
      this.#setPreviewLive(false);
      this.#resetAcknowledgements();
      this.#setCheckoutVisible(false);
      if (this.qualityBadge) this.qualityBadge.hidden = true;
      if (this.qualityRow) {
        this.qualityRow.hidden = true;
        this.qualityRow.classList.remove('is-upscale');
      }
      if (this.qualityUpscaleNote) this.qualityUpscaleNote.hidden = true;
      this.#syncPreviewOwnershipNote(null);
      this.#syncPreviewStatusBar();
      if (this.wallOverlay) this.wallOverlay.hidden = true;
      if (this.panelLinesEl) {
        this.panelLinesEl.hidden = true;
        this.panelLinesEl.replaceChildren();
      }
      this.preview?.classList.remove('is-preview-busy', 'is-preview-generating', 'is-preview-tiling');
      if (this.skeleton) this.skeleton.hidden = true;
      if (this.image) {
        this.image.hidden = true;
        this.image.removeAttribute('src');
      }
      if (this.placeholder) this.placeholder.hidden = false;
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }
      this.#hideUploadCrop();
      this.#resetAdjustments(false);
      this.#syncRefineUi();
      this.#syncPreviewActions();
      this.#syncPreviewPlaceholder(this.#computeMeasureState());
      this.#clearDesignGridHeight();
      this.#schedulePreviewLayout(true);
    }

    #setGeneratingState(loading) {
      this.isGenerating = loading;
      this.root.classList.toggle('is-loading', loading || this.isTiling || this.isUpscaling);
      this.preview?.classList.toggle('is-preview-busy', loading || this.isTiling || this.isUpscaling);
      this.preview?.classList.toggle('is-preview-generating', loading);
      if (this.generateButton) this.generateButton.disabled = loading;
      if (this.skeleton) this.skeleton.hidden = !loading;
      if (this.skeletonText && loading) {
        this.skeletonText.textContent = this.previewGeneratingText;
      }
      if (loading && this.placeholder) this.placeholder.hidden = true;
      if (!loading && !this.preview?.classList.contains('has-content') && this.placeholder) {
        this.placeholder.hidden = false;
      }
      if (loading) this.#schedulePreviewLayout(true);
      this.#syncRefineUi();
      this.#syncPreviewActions();
    }

    #syncPreviewPlaceholder(state = {}) {
      if (!this.placeholderText) return;
      if (this.preview?.classList.contains('has-content')) {
        this.placeholder?.classList.remove('is-wall-hint');
        return;
      }
      const printWidthCm = Number(state.printWidthCm) || 0;
      const printHeightCm = Number(state.printHeightCm) || 0;
      const needsWallHint =
        this.isWallShape && !(printWidthCm > 0 && printHeightCm > 0);
      if (needsWallHint) {
        this.placeholderText.textContent = this.previewWallHint;
        this.placeholder?.classList.add('is-wall-hint');
        if (this.placeholderWallJump) this.placeholderWallJump.hidden = false;
        return;
      }
      if (!this.isWallShape && !(printWidthCm > 0 && printHeightCm > 0)) {
        const ratioText = this.#formatAspectRatio(this.#previewAspectRatio(0, 0));
        this.placeholderText.textContent = `Preview shape: ${this.aspectSelect?.value || 'fixed ratio'} (${ratioText}). Enter wall size for print measurements and checkout.`;
        this.placeholder?.classList.remove('is-wall-hint');
        if (this.placeholderWallJump) this.placeholderWallJump.hidden = true;
        return;
      }
      this.placeholderText.textContent = this.previewIdleText;
      this.placeholder?.classList.remove('is-wall-hint');
      if (this.placeholderWallJump) this.placeholderWallJump.hidden = true;
    }

    #isInstallSelected() {
      return Boolean(this.regionSelect?.value);
    }

    #initInstallationUnavailable() {
      if (!this.installationUnavailable) return;
      const installWrap = this.root.querySelector('[data-ai-install-wrap]');
      installWrap?.classList.add('ai-generator__wall-panel-action--construction');
      if (this.regionSelect) {
        this.regionSelect.disabled = true;
        this.regionSelect.setAttribute('aria-disabled', 'true');
        this.regionSelect.value = '';
        this.#syncStyledSelect(this.regionSelect);
      }
      const styledWrap = installWrap?.querySelector('.ai-generator__styled-select--wall-action');
      if (styledWrap) {
        styledWrap.classList.add('is-disabled');
        const trigger = styledWrap.querySelector('.ai-generator__styled-select-trigger');
        if (trigger instanceof HTMLButtonElement) {
          trigger.disabled = true;
          trigger.setAttribute('aria-disabled', 'true');
        }
      }
    }

    /** Collapsible design-tools tray — collapsed on mobile, open on wide screens. */
    #upgradeLegacyDesignToolsChrome() {
      const head = this.root.querySelector('.ai-generator__tool-panel-head');
      const legacyWrap = head?.querySelector('.ai-generator__layout-toggle-head-wrap');
      if (head && legacyWrap && !head.querySelector('.ai-generator__design-tools-label, .ai-generator__design-tools-head')) {
        const title = head.querySelector('.ai-generator__tool-panel-title');
        const hint = head.querySelector('.ai-generator__tool-panel-hint');
        const toggleBtn = legacyWrap.querySelector('[data-ai-preview-expand-toggle]');
        if (title && hint && toggleBtn) {
          const asInlineLabel = (el, className) => {
            if (!(el instanceof HTMLElement)) return null;
            if (el.tagName === 'SPAN') {
              el.classList.add(className);
              return el;
            }
            const span = document.createElement('span');
            span.className = className;
            span.textContent = el.textContent?.trim() || '';
            return span;
          };

          const titleEl = asInlineLabel(title, 'ai-generator__tool-panel-title');
          const hintEl = asInlineLabel(hint, 'ai-generator__tool-panel-hint');
          const titleRow = document.createElement('div');
          titleRow.className = 'ai-generator__tool-panel-title-row';
          const toolsLabel = document.createElement('div');
          toolsLabel.className = 'ai-generator__design-tools-label';
          const sep = document.createElement('span');
          sep.className = 'ai-generator__tool-panel-head-sep';
          sep.setAttribute('aria-hidden', 'true');
          const cta = document.createElement('div');
          cta.className = 'ai-generator__layout-view-cta ai-generator__layout-view-cta--inline';
          const ctaTitle = document.createElement('span');
          ctaTitle.className = 'ai-generator__layout-view-cta-title';
          ctaTitle.textContent = 'Layout view';
          const label = toggleBtn.querySelector('[data-ai-preview-expand-label]');
          if (label) {
            label.classList.remove('visually-hidden');
            label.classList.add('ai-generator__layout-toggle-label');
          }
          cta.append(ctaTitle, toggleBtn);
          if (titleEl && hintEl) {
            toolsLabel.append(titleEl, hintEl);
          }
          titleRow.append(toolsLabel, sep, cta);
          head.replaceChildren(titleRow);
        }
      }

      this.#ensureLegacyDesignToolsTray();
      this.#upgradeLegacyDesignToolsToolbar();

      this.designToolsTray = this.root.querySelector('[data-ai-design-tools-tray]');
      this.designToolbar = this.root.querySelector('[data-ai-design-toolbar]');
      this.#ensureToolbarOverflowWrapper();
      this.previewExpandToggles = Array.from(
        this.root.querySelectorAll('[data-ai-preview-expand-toggle]')
      );
      this.#bindPreviewExpandToggles();
      this.#syncPreviewExpandToggles();
    }

    #ensureLegacyDesignToolsTray() {
      const panelSection = this.root.querySelector('.ai-generator__tool-panel-section');
      const head = panelSection?.querySelector('.ai-generator__tool-panel-head');
      const trayInsideHead = head?.querySelector(':scope > [data-ai-design-tools-tray]');
      if (trayInsideHead && head?.parentElement) {
        head.after(trayInsideHead);
      }

      if (this.root.querySelector('[data-ai-design-tools-tray]')) return;

      const wrap =
        panelSection?.querySelector(':scope > [data-ai-design-tools-tray] .ai-generator__design-toolbar-wrap') ||
        panelSection?.querySelector(':scope > .ai-generator__design-toolbar-wrap');
      if (!panelSection || !wrap) return;

      const titleRow = head?.querySelector('.ai-generator__tool-panel-title-row');
      const shopifySection = this.root.closest('.shopify-section');
      const sectionKey = shopifySection?.id?.replace('shopify-section-', '') || 'design-tools';

      const details = document.createElement('details');
      details.className = 'ai-generator__design-tools-tray';
      details.dataset.aiDesignToolsTray = '';
      details.id = `ai-design-tools-tray-${sectionKey}`;
      details.open = true;

      const summary = document.createElement('summary');
      summary.className = 'visually-hidden';
      summary.textContent = 'Design tool buttons';

      wrap.before(details);
      details.append(summary, wrap);

      if (titleRow && !titleRow.querySelector('[data-ai-design-tools-tray-toggle]')) {
        const toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = 'ai-generator__design-tools-tray-toggle';
        toggleBtn.dataset.aiDesignToolsTrayToggle = '';
        toggleBtn.setAttribute('aria-expanded', String(details.open));
        toggleBtn.setAttribute('aria-controls', details.id);
        toggleBtn.setAttribute('aria-label', 'Show or hide design tools');
        toggleBtn.innerHTML =
          '<span class="ai-generator__design-tools-tray-chevron" aria-hidden="true">' +
          '<svg class="ai-generator__design-tools-tray-chevron-svg" viewBox="0 0 20 20" fill="currentColor" focusable="false">' +
          '<path d="M5.5 7.75 10 12.25 14.5 7.75" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg></span>';
        titleRow.appendChild(toggleBtn);
      }
    }

    #ensureToolbarOverflowWrapper() {
      const wrap =
        this.root.querySelector('[data-ai-toolbar-wrap]') ||
        this.root.querySelector('.ai-generator__design-toolbar-wrap');
      if (wrap && !wrap.hasAttribute('data-ai-toolbar-wrap')) {
        wrap.setAttribute('data-ai-toolbar-wrap', '');
      }

      const toolbar = wrap?.querySelector('[data-ai-design-toolbar]');
      if (!toolbar || toolbar.closest('[data-ai-toolbar-collapsible]')) return;

      const details = document.createElement('details');
      details.className = 'ai-generator__toolbar-collapsible';
      details.dataset.aiToolbarCollapsible = '';
      details.open = true;

      const summary = document.createElement('summary');
      summary.className = 'ai-generator__toolbar-collapsible-summary';
      summary.innerHTML =
        '<span class="ai-generator__toolbar-collapsible-main">' +
        '<span class="ai-generator__toolbar-collapsible-label">All tools</span>' +
        '<span class="ai-generator__toolbar-collapsible-hint">Tap to show every design tool</span>' +
        '</span>' +
        '<span class="ai-generator__adjust-chevron" aria-hidden="true">' +
        '<svg class="ai-generator__design-tools-tray-chevron-svg" viewBox="0 0 20 20" fill="currentColor" focusable="false">' +
        '<path d="M5.5 7.75 10 12.25 14.5 7.75" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path>' +
        '</svg></span>';

      toolbar.parentElement?.insertBefore(details, toolbar);
      details.append(summary, toolbar);
    }

    #bindToolbarOverflowMenu() {
      this.#ensureToolbarOverflowWrapper();
      const wrap = this.root.querySelector('[data-ai-toolbar-wrap]');
      const details = this.root.querySelector('[data-ai-toolbar-collapsible]');
      const toolbar = this.designToolbar || this.root.querySelector('[data-ai-design-toolbar]');
      if (!wrap || !details || !toolbar) return;

      if (this._toolbarOverflowRo) {
        this._toolbarOverflowRo.disconnect();
        this._toolbarOverflowRo = null;
      }

      const sync = () => {
        if (!wrap.isConnected) return;
        const onPreviewRail = Boolean(wrap.closest('.ai-generator__design-preview-rail'));
        if (onPreviewRail) {
          wrap.classList.remove('is-toolbar-menu-mode');
          details.open = true;
          this._toolbarMenuPinnedOpen = false;
          return;
        }
        const tray = this.designToolsTray;
        if (tray && !tray.open) {
          wrap.classList.remove('is-toolbar-menu-mode');
          details.open = true;
          return;
        }

        wrap.classList.remove('is-toolbar-menu-mode');
        details.open = true;
        const needsMenu = toolbar.scrollWidth > wrap.clientWidth + 4;
        wrap.classList.toggle('is-toolbar-menu-mode', needsMenu);

        if (needsMenu) {
          if (!this._toolbarMenuPinnedOpen) {
            details.open = false;
          }
        } else {
          this._toolbarMenuPinnedOpen = false;
          details.open = true;
        }
        this.#syncToolbarWallMeasureLayout();
      };

      if (!this._toolbarOverflowToggleBound) {
        this._toolbarOverflowToggleBound = true;
        details.addEventListener('toggle', () => {
          if (!wrap.classList.contains('is-toolbar-menu-mode')) return;
          this._toolbarMenuPinnedOpen = details.open;
          window.requestAnimationFrame(() => this.calculate({ layout: true }));
        });
      }

      this._toolbarOverflowSync = sync;
      this._toolbarOverflowRo = new ResizeObserver(() => {
        window.requestAnimationFrame(sync);
      });
      this._toolbarOverflowRo.observe(wrap);
      this._toolbarOverflowRo.observe(toolbar);
      sync();
    }

    #upgradeLegacyDesignToolsToolbar() {
      const wrap = this.root.querySelector('.ai-generator__design-toolbar-wrap');
      const toolbar = wrap?.querySelector('[data-ai-design-toolbar]');
      if (!toolbar) return;

      wrap?.classList.add('ai-generator__design-toolbar-wrap--wall-surface');
      toolbar.classList.add('ai-generator__design-toolbar--single-row');
      toolbar.classList.remove('ai-generator__design-toolbar--tools', 'ai-generator__design-toolbar--two-rows');
      toolbar.setAttribute('aria-label', 'Design and save options');

      const actionChipSelector =
        '[data-ai-share-open], [data-ai-save-btn], [data-ai-save-list], [data-ai-save-compare], [data-ai-scroll-cart]';

      const legacyActionsRow = wrap?.querySelector('.ai-generator__toolbar-actions-row');
      if (legacyActionsRow) {
        Array.from(legacyActionsRow.children).forEach((chip) => {
          toolbar.appendChild(chip);
        });
        legacyActionsRow.remove();
      }

      const nestedRows = Array.from(toolbar.querySelectorAll(':scope > .ai-generator__toolbar-row'));
      if (nestedRows.length) {
        nestedRows.forEach((row) => {
          Array.from(row.children).forEach((chip) => toolbar.appendChild(chip));
          row.remove();
        });
      }

      const looseChips = Array.from(toolbar.querySelectorAll(':scope > .ai-generator__toolbar-chip'));
      if (looseChips.length) {
        toolbar.querySelector('.ai-generator__toolbar-row-break')?.remove();
        return;
      }

      const allControls = Array.from(
        toolbar.querySelectorAll(
          ':scope > .ai-generator__overlay-toggle, :scope > .ai-generator__save-btn, :scope > .ai-generator__panels-control, :scope > .ai-generator__pro-teaser-control, :scope > .ai-generator__panels-power'
        )
      );
      if (!allControls.length) return;

      toolbar.querySelector('.ai-generator__toolbar-row-break')?.remove();
      toolbar.replaceChildren();
      allControls.forEach((control) => {
        const chip = document.createElement('div');
        chip.className = 'ai-generator__toolbar-chip';
        if (control.matches('[data-ai-upscale-pro]')) chip.classList.add('ai-generator__toolbar-chip--pro-upscale');
        if (control.matches('.ai-generator__pro-teaser-control--export')) {
          chip.classList.add('ai-generator__toolbar-chip--pro-export');
        }
        if (control.matches('[data-ai-panels-control], .ai-generator__panels-control')) {
          chip.classList.add('ai-generator__toolbar-chip--panels');
        }
        if (control.matches('[data-ai-align-grid-toggle]')) {
          chip.classList.add('ai-generator__toolbar-chip--align-grid');
        }
        chip.appendChild(control);
        toolbar.appendChild(chip);
      });

      this.#repairBrokenDesignToolsActionsRow(toolbar, actionChipSelector);
    }

    #repairBrokenDesignToolsActionsRow(toolbar, actionChipSelector) {
      Array.from(toolbar.querySelectorAll(':scope > .ai-generator__toolbar-row')).forEach((row) => {
        Array.from(row.children).forEach((chip) => toolbar.appendChild(chip));
        row.remove();
      });

      Array.from(toolbar.querySelectorAll(':scope > .ai-generator__toolbar-chip')).forEach((chip) => {
        if (!chip.querySelector(actionChipSelector)) return;
        toolbar.appendChild(chip);
      });

      const wrapBareControl = (control) => {
        if (!control || control.closest('.ai-generator__toolbar-chip')) return;
        const tip =
          control.nextElementSibling?.classList.contains('ai-generator__toolbar-tip')
            ? control.nextElementSibling
            : null;
        const chip = document.createElement('div');
        chip.className = 'ai-generator__toolbar-chip';
        toolbar.appendChild(chip);
        chip.appendChild(control);
        if (tip) chip.appendChild(tip);
      };

      wrapBareControl(toolbar.querySelector(':scope > [data-ai-share-open]'));

      Array.from(toolbar.querySelectorAll(':scope > [data-ai-save-design], :scope > [data-ai-open-design-library]')).forEach(
        (control) => wrapBareControl(control)
      );
    }

    #initDesignToolsTray() {
      const tray = this.designToolsTray;
      if (!tray) return;

      const toggleBtn = this.root.querySelector('[data-ai-design-tools-tray-toggle]');
      const wideMq = window.matchMedia('(min-width: 900px)');

      const syncToggleState = () => {
        if (toggleBtn) {
          toggleBtn.setAttribute('aria-expanded', String(tray.open));
          toggleBtn.classList.toggle('is-tray-open', tray.open);
        }
      };

      const sync = () => {
        tray.classList.toggle('is-tray-wide', wideMq.matches);
        tray.dataset.syncingTray = '1';
        if (wideMq.matches || !tray.dataset.userToggled) {
          tray.open = true;
        }
        delete tray.dataset.syncingTray;
        syncToggleState();
      };

      toggleBtn?.addEventListener('click', () => {
        tray.open = !tray.open;
        if (!wideMq.matches) tray.dataset.userToggled = '1';
        syncToggleState();
        window.requestAnimationFrame(() => this._toolbarOverflowSync?.());
      });

      tray.addEventListener('toggle', () => {
        if (tray.dataset.syncingTray) return;
        if (!wideMq.matches) tray.dataset.userToggled = '1';
        syncToggleState();
        window.requestAnimationFrame(() => this._toolbarOverflowSync?.());
      });

      if (typeof wideMq.addEventListener === 'function') {
        wideMq.addEventListener('change', sync);
      } else if (typeof wideMq.addListener === 'function') {
        wideMq.addListener(sync);
      }

      sync();
    }

    /** Custom dropdowns — native select popups can't match our rounded UI. */
    #initStyledSelects() {
      if (!this.root.dataset.aiStyledSelectsBound) {
        this.root.dataset.aiStyledSelectsBound = 'true';
        document.addEventListener('click', (event) => {
          if (event.target.closest('.ai-generator__styled-select')) return;
          if (event.target.closest('.ai-generator__styled-select-menu--portaled')) return;
          document.querySelectorAll('.ai-generator__styled-select.is-open').forEach((wrap) => {
            this.#closeStyledSelect(/** @type {HTMLElement} */ (wrap));
          });
        });
        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          document.querySelectorAll('.ai-generator__styled-select.is-open').forEach((wrap) => {
            this.#closeStyledSelect(/** @type {HTMLElement} */ (wrap));
          });
        });
      }

      this.root
        .querySelectorAll(
          'select.ai-generator__wall-chip-select, select.ai-generator__wall-panel-action-select, select[data-ai-wallpaper-layout], select.ai-generator__pro-teaser-select--panel, select.ai-generator__pro-teaser-select--export, select[data-ai-style-starter-select]'
        )
        .forEach((select) => {
          if (!(select instanceof HTMLSelectElement) || select.dataset.aiStyledSelect) return;
          try {
            this.#enhanceStyledSelect(select);
          } catch (error) {
            console.error('[ai-generator] styled select failed', select, error);
          }
        });
    }

    #enhanceStyledSelect(select) {
      select.dataset.aiStyledSelect = 'true';
      select.classList.add('ai-generator__styled-select-native');

      const presetWrap = select.closest('[data-ai-panels-width-picker]');
      /** @type {HTMLElement} */
      let wrap;
      /** @type {HTMLButtonElement | null} */
      let trigger;
      /** @type {HTMLElement} */
      let menu;
      /** @type {HTMLElement | null} */
      let triggerLabel = null;

      const isPanelSelect = select.classList.contains('ai-generator__pro-teaser-select--panel');
      const isProExportSelect = select.classList.contains('ai-generator__pro-teaser-select--export');
      const isStyleStarterSelect = select.matches('[data-ai-style-starter-select]');

      if (presetWrap instanceof HTMLElement) {
        wrap = presetWrap;
        trigger =
          presetWrap.querySelector('[data-ai-panels-width-trigger]') instanceof HTMLButtonElement
            ? presetWrap.querySelector('[data-ai-panels-width-trigger]')
            : null;
        menu =
          presetWrap.querySelector('[data-ai-panels-width-menu]') instanceof HTMLElement
            ? presetWrap.querySelector('[data-ai-panels-width-menu]')
            : null;
        triggerLabel = presetWrap.querySelector('[data-ai-panels-width-label]');
        if (!trigger || !menu) return;
      } else {
        wrap = document.createElement('div');
        wrap.className = 'ai-generator__styled-select';
        if (select.classList.contains('ai-generator__pro-teaser-select--panel')) {
          wrap.classList.add(
            'ai-generator__styled-select--toolbar',
            'ai-generator__styled-select--pro-dropdown',
            'ai-generator__styled-select--pro-panel'
          );
        }
        if (select.classList.contains('ai-generator__pro-teaser-select--export')) {
          wrap.classList.add(
            'ai-generator__styled-select--toolbar',
            'ai-generator__styled-select--pro-dropdown',
            'ai-generator__styled-select--pro-export'
          );
        }
        if (select.classList.contains('ai-generator__wall-panel-action-select')) {
          wrap.classList.add('ai-generator__styled-select--wall-action');
        }
        if (select.matches('[data-ai-wallpaper-layout]')) {
          wrap.classList.add('ai-generator__styled-select--wall-layout');
        }
        if (isStyleStarterSelect) {
          wrap.classList.add('ai-generator__styled-select--style-starter');
        }
        select.parentNode?.insertBefore(wrap, select);
        wrap.appendChild(select);

        trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'ai-generator__styled-select-trigger';
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');

        menu = document.createElement('ul');
        menu.className = 'ai-generator__styled-select-menu';
        if (isStyleStarterSelect) {
          wrap._styleStarterMenu = menu;
        }
        if (
          select.classList.contains('ai-generator__pro-teaser-select--export') ||
          select.classList.contains('ai-generator__pro-teaser-select--panel')
        ) {
          menu.classList.add('ai-generator__styled-select-menu--pro');
        }
        if (select.classList.contains('ai-generator__pro-teaser-select--export')) {
          menu.classList.add('ai-generator__pro-export-menu');
        }
        menu.hidden = true;
        menu.setAttribute('role', 'listbox');
      }

      const needsLabelSpan =
        !triggerLabel &&
        (isPanelSelect ||
          isProExportSelect ||
          isStyleStarterSelect ||
          select.classList.contains('ai-generator__wall-panel-action-select') ||
          select.matches('[data-ai-wallpaper-layout]'));

      if (needsLabelSpan) {
        triggerLabel = document.createElement('span');
        triggerLabel.className = 'ai-generator__styled-select-label';
        if (isPanelSelect || isStyleStarterSelect) {
          trigger.classList.add('ai-generator__styled-select-trigger--compact');
          if (isStyleStarterSelect) {
            trigger.classList.add('ai-generator__prompt-toolbar-btn');
          }
          const chevron = document.createElement('span');
          chevron.className = 'ai-generator__styled-select-chevron';
          chevron.setAttribute('aria-hidden', 'true');
          trigger.replaceChildren(triggerLabel, chevron);
        } else {
          trigger.replaceChildren(triggerLabel);
        }
      }

      const syncTrigger = () => {
        const opt = select.options[select.selectedIndex];
        const text = opt?.textContent?.trim() || 'Choose…';
        if (triggerLabel) {
          triggerLabel.textContent = text;
        } else {
          trigger.textContent = text;
        }
      };

      const rebuildMenu = () => {
        menu.replaceChildren();
        const appendOption = (opt) => {
          const li = document.createElement('li');
          li.className = 'ai-generator__styled-select-option';
          li.setAttribute('role', 'presentation');
          const optionBtn = document.createElement('button');
          optionBtn.type = 'button';
          optionBtn.className = 'ai-generator__styled-select-option-btn';
          optionBtn.setAttribute('role', 'option');
          optionBtn.dataset.value = opt.value;
          optionBtn.textContent = opt.textContent?.trim() || opt.value;
          if (opt.selected) optionBtn.setAttribute('aria-selected', 'true');
          if (opt.disabled) {
            li.classList.add('is-disabled');
            optionBtn.disabled = true;
            optionBtn.setAttribute('aria-disabled', 'true');
            optionBtn.title = 'Available in WallPrint Pro';
          } else {
            optionBtn.addEventListener('click', () => {
              select.value = opt.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              syncTrigger();
              rebuildMenu();
              this.#closeStyledSelect(wrap);
            });
          }
          li.appendChild(optionBtn);
          menu.appendChild(li);
        };

        Array.from(select.children).forEach((child) => {
          if (child instanceof HTMLOptGroupElement) {
            const group = document.createElement('li');
            group.className = 'ai-generator__styled-select-group';
            group.textContent = child.label;
            menu.appendChild(group);
            Array.from(child.children).forEach((opt) => {
              if (opt instanceof HTMLOptionElement) appendOption(opt);
            });
            return;
          }
          if (child instanceof HTMLOptionElement) appendOption(child);
        });
      };

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        if (select.disabled) return;
        if (wrap.classList.contains('is-open')) {
          this.#closeStyledSelect(wrap);
          return;
        }
        document.querySelectorAll('.ai-generator__styled-select.is-open').forEach((other) => {
          if (other !== wrap) this.#closeStyledSelect(/** @type {HTMLElement} */ (other));
        });
        wrap.classList.add('is-open');
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        if (wrap.classList.contains('ai-generator__styled-select--style-starter')) {
          requestAnimationFrame(() => {
            this.#positionStyleStarterMenu(wrap);
            this.#bindStyleStarterMenuPosition(wrap);
          });
        }
      });

      select.addEventListener('change', () => {
        syncTrigger();
        rebuildMenu();
      });

      rebuildMenu();
      syncTrigger();
      this.#syncStyledSelect(select);

      if (!presetWrap) {
        wrap.appendChild(trigger);
        wrap.appendChild(menu);
      }
    }

    #closeStyledSelect(wrap) {
      wrap.classList.remove('is-open');
      const menu =
        wrap._styleStarterMenu instanceof HTMLElement
          ? wrap._styleStarterMenu
          : wrap.querySelector('.ai-generator__styled-select-menu');
      const trigger = wrap.querySelector('.ai-generator__styled-select-trigger');
      if (menu instanceof HTMLElement) {
        menu.hidden = true;
        menu.classList.remove('ai-generator__styled-select-menu--portaled');
        menu.style.removeProperty('position');
        menu.style.removeProperty('top');
        menu.style.removeProperty('left');
        menu.style.removeProperty('right');
        menu.style.removeProperty('width');
        menu.style.removeProperty('min-width');
        menu.style.removeProperty('max-height');
        menu.style.removeProperty('z-index');
        menu.style.removeProperty('background-color');
        [
          '--ai-shell',
          '--ai-accent-bright',
          '--ai-accent-2',
          '--ai-accent-line',
          '--ai-text-body',
        ].forEach((name) => menu.style.removeProperty(name));
        if (menu.dataset.styleStarterPortaled === '1' && wrap instanceof HTMLElement) {
          wrap.appendChild(menu);
          delete menu.dataset.styleStarterPortaled;
        }
      }
      trigger?.setAttribute('aria-expanded', 'false');
      this.#clearStyleStarterMenuPosition(wrap);
    }

    #bindStyleStarterMenuPosition(wrap) {
      if (!(wrap instanceof HTMLElement)) return;
      if (!wrap.classList.contains('ai-generator__styled-select--style-starter')) return;
      if (wrap._styleStarterMenuReposition) return;
      const reposition = () => this.#positionStyleStarterMenu(wrap);
      wrap._styleStarterMenuReposition = reposition;
      window.addEventListener('scroll', reposition, true);
      window.addEventListener('resize', reposition);
    }

    #clearStyleStarterMenuPosition(wrap) {
      if (!(wrap instanceof HTMLElement)) return;
      const reposition = wrap._styleStarterMenuReposition;
      if (!reposition) return;
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      wrap._styleStarterMenuReposition = null;
    }

    /** Style starter menu — portal to body so fixed coords work inside backdrop-filter sidebars. */
    #positionStyleStarterMenu(wrap) {
      if (!(wrap instanceof HTMLElement)) return;
      if (!wrap.classList.contains('ai-generator__styled-select--style-starter')) return;
      const menu =
        wrap._styleStarterMenu instanceof HTMLElement
          ? wrap._styleStarterMenu
          : wrap.querySelector('.ai-generator__styled-select-menu');
      const trigger = wrap.querySelector('.ai-generator__styled-select-trigger');
      if (!(menu instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

      if (menu.parentElement !== document.body) {
        document.body.appendChild(menu);
        menu.dataset.styleStarterPortaled = '1';
      }
      menu.classList.add('ai-generator__styled-select-menu--portaled');

      const generatorRoot = wrap.closest('.ai-generator');
      const wideView =
        generatorRoot?.classList.contains('is-wide-view') ||
        generatorRoot?.classList.contains('is-preview-expanded');
      if (generatorRoot instanceof HTMLElement) {
        const theme = getComputedStyle(generatorRoot);
        [
          '--ai-shell',
          '--ai-accent-bright',
          '--ai-accent-2',
          '--ai-accent-line',
          '--ai-text-body',
        ].forEach((name) => {
          const value = theme.getPropertyValue(name).trim();
          if (value) menu.style.setProperty(name, value);
        });
      }
      menu.style.backgroundColor = 'rgb(44 48 54)';

      const rect = trigger.getBoundingClientRect();
      const gap = 6;
      const minW = Math.max(Math.round(rect.width), 220);
      let left = Math.round(rect.left);
      if (left + minW > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - minW - 8);
      }

      menu.style.position = 'fixed';
      menu.style.left = `${left}px`;
      menu.style.width = `${minW}px`;
      menu.style.minWidth = `${minW}px`;
      menu.style.right = 'auto';
      menu.style.zIndex = '120';

      /* Wide view — toolbar sits directly above the prompt row; open upward. */
      if (wideView) {
        const spaceAbove = Math.max(120, Math.round(rect.top - gap - 12));
        menu.style.top = 'auto';
        menu.style.bottom = `${Math.round(window.innerHeight - rect.top + gap)}px`;
        menu.style.maxHeight = `${spaceAbove}px`;
        return;
      }

      const spaceBelow = Math.max(120, window.innerHeight - rect.bottom - gap - 12);
      const spaceAbove = Math.max(120, rect.top - gap - 12);
      const menuEstimate = Math.min(menu.scrollHeight || 240, 280);
      const openUp = spaceBelow < menuEstimate && spaceAbove > spaceBelow;

      menu.style.bottom = 'auto';
      if (openUp) {
        menu.style.top = `${Math.max(8, Math.round(rect.top - gap - Math.min(menuEstimate, spaceAbove)))}px`;
        menu.style.maxHeight = `${spaceAbove}px`;
      } else {
        menu.style.top = `${Math.round(rect.bottom + gap)}px`;
        menu.style.maxHeight = `${spaceBelow}px`;
      }
    }

    #syncStyledSelect(select) {
      const wrap = select.closest('.ai-generator__styled-select, [data-ai-panels-width-picker]');
      if (!(wrap instanceof HTMLElement)) return;
      wrap.classList.toggle('is-disabled', select.disabled);
      const trigger = wrap.querySelector('.ai-generator__styled-select-trigger');
      if (trigger instanceof HTMLButtonElement) trigger.disabled = select.disabled;
      const label = wrap.querySelector('.ai-generator__styled-select-label');
      if (label instanceof HTMLElement && select instanceof HTMLSelectElement) {
        const opt = select.options[select.selectedIndex];
        label.textContent = opt?.textContent?.trim() || '';
      }
    }

    #syncRefineUi() {
      const isGenerated = this.currentAsset?.source === 'generated';
      const busy = this.isGenerating || this.isUploading || this.isTiling || this.isUpscaling;
      if (this.refineBlock) this.refineBlock.hidden = !isGenerated;
      if (this.refineLoginEl) this.refineLoginEl.hidden = this.customerLoggedIn;
      if (this.refinePromptInput) {
        this.refinePromptInput.hidden = !this.customerLoggedIn;
        this.refinePromptInput.disabled = !this.customerLoggedIn;
      }
      if (this.refineBtn) {
        this.refineBtn.hidden = !this.customerLoggedIn;
        this.refineBtn.disabled = !this.customerLoggedIn || !isGenerated || busy;
      }
    }

    #setTilingState(loading) {
      this.isTiling = loading;
      this.root.classList.toggle('is-tiling', loading);
      this.root.classList.toggle('is-loading', loading || this.isGenerating);
      this.preview?.classList.toggle('is-preview-busy', loading || this.isGenerating);
      this.preview?.classList.toggle('is-preview-tiling', loading);
      if (this.skeleton) this.skeleton.hidden = !loading;
      if (this.skeletonText && loading) {
        this.skeletonText.textContent = this.previewTilingText;
      }
      if (loading && this.placeholder) this.placeholder.hidden = true;
      if (!loading && !this.preview?.classList.contains('has-content') && this.placeholder) {
        this.placeholder.hidden = false;
      }
      if (loading) this.#schedulePreviewLayout(true);
      this.#syncRefineUi();
      this.#syncPreviewActions();
    }

    /** Account holders only — runs a follow-up prompt against the current AI design. */
    async refine() {
      if (!this.customerLoggedIn) {
        if (this.refineLoginEl) this.refineLoginEl.hidden = false;
        return;
      }
      if (!this.currentAsset || this.currentAsset.source !== 'generated') {
        this.#showError('Generate a design first, then you can refine it.');
        return;
      }

      const refineText = (this.refinePromptInput?.value || '').trim();
      if (refineText.length < 3) {
        this.#showError('Describe what you want to change in at least a few words.');
        this.refinePromptInput?.focus();
        return;
      }

      const base = (this.currentAsset.prompt || this.promptInput?.value || '').trim();
      const combined = `${base} Refinement: ${refineText}`;
      if (this.promptInput) this.promptInput.value = combined;
      await this.generate();
    }

    #setUpscalingState(loading) {
      this.isUpscaling = loading;
      this.root.classList.toggle('is-upscaling', loading);
      this.root.classList.toggle('is-loading', loading || this.isGenerating || this.isTiling);
      this.preview?.classList.toggle('is-preview-busy', loading || this.isGenerating || this.isTiling);
      this.preview?.classList.toggle('is-preview-upscaling', loading);
      if (this.skeleton) this.skeleton.hidden = !loading;
      if (this.skeletonText && loading) {
        this.skeletonText.textContent = this.previewUpscalingText;
      }
      if (loading && this.placeholder) this.placeholder.hidden = true;
      if (!loading && !this.preview?.classList.contains('has-content') && this.placeholder) {
        this.placeholder.hidden = false;
      }
      if (loading) this.#schedulePreviewLayout(true);
      this.#syncRefineUi();
      this.#syncPreviewActions();
    }

    /** Upscale the current design toward print resolution (300 DPI target, capped). */
    async upscaleDesign() {
      if (this.isUpscaling || this.isGenerating || this.isMockup || this.isTiling) return;

      if (!this.customerLoggedIn && !isStudioGuestMode(this)) {
        this.#showError('Log in to upscale your design for print.');
        return;
      }

      const asset = this.currentAsset;
      const state = this.calculate({ sync: true });
      if (!asset || !this.#isServerBackedAsset(asset)) {
        this.#showError(
          asset
            ? 'Upscale needs a server-saved design. Upload or generate again while WallPrint AI is connected.'
            : 'Add a design and wall size first, then upscale for print.'
        );
        return;
      }
      if (!(state.printWidthCm > 0 && state.printHeightCm > 0)) {
        this.#showError('Enter your wall width and height before upscaling for print.');
        return;
      }

      this.#clearError();
      this.#setUpscalingState(true);
      this.#persistCropToAsset();

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 300000);

      try {
        const upscaleUrl = this.#customerGatedEndpointUrl(this.upscaleEndpoint);
        const response = await fetch(upscaleUrl, {
          method: 'POST',
          headers: this.#apiHeaders({ json: true, url: upscaleUrl }),
          signal: controller.signal,
          body: JSON.stringify({
            ai_image_id: asset.aiImageId,
            prompt: asset.prompt || this.promptInput?.value || '',
            print_width_cm: state.printWidthCm,
            print_height_cm: state.printHeightCm,
            watermark_logo_url: this.watermarkLogoUrl || undefined,
            metadata: {
              ...asset.metadata,
              print_width_cm: state.printWidthCm,
              print_height_cm: state.printHeightCm,
              ...(this.cropViewport && !this.cropViewport.hidden ? this.#getUploadCropMetadata() : {}),
              ...this.#getAdjustmentMetadata(),
            },
          }),
        });

        if (!response.ok) {
          let errBody = {};
          try {
            errBody = await response.json();
          } catch {
            const text = await response.text().catch(() => '');
            if (text) {
              try {
                errBody = JSON.parse(text);
              } catch {
                errBody = { message: text.slice(0, 240) };
              }
            }
          }
          if (response.status === 401 && errBody.error === 'login_required') {
            throw new Error(errBody.message || 'Log in to upscale your design for print.');
          }
          const errText = typeof errBody.message === 'string' ? errBody.message : '';
          if (this.#apiUnreachableBody(errText)) {
            throw new Error(
              'Could not reach the WallPrint AI server. Check the API is running and FAL_KEY is set for upscaling.'
            );
          }
          throw new Error(errBody.message || errBody.error || `Upscale failed (${response.status})`);
        }

        const data = await response.json();
        if (!data?.watermarked_url) throw new Error('Malformed response from upscale service.');

        const previewUrl = this.#resolveApiAssetUrl(data.watermarked_url);
        const dims = { w: data.width || 0, h: data.height || 0 };

        this.currentAsset = {
          ...asset,
          upscaled: true,
          watermarked: true,
          metadata: { ...asset.metadata, ...(data.metadata || {}), upscaled: true },
        };
        if (dims.w && dims.h) {
          if (asset.source === 'uploaded') {
            this.currentAsset.uploadWidth = dims.w;
            this.currentAsset.uploadHeight = dims.h;
          } else {
            this.currentAsset.genWidth = dims.w;
            this.currentAsset.genHeight = dims.h;
          }
        }

        await this.#renderGeneratedPreview(previewUrl, previewUrl);
        this.#setNoteForSource(asset.source || 'generated');
        this.#setCheckoutVisible(true);
        this.calculate({ sync: true });
        this.#syncPreviewActions();
      } catch (error) {
        console.error('[ai-generator] upscale', error);
        const message =
          error?.name === 'AbortError'
            ? 'Upscale timed out. Please try again.'
            : error?.message || 'Something went wrong while upscaling your design.';
        this.#showError(message);
      } finally {
        window.clearTimeout(timeoutId);
        this.#setUpscalingState(false);
        if (!this.isGenerating && !this.isTiling && !this.isUpscaling) {
          this.preview?.classList.remove('is-preview-busy');
          if (this.skeleton) this.skeleton.hidden = true;
          if (!this.preview?.classList.contains('has-content') && this.placeholder) {
            this.placeholder.hidden = false;
          }
        }
        this.root.classList.toggle(
          'is-loading',
          this.isGenerating || this.isTiling || this.isUpscaling || this.isMockup
        );
      }
    }

    /** Account holders only — adapts the current design into a seamless repeat. */
    async tilePattern() {
      if (this.isTiling || this.isUpscaling || this.isGenerating || this.isMockup) return;

      if (!this.customerLoggedIn) {
        if (this.tileLoginEl) this.tileLoginEl.hidden = false;
        this.#showError('Log in to adapt your design for wallpaper repeat.');
        return;
      }

      const asset = this.currentAsset;
      if (!asset || !this.#isServerBackedAsset(asset)) {
        this.#showError(
          asset
            ? 'Make seamless needs a server-saved design. Upload or generate again while WallPrint AI is connected.'
            : 'Add a design first, then you can adapt it for wallpaper repeat.'
        );
        return;
      }

      this.#clearError();
      this.#setTilingState(true);
      this.#persistCropToAsset();

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 180000);

      try {
        const tileUrl = this.#customerGatedEndpointUrl(this.tilePatternEndpoint);
        const response = await fetch(tileUrl, {
          method: 'POST',
          headers: this.#apiHeaders({ json: true, url: tileUrl }),
          signal: controller.signal,
          body: JSON.stringify({
            ai_image_id: asset.aiImageId,
            prompt: asset.prompt || this.promptInput?.value || '',
            watermark_logo_url: this.watermarkLogoUrl || undefined,
            wallpaper_layout: this.#getWallpaperLayout(),
            pattern_repeat_cm: this.#getPatternRepeatCm(),
            metadata: {
              ...asset.metadata,
              ...(this.cropViewport && !this.cropViewport.hidden ? this.#getUploadCropMetadata() : {}),
              ...this.#getAdjustmentMetadata(),
            },
          }),
        });

        if (!response.ok) {
          let errBody = {};
          try {
            errBody = await response.json();
          } catch {
            const text = await response.text().catch(() => '');
            if (text) {
              try {
                errBody = JSON.parse(text);
              } catch {
                errBody = { message: text.slice(0, 240) };
              }
            }
          }
          if (response.status === 401 && errBody.error === 'login_required') {
            if (this.tileLoginEl) this.tileLoginEl.hidden = false;
            throw new Error(errBody.message || 'Log in to adapt your design for print.');
          }
          if (response.status === 401 && errBody.error !== 'login_required') {
            throw new Error(
              errBody.message ||
                'Authentication failed. Log in and try again, or ask the store admin to set the Studio Storefront API key.'
            );
          }
          if (response.status === 429) {
            throw new Error(
              errBody.message || 'You have reached your repeat limit. Try again later or contact us.'
            );
          }
          const errText = typeof errBody.message === 'string' ? errBody.message : '';
          if (this.#apiUnreachableBody(errText)) {
            throw new Error(
              'Could not reach the WallPrint AI server. Check the API is running and FAL_KEY is set for tiling.'
            );
          }
          throw new Error(errBody.message || errBody.error || `Tile pattern failed (${response.status})`);
        }

        const data = await response.json();
        if (!data?.watermarked_url) throw new Error('Malformed response from tile service.');

        const previewUrl = this.#resolveApiAssetUrl(data.watermarked_url);
        const originalUrl = data.original_url
          ? this.#resolveApiAssetUrl(data.original_url)
          : previewUrl;

        this.currentAsset = {
          ...asset,
          tiled: true,
          watermarked: true,
          originalUrl,
          metadata: { ...asset.metadata, ...(data.metadata || {}), tiled: true },
        };

        await this.#renderGeneratedPreview(previewUrl, originalUrl);
        this.#setNoteForSource(asset.source || 'generated');
        this.#setCheckoutVisible(true);
        this.calculate({ sync: true });
        this.#syncPreviewActions();
      } catch (error) {
        console.error('[ai-generator] tile', error);
        const message =
          error?.name === 'AbortError'
            ? 'Tiling timed out. Please try again.'
            : error?.message || 'Something went wrong while making the seamless pattern.';
        this.#showError(message);
      } finally {
        window.clearTimeout(timeoutId);
        this.#setTilingState(false);
        if (!this.isGenerating && !this.isTiling && !this.isUpscaling) {
          this.preview?.classList.remove('is-preview-busy');
          if (this.skeleton) this.skeleton.hidden = true;
          if (!this.preview?.classList.contains('has-content') && this.placeholder) {
            this.placeholder.hidden = false;
          }
        }
        this.root.classList.toggle('is-loading', this.isGenerating || this.isTiling || this.isUpscaling || this.isMockup);
      }
    }

    #openRoomDialog() {
      if (!this.customerLoggedIn && !isStudioGuestMode(this)) {
        if (this.tileLoginEl) this.tileLoginEl.hidden = false;
        this.#showError('Log in to preview your design on a room photo.');
        return;
      }
      if (!this.currentAsset) {
        this.#showError('Add a design before creating a room preview.');
        return;
      }
      if (!this.#isServerBackedAsset()) {
        this.#showError(
          'Your design is preview-only in the browser. Upload or generate again while the WallPrint AI server is connected, then try wall mockup.'
        );
        return;
      }
      this.#syncRoomDialogUi();
      this.#ensureStockRoomsLoaded();
      this.roomDialog?.showModal();
    }

    #stockRoomsListUrl() {
      const explicit = String(this.stockRoomsEndpoint || '').trim();
      if (explicit) return explicit;
      const endpoint = String(this.roomMockupEndpoint || '').trim();
      if (!endpoint) return '';
      return endpoint.replace(/\/room-mockup(\?.*)?$/i, '/stock-rooms');
    }

    #stockRoomsPreviewUrl() {
      const explicit = String(this.stockRoomsPreviewEndpoint || '').trim();
      if (explicit) return this.#customerGatedEndpointUrl(explicit);
      const endpoint = String(this.roomMockupEndpoint || '').trim();
      if (!endpoint) return '';
      return this.#customerGatedEndpointUrl(
        endpoint.replace(/\/room-mockup(\?.*)?$/i, '/stock-rooms/preview')
      );
    }

    async #ensureStockRoomsLoaded() {
      if (this._stockRoomsLoaded || this._stockRoomsLoading || !this.roomStockList) return;
      const url = this.#stockRoomsListUrl();
      if (!url) return;
      this._stockRoomsLoading = true;
      if (this.roomStockWrap) this.roomStockWrap.hidden = false;
      if (this.roomStockNote) {
        this.roomStockNote.hidden = false;
        this.roomStockNote.textContent = 'Loading free rooms…';
      }
      try {
        const response = await fetch(url, { headers: this.#apiHeaders({ url }) });
        if (!response.ok) {
          if (this.roomStockNote) {
            this.roomStockNote.textContent = 'Could not load stock rooms — try again or upload your own photo.';
          }
          return;
        }
        const data = await response.json();
        const rooms = Array.isArray(data?.rooms) ? data.rooms : [];
        if (!rooms.length) {
          if (this.roomStockNote) {
            this.roomStockNote.textContent = 'No stock rooms available yet — upload your own room photo below.';
          }
          return;
        }
        this.roomStockList.replaceChildren();
        for (const room of rooms) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ai-generator__room-stock-item';
          btn.dataset.aiRoomStockId = room.id;
          btn.setAttribute('role', 'listitem');
          btn.title = room.description || room.name || 'Stock room';
          const img = document.createElement('img');
          img.alt = room.name || 'Stock room';
          img.loading = 'lazy';
          img.src = this.#resolveApiAssetUrl(room.thumb_url || room.image_url || '');
          const label = document.createElement('span');
          label.textContent = room.name || room.id;
          btn.append(img, label);
          btn.addEventListener('click', () => this.#selectStockRoom(room));
          this.roomStockList.append(btn);
        }
        if (this.roomStockNote) {
          this.roomStockNote.textContent =
            'Tap a room for a free composite preview — no credits, no weekly limit.';
        }
        this._stockRoomsLoaded = true;
      } catch (err) {
        console.warn('[ai-generator] stock rooms', err);
        if (this.roomStockNote) {
          this.roomStockNote.textContent = 'Could not load stock rooms — upload your own photo instead.';
        }
      } finally {
        this._stockRoomsLoading = false;
      }
    }

    #clearStockRoomSelection() {
      this._stockRoomSelection = null;
      this.roomEditorWrap?.classList.remove('is-stock-room');
      this.roomStockList?.querySelectorAll('.ai-generator__room-stock-item.is-selected').forEach((el) => {
        el.classList.remove('is-selected');
      });
    }

    async #selectStockRoom(room) {
      if (!room?.id) return;
      this._stockRoomSelection = room;
      this._roomPhotoFile = null;
      if (this.roomFileInput) this.roomFileInput.value = '';
      this._roomPhotoCompressed = null;
      this._roomLayoutSessionId = '';
      this.roomStockList?.querySelectorAll('.ai-generator__room-stock-item').forEach((el) => {
        el.classList.toggle('is-selected', el.dataset.aiRoomStockId === room.id);
      });
      if (this.roomFileNameEl) {
        this.roomFileNameEl.textContent = room.name ? `Stock: ${room.name}` : 'Stock room selected';
      }
      this._roomCorners = Array.isArray(room.corners) && room.corners.length === 4
        ? room.corners.map((p) => ({ x: Number(p.x), y: Number(p.y) }))
        : [
            { x: 0.15, y: 0.15 },
            { x: 0.85, y: 0.15 },
            { x: 0.85, y: 0.85 },
            { x: 0.15, y: 0.85 },
          ];
      this._roomExclusions = [];
      if (this.roomCornerToggle) this.roomCornerToggle.checked = true;
      const imageUrl = this.#resolveApiAssetUrl(room.image_url || '');
      if (this.roomCornerImg && imageUrl) {
        if (this._roomCornerImgUrl?.startsWith('blob:')) URL.revokeObjectURL(this._roomCornerImgUrl);
        this._roomCornerImgUrl = '';
        this._roomCornerFile = null;
        this.roomCornerImg.src = imageUrl;
      }
      if (this.roomEditorWrap) {
        this.roomEditorWrap.hidden = false;
        this.roomEditorWrap.classList.add('is-stock-room');
      }
      if (this.roomEditorHint) {
        this.roomEditorHint.textContent =
          room.description ||
          'Wall corners are preset for this room — run the free preview below.';
      }
      this.#renderRoomEditor();
      this.#syncRoomDialogUi();
      this.#syncRoomUploadPreview();
    }

    #canRunRoomMockup() {
      return Boolean(this.#getRoomPhotoFile() || this._stockRoomSelection?.id);
    }

    #getRoomPhotoFile() {
      return this._roomPhotoFile || this.roomFileInput?.files?.[0] || null;
    }

    #syncRoomDialogUi() {
      const file = this.#getRoomPhotoFile();
      const stock = this._stockRoomSelection;
      const hasSource = Boolean(file || stock?.id);
      if (this.roomFileNameEl && !stock?.id) {
        this.roomFileNameEl.textContent = file ? file.name : 'No photo chosen';
      }
      if (this.roomRunBtn) {
        this.roomRunBtn.disabled = this.isMockup;
        this.roomRunBtn.setAttribute('aria-disabled', String(!hasSource || this.isMockup));
        this.roomRunBtn.classList.toggle('is-action-locked', !hasSource && !this.isMockup);
        this.roomRunBtn.classList.toggle('is-loading', this.isMockup);
      }
      if (this.roomRunSpinner) this.roomRunSpinner.hidden = !this.isMockup;
      if (this.roomRunLabel) {
        this.roomRunLabel.textContent = stock?.id ? 'Create free preview' : 'Create wall mockup';
      }
      if (this.roomFastPreviewWrap) {
        this.roomFastPreviewWrap.hidden = Boolean(stock?.id) || !file;
      }
      if (this.isMockup) {
        if (this.roomEditorWrap) this.roomEditorWrap.hidden = true;
      } else {
        this.#stopMockupTheater();
        this.#syncCornerPinUi();
      }
      this.#syncWallPhotoUi();
    }

    #ensureRoomEditorLayoutObserver() {
      if (this._roomEditorLayoutObserver || !this.roomCornerStage) return;
      const relayout = () => {
        if (this._roomCorners?.length && this.#getRoomPhotoFile()) this.#renderRoomEditor();
      };
      this._roomEditorLayoutObserver = new ResizeObserver(relayout);
      this._roomEditorLayoutObserver.observe(this.roomCornerStage);
      if (this.roomCornerImg) {
        this._roomEditorLayoutObserver.observe(this.roomCornerImg);
        this.roomCornerImg.addEventListener('load', relayout);
      }
    }

    #syncCornerPinUi() {
      const wrap = this.roomEditorWrap || this.roomCornerWrap;
      if (!wrap) return;
      const file = this.#getRoomPhotoFile();
      const stock = this._stockRoomSelection;
      const hasSource = Boolean(file || stock?.id);
      wrap.hidden = !hasSource;
      if (!hasSource) {
        if (this._roomCornerImgUrl) {
          URL.revokeObjectURL(this._roomCornerImgUrl);
          this._roomCornerImgUrl = '';
        }
        this._roomExclusions = [];
        this._roomLayoutSessionId = '';
        this._roomCornersUserEdited = false;
        return;
      }

      if (stock?.id) {
        wrap.classList.remove('is-disabled');
        if (this.roomCornerToggle) this.roomCornerToggle.checked = true;
        this.#renderRoomEditor();
        return;
      }

      const isNewFile = this.roomCornerImg && this._roomCornerFile !== file;
      if (isNewFile) {
        this._roomCornerFile = file;
        if (this._roomCornerImgUrl) URL.revokeObjectURL(this._roomCornerImgUrl);
        this._roomCornerImgUrl = URL.createObjectURL(file);
        this.roomCornerImg.src = this._roomCornerImgUrl;
        this._roomCorners = [
          { x: 0.15, y: 0.15 },
          { x: 0.85, y: 0.15 },
          { x: 0.85, y: 0.85 },
          { x: 0.15, y: 0.85 },
        ];
        this._roomExclusions = [];
        if (this.roomCornerToggle) this.roomCornerToggle.checked = true;
        this._roomCornersUserEdited = false;
        this.#fetchRoomLayoutSuggest();
      }

      if (this.roomCornerToggle) this.roomCornerToggle.checked = true;
      wrap.classList.remove('is-disabled');
      this.#renderRoomEditor();
    }

    #roomLayoutSuggestUrl() {
      const endpoint = String(this.roomMockupEndpoint || '').trim();
      if (!endpoint) return '';
      const url = endpoint.replace(/\/room-mockup(\?.*)?$/i, '/room-layout-suggest');
      return this.#customerGatedEndpointUrl(url);
    }

    #roomLayoutPrefetchUrl() {
      const endpoint = String(this.roomMockupEndpoint || '').trim();
      if (!endpoint) return '';
      const url = endpoint.replace(/\/room-mockup(\?.*)?$/i, '/room-layout-prefetch');
      return this.#customerGatedEndpointUrl(url);
    }

    #setRoomSuggestLoading(loading, message = 'Detecting wall…') {
      this._roomSuggestLoading = loading;
      // Keep pins draggable — never show the blocking overlay during auto-detect.
      if (this.roomEditorLoading) this.roomEditorLoading.hidden = true;
      if (this.roomEditorLoadingText && loading) this.roomEditorLoadingText.textContent = message;
      if (this.roomCornerStage) {
        this.roomCornerStage.classList.remove('is-detecting');
        this.roomCornerStage.classList.toggle('is-suggest-pending', loading);
      }
    }

    async #compressRoomPhoto(file, maxSide = 960) {
      if (!file?.type?.startsWith('image/')) return file;
      try {
        const bmp = await createImageBitmap(file);
        const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
        if (scale >= 0.99) {
          bmp.close?.();
          return file;
        }
        const w = Math.max(1, Math.round(bmp.width * scale));
        const h = Math.max(1, Math.round(bmp.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d')?.drawImage(bmp, 0, 0, w, h);
        bmp.close?.();
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
        if (!blob) return file;
        const base = String(file.name || 'room.jpg').replace(/\.[^.]+$/, '') || 'room';
        return new File([blob], `${base}-mockup.jpg`, { type: 'image/jpeg' });
      } catch {
        return file;
      }
    }

    async #compressRoomPhotoForSuggest(file) {
      return this.#compressRoomPhoto(file, 960);
    }

    #prefetchRoomMockupCache(compressedFile, seq) {
      const url = this.#roomLayoutPrefetchUrl();
      if (!url || !compressedFile || (!this.customerLoggedIn && !isStudioGuestMode(this))) return;
      this._roomPrefetchSeq = seq;
      this._roomPrefetchForegroundRatio = null;
      const formData = new FormData();
      formData.append('room', compressedFile);
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 120000);
      fetch(url, {
        method: 'POST',
        headers: this.#apiHeaders({ url }),
        body: formData,
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok || seq !== this._roomPrefetchSeq) return null;
          return response.json();
        })
        .then((data) => {
          if (!data || seq !== this._roomPrefetchSeq) return;
          const ratio = Number(data.foreground_ratio);
          if (Number.isFinite(ratio)) this._roomPrefetchForegroundRatio = ratio;
          if (data.suggest_quick_preview && this.roomFastPreviewInput && !this.roomFastPreviewInput.checked) {
            this.roomFastPreviewInput.checked = true;
          }
          if (data.suggest_quick_preview && this.roomEditorHint) {
            this.roomEditorHint.textContent =
              'Busy room detected — Quick preview is on for a faster result. Use Enhance lighting after preview for AI relighting.';
          }
        })
        .catch(() => {})
        .finally(() => window.clearTimeout(timeoutId));
    }

    #syncRoomUploadPreview() {
      const file = this.#getRoomPhotoFile();
      if (this._roomUploadPreviewUrl) {
        URL.revokeObjectURL(this._roomUploadPreviewUrl);
        this._roomUploadPreviewUrl = '';
      }
      if (!file || !this.roomUploadPreview || !this.roomUploadPreviewImg) return;
      this._roomUploadPreviewUrl = URL.createObjectURL(file);
      this.roomUploadPreviewImg.src = this._roomUploadPreviewUrl;
      this.roomUploadPreview.hidden = false;
      this.roomUploadPreview.setAttribute('aria-hidden', 'false');
    }

    #syncMainMockupThumb(url) {
      if (!this.roomMockupThumbWrap || !this.roomMockupThumb) return;
      if (!url) {
        this.roomMockupThumbWrap.hidden = true;
        this.roomMockupThumb.removeAttribute('src');
        return;
      }
      this.roomMockupThumb.src = url;
      this.roomMockupThumbWrap.hidden = false;
    }

    async #fetchRoomLayoutSuggest() {
      const file = this.#getRoomPhotoFile();
      const url = this.#roomLayoutSuggestUrl();
      if (!file || !url) {
        this.#renderRoomEditor();
        return;
      }
      this._roomSuggestSeq = (this._roomSuggestSeq || 0) + 1;
      const seq = this._roomSuggestSeq;
      this.#renderRoomEditor();
      if (this.roomEditorHint) {
        this.roomEditorHint.textContent =
          'Drag the teal pins now — we are detecting the wall and pre-warming the mockup (usually 15–40 seconds).';
      }
      this.#setRoomSuggestLoading(true);
      this.#syncRoomUploadPreview();
      const compressed = await this.#compressRoomPhotoForSuggest(file);
      this._roomPhotoCompressed = compressed;
      const formData = new FormData();
      formData.append('room', compressed);
      this.#prefetchRoomMockupCache(compressed, seq);
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 60000);
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: this.#apiHeaders({ url }),
          body: formData,
          signal: controller.signal,
        });
        if (seq !== this._roomSuggestSeq) return;
        if (!response.ok) throw new Error('suggest failed');
        const data = await response.json();
        if (seq !== this._roomSuggestSeq) return;
        const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0));
        if (Array.isArray(data.corners) && data.corners.length === 4 && !this._roomCornersUserEdited) {
          this._roomCorners = data.corners.map((p) => ({ x: clamp01(p.x), y: clamp01(p.y) }));
          this.#fitRoomCornersToPrintAspect({ force: true });
        }
        if (Array.isArray(data.exclusions)) {
          this._roomExclusions = data.exclusions
            .map((ex) => this.#normalizeExclusionShape(ex))
            .filter(Boolean)
            .slice(0, 4);
        }
        this._roomLayoutSessionId = String(data.layout_session_id || '').trim();
        if (this.roomEditorHint) {
          this.roomEditorHint.textContent =
            Number(data.auto_fixtures) > 0
              ? 'Wall detected — drag pins if needed. We will skip outlets and switches automatically.'
              : 'Wall detected — drag the teal pins if needed, then create your mockup.';
        }
      } catch (err) {
        if (seq !== this._roomSuggestSeq) return;
        if (this.roomEditorHint) {
          this.roomEditorHint.textContent =
            err?.name === 'AbortError'
              ? 'Detection timed out — drag the teal pins to your wall corners, then create your mockup.'
              : 'Auto-detect unavailable — drag the teal pins to your wall corners.';
        }
      } finally {
        if (seq !== this._roomSuggestSeq) return;
        window.clearTimeout(timeoutId);
        this.#setRoomSuggestLoading(false);
        this.#renderRoomEditor();
      }
    }

    #normalizeExclusionRect(raw) {
      if (!raw || typeof raw !== 'object') return null;
      const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0));
      const x0 = clamp01(raw.x0 ?? raw.x ?? raw.left);
      const y0 = clamp01(raw.y0 ?? raw.y ?? raw.top);
      const x1 = clamp01(raw.x1 ?? (raw.x != null && raw.w != null ? raw.x + raw.w : raw.right));
      const y1 = clamp01(raw.y1 ?? (raw.y != null && raw.h != null ? raw.y + raw.h : raw.bottom));
      const left = Math.min(x0, x1);
      const right = Math.max(x0, x1);
      const top = Math.min(y0, y1);
      const bottom = Math.max(y0, y1);
      if (right - left < 0.008 || bottom - top < 0.008) return null;
      return { x0: left, y0: top, x1: right, y1: bottom };
    }

    #exclusionQuadArea(corners) {
      let area = 0;
      for (let i = 0; i < 4; i++) {
        const a = corners[i];
        const b = corners[(i + 1) % 4];
        area += a.x * b.y - b.x * a.y;
      }
      return Math.abs(area) * 0.5;
    }

    #normalizeExclusionShape(raw) {
      if (!raw || typeof raw !== 'object') return null;
      const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0));
      if (Array.isArray(raw.corners) && raw.corners.length === 4) {
        const corners = raw.corners.map((p) => ({
          x: clamp01(p.x),
          y: clamp01(p.y),
        }));
        if (corners.some((p) => !Number.isFinite(p.x + p.y))) return null;
        if (this.#exclusionQuadArea(corners) < 0.00004) return null;
        return { corners };
      }
      const rect = this.#normalizeExclusionRect(raw);
      if (!rect) return null;
      return {
        corners: [
          { x: rect.x0, y: rect.y0 },
          { x: rect.x1, y: rect.y0 },
          { x: rect.x1, y: rect.y1 },
          { x: rect.x0, y: rect.y1 },
        ],
      };
    }
    #addRoomExclusion(preset = null) {
      if (this._roomExclusions.length >= 4) {
        if (this.roomStatusEl) {
          this.roomStatusEl.hidden = false;
          this.roomStatusEl.textContent = 'Up to 4 door or window areas per photo.';
        }
        return;
      }
      const ex =
        preset ||
        (this._roomExclusions.length === 0
          ? {
              corners: [
                { x: 0.06, y: 0.18 },
                { x: 0.34, y: 0.18 },
                { x: 0.34, y: 0.94 },
                { x: 0.06, y: 0.94 },
              ],
            }
          : {
              corners: [
                { x: 0.35, y: 0.22 },
                { x: 0.55, y: 0.22 },
                { x: 0.55, y: 0.78 },
                { x: 0.35, y: 0.78 },
              ],
            });
      this._roomExclusions.push(this.#normalizeExclusionShape(ex) || ex);
      this.#renderRoomEditor();
    }

    #removeRoomExclusion(index) {
      if (index < 0 || index >= this._roomExclusions.length) return;
      this._roomExclusions.splice(index, 1);
      this.#renderRoomEditor();
    }

    #getWallExclusions() {
      return this._roomExclusions
        .map((ex) => this.#normalizeExclusionShape(ex))
        .filter(Boolean)
        .slice(0, 4);
    }

    #renderRoomEditor() {
      this.#renderCornerPin();
      this.#renderExclusions();
      this.#renderExclusionList();
    }

    #renderCornerPin() {
      const c = this._roomCorners;
      if (this.roomCornerPoly) {
        this.roomCornerPoly.setAttribute(
          'points',
          c
            .map((p) => {
              const { left, top } = this.#roomNormToStagePct(p.x, p.y);
              return `${left.toFixed(2)},${top.toFixed(2)}`;
            })
            .join(' ')
        );
      }
      this.roomCornerHandles.forEach((handle, i) => {
        if (!c[i]) return;
        const { left, top } = this.#roomNormToStagePct(c[i].x, c[i].y);
        handle.style.left = `${left.toFixed(3)}%`;
        handle.style.top = `${top.toFixed(3)}%`;
      });
    }

    #renderExclusions() {
      if (!this.roomExclusionSvg) return;
      while (this.roomExclusionSvg.firstChild) {
        this.roomExclusionSvg.removeChild(this.roomExclusionSvg.firstChild);
      }
      if (this.roomExclusionHandles) this.roomExclusionHandles.innerHTML = '';

      this._roomExclusions.forEach((raw, i) => {
        const ex = this.#normalizeExclusionShape(raw);
        if (!ex) return;
        this._roomExclusions[i] = ex;
        const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        poly.setAttribute('class', 'ai-generator__room-exclusion-poly');
        poly.setAttribute(
          'points',
          ex.corners
            .map((p) => {
              const { left, top } = this.#roomNormToStagePct(p.x, p.y);
              return `${left.toFixed(2)},${top.toFixed(2)}`;
            })
            .join(' ')
        );
        this.roomExclusionSvg.appendChild(poly);

        ex.corners.forEach((p, c) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ai-generator__room-exclusion-handle';
          const { left, top } = this.#roomNormToStagePct(p.x, p.y);
          btn.style.left = `${left.toFixed(3)}%`;
          btn.style.top = `${top.toFixed(3)}%`;
          btn.dataset.aiRoomExclusion = String(i);
          btn.dataset.aiRoomExclusionCorner = String(c);
          btn.setAttribute('aria-label', `Excluded area ${i + 1} corner ${c + 1}`);
          btn.addEventListener('pointerdown', (event) => this.#onExclusionPointerDown(event, i, c));
          this.roomExclusionHandles?.appendChild(btn);
        });
      });
    }

    #renderExclusionList() {
      if (!this.roomExclusionList) return;
      if (!this._roomExclusions.length) {
        this.roomExclusionList.hidden = true;
        this.roomExclusionList.innerHTML = '';
        return;
      }
      this.roomExclusionList.hidden = false;
      this.roomExclusionList.innerHTML = this._roomExclusions
        .map(
          (_, i) =>
            `<li class="ai-generator__room-exclusion-item"><span>Door / window ${i + 1}</span><button type="button" class="ai-generator__room-exclusion-remove" data-ai-room-exclusion-remove="${i}">Remove</button></li>`
        )
        .join('');
      this.roomExclusionList.querySelectorAll('[data-ai-room-exclusion-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.#removeRoomExclusion(Number(btn.getAttribute('data-ai-room-exclusion-remove')));
        });
      });
    }

    #updateExclusionCorner(ex, corner, x, y) {
      if (!ex?.corners?.[corner]) return;
      const clamp01 = (n) => Math.max(0, Math.min(1, n));
      ex.corners[corner] = { x: clamp01(x), y: clamp01(y) };
    }

    #onExclusionPointerDown(event, exIndex, corner) {
      event.preventDefault();
      this._roomExclusionActive = { exIndex, corner };
      this._roomCornerActive = -1;
      const moveTo = (clientX, clientY) => {
        const active = this._roomExclusionActive;
        if (!active || !this.roomCornerStage) return;
        const ex = this._roomExclusions[active.exIndex];
        if (!ex) return;
        const norm = this.#clientToRoomNorm(clientX, clientY);
        if (!norm) return;
        this.#updateExclusionCorner(ex, active.corner, norm.x, norm.y);
        this.#renderRoomEditor();
      };
      const onPointerMove = (e) => {
        e.preventDefault();
        moveTo(e.clientX, e.clientY);
      };
      const onPointerUp = () => {
        this._roomExclusionActive = null;
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    }

    #bindCornerPinDrag() {
      if (!this.roomCornerStage || !this.roomCornerHandles.length) return;
      const clamp01 = (n) => Math.max(0, Math.min(1, n));

      const moveTo = (clientX, clientY) => {
        const i = this._roomCornerActive;
        if (i < 0 || !this._roomCorners[i]) return;
        const norm = this.#clientToRoomNorm(clientX, clientY);
        if (!norm) return;
        this._roomCorners[i] = { x: clamp01(norm.x), y: clamp01(norm.y) };
        this._roomCornersUserEdited = true;
        this.#renderCornerPin();
      };

      const onPointerMove = (event) => {
        if (this._roomCornerActive < 0) return;
        event.preventDefault();
        moveTo(event.clientX, event.clientY);
      };
      const onPointerUp = () => {
        this._roomCornerActive = -1;
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };

      this.roomCornerHandles.forEach((handle) => {
        handle.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          this._roomCornersUserEdited = true;
          this._roomCornerActive = Number(handle.dataset.aiRoomCorner);
          this._roomExclusionActive = null;
          window.addEventListener('pointermove', onPointerMove);
          window.addEventListener('pointerup', onPointerUp);
        });
        // Keyboard nudge for accessibility.
        handle.addEventListener('keydown', (event) => {
          const i = Number(handle.dataset.aiRoomCorner);
          if (!this._roomCorners[i]) return;
          const step = event.shiftKey ? 0.05 : 0.01;
          let handled = true;
          if (event.key === 'ArrowLeft') this._roomCorners[i].x = clamp01(this._roomCorners[i].x - step);
          else if (event.key === 'ArrowRight') this._roomCorners[i].x = clamp01(this._roomCorners[i].x + step);
          else if (event.key === 'ArrowUp') this._roomCorners[i].y = clamp01(this._roomCorners[i].y - step);
          else if (event.key === 'ArrowDown') this._roomCorners[i].y = clamp01(this._roomCorners[i].y + step);
          else handled = false;
          if (handled) {
            event.preventDefault();
            this.#renderCornerPin();
          }
        });
      });
    }

    #getWallCorners() {
      const hasFile = Boolean(this.#getRoomPhotoFile());
      if (!hasFile) return null;
      this.#fitRoomCornersToPrintAspect();
      return this._roomCorners.map((p) => ({
        x: Math.max(0, Math.min(1, p.x)),
        y: Math.max(0, Math.min(1, p.y)),
      }));
    }

    /** Keep the pin quad aspect aligned with print size so murals are not squashed into a tall strip. */
    #fitRoomCornersToPrintAspect() {
      if (!Array.isArray(this._roomCorners) || this._roomCorners.length !== 4) return;
      const state = this.#computeMeasureState();
      const printW = state.printWidthCm || state.widthCm;
      const printH = state.printHeightCm || state.heightCm;
      if (!(printW > 0 && printH > 0)) return;
      const targetAspect = printW / printH;
      const clamp01 = (n) => Math.max(0.02, Math.min(0.98, n));
      const corners = this._roomCorners;
      const cx = corners.reduce((s, p) => s + p.x, 0) / 4;
      const cy = corners.reduce((s, p) => s + p.y, 0) / 4;
      let quadW = Math.max(0.08, corners[1].x - corners[0].x, corners[2].x - corners[3].x);
      let quadH = Math.max(0.08, corners[3].y - corners[0].y, corners[2].y - corners[1].y);
      const currentAspect = quadW / quadH;
      if (Math.abs(currentAspect - targetAspect) < 0.04) return;
      if (currentAspect > targetAspect) {
        quadH = quadW / targetAspect;
      } else {
        quadW = quadH * targetAspect;
      }
      const x0 = clamp01(cx - quadW / 2);
      const x1 = clamp01(cx + quadW / 2);
      const y0 = clamp01(cy - quadH / 2);
      const y1 = clamp01(cy + quadH / 2);
      this._roomCorners = [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: y1 },
        { x: x0, y: y1 },
      ];
    }

    #getPreviewTrimSettings() {
      return {
        skirting: this._previewTrim?.skirting || 'none',
        cornice: this._previewTrim?.cornice || 'none',
        color: this._previewTrim?.color || 'white',
      };
    }

    #togglePreviewTrimPop(force) {
      if (!this.previewTrimPop || !this.previewTrimToggle) return;
      const open = typeof force === 'boolean' ? force : this.previewTrimPop.hidden;
      this.previewTrimPop.hidden = !open;
      this.previewTrimToggle.classList.toggle('is-open', open);
    }

    #syncPreviewTrimUi() {
      const trim = this.#getPreviewTrimSettings();
      for (const btn of this.previewTrimSkirtingBtns) {
        btn.classList.toggle('is-active', btn.dataset.aiPreviewTrimSkirting === trim.skirting);
      }
      for (const btn of this.previewTrimCorniceBtns) {
        btn.classList.toggle('is-active', btn.dataset.aiPreviewTrimCornice === trim.cornice);
      }
      for (const btn of this.previewTrimColorBtns) {
        btn.classList.toggle('is-active', btn.dataset.aiPreviewTrimColor === trim.color);
      }
    }

    #schedulePreviewTrimUpdate() {
      if (this._previewTrimRaf) cancelAnimationFrame(this._previewTrimRaf);
      this._previewTrimRaf = requestAnimationFrame(() => {
        this._previewTrimRaf = 0;
        this.#updatePreviewTrimOverlay();
      });
    }

    #updatePreviewTrimOverlay() {
      const canvas = this.previewTrimCanvas;
      const viewport = this.cropViewport;
      if (!canvas || !viewport || viewport.hidden) return;

      const trim = this.#getPreviewTrimSettings();
      const hasTrim = trim.skirting !== 'none' || trim.cornice !== 'none';
      canvas.hidden = !hasTrim;
      if (!hasTrim) return;

      const { w: vw, h: vh } = this.#getCropViewportSize();
      if (!vw || !vh) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = Math.max(1, Math.round(vw * dpr));
      const ch = Math.max(1, Math.round(vh * dpr));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }
      canvas.style.width = `${vw}px`;
      canvas.style.height = `${vh}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, vw, vh);
      drawPreviewTrimOverlay(ctx, vw, vh, trim);
    }

    #onWallPhotoSelected() {
      const file = this.wallPhotoInput?.files?.[0];
      if (!file) return;
      this.#setRoomFile(file);
      if (this.wallPhotoInput) this.wallPhotoInput.value = '';
      this.#syncRoomDialogUi();
    }

    #clearWallPhoto() {
      this.#setRoomFile(null);
      if (this.wallPhotoInput) this.wallPhotoInput.value = '';
      this.#syncRoomDialogUi();
    }

    #toggleWallPhotoPop(force) {
      if (!this.wallPhotoPop) return;
      const open = typeof force === 'boolean' ? force : this.wallPhotoPop.hidden;
      this.wallPhotoPop.hidden = !open;
      this.wallPhotoInfo?.setAttribute('aria-expanded', String(open));
    }

    #toggleWallBleedPop(force) {
      if (!this.wallBleedPop) return;
      const open = typeof force === 'boolean' ? force : this.wallBleedPop.hidden;
      this.wallBleedPop.hidden = !open;
      this.wallBleedInfo?.setAttribute('aria-expanded', String(open));
    }

    #setRoomFile(file) {
      this._roomPhotoFile = file || null;
      this._roomPhotoCompressed = null;
      if (!this.roomFileInput) return;
      try {
        const dt = new DataTransfer();
        if (file) dt.items.add(file);
        this.roomFileInput.files = dt.files;
      } catch {
        if (!file) this.roomFileInput.value = '';
      }
    }

    #syncWallPhotoUi() {
      if (!this.wallPhotoWrap) return;
      const file = this.#getRoomPhotoFile();
      const hasFile = Boolean(file);

      this.wallPhotoWrap.classList.toggle('is-filled', hasFile);
      if (this.wallPhotoRemove) this.wallPhotoRemove.hidden = !hasFile;

      if (this.wallPhotoThumbUrl) {
        URL.revokeObjectURL(this.wallPhotoThumbUrl);
        this.wallPhotoThumbUrl = '';
      }
      if (this.wallPhotoThumb) {
        if (hasFile) {
          this.wallPhotoThumbUrl = URL.createObjectURL(file);
          this.wallPhotoThumb.style.backgroundImage = `url("${this.wallPhotoThumbUrl}")`;
        } else {
          this.wallPhotoThumb.style.backgroundImage = '';
        }
      }
      if (this.wallPhotoTitle) {
        this.wallPhotoTitle.textContent = hasFile ? file.name : 'Photo of wall';
      }
      if (this.wallPhotoSub) {
        this.wallPhotoSub.textContent = hasFile
          ? 'Saved for mockup preview — not sent to the printer.'
          : 'Optional — for your mockup preview only, not sent to the printer.';
      }
    }

    #setMockupState(loading) {
      this.isMockup = loading;
      this.root.classList.toggle('is-mockup', loading);
      this.#syncPreviewActions();
      this.#syncRoomDialogUi();
    }

    #setMockupFinishMode(mode) {
      if (!this.lastHasPhotoreal) return;
      const next = mode === 'standard' ? 'standard' : 'photoreal';
      this._mockupFinishMode = next;
      this.roomFinishStandardBtn?.classList.toggle('is-active', next === 'standard');
      this.roomFinishPhotorealBtn?.classList.toggle('is-active', next === 'photoreal');
      const url =
        next === 'standard' && this.lastCompositeUrl ? this.lastCompositeUrl : this.lastMockupUrl;
      if (url && this.roomResultImg) {
        this.roomResultImg.src = url;
        this.#syncMainMockupThumb(url);
      }
    }

    #syncMockupFinishToggle(hasPhotoreal = this.lastHasPhotoreal) {
      if (!this.roomFinishToggle) return;
      const show = Boolean(hasPhotoreal && this.lastCompositeUrl && this.lastMockupUrl);
      this.roomFinishToggle.hidden = !show;
      if (show) {
        this.roomFinishStandardBtn?.classList.toggle('is-active', this._mockupFinishMode === 'standard');
        this.roomFinishPhotorealBtn?.classList.toggle('is-active', this._mockupFinishMode === 'photoreal');
      }
      this.#syncRoomEnhanceBtn();
    }

    #syncRoomEnhanceBtn(canEnhance = this.lastCanEnhance) {
      if (!this.roomEnhanceBtn) return;
      const show = Boolean(canEnhance && this.lastMockupRoomId && !this.lastHasPhotoreal);
      this.roomEnhanceBtn.hidden = !show;
      this.roomEnhanceBtn.disabled = this.isMockup;
      this.roomEnhanceBtn.classList.toggle('is-loading', this.isMockup && show);
    }

    #roomEnhanceUrl() {
      const endpoint = String(this.roomMockupEndpoint || '').trim();
      if (!endpoint) return '';
      return this.#customerGatedEndpointUrl(
        endpoint.replace(/\/room-mockup(\?.*)?$/i, '/room-mockup/enhance')
      );
    }

    async #parseMockupStreamResponse(response) {
      const ct = String(response.headers.get('content-type') || '');
      if (!ct.includes('ndjson')) return response.json();
      const reader = response.body?.getReader?.();
      if (!reader) return response.json();
      const decoder = new TextDecoder();
      let buffer = '';
      let result = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const msg = JSON.parse(line);
          if (msg.type === 'result') result = msg;
          if (msg.type === 'error') {
            throw new Error(msg.message || msg.error || 'Room preview failed.');
          }
        }
      }
      if (!result?.mockup_url) throw new Error('Malformed response from room preview.');
      return result;
    }

    #applyRoomMockupResult(data, { isStock = false, quickPreview = false } = {}) {
      const photorealSrc = this.#resolveApiAssetUrl(data.mockup_url);
      const compositeSrc = data.composite_url
        ? this.#resolveApiAssetUrl(data.composite_url)
        : photorealSrc;
      this.lastMockupUrl = photorealSrc;
      this.lastCompositeUrl = compositeSrc;
      this.lastHasPhotoreal = Boolean(data.has_photoreal);
      this.lastCanEnhance = Boolean(data.can_enhance);
      this.lastMockupRoomId = data.room_id || '';
      this._mockupFinishMode = this.lastHasPhotoreal ? 'photoreal' : 'standard';
      return { photorealSrc, compositeSrc };
    }

    async #displayRoomMockupImage(data, { isStock = false, quickPreview = false } = {}) {
      const { photorealSrc, compositeSrc } = this.#applyRoomMockupResult(data, {
        isStock,
        quickPreview,
      });

      if (this.roomMockupTheater) {
        this.roomMockupTheater.style.setProperty('--mockup-roll', '100');
        this.roomMockupTheater.classList.add('is-complete');
      }
      if (this.roomMockupTheaterProgress) {
        this.roomMockupTheaterProgress.style.width = '100%';
      }
      await new Promise((resolve) => window.setTimeout(resolve, 480));

      if (this.roomResultImg) {
        const displaySrc =
          this.lastHasPhotoreal && this._mockupFinishMode === 'photoreal'
            ? photorealSrc
            : compositeSrc;
        this.#syncMainMockupThumb(displaySrc);
        this.root.wallprintAiDesignShare?.syncButton?.();
        await new Promise((resolve, reject) => {
          const img = this.roomResultImg;
          const done = () => resolve();
          const fail = () => reject(new Error('Mockup image could not load.'));
          img.onload = done;
          img.onerror = fail;
          img.src = displaySrc;
          if (img.complete && img.naturalWidth > 0) done();
        });
        this.roomResultImg.hidden = false;
      }
      this.#syncMockupFinishToggle(this.lastHasPhotoreal);
      if (this.roomStatusEl) {
        const stockNote = isStock ? ' Free stock preview — unlimited. ' : ' ';
        let finishNote = '';
        if (!isStock && !this.lastHasPhotoreal && quickPreview && this.lastCanEnhance) {
          finishNote = ' Quick preview — tap Enhance lighting for realistic AI relighting (no extra credit).';
        } else if (!isStock && !this.lastHasPhotoreal && quickPreview) {
          finishNote =
            ' Quick preview — turn off Quick preview and run again for realistic AI lighting.';
        }
        this.roomStatusEl.textContent =
          `${stockNote}Preview only — watermarked. Share this with your client using the Share button.${finishNote}`.trim();
      }
    }

    async runRoomMockup() {
      if (this.isMockup || this.isGenerating || this.isTiling || this.isUpscaling) return;
      ensureStudioGuestSession(this);
      const file = this.#getRoomPhotoFile();
      const stockRoom = this._stockRoomSelection;
      const asset = this.currentAsset;
      if ((!file && !stockRoom?.id) || !asset) {
        if (this.roomStatusEl) {
          this.roomStatusEl.hidden = false;
          this.roomStatusEl.textContent = 'Choose a room photo or pick a stock room first.';
        }
        return;
      }

      if (!this.customerLoggedIn && !isStudioGuestMode(this)) {
        this.#showError('Log in to preview your design on a room photo.');
        return;
      }

      if (!this.#isServerBackedAsset(asset)) {
        const msg =
          'Wall mockup needs a server-saved design. Re-upload or generate while WallPrint AI is connected.';
        if (this.roomStatusEl) {
          this.roomStatusEl.hidden = false;
          this.roomStatusEl.textContent = msg;
        }
        this.#showError(msg);
        return;
      }

      this.#clearError();
      this.#setMockupState(true);
      this.#startMockupTheater();
      let mockupProgressTimer = 0;
      const mockupStarted = Date.now();
      const isStockRun = Boolean(stockRoom?.id && !file);
      const updateMockupStatus = () => {
        if (!this.roomStatusEl) return;
        const sec = Math.floor((Date.now() - mockupStarted) / 1000);
        if (isStockRun) {
          this.roomStatusEl.textContent =
            sec < 20
              ? `Creating free stock preview — compositing your wallpaper… (${sec}s)`
              : `Still working — stock previews skip AI lighting and are usually quick… (${sec}s)`;
          return;
        }
        if (sec < 25) {
          this.roomStatusEl.textContent = `Creating wall mockup — compositing your design on the wall… (${sec}s)`;
        } else if (sec < 75) {
          this.roomStatusEl.textContent = `Still working — furniture detection and lighting usually take 45–90 seconds… (${sec}s)`;
        } else {
          this.roomStatusEl.textContent = `Almost there — complex rooms can take up to 2 minutes… (${sec}s)`;
        }
      };
      updateMockupStatus();
      mockupProgressTimer = window.setInterval(updateMockupStatus, 2500);
      if (this.roomResultImg) this.roomResultImg.hidden = true;
      if (this.roomFinishToggle) this.roomFinishToggle.hidden = true;
      this.#syncRoomEnhanceBtn(false);

      if (!(await this.#ensureApiReady())) {
        this.#setMockupState(false);
        this.#stopMockupTheater();
        return;
      }

      this.#persistCropToAsset();
      const state = this.#computeMeasureState();
      const formData = new FormData();
      const isStock = Boolean(stockRoom?.id && !file);
      if (!isStock) {
        const roomUpload = await this.#compressRoomPhoto(file, 960);
        formData.append('room', roomUpload);
      } else {
        formData.append('stock_room_id', stockRoom.id);
      }
      formData.append('ai_image_id', asset.aiImageId);
      formData.append('wall_width_cm', String(Math.round(state.printWidthCm || state.widthCm)));
      formData.append('wall_height_cm', String(Math.round(state.printHeightCm || state.heightCm)));
      formData.append('wallpaper_layout', this.#getMockupWallpaperLayout(asset));
      formData.append('pattern_repeat_cm', String(this.#getPatternRepeatCm()));
      formData.append('wall_trim', JSON.stringify(this.#getPreviewTrimSettings()));
      formData.append(
        'metadata',
        JSON.stringify({
          ...asset.metadata,
          ...(this.cropViewport && !this.cropViewport.hidden ? this.#getUploadCropMetadata() : {}),
          ...this.#getAdjustmentMetadata(),
          tiled: Boolean(
            this.seamlessRepeatInput?.checked || asset.tiled || asset.metadata?.tiled
          ),
          seamless_repeat: Boolean(this.seamlessRepeatInput?.checked || asset.metadata?.seamless_repeat),
          wallpaper_layout: this.#getMockupWallpaperLayout(asset),
          pattern_repeat_cm: this.#getPatternRepeatCm(),
        })
      );
      if (this.watermarkLogoUrl) formData.append('watermark_logo_url', this.watermarkLogoUrl);
      if (!isStock) {
        formData.append(
          'mockup_finish',
          this.roomFastPreviewInput?.checked ? 'preview' : 'photoreal'
        );
        const wallCorners = this.#getWallCorners();
        if (wallCorners) formData.append('wall_corners', JSON.stringify(wallCorners));
        const wallExclusions = this.#getWallExclusions();
        if (wallExclusions.length) formData.append('wall_exclusions', JSON.stringify(wallExclusions));
        if (this._roomLayoutSessionId) {
          formData.append('layout_session_id', this._roomLayoutSessionId);
        }
        if (Number.isFinite(this._roomPrefetchForegroundRatio)) {
          formData.append('prefetch_foreground_ratio', String(this._roomPrefetchForegroundRatio));
        }
        formData.append('stream_progress', '1');
      }

      if (this.roomStatusEl) this.roomStatusEl.hidden = false;

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 240000);

      try {
        const mockupUrl = isStock
          ? this.#stockRoomsPreviewUrl()
          : this.#customerGatedEndpointUrl(this.roomMockupEndpoint);
        const response = await fetch(mockupUrl, {
          method: 'POST',
          headers: this.#apiHeaders({ url: mockupUrl }),
          signal: controller.signal,
          body: formData,
        });

        if (!response.ok) {
          let errBody = {};
          try {
            errBody = await response.json();
          } catch {
            const text = await response.text().catch(() => '');
            if (text) {
              try {
                errBody = JSON.parse(text);
              } catch {
                errBody = { message: text.slice(0, 240) };
              }
            }
          }
          if (response.status === 401 && errBody.error === 'login_required') {
            if (this.tileLoginEl) this.tileLoginEl.hidden = false;
            throw new Error(errBody.message || 'Log in to use room preview.');
          }
          if (response.status === 400 && /design_id required/i.test(String(errBody.error || ''))) {
            throw new Error(
              'Design was not saved on the server. Re-upload or generate your wallpaper, then try again.'
            );
          }
          if (response.status === 401 && errBody.error !== 'login_required') {
            throw new Error(
              errBody.message ||
                'Wall mockup authentication failed. Ask the store admin to set the Studio Storefront API key in the theme editor.'
            );
          }
          if (response.status === 429) {
            throw new Error(
              errBody.message || 'You have used your room previews for now. Try again later.'
            );
          }
          throw new Error(errBody.message || errBody.error || `Room preview failed (${response.status})`);
        }

        const data = await this.#parseMockupStreamResponse(response);
        await this.#displayRoomMockupImage(data, {
          isStock,
          quickPreview: Boolean(!isStock && this.roomFastPreviewInput?.checked),
        });
      } catch (error) {
        const networkFailure = this.#isApiUnreachableError(error);
        if (networkFailure) {
          console.warn('[ai-generator] room mockup network', error);
        } else {
          console.error('[ai-generator] room mockup', error);
        }
        const message =
          error?.name === 'AbortError'
            ? 'Room preview timed out. Try a smaller photo or try again.'
            : networkFailure
              ? 'Lost connection to WallPrint AI while creating the mockup. The server may still be working — wait a moment and try again, or use a stock room for a faster preview.'
              : error?.message || 'Could not create room preview.';
        if (this.roomStatusEl) {
          this.roomStatusEl.hidden = false;
          this.roomStatusEl.textContent = message;
        }
        this.#showError(message);
      } finally {
        window.clearTimeout(timeoutId);
        if (mockupProgressTimer) window.clearInterval(mockupProgressTimer);
        this.#stopMockupTheater();
        this.#setMockupState(false);
      }
    }

    async runRoomEnhance() {
      if (this.isMockup || !this.lastMockupRoomId || !this.lastCanEnhance || this.lastHasPhotoreal) {
        return;
      }
      ensureStudioGuestSession(this);
      if (!this.customerLoggedIn && !isStudioGuestMode(this)) {
        this.#showError('Log in to enhance room preview lighting.');
        return;
      }
      const enhanceUrl = this.#roomEnhanceUrl();
      if (!enhanceUrl) {
        this.#showError('Room enhance endpoint is not configured.');
        return;
      }
      if (!(await this.#ensureApiReady())) return;

      this.#clearError();
      this.#setMockupState(true);
      if (this.roomStatusEl) {
        this.roomStatusEl.hidden = false;
        this.roomStatusEl.textContent = 'Enhancing lighting — usually 45–90 seconds…';
      }
      this.#syncRoomEnhanceBtn(true);

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 240000);

      try {
        const response = await fetch(enhanceUrl, {
          method: 'POST',
          headers: {
            ...this.#apiHeaders({ url: enhanceUrl }),
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            room_id: this.lastMockupRoomId,
            watermark_logo_url: this.watermarkLogoUrl || undefined,
          }),
        });
        if (!response.ok) {
          let errBody = {};
          try {
            errBody = await response.json();
          } catch {
            errBody = {};
          }
          throw new Error(errBody.message || errBody.error || `Enhance failed (${response.status})`);
        }
        const data = await response.json();
        await this.#displayRoomMockupImage(data, { isStock: false, quickPreview: false });
      } catch (error) {
        const networkFailure = this.#isApiUnreachableError(error);
        if (networkFailure) {
          console.warn('[ai-generator] room enhance network', error);
        } else {
          console.error('[ai-generator] room enhance', error);
        }
        const message =
          error?.name === 'AbortError'
            ? 'Enhance lighting timed out — try again.'
            : networkFailure
              ? 'Lost connection while enhancing lighting. Wait a moment and try again.'
              : error?.message || 'Could not enhance room preview.';
        if (this.roomStatusEl) {
          this.roomStatusEl.hidden = false;
          this.roomStatusEl.textContent = message;
        }
        this.#showError(message);
      } finally {
        window.clearTimeout(timeoutId);
        this.#setMockupState(false);
        this.#syncRoomEnhanceBtn(this.lastCanEnhance);
      }
    }

    #setAddingState(adding) {
      this.isAdding = adding;
      this.root.classList.toggle('is-adding', adding);
      this.#syncCheckoutAck();
    }

    #resolveGenerationShape(state) {
      if (this.isWallShape) {
        const ratio = state.printWidthCm / state.printHeightCm;
        const { width, height } = dimensionsForRatio(ratio, this.genLongEdge);
        return { ratio, width, height, label: `wall:${Math.round(state.printWidthCm)}x${Math.round(state.printHeightCm)}cm` };
      }
      const map = { '1:1': 1, '16:9': 16 / 9, '4:5': 4 / 5 };
      const value = this.aspectSelect?.value || '1:1';
      const ratio = map[value] || 1;
      const { width, height } = dimensionsForRatio(ratio, this.genLongEdge);
      return { ratio, width, height, label: value };
    }

    /** Show upload + watermark notes below the preview for the current source. */
    #setNoteForSource(source) {
      const isUpload = source === 'uploaded';
      if (this.uploadNoteEl) {
        this.uploadNoteEl.hidden = !isUpload;
        const uploadText = this.uploadNoteEl.querySelector('[data-ai-upload-note-text]');
        if (uploadText) uploadText.textContent = this.uploadPreviewNote;
      }
      // Generated: ownership copy sits beside the quality badge; hide the large note below.
      if (this.watermarkNoteEl) {
        this.watermarkNoteEl.hidden = true;
      }
      if (this.watermarkNoteText) {
        this.watermarkNoteText.textContent = this.generatedPreviewNote;
      }
      this.#syncPreviewOwnershipNote(
        source === 'generated' ? this.currentAsset : null
      );
      this.#syncRefineUi();
      this.#schedulePreviewLayout(true);
    }

    /** Small ownership line beside the quality badge (generated previews only). */
    #syncPreviewOwnershipNote(asset) {
      if (!this.previewOwnershipEl) return;
      const show = asset?.source === 'generated' && Boolean(this.ownershipPreviewNote);
      this.previewOwnershipEl.hidden = !show;
      if (show && this.previewOwnershipText) {
        this.previewOwnershipText.textContent = this.ownershipPreviewNote;
      }
    }

    /** True when Shopify app proxy / backend URL is not configured (HTML error page). */
    #apiUnreachableBody(text) {
      const t = String(text || '');
      return (
        t.includes('<!DOCTYPE') ||
        t.includes('<html') ||
        /ERR_NGROK/i.test(t) ||
        /is offline/i.test(t) ||
        /ngrok\.com\/docs\/errors/i.test(t)
      );
    }

    #usesAppProxyEndpoints() {
      const gen = this.generateEndpoint || '';
      const up = this.uploadEndpoint || '';
      return gen.includes('/apps/wallprint-ai') || up.includes('/apps/wallprint-ai');
    }

    /** Dev API points at this machine (localhost / LAN IP) — not reachable from a phone on the live site. */
    #isLocalDevApiEndpoint() {
      try {
        const url = new URL(this.uploadEndpoint, window.location.origin);
        if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') return true;
        if (/^192\.168\.|^10\.|^172\.(1[6-9]|2\d|3[01])\./.test(url.hostname)) return true;
        return false;
      } catch {
        return false;
      }
    }

    /** True when the page itself is served from the same machine as the dev API. */
    #canUseLocalDevApiFromBrowser() {
      if (!this.#isLocalDevApiEndpoint()) return true;
      const host = window.location.hostname;
      return host === 'localhost' || host === '127.0.0.1';
    }

    /** Account-gated tools only — never attach to generate/upload (breaks CORS + open routes). */
    #needsCustomerAuth(url) {
      if (!url) return false;
      return /\/(tile-pattern|upscale|room-mockup(?:\/enhance)?|room-layout-suggest|room-layout-prefetch|stock-rooms\/preview|share-preview|community-gallery\/publish|prompt-assistant|assistant-feedback|map-design|map-place-story)(\/|\?|$)/i.test(String(url));
    }

    /**
     * Login-gated routes (mockup, seamless tile). Direct Fly/ngrok URLs need the theme
     * storefront key; without it, use the signed Shopify app proxy on the store domain.
     */
    #customerGatedEndpointUrl(url) {
      if (!url) return url;
      if (!this.#needsCustomerAuth(url)) return this.#apiEndpointUrl(url);
      const isExternal = /^https?:\/\//i.test(String(url));
      if (isExternal && !this.storefrontApiKey) {
        const isStandaloneStudio =
          this.studioMode === 'standalone' || this.studioMode === 'embed';
        if (!isStandaloneStudio) {
          if (/\/tile-pattern(\/|\?|$)/i.test(url)) return '/apps/wallprint-ai/tile-pattern';
          if (/\/upscale(\/|\?|$)/i.test(url)) return '/apps/wallprint-ai/upscale';
          if (/\/room-mockup\/enhance(\/|\?|$)/i.test(url)) return '/apps/wallprint-ai/room-mockup/enhance';
          if (/\/room-mockup(\/|\?|$)/i.test(url)) return '/apps/wallprint-ai/room-mockup';
          if (/\/room-layout-suggest(\/|\?|$)/i.test(url)) return '/apps/wallprint-ai/room-layout-suggest';
          if (/\/room-layout-prefetch(\/|\?|$)/i.test(url)) return '/apps/wallprint-ai/room-layout-prefetch';
          if (/\/stock-rooms\/preview(\/|\?|$)/i.test(url)) return '/apps/wallprint-ai/stock-rooms/preview';
        }
      }
      return this.#apiEndpointUrl(url);
    }

    /** Direct Fly.dev base URL (no auth query). */
    #directApiBaseUrl() {
      const endpoint = String(this.uploadEndpoint || this.generateEndpoint || '').trim();
      if (!endpoint) return '';
      return endpoint
        .replace(/\/(upload|generate|tile-pattern|upscale|room-mockup|room-layout-suggest|room-layout-prefetch|ip-scan)(\/|\?.*)?$/i, '')
        .replace(/\?.*$/, '')
        .replace(/\/$/, '');
    }

    /** Health probe — unsigned so a mismatched theme storefront key cannot block generate. */
    #directHealthUrl() {
      const base = this.#directApiBaseUrl();
      if (base && /^https?:\/\//i.test(base)) return `${base}/health`;
      if (this.#usesAppProxyEndpoints()) return '/apps/wallprint-ai/health';
      return '/health';
    }

    /** Check API reachability before generate (retries each attempt). */
    async #ensureApiReady() {
      if (this.#usesAppProxyEndpoints()) return true;

      if (this.#isLocalDevApiEndpoint() && !this.#canUseLocalDevApiFromBrowser()) {
        this.#showError(
          'Dev API is set to localhost — desktop only. On your phone, clear Dev API base URL in the theme editor or use an https ngrok URL.'
        );
        return false;
      }

      const healthUrl = this.#directHealthUrl();
      const healthHeaders = { Accept: 'application/json' };
      if (this.#usesNgrokTunnel()) healthHeaders['ngrok-skip-browser-warning'] = '1';
      try {
        const res = await fetch(healthUrl, {
          headers: healthHeaders,
        });
        const text = await res.text();
        if (!res.ok || this.#apiUnreachableBody(text)) throw new Error('unreachable');
        let payload = null;
        try {
          payload = JSON.parse(text);
        } catch {
          /* non-json health */
        }
        if (payload && !(await this.#checkStorageHeadroom(payload))) return false;
        this.#clearError();
        return true;
      } catch {
        this.#showError(
          'Cannot reach WallPrint AI. Check the API is running at wallprint-ai.fly.dev or clear Dev API base URL to use the store app proxy.'
        );
        return false;
      }
    }

    /** Health JSON reports low storage — block generate with a clear message. */
    async #checkStorageHeadroom(healthPayload) {
      const storage = healthPayload?.storage;
      if (!storage || storage.ok !== false) return true;
      const freeMb = Number(storage.freeMb);
      const detail =
        Number.isFinite(freeMb) && freeMb >= 0
          ? `Server storage is nearly full (${freeMb} MB free).`
          : 'Server storage is nearly full.';
      this.#showError(
        `${detail} Generation may fail until space is freed — try again shortly or contact support.`
      );
      return false;
    }

    /** Network / tunnel down — fall back to local preview so upload still works in the browser. */
    #isApiUnreachableError(error, errText = '') {
      if (errText && this.#apiUnreachableBody(errText)) return true;
      if (!error) return false;
      const msg = String(error.message || error);
      return (
        error.name === 'AbortError' ||
        msg.includes('Failed to fetch') ||
        msg.includes('NetworkError') ||
        msg.includes('Load failed')
      );
    }

    #usesNgrokTunnel() {
      return /ngrok/i.test(this.generateEndpoint) || /ngrok/i.test(this.uploadEndpoint);
    }

    /** Headers for WallPrint AI API (ngrok free tier needs skip-browser-warning). */
    #apiHeaders({ json = false, url = '' } = {}) {
      ensureStudioGuestSession(this);
      const headers = { Accept: 'application/json' };
      if (json) headers['Content-Type'] = 'application/json';
      if (this.#usesNgrokTunnel()) headers['ngrok-skip-browser-warning'] = '1';
      const needsCustomer = this.#needsCustomerAuth(url);
      if (needsCustomer && this.storefrontApiKey) {
        headers['X-WallPrint-Storefront-Key'] = this.storefrontApiKey;
      }
      if (needsCustomer) {
        const customerId =
          (this.customerLoggedIn && this.customerId) ||
          (isStudioGuestMode(this) ? this.#resolveDesignLibraryCustomerId() : '');
        if (customerId) headers['X-WallPrint-Customer-Id'] = customerId;
      }
      if (this.designLibraryCustomerId) {
        headers['X-WallPrint-Design-Library-Id'] = this.designLibraryCustomerId;
      }
      return headers;
    }

    /** Authenticated JSON fetch for login-gated studio tools (share, mockup, tile, etc.). */
    customerApiFetch(url, init = {}) {
      ensureStudioGuestSession(this);
      const resolvedUrl = this.#customerGatedEndpointUrl(url);
      const headers = {
        ...this.#apiHeaders({ json: Boolean(init.body), url: resolvedUrl }),
        ...(init.headers || {}),
      };
      return fetch(resolvedUrl, {
        ...init,
        headers,
        credentials: init.credentials ?? 'same-origin',
      });
    }

    #resolveDesignLibraryCustomerId() {
      const configured = String(this.root.dataset.customerId || '').trim();
      const isRealShopifyCustomer =
        this.customerLoggedIn &&
        configured &&
        configured !== 'studio_wallprint_ai' &&
        /^\d+$/.test(configured);
      if (isRealShopifyCustomer) return configured;

      const key = 'wallprint_studio_guest_id';
      try {
        let id = localStorage.getItem(key);
        if (!id) {
          id = `studio_${
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID().replace(/-/g, '')
              : Date.now().toString(36)
          }`;
          localStorage.setItem(key, id);
        }
        return id;
      } catch {
        return configured || 'studio_wallprint_ai';
      }
    }

    /** Append storefront key (+ customer id) for account-gated direct Fly.dev calls only. */
    #apiEndpointUrl(url) {
      if (!url) return url;
      const isExternal = /^https?:\/\//i.test(String(url));
      const needsCustomer = this.#needsCustomerAuth(url);
      if (!needsCustomer) return url;
      // wallprint.ai standalone: auth via headers only; query params trigger app-proxy HMAC checks.
      if (!this.storefrontApiKey) return url;
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('key', this.storefrontApiKey);
        if (isExternal && this.customerLoggedIn && this.customerId) {
          u.searchParams.set('logged_in_customer_id', this.customerId);
        }
        if (isExternal) return u.toString();
        if (u.search) return `${u.pathname}${u.search}`;
        return url;
      } catch {
        const params = [`key=${encodeURIComponent(this.storefrontApiKey)}`];
        if (isExternal && this.customerLoggedIn && this.customerId) {
          params.push(`logged_in_customer_id=${encodeURIComponent(this.customerId)}`);
        }
        const sep = url.includes('?') ? '&' : '?';
        return `${url}${sep}${params.join('&')}`;
      }
    }

    /** Dev API may return localhost file URLs — rewrite to the public tunnel origin. */
    #resolveApiAssetUrl(url) {
      if (!url || typeof url !== 'string') return url;
      try {
        const asset = new URL(url, window.location.href);
        const host = asset.hostname;
        if (host !== 'localhost' && host !== '127.0.0.1') return url;

        const endpoint = this.generateEndpoint || this.uploadEndpoint || '';
        if (!/^https?:\/\//i.test(endpoint)) return url;

        const api = new URL(endpoint);
        asset.protocol = api.protocol;
        asset.host = api.host;
        return asset.toString();
      } catch {
        return url;
      }
    }

    #localAssetId(prefix) {
      const rand =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID().replace(/-/g, '')
          : String(Date.now());
      return `${prefix}_${rand}`;
    }

    /**
     * Browser-only preview when /apps/wallprint-ai/* is not wired yet.
     * Cart still works; fulfilment needs the Node service + app proxy.
     */
    async #buildLocalMockPreviewDataUrl(prompt, width, height) {
      const w = Math.min(768, Math.max(480, width));
      const h = Math.min(768, Math.max(480, height));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const gradient = ctx.createLinearGradient(0, 0, w, h);
      gradient.addColorStop(0, '#3f676f');
      gradient.addColorStop(0.5, '#486c74');
      gradient.addColorStop(1, '#2a4a52');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);

      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
      ctx.font = `${Math.round(w / 22)}px Georgia, serif`;
      ctx.fillText('WallPrint AI Preview', w / 2, h * 0.46);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
      ctx.font = `${Math.round(w / 38)}px Arial, sans-serif`;
      const snippet = String(prompt).slice(0, 100);
      ctx.fillText(snippet, w / 2, h * 0.56);

      ctx.font = `${Math.round(w / 48)}px Arial, sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.fillText('Connect WallPrint AI backend for production output', w / 2, h * 0.68);

      await this.#drawCanvasWatermark(ctx, w, h);

      return canvas.toDataURL('image/png');
    }

    /** Full-bleed faint watermark over the crop preview (server URLs may also be watermarked). */
    async #drawCanvasWatermark(ctx, w, h) {
      const label = 'WallPrint Preview';
      const base = Math.min(w, h);
      const fontSize = Math.max(16, Math.round(base * 0.07));
      const stepX = fontSize * 4.6;
      const stepY = fontSize * 2.8;
      const span = Math.hypot(w, h);

      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate(-0.38);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `700 ${fontSize}px Arial, sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.032)';

      for (let y = -span; y <= span; y += stepY) {
        const rowOffset = Math.round(y / stepY) % 2 ? stepX * 0.5 : 0;
        for (let x = -span + rowOffset; x <= span; x += stepX) {
          ctx.fillText(label, x, y);
        }
      }
      ctx.restore();
    }

    async #completeLocalGenerate({ prompt, shape, state }) {
      const previewUrl = await this.#buildLocalMockPreviewDataUrl(prompt, shape.width, shape.height);
      if (!previewUrl) throw new Error('Could not render local preview.');

      this.currentAsset = {
        source: 'generated',
        aiImageId: this.#localAssetId('local_gen'),
        seed: 'local',
        model: 'local-preview',
        prompt,
        shapeLabel: shape.label,
        genWidth: shape.width,
        genHeight: shape.height,
        watermarked: true,
        metadata: {
          local_only: true,
          prompt,
          wall: this.isWallShape
            ? {
                print_width_cm: Math.round(state.printWidthCm),
                print_height_cm: Math.round(state.printHeightCm),
              }
            : null,
        },
      };

      await this.#renderGeneratedPreview(previewUrl);
      this.#setNoteForSource('generated');
      this.#setCheckoutVisible(true);
      this.calculate();
      this.#emitPreviewChanged();
      console.warn(
        '[ai-generator] Using local preview — configure Shopify app proxy (wallprint-ai) or Dev API base URL for real AI + print fulfilment.'
      );
    }

    #completeLocalUpload(file, dims, quality, state) {
      this.currentAsset = {
        source: 'uploaded',
        aiImageId: this.#localAssetId('local_upload'),
        seed: '',
        model: 'customer-upload-local',
        prompt: '',
        shapeLabel: this.isWallShape ? 'wall' : this.aspectSelect?.value || '',
        fileName: file.name,
        uploadWidth: dims.w,
        uploadHeight: dims.h,
        watermarked: false,
        originalUrl: '',
        editUrl: this.objectUrl || '',
        metadata: {
          local_only: true,
          original_filename: file.name,
          file_type: file.type,
          upload_width_px: dims.w,
          upload_height_px: dims.h,
          ...this.#getUploadCropMetadata(),
        },
      };
      this.#persistCropToAsset();
      this.#setUploadStatus(
        `${file.name} — ${quality.message} (preview saved — start ngrok http 8787 for server upload)`,
        quality.status
      );
      this.#setNoteForSource('uploaded');
      this.#setCheckoutVisible(true);
      this.#syncDesignEmphasis();
      this.calculate();
      this.#emitPreviewChanged();
      console.warn('[ai-generator] Upload stored locally only — connect WallPrint AI backend for print fulfilment.');
    }

    #parseHexColor(input) {
      const raw = String(input || '').trim().replace(/^#/, '');
      if (!/^[0-9a-f]{3}$/i.test(raw) && !/^[0-9a-f]{6}$/i.test(raw)) return null;
      const hex =
        raw.length === 3
          ? raw
              .split('')
              .map((c) => c + c)
              .join('')
          : raw;
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }

    #rgbToHex({ r, g, b }) {
      const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
      return `#${[clamp(r), clamp(g), clamp(b)]
        .map((n) => n.toString(16).padStart(2, '0'))
        .join('')}`;
    }

    #rgbToHue(r, g, b) {
      const rn = r / 255;
      const gn = g / 255;
      const bn = b / 255;
      const max = Math.max(rn, gn, bn);
      const min = Math.min(rn, gn, bn);
      const d = max - min;
      if (d < 0.01) return 0;
      let h;
      if (max === rn) h = ((gn - bn) / d) % 6;
      else if (max === gn) h = (bn - rn) / d + 2;
      else h = (rn - gn) / d + 4;
      h *= 60;
      if (h < 0) h += 360;
      return h;
    }

    #describeColorName({ r, g, b }) {
      const hue = this.#rgbToHue(r, g, b);
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const sat = max === 0 ? 0 : (max - min) / max;
      const light = max / 255;

      if (sat < 0.08) {
        if (light > 0.88) return 'soft white';
        if (light > 0.72) return 'light grey';
        if (light > 0.45) return 'mid grey';
        if (light > 0.22) return 'charcoal';
        return 'deep black';
      }

      let family;
      if (hue < 20 || hue >= 340) family = 'red';
      else if (hue < 45) family = 'orange';
      else if (hue < 70) family = 'gold';
      else if (hue < 95) family = 'yellow-green';
      else if (hue < 150) family = 'green';
      else if (hue < 190) family = 'teal';
      else if (hue < 220) family = 'cyan';
      else if (hue < 260) family = 'blue';
      else if (hue < 290) family = 'indigo';
      else if (hue < 320) family = 'purple';
      else family = 'magenta';

      if (light > 0.78 && sat < 0.35) return `soft ${family}`;
      if (light > 0.65) return sat > 0.45 ? `bright ${family}` : `light ${family}`;
      if (light < 0.28) return `deep ${family}`;
      if (sat < 0.35) return `muted ${family}`;
      return family;
    }

    #getDesignPalette() {
      if (!this._paletteTouched) return null;
      const primary = this.#parseHexColor(this.palettePrimaryInput?.value);
      if (!primary) return null;
      const accent = this.#parseHexColor(this.paletteAccentInput?.value);
      return {
        primary: this.#rgbToHex(primary),
        ...(accent ? { accent: this.#rgbToHex(accent) } : {}),
      };
    }

    #applyPaletteNeutralValues() {
      if (this.palettePrimaryInput) this.palettePrimaryInput.value = PALETTE_NEUTRAL_PRIMARY;
      if (this.paletteAccentInput) this.paletteAccentInput.value = PALETTE_NEUTRAL_ACCENT;
    }

    #resetDesignPalette() {
      this._paletteTouched = false;
      this._activePalettePresetId = '';
      this.#applyPaletteNeutralValues();
      this.#syncPaletteUi();
      this.#setPalettePresetsOpen(false);
      this.#emitPaletteChanged();
    }

    #syncPaletteUi() {
      const touched = this._paletteTouched;
      const primary = this.#parseHexColor(this.palettePrimaryInput?.value || PALETTE_NEUTRAL_PRIMARY);
      const accent = this.#parseHexColor(this.paletteAccentInput?.value || PALETTE_NEUTRAL_ACCENT);

      const applyFill = (el, rgb) => {
        if (!el || !rgb) return;
        const hex = this.#rgbToHex(rgb);
        el.style.setProperty('background-color', hex);
        el.style.setProperty('background', hex);
      };

      applyFill(this.palettePrimaryPreview, primary);
      applyFill(this.paletteAccentPreview, accent);

      if (this.palettePrimaryName && primary) {
        this.palettePrimaryName.textContent = touched
          ? `Main colour: ${this.#describeColorName(primary)}`
          : 'Main colour';
      }
      if (this.paletteAccentName && accent) {
        this.paletteAccentName.textContent = touched
          ? `Accent colour: ${this.#describeColorName(accent)}`
          : 'Accent colour';
      }
      this.palettePrimaryInput
        ?.closest('.ai-generator__palette-dot')
        ?.classList.toggle('is-active', touched);
      this.paletteAccentInput
        ?.closest('.ai-generator__palette-dot')
        ?.classList.toggle('is-active', touched);
      this.palettePrimaryInput
        ?.closest('.ai-generator__palette-dot')
        ?.classList.toggle('is-neutral', !touched);
      this.paletteAccentInput
        ?.closest('.ai-generator__palette-dot')
        ?.classList.toggle('is-neutral', !touched);
      if (this.paletteClearBtn) {
        this.paletteClearBtn.hidden = false;
        this.paletteClearBtn.disabled = !touched;
        this.paletteClearBtn.classList.toggle('is-inactive', !touched);
        this.paletteClearBtn.setAttribute('aria-disabled', touched ? 'false' : 'true');
      }
      if (this.paletteHintEl) {
        this.paletteHintEl.textContent = touched
          ? 'Locked — generation and library follow these tones.'
          : 'Optional — steers AI generation and library search.';
      }
      this.#syncPalettePresetUi();
    }

    #applyPalettePreset(primary, accent, presetId = '') {
      if (!primary) return;
      if (this.palettePrimaryInput) this.palettePrimaryInput.value = primary;
      if (this.paletteAccentInput && accent) this.paletteAccentInput.value = accent;
      this._paletteTouched = true;
      this._activePalettePresetId = presetId || '';
      this.#syncPaletteUi();
      this.#emitPaletteChanged();
    }

    #syncPalettePresetUi() {
      const activeId = this._activePalettePresetId;
      this.root?.querySelectorAll('[data-ai-palette-preset]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.aiPalettePreset === activeId);
      });
      this.root?.querySelectorAll('[data-ai-palette-paint]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.aiPalettePaint === activeId);
      });
    }

    #setPalettePresetsOpen(open) {
      const next = Boolean(open);
      if (this._palettePresetsOpen === next) return;
      this._palettePresetsOpen = next;
      if (this.palettePresetsPanel) this.palettePresetsPanel.hidden = !next;
      this.palettePresetsToggle?.setAttribute('aria-expanded', next ? 'true' : 'false');
      this.palettePresetsToggle?.classList.toggle('is-open', next);
      if (next) this.#syncPalettePresetUi();
    }

    #bindPalettePresets() {
      if (!this.palettePresetsToggle || this.palettePresetsToggle.dataset.wpPalettePresetsBound === '1') {
        return;
      }
      this.palettePresetsToggle.dataset.wpPalettePresetsBound = '1';

      this.palettePresetsToggle.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#setPalettePresetsOpen(!this._palettePresetsOpen);
      });

      this.root?.querySelectorAll('[data-ai-palette-preset]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.#applyPalettePreset(
            btn.dataset.palettePrimary,
            btn.dataset.paletteAccent,
            btn.dataset.aiPalettePreset || ''
          );
          this.#setPalettePresetsOpen(false);
        });
      });

      this.root?.querySelectorAll('[data-ai-palette-paint]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.#applyPalettePreset(
            btn.dataset.palettePrimary,
            btn.dataset.paletteAccent,
            btn.dataset.aiPalettePaint || ''
          );
          this.#setPalettePresetsOpen(false);
        });
      });

      if (!this._palettePresetsDocBound) {
        this._palettePresetsDocBound = true;
        document.addEventListener('click', (event) => {
          if (!this._palettePresetsOpen) return;
          const wrap = this.palettePresetsToggle?.closest('.ai-generator__palette-presets-wrap');
          if (wrap?.contains(event.target)) return;
          this.#setPalettePresetsOpen(false);
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this._palettePresetsOpen) {
            this.#setPalettePresetsOpen(false);
          }
        });
      }
    }

    #syncStyleStarterUi() {
      if (!this.styleStarterSelect) return;
      if (this._activeStyleStarter && this.styleStarterSelect.value !== this._activeStyleStarter) {
        this.styleStarterSelect.value = this._activeStyleStarter;
      }
      if (!this._activeStyleStarter && this.styleStarterSelect.value) {
        this.styleStarterSelect.value = '';
      }
    }

    #emitPaletteChanged() {
      this.root.dispatchEvent(
        new CustomEvent('wallprint:palette-changed', {
          detail: { palette: this.#getDesignPalette() },
        })
      );
    }

    #bindPaletteAndStyleStarters() {
      const onPaletteInput = () => {
        this._paletteTouched = true;
        this._activePalettePresetId = '';
        this.#syncPaletteUi();
        this.#emitPaletteChanged();
      };

      this.palettePrimaryInput?.addEventListener('input', onPaletteInput);
      this.paletteAccentInput?.addEventListener('input', onPaletteInput);

      this.paletteClearBtn?.addEventListener('click', () => {
        if (this.paletteClearBtn?.disabled || this.paletteClearBtn?.classList.contains('is-inactive')) {
          return;
        }
        this.#resetDesignPalette();
      });

      this.#bindPalettePresets();

      this.styleStarterSelect?.addEventListener('change', () => {
        const key = this.styleStarterSelect?.value || '';
        const prefsRow = this.styleStarterSelect?.closest('[data-ai-style-starters]');
        prefsRow?.classList.toggle('is-style-selected', Boolean(key));
        if (!key) {
          this._activeStyleStarter = '';
          return;
        }
        const starter = STYLE_STARTERS[key];
        if (!starter) return;

        this._activeStyleStarter = key;

        if (this.promptInput) {
          const current = (this.promptInput.value || '').trim();
          this.promptInput.value = current ? `${current}, ${starter}` : starter;
          this.promptInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.root.dispatchEvent(new CustomEvent('wallprint:prompt-changed'));
        }
      });

      if (!this._paletteTouched) {
        this.#applyPaletteNeutralValues();
      }
      this.#syncPaletteUi();
    }

    #countPromptWords(prompt) {
      return String(prompt || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
    }

    #isVagueWallpaperPrompt(prompt) {
      const words = this.#countPromptWords(prompt);
      if (words > 2) return false;
      if (this._paletteTouched && this.#getDesignPalette()) return false;
      if (this._activeStyleStarter || this.styleStarterSelect?.value) return false;
      if (PROMPT_PATTERN_HINT.test(prompt)) return false;
      return true;
    }

    async generate() {
      if (this.isGenerating) return;

      this.setMode('generate');
      const prompt = (this.promptInput?.value || '').trim();
      const state = this.#computeMeasureState();
      this.#clearError();

      if (prompt.length < 3) {
        this.#showError('Please describe the wallpaper you want to generate.');
        this.promptInput?.focus();
        return;
      }
      if (this.#isVagueWallpaperPrompt(prompt)) {
        this.#showError(
          'Add more pattern detail — pick a colour swatch, choose a quick style, or describe motifs and repeat (e.g. "sage botanical leaves, soft watercolor repeat").'
        );
        this.promptInput?.focus();
        return;
      }
      if (this.isWallShape && (!state.widthDisp || !state.heightDisp)) {
        this.#showError('Enter your wall width and height first so we can match the image to your wall shape.');
        this.widthInput?.focus();
        return;
      }

      const shape = this.#resolveGenerationShape(state);
      if (!(await this.#ensureApiReady())) return;

      this.#resetResult();
      this.#setGeneratingState(true);

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 120000);

      try {
        const palette = this.#getDesignPalette();
        const response = await fetch(this.#apiEndpointUrl(this.generateEndpoint), {
          method: 'POST',
          headers: this.#apiHeaders({ json: true, url: this.generateEndpoint }),
          signal: controller.signal,
          body: JSON.stringify({
            prompt,
            ...(palette ? { palette } : {}),
            aspect_ratio: Number(shape.ratio.toFixed(4)),
            width: shape.width,
            height: shape.height,
            wall: this.isWallShape
              ? {
                  width_cm: Math.round(state.widthCm),
                  height_cm: Math.round(state.heightCm),
                  print_width_cm: Math.round(state.printWidthCm),
                  print_height_cm: Math.round(state.printHeightCm),
                }
              : null,
            seamless_repeat: Boolean(this.seamlessRepeatInput?.checked),
            wallpaper_layout: this.#getWallpaperLayout(),
            pattern_repeat_cm: this.#getPatternRepeatCm(),
            watermark_logo_url: this.watermarkLogoUrl || undefined,
          }),
        });
        if (!response.ok) {
          const errText = await response.text().catch(() => '');
          if (this.#apiUnreachableBody(errText)) {
            throw new Error(
              'Could not reach the WallPrint AI server. Check wallprint-ai.fly.dev is running or clear Dev API base URL to use the store app proxy.'
            );
          }
          let detail = errText;
          try {
            const parsed = JSON.parse(errText);
            if (parsed?.message) detail = String(parsed.message);
            else if (parsed?.error) detail = String(parsed.error);
          } catch {
            /* plain text */
          }
          if (response.status === 401 && /proxy signature/i.test(errText)) {
            throw new Error(
              'WallPrint AI rejected the store request. Set Storefront API key in the theme editor to match STOREFRONT_API_KEY on Fly, or clear Dev API base URL to use the app proxy.'
            );
          }
          if (
            response.status === 503 ||
            response.status === 500 &&
            (/storage is full|volume unavailable|storage_unavailable|no space left|ENOENT.*mkdir.*\/data\//i.test(detail) ||
              /storage is full|volume unavailable|storage_unavailable|no space left|ENOENT.*mkdir.*\/data\//i.test(errText))
          ) {
            throw new Error(
              'WallPrint AI could not save your design — server storage is full or temporarily unavailable. Try again in a few minutes or contact support.'
            );
          }
          throw new Error(detail.trim() || `Generation failed (${response.status})`);
        }

        const data = await response.json();
        if (!data || !data.watermarked_url) throw new Error('Malformed response from generator.');

        const previewUrl = this.#resolveApiAssetUrl(data.watermarked_url);
        const originalUrl = data.original_url
          ? this.#resolveApiAssetUrl(data.original_url)
          : previewUrl;

        this.currentAsset = {
          source: 'generated',
          aiImageId: data.ai_image_id,
          seed: data.seed,
          model: data.model,
          prompt,
          shapeLabel: shape.label,
          genWidth: shape.width,
          genHeight: shape.height,
          watermarked: true,
          tiled: Boolean(data.tiled),
          originalUrl,
          metadata: {
            ...(data.metadata || {}),
            ...this.#getWallpaperPrefsMetadata(),
            ...(this.#getDesignPalette() ? { palette: this.#getDesignPalette() } : {}),
            ...(this._activeStyleStarter ? { style_starter: this._activeStyleStarter } : {}),
          },
        };

        await this.#renderGeneratedPreview(previewUrl, originalUrl);
        this.#setNoteForSource('generated');
        this.#setCheckoutVisible(true);
        this.calculate({ sync: true });
        this._previewFitKey = '';
        this.#schedulePreviewLayout(true);
        this.#syncPreviewActions();
        this.#emitGenerationHistory({
          ai_image_id: data.ai_image_id,
          prompt,
          preview_url: previewUrl,
          thumb_url: data.inspiration_thumb_url
            ? this.#resolveApiAssetUrl(data.inspiration_thumb_url)
            : previewUrl,
          original_url: originalUrl,
          seed: data.seed,
          model: data.model,
          tiled: Boolean(data.tiled),
          shape_label: shape.label,
          gen_width: shape.width,
          gen_height: shape.height,
          metadata: {
            ...(this.currentAsset?.metadata || {}),
            ...(this.#getDesignPalette() ? { palette: this.#getDesignPalette() } : {}),
          },
        });
        this.#emitPreviewChanged();
      } catch (error) {
        console.error('[ai-generator]', error);
        const message =
          error?.name === 'AbortError'
            ? 'Generation timed out. Keep npm run dev and ngrok running, then try again.'
            : error?.message ||
              'Something went wrong while generating. Check the AI server and ngrok, then try again.';
        this.#showError(message);
        this.#resetResult();
      } finally {
        window.clearTimeout(timeoutId);
        this.#setGeneratingState(false);
        this._previewFitKey = '';
        this.#schedulePreviewLayout(true);
        if (!this.isTiling) {
          this.preview?.classList.toggle(
            'is-preview-busy',
            this.isGenerating || this.isTiling
          );
        }
      }
    }

    /**
     * Reads an image file's natural pixel dimensions.
     * @param {string} url - object URL for the file
     * @returns {Promise<{ w: number, h: number } | null>}
     */
    #readImageSize(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = url;
      });
    }

    /**
     * Decides whether an upload is acceptable for printing at the wall size.
     * @param {{ w: number, h: number }} dims
     * @param {ReturnType<AIImageGenerator['calculate']>} state
     * @returns {{ block: boolean, warn: boolean, message: string, status: string }}
     */
    #assessUploadQuality(dims, state) {
      const shortSide = Math.min(dims.w, dims.h);

      // 1) Absolute floor — reject tiny thumbnails regardless of wall size.
      if (shortSide < this.minUploadDimension) {
        return {
          block: true,
          warn: false,
          message: `This image is only ${dims.w}×${dims.h}px. Please upload at least ${this.minUploadDimension}px on its shortest side.`,
          status: '',
        };
      }

      // 2) Effective DPI at the wall size (needs measurements).
      if (state.printWidthCm > 0 && state.printHeightCm > 0) {
        const dpiW = dims.w / (state.printWidthCm / CM_PER_INCH);
        const dpiH = dims.h / (state.printHeightCm / CM_PER_INCH);
        const effDpi = Math.floor(Math.min(dpiW, dpiH));
        const printLabel = `${this.#roundDisp(this.#fromCm(state.printWidthCm))}×${this.#roundDisp(this.#fromCm(state.printHeightCm))} ${state.lengthUnit}`;

        if (effDpi < this.recommendedPrintDpi) {
          return {
            block: false,
            warn: false,
            message: `Good print quality (~${effDpi} DPI at ${printLabel}) — we upscale before printing when needed.`,
            status: 'ok',
          };
        }
        return {
          block: false,
          warn: false,
          message: `Good print quality (~${effDpi} DPI at your wall size).`,
          status: 'ok',
        };
      }

      // No measurements yet — passed the absolute floor, can't DPI-check.
      return {
        block: false,
        warn: true,
        message: 'Enter your wall size above to confirm this image is high enough resolution.',
        status: 'warn',
      };
    }

    #setUploadStatus(message, status) {
      if (!this.uploadStatus) return;
      this.uploadStatus.hidden = !message;
      this.uploadStatus.textContent = message || '';
      this.uploadStatus.classList.remove('ai-generator__upload-status--warn', 'ai-generator__upload-status--ok');
      if (status === 'warn') this.uploadStatus.classList.add('ai-generator__upload-status--warn');
      if (status === 'ok') this.uploadStatus.classList.add('ai-generator__upload-status--ok');
    }

    /** Open the design upload file picker (Safari needs a visible, not display:none, input). */
    #openUploadPicker() {
      if (this.isUploading || !this.fileInput) return;
      this.fileInput.click();
    }

    /**
     * Validates, quality-checks, previews and uploads a customer-provided file.
     * @param {File} file
     */
    async handleFile(file) {
      if (this.isUploading) return;
      this.#clearError();

      if (!isAllowedUploadFile(file)) {
        this.#showError('Please upload a PNG, JPG or WEBP image.');
        if (this.fileInput) this.fileInput.value = '';
        return;
      }
      if (file.size > this.maxUploadBytes) {
        const mb = Math.round(this.maxUploadBytes / (1024 * 1024));
        this.#showError(`That file is too large. Maximum size is ${mb}MB.`);
        if (this.fileInput) this.fileInput.value = '';
        return;
      }

      const url = URL.createObjectURL(file);
      const dims = await this.#readImageSize(url);
      if (!dims) {
        URL.revokeObjectURL(url);
        this.#showError('We could not read that image. Please try a different file.');
        if (this.fileInput) this.fileInput.value = '';
        return;
      }

      const state = this.#computeMeasureState();
      const quality = this.#assessUploadQuality(dims, state);
      if (quality.block) {
        URL.revokeObjectURL(url);
        this.#resetResult();
        this.#setUploadStatus(quality.message, '');
        this.#showError(quality.message);
        this.#scrollToSection('design');
        this.errorEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        if (this.fileInput) this.fileInput.value = '';
        return;
      }

      this.setMode('upload');
      this.#resetResult();
      this.objectUrl = url;

      try {
        await this.#renderUploadCrop(this.objectUrl, dims.w, dims.h);
      } catch (error) {
        console.error('[ai-generator] crop preview', error);
        URL.revokeObjectURL(url);
        this.#showError('Could not show a preview for that image. Try a different file.');
        if (this.fileInput) this.fileInput.value = '';
        return;
      }

      this.#scrollToSection('upload');
      this.isUploading = true;
      this.designPreviewCol?.classList.add('is-preview-uploading');
      this.#setUploadStatus('Uploading…', '');

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 120000);

      try {
        if (this.#isLocalDevApiEndpoint() && !this.#canUseLocalDevApiFromBrowser()) {
          this.#completeLocalUpload(file, dims, quality, state);
          return;
        }

        if (!(await this.#ensureApiReady())) return;

        const formData = new FormData();
        formData.append('file', file);
        if (this.watermarkLogoUrl) {
          formData.append('watermark_logo_url', this.watermarkLogoUrl);
        }

        const response = await fetch(this.#apiEndpointUrl(this.uploadEndpoint), {
          method: 'POST',
          headers: this.#apiHeaders({ url: this.uploadEndpoint }),
          signal: controller.signal,
          body: formData,
        });

        if (!response.ok) {
          const errText = await response.text().catch(() => '');
          if (this.#isApiUnreachableError(null, errText) || this.#apiUnreachableBody(errText)) {
            this.#completeLocalUpload(file, dims, quality, state);
            return;
          }
          let detail = errText;
          try {
            const parsed = JSON.parse(errText);
            if (parsed?.error) detail = String(parsed.error);
            if (parsed?.message) detail = `${detail}: ${parsed.message}`;
          } catch {
            /* plain text */
          }
          if (/unsupported file type/i.test(detail)) {
            throw new Error(
              'The server rejected this file type. Restart npm run dev after updating WallPrint AI, then try again.'
            );
          }
          if (
            response.status === 503 ||
            /storage_unavailable|storage is full|volume unavailable|no space left|ENOENT.*mkdir.*\/data\//i.test(
              detail
            ) ||
            /storage is full|volume unavailable|no space left|ENOENT.*mkdir.*\/data\//i.test(errText)
          ) {
            throw new Error(
              'WallPrint AI could not save your upload — server storage is full or temporarily unavailable. Try again in a few minutes or contact support.'
            );
          }
          throw new Error(detail.trim() || `Upload failed (${response.status})`);
        }

        const data = await response.json();
        if (!data?.asset_id) throw new Error('Malformed upload response.');

        const originalUrl = data.original_url
          ? this.#resolveApiAssetUrl(data.original_url)
          : this.objectUrl;
        let editUrl = this.objectUrl;
        if (data.original_url) {
          try {
            editUrl = await this.#resolvePreviewSrc(originalUrl);
          } catch (err) {
            console.warn('[ai-generator] clean upload preview', err);
            editUrl = this.objectUrl;
          }
        }
        try {
          await this.#renderUploadCrop(editUrl, dims.w, dims.h);
        } catch (err) {
          console.warn('[ai-generator] upload crop preview', err);
        }

        this.currentAsset = {
          source: 'uploaded',
          aiImageId: data.asset_id,
          seed: '',
          model: 'customer-upload',
          prompt: '',
          shapeLabel: this.isWallShape ? 'wall' : this.aspectSelect?.value || '',
          fileName: file.name,
          uploadWidth: dims.w,
          uploadHeight: dims.h,
          watermarked: false,
          originalUrl: data.original_url || '',
          editUrl,
          metadata: {
            original_filename: file.name,
            file_type: file.type,
            upload_width_px: dims.w,
            upload_height_px: dims.h,
            ...this.#getUploadCropMetadata(),
          },
        };
        this.#persistCropToAsset();

        this.#setUploadStatus(`${file.name} — ${quality.message}`, quality.status);
        this.#setNoteForSource('uploaded');
        this.#setCheckoutVisible(true);
        this.#syncDesignEmphasis();
        this.calculate({ sync: true });
        this.#scrollToSection('design');
        this.#emitGenerationHistory({
          ai_image_id: data.asset_id,
          design_source: 'uploaded',
          file_name: file.name,
          preview_url: this.#resolveApiAssetUrl(data.watermarked_url || data.url),
          thumb_url: this.#resolveApiAssetUrl(data.watermarked_url || data.url),
          original_url: originalUrl,
        });
        this.#emitPreviewChanged();
      } catch (error) {
        console.error('[ai-generator] upload', error);
        const hadCropPreview = this.cropViewport && !this.cropViewport.hidden;
        if (
          hadCropPreview &&
          (this.#isApiUnreachableError(error) ||
            /failed to fetch|networkerror|load failed|offline/i.test(String(error?.message || '')))
        ) {
          this.#completeLocalUpload(file, dims, quality, state);
          return;
        }
        this.#showError(
          error?.name === 'AbortError'
            ? 'Upload timed out. Check npm run dev and ngrok are running, then try again.'
            : error?.message || 'We could not upload your image. Please try again.'
        );
        this.#setUploadStatus(error?.message || 'Upload failed', '');
        if (hadCropPreview) this.#scrollToSection('design');
      } finally {
        window.clearTimeout(timeoutId);
        this.isUploading = false;
        this.designPreviewCol?.classList.remove('is-preview-uploading');
        if (this.fileInput) this.fileInput.value = '';
      }
    }

    #bindUploadCropEvents() {
      if (this.cropEventsBound || !this.cropViewport) return;
      this.cropEventsBound = true;

      this.cropViewport.addEventListener('pointerdown', (e) => this.#onCropPointerDown(e));
      this.cropViewport.addEventListener('pointermove', (e) => this.#onCropPointerMove(e));
      this.cropViewport.addEventListener('pointerup', (e) => this.#onCropPointerUp(e));
      this.cropViewport.addEventListener('pointercancel', (e) => this.#onCropPointerUp(e));
      this.cropViewport.addEventListener('wheel', (e) => this.#onCropWheel(e), { passive: false });

      this.cropResizeObserver = new ResizeObserver(() => {
        this.#refreshUploadCropLayout(true);
        this.#syncCropWatermarkOverlay();
        this.#scheduleRepeatOverlayUpdate();
        this.#schedulePreviewLayout(true);
      });
      this.cropResizeObserver.observe(this.cropViewport);
    }

    #getCropViewportSize() {
      if (!this.cropViewport) return { w: 0, h: 0 };
      const w = this.cropViewport.clientWidth;
      const h = this.cropViewport.clientHeight;
      if (h > 0) return { w, h };
      const state = this.#computeMeasureState();
      const ratio = this.#previewAspectRatio(state.printWidthCm, state.printHeightCm);
      return { w, h: w > 0 ? w / ratio : 0 };
    }

    #getMinCropScale() {
      if (!this.cropNaturalW || !this.cropNaturalH) return 1;
      const { w, h } = this.#getCropViewportSize();
      if (w <= 0 || h <= 0) return 1;
      return Math.max(w / this.cropNaturalW, h / this.cropNaturalH);
    }

    #clampCropPan() {
      const { w: vw, h: vh } = this.#getCropViewportSize();
      const iw = this.cropNaturalW * this.cropScale;
      const ih = this.cropNaturalH * this.cropScale;
      if (iw <= vw) this.cropPanX = 0;
      else {
        const max = (iw - vw) / 2;
        this.cropPanX = Math.max(-max, Math.min(max, this.cropPanX));
      }
      if (ih <= vh) this.cropPanY = 0;
      else {
        const max = (ih - vh) / 2;
        this.cropPanY = Math.max(-max, Math.min(max, this.cropPanY));
      }
    }

    #applyUploadCropTransform() {
      if (!this.cropImage || !this.cropNaturalW) return;
      this.#clampCropPan();
      const { w: vw, h: vh } = this.#getCropViewportSize();
      const iw = this.cropNaturalW * this.cropScale;
      const ih = this.cropNaturalH * this.cropScale;
      const left = (vw - iw) / 2 + this.cropPanX;
      const top = (vh - ih) / 2 + this.cropPanY;
      this.cropImage.style.width = `${iw}px`;
      this.cropImage.style.height = `${ih}px`;
      this.cropImage.style.left = `${left}px`;
      this.cropImage.style.top = `${top}px`;
      this.#scheduleRepeatOverlayUpdate();
      this.#schedulePreviewTrimUpdate();
    }

    /**
     * Crop rectangle for cart / fulfilment (fractions of the original upload, 0–1).
     * Backend should apply this box to the stored original, then upscale to print_width_cm × print_height_cm.
     */
    #getUploadCropMetadata() {
      const { w: vw, h: vh } = this.#getCropViewportSize();
      const iw = this.cropNaturalW * this.cropScale;
      const ih = this.cropNaturalH * this.cropScale;
      const W = this.cropNaturalW;
      const H = this.cropNaturalH;
      if (!vw || !vh || !iw || !ih || !W || !H) return {};

      const left = (vw - iw) / 2 + this.cropPanX;
      const top = (vh - ih) / 2 + this.cropPanY;
      const visLeft = Math.max(0, left);
      const visTop = Math.max(0, top);
      const visRight = Math.min(vw, left + iw);
      const visBottom = Math.min(vh, top + ih);
      const visW = Math.max(0, visRight - visLeft);
      const visH = Math.max(0, visBottom - visTop);

      const cropLeftPx = (visLeft - left) / this.cropScale;
      const cropTopPx = (visTop - top) / this.cropScale;
      const cropWidthPx = visW / this.cropScale;
      const cropHeightPx = visH / this.cropScale;

      const norm = (v, max) => Number(Math.max(0, Math.min(1, v / max)).toFixed(5));

      return {
        crop_focus_x: Number(((vw / 2 - left) / iw).toFixed(4)),
        crop_focus_y: Number(((vh / 2 - top) / ih).toFixed(4)),
        crop_scale: Number(this.cropScale.toFixed(4)),
        crop_min_scale: Number(this.cropMinScale.toFixed(4)),
        crop_viewport_ratio: Number((vw / vh).toFixed(4)),
        crop_norm_left: norm(cropLeftPx, W),
        crop_norm_top: norm(cropTopPx, H),
        crop_norm_width: norm(cropWidthPx, W),
        crop_norm_height: norm(cropHeightPx, H),
        crop_px_left: Math.round(cropLeftPx),
        crop_px_top: Math.round(cropTopPx),
        crop_px_width: Math.round(cropWidthPx),
        crop_px_height: Math.round(cropHeightPx),
      };
    }

    #persistCropToAsset() {
      if (!this.currentAsset) return;
      // Both uploaded and generated designs now use the crop viewport.
      if (this.currentAsset.source !== 'uploaded' && this.currentAsset.source !== 'generated') {
        return;
      }
      const crop = this.#getUploadCropMetadata();
      this.currentAsset.metadata = {
        ...this.currentAsset.metadata,
        ...crop,
        ...this.#getAdjustmentMetadata(),
        ...this.#getWallpaperPrefsMetadata(),
      };
    }

    /**
     * Re-fit crop when the wall aspect ratio or viewport size changes.
     * @param {boolean} preservePan - keep pan when possible (resize); reset on aspect change
     */
    #refreshUploadCropLayout(preservePan = false) {
      if (!this.cropViewport || this.cropViewport.hidden || !this.cropNaturalW) return;
      const prevMin = this.cropMinScale;
      this.cropMinScale = this.#getMinCropScale();
      if (!preservePan || !prevMin) {
        this.cropScale = this.cropMinScale;
        this.cropPanX = 0;
        this.cropPanY = 0;
      } else if (this.cropScale < this.cropMinScale) {
        this.cropScale = this.cropMinScale;
      }
      this.cropScale = Math.min(this.cropScale, this.cropMinScale * CROP_MAX_ZOOM);
      this.#applyUploadCropTransform();
      this.#persistCropToAsset();
    }

    #hideUploadCrop() {
      if (this.cropViewport) this.cropViewport.hidden = true;
      if (this.cropHint) this.cropHint.hidden = true;
      if (this.cropWatermarkCanvas) this.cropWatermarkCanvas.hidden = true;
      if (this.repeatOverlayCanvas) this.repeatOverlayCanvas.hidden = true;
      if (this.previewTrimCanvas) this.previewTrimCanvas.hidden = true;
      this.#togglePreviewTrimPop(false);
      this.root.classList.remove('is-repeat-layout');
      this.#syncPreviewStatusBar();
      if (this.cropImage) {
        this.cropImage.removeAttribute('src');
      }
      this.cropNaturalW = 0;
      this.cropNaturalH = 0;
      this._cropAspectKey = '';
      this.#syncPreviewActions();
    }

    #showUploadCropUi() {
      if (this.cropViewport) this.cropViewport.hidden = false;
      if (this.cropHint) this.cropHint.hidden = false;
      if (this.image) this.image.hidden = true;
      if (this.placeholder) this.placeholder.hidden = true;
      if (this.wallOverlay) this.wallOverlay.hidden = true;
      this.#setPreviewLive(true);
      this.#syncPanelLinesOnPreview(this.calculate());
      this.#syncPreviewStatusBar();
      this.#bindUploadCropEvents();
      this.#applyAdjustmentPreview();
      this.#schedulePreviewLayout(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#refreshUploadCropLayout(false);
          if (this.cropWatermarkCanvas) this.cropWatermarkCanvas.hidden = true;
          this.#syncCropWatermarkOverlay();
          this.#scheduleRepeatOverlayUpdate();
        });
      });
      this.#scheduleHeroScrollSync();
    }

    #onCropPointerDown(event) {
      if (!this.cropViewport || this.cropViewport.hidden) return;
      if (event.button !== undefined && event.button !== 0) return;
      this.cropDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        panX: this.cropPanX,
        panY: this.cropPanY,
      };
      this.cropViewport.setPointerCapture(event.pointerId);
      this.cropViewport.classList.add('is-dragging');
      event.preventDefault();
    }

    #onCropPointerMove(event) {
      if (!this.cropDrag || this.cropDrag.pointerId !== event.pointerId) return;
      this.cropPanX = this.cropDrag.panX + (event.clientX - this.cropDrag.startX);
      this.cropPanY = this.cropDrag.panY + (event.clientY - this.cropDrag.startY);
      this.#applyUploadCropTransform();
      event.preventDefault();
    }

    #onCropPointerUp(event) {
      if (!this.cropDrag || this.cropDrag.pointerId !== event.pointerId) return;
      this.cropDrag = null;
      this.cropViewport?.classList.remove('is-dragging');
      this.cropViewport?.releasePointerCapture(event.pointerId);
      this.#persistCropToAsset();
    }

    #onCropWheel(event) {
      if (!this.cropViewport || this.cropViewport.hidden) return;
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
      const maxScale = this.cropMinScale * CROP_MAX_ZOOM;
      this.cropScale = Math.max(this.cropMinScale, Math.min(maxScale, this.cropScale * factor));
      this.cropViewport.classList.add('is-zooming');
      window.clearTimeout(this._cropZoomTimer);
      this._cropZoomTimer = window.setTimeout(() => {
        this.cropViewport?.classList.remove('is-zooming');
      }, 180);
      this.#applyUploadCropTransform();
      this.#persistCropToAsset();
    }

    /** Reset crop pan/zoom so the full image fits the wall preview (minimum zoom). */
    #fitPreviewCropOut() {
      if (!this.cropViewport || this.cropViewport.hidden || !this.cropNaturalW) return;
      this.#refreshUploadCropLayout(false);
    }

    #renderUploadCrop(url, naturalW, naturalH) {
      return new Promise((resolve) => {
        if (!this.cropImage || !this.cropViewport) {
          resolve();
          return;
        }
        this.cropNaturalW = naturalW;
        this.cropNaturalH = naturalH;
        const onDone = () => {
          this.#loadAdjustmentsFromAsset();
          this.#showUploadCropUi();
          requestAnimationFrame(() => {
            this._cropAspectKey = '';
            const state = this.calculate();
            this.#refreshUploadCropLayout(false);
            this.#schedulePreviewLayout(true);
            this.#setCheckoutVisible(true);
            this.#syncPreviewWallOverlay(state);
            this.#syncCropWatermarkOverlay();
            this.#scheduleRepeatOverlayUpdate();
            this.#syncPreviewTrimUi();
            this.#schedulePreviewTrimUpdate();
            resolve();
          });
        };
        this.cropImage.onload = onDone;
        this.cropImage.onerror = onDone;
        this.cropImage.src = url;
      });
    }

    /**
     * Load a remote preview into a same-origin blob URL so it does not break when
     * temporary CDN links (e.g. Replicate) expire.
     * @param {string} url
     * @returns {Promise<string>}
     */
    async #resolvePreviewSrc(url) {
      if (!url || url.startsWith('data:') || url.startsWith('blob:')) return url;

      // Same-origin app-proxy assets can load directly; cross-origin (ngrok) must
      // be fetched with the skip-warning header, otherwise ngrok free returns its
      // HTML interstitial instead of the image and the <img> fails to decode.
      const sameOrigin = url.startsWith('/') || url.startsWith(window.location.origin);
      try {
        const res = await fetch(url, {
          credentials: sameOrigin ? 'same-origin' : 'omit',
          headers: this.#apiHeaders(),
        });
        if (!res.ok) throw new Error(`Preview fetch failed (${res.status})`);
        const blob = await res.blob();
        if (!/^image\//i.test(blob.type)) {
          throw new Error(`Preview returned non-image (${blob.type || 'unknown'})`);
        }
        if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = URL.createObjectURL(blob);
        return this.objectUrl;
      } catch (err) {
        console.warn('[ai-generator] Could not cache preview blob, using direct URL', err);
        return url;
      }
    }

    async #renderGeneratedPreview(watermarkedUrl, _originalUrl) {
      const src = await this.#resolvePreviewSrc(this.#resolveApiAssetUrl(watermarkedUrl));
      const dims = await this.#readImageSize(src);
      if (!dims) {
        if (this.image) this.image.hidden = true;
        if (this.placeholder) this.placeholder.hidden = false;
        this.#showError(
          'Preview image could not load. Check the WallPrint AI server (npm run dev) is running, then generate again.'
        );
        return;
      }

      // Record the real output size (Ultra ≈ 4MP) so the quality badge is accurate.
      if (this.currentAsset && this.currentAsset.source === 'generated') {
        this.currentAsset.genWidth = dims.w;
        this.currentAsset.genHeight = dims.h;
      }

      // Generated images use the SAME drag/zoom crop UI as uploads, so the print
      // always matches the wall aspect ratio (the customer fine-tunes the framing).
      this.objectUrl = src;
      try {
        await this.#renderUploadCrop(src, dims.w, dims.h);
      } catch (error) {
        console.error('[ai-generator] generated crop preview', error);
        this.#showError('Could not display the generated image. Please generate again.');
      }
    }

    #emitPreviewChanged() {
      const asset = this.currentAsset;
      if (!asset?.aiImageId) {
        this.root.dispatchEvent(new CustomEvent('wallprint:preview-changed', { detail: { asset: null } }));
        return;
      }
      const kind = asset.source === 'uploaded' ? 'uploads' : 'assets';
      this.root.dispatchEvent(
        new CustomEvent('wallprint:preview-changed', {
          detail: {
            asset: {
              kind,
              asset_id: asset.aiImageId,
              source: asset.source,
            },
          },
        })
      );
    }

    /** @returns {Promise<Blob|null>} Small JPEG of the active preview for visual similarity search. */
    async getPreviewImageBlob() {
      const img =
        this.cropViewport?.querySelector('img') ||
        this.preview?.querySelector('img') ||
        this.repeatCanvas?.parentElement?.querySelector('img');
      if (!img?.src) return null;
      try {
        if (!img.complete) {
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });
        }
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        const w = img.naturalWidth || img.width || 256;
        const h = img.naturalHeight || img.height || 256;
        const scale = Math.max(256 / w, 256 / h);
        const dw = w * scale;
        const dh = h * scale;
        ctx.drawImage(img, (256 - dw) / 2, (256 - dh) / 2, dw, dh);
        return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      } catch {
        return null;
      }
    }

    #emitGenerationHistory(payload) {
      const thumbUrl = payload.thumb_url || payload.preview_url;
      const isUpload =
        payload.design_source === 'uploaded' ||
        payload.source === 'uploaded' ||
        String(payload.ai_image_id || '').startsWith('upload_');
      const prompt = payload.prompt || '';
      const historyItem = {
        id: payload.ai_image_id,
        source: 'history',
        design_source: isUpload ? 'uploaded' : 'generated',
        ai_image_id: payload.ai_image_id,
        title: isUpload
          ? (payload.file_name || 'Uploaded design').slice(0, 64)
          : prompt.slice(0, 64),
        prompt,
        file_name: payload.file_name || '',
        thumb_url: thumbUrl,
        preview_url: payload.preview_url,
        original_url: payload.original_url || payload.preview_url,
        seed: payload.seed,
        model: payload.model,
        tiled: Boolean(payload.tiled),
        shape_label: payload.shape_label,
        gen_width: payload.gen_width,
        gen_height: payload.gen_height,
        metadata: payload.metadata || {},
        created_at: new Date().toISOString(),
        saved: true,
        tags: String(prompt || payload.file_name || '')
          .toLowerCase()
          .split(/[^\p{L}\p{N}]+/u)
          .filter((w) => w.length > 2),
      };
      this.root.dispatchEvent(
        new CustomEvent('wallprint:generation-complete', { detail: { historyItem } })
      );
    }

    /** True when the preview has wall measurements or a fixed aspect ratio selected. */
    #hasPreviewShapeContext() {
      const state = this.#computeMeasureState();
      if (state.printWidthCm > 0 && state.printHeightCm > 0) return true;
      if (!this.isWallShape) return true;
      return false;
    }

    /** Remind the customer to set wall size or aspect ratio before previewing a design. */
    #promptPreviewShapeSetup(options = {}) {
      if (this.#hasPreviewShapeContext()) return false;
      this.#showError(
        'Add your wall width and height, or choose a fixed aspect ratio above, so this design fits your print shape.'
      );
      if (options.scroll !== false) this.#scrollToSection('wall');
      return true;
    }

    async restoreGenerationHistory(item) {
      if (!item?.preview_url) return false;
      const isUpload =
        item.design_source === 'uploaded' ||
        String(item.ai_image_id || item.id || '').startsWith('upload_');

      if (isUpload) {
        this.setMode('upload');
        this.currentAsset = {
          source: 'uploaded',
          aiImageId: item.ai_image_id || item.id,
          seed: '',
          model: item.model || 'customer-upload',
          prompt: '',
          shapeLabel: this.isWallShape ? 'wall' : this.aspectSelect?.value || '',
          fileName: item.file_name || item.title || 'Uploaded design',
          watermarked: true,
          originalUrl: item.original_url || item.preview_url,
          editUrl: item.preview_url,
          metadata: item.metadata || {},
        };
        await this.#renderGeneratedPreview(item.preview_url, item.original_url || item.preview_url);
        this.#setNoteForSource('uploaded');
        this.#setCheckoutVisible(true);
        this.calculate({ sync: true });
        this.#syncPreviewActions();
        this.#emitPreviewChanged();
        this.#promptPreviewShapeSetup();
        return true;
      }

      this.setMode('generate');
      if (item.prompt && this.promptInput) {
        this.promptInput.value = item.prompt;
        this.promptInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      this.currentAsset = {
        source: 'generated',
        aiImageId: item.ai_image_id || item.id,
        seed: item.seed || '',
        model: item.model || '',
        prompt: item.prompt || '',
        shapeLabel: item.shape_label || '',
        genWidth: item.gen_width || 0,
        genHeight: item.gen_height || 0,
        watermarked: true,
        tiled: Boolean(item.tiled),
        originalUrl: item.original_url || item.preview_url,
        metadata: item.metadata || {},
      };

      await this.#renderGeneratedPreview(item.preview_url, item.original_url);
      this.#setNoteForSource('generated');
      this.#setCheckoutVisible(true);
      this.calculate({ sync: true });
      this.#syncPreviewActions();
      this.#emitPreviewChanged();
      this.#promptPreviewShapeSetup();
      return true;
    }

    async addToCart() {
      if (this.isAdding) return;

      if (!this.currentAsset) {
        this.#showError('Create or upload a design before adding it to your cart.');
        return;
      }
      if (!this.variantId) {
        this.#showError('This generator is not linked to a product yet. Please set one in the theme editor.');
        return;
      }

      const state = this.calculate();
      if (this.isWallShape && (!state.widthDisp || !state.heightDisp)) {
        this.#showError('Please enter your wall measurements.');
        return;
      }
      if (state.install && !state.installRegion) {
        this.#showError('Please select an installation region.');
        return;
      }
      if (!this.#checkoutAcksComplete()) {
        if (!this.#ipScanReady()) {
          this.#showError('Run the IP safety check and review the results before adding to cart.');
        } else if (this.currentAsset?.ipScan?.status === 'unsafe') {
          this.#showError('This design flagged a high IP risk and cannot be ordered as-is.');
        } else if (!this.#activeAckChecked()) {
          this.#showError('Please tick the intellectual property acknowledgement before adding to cart.');
        } else if (this.#needsUpscaleAck() && !this.ackUpscaleInput?.checked) {
          this.#showError('Please confirm the upscaling note before adding to cart.');
        } else {
          this.#showError('Complete the checkout acknowledgements before adding to cart.');
        }
        this.checkoutAckEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }

      this.#clearError();
      this.#clearCartSuccess();
      this.#setAddingState(true);
      this.#persistCropToAsset();

      const asset = this.currentAsset;
      const lu = state.lengthUnit;
      // Customer-facing values in the unit they chose; metadata always in cm.
      const wallSize = `${state.widthDisp} × ${state.heightDisp} ${lu}`;
      const printSize = `${this.#roundDisp(this.#fromCm(state.printWidthCm))} × ${this.#roundDisp(this.#fromCm(state.printHeightCm))} ${lu}`;
      const areaDisp = `${this.#areaDisp(state.actualAreaM2).toFixed(2)} ${this.areaUnit}`;

      const properties = {
        _ai_image_id: asset.aiImageId,
        _ai_source: asset.source,
        _ai_seed: String(asset.seed ?? ''),
        _ai_generation_metadata: JSON.stringify({
          source: asset.source,
          model: asset.model,
          shape: asset.shapeLabel,
          gen_width: asset.genWidth,
          gen_height: asset.genHeight,
          prompt: asset.prompt,
          file_name: asset.fileName,
          unit: state.unit,
          wall_width_cm: Math.round(state.widthCm),
          wall_height_cm: Math.round(state.heightCm),
          trim_allowance_cm: state.bleedCm,
          print_width_cm: Math.round(state.printWidthCm),
          print_height_cm: Math.round(state.printHeightCm),
          panels: state.panels,
          ...asset.metadata,
          ...this.#getWallpaperPrefsMetadata(),
          ...(this.cropViewport && !this.cropViewport.hidden ? this.#getUploadCropMetadata() : {}),
        }),
        'Design source': asset.source === 'uploaded' ? 'Customer upload' : 'AI generated',
        'Wall Size': wallSize,
        'Print Size': printSize,
        'Area': areaDisp,
        'Panels': `${state.panels} ±`,
        'Installation': state.install ? 'Yes' : 'No',
      };
      if (asset.source === 'generated') {
        properties['AI Prompt'] = asset.prompt;
        properties['AI Model'] = asset.model || 'auto';
      } else if (asset.fileName) {
        properties['Uploaded file'] = asset.fileName;
      }
      if (asset.tiled || asset.metadata?.tiled) {
        properties['Seamless tile'] = 'Yes';
        properties._tiled = 'true';
      }
      if (state.install) properties['Installation Region'] = state.installRegion;
      if (state.minApplied) properties['Minimum Charge Applied'] = `Yes (${this.#areaDisp(this.minArea).toFixed(2)} ${this.areaUnit} minimum)`;
      if (asset.ipScan?.report_id) {
        properties._ip_scan_report_id = asset.ipScan.report_id;
        properties['IP scan'] = `${asset.ipScan.status || 'unknown'} (${asset.ipScan.score ?? '—'}/100)`;
        if (asset.ipScan.report_url) {
          properties._ip_scan_report_url = asset.ipScan.report_url;
        }
      }

      const items = [
        {
          id: Number(this.variantId),
          quantity: Math.max(1, state.units),
          properties,
        },
      ];

      if (state.install && this.installVariantId) {
        items.push({
          id: Number(this.installVariantId),
          quantity: Math.max(1, state.units),
          properties: {
            'Linked To': this.productTitle,
            'Area': `${this.#areaDisp(state.chargeableAreaM2).toFixed(2)} ${this.areaUnit}`,
            'Installation Region': state.installRegion,
          },
        });
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody.description || 'Add to cart failed.');
        }

        if (this.redirectToCart) {
          this.#showCartSuccess();
          window.setTimeout(() => {
            window.location.assign('/cart');
          }, 2200);
          return;
        }

        this.#showCartSuccess();

        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((res) => res.json())
          .catch(() => null);

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'ai-image-generator',
              data: { source: 'ai-image-generator', itemCount: cart?.item_count, variantId: this.variantId },
            },
          })
        );
      } catch (error) {
        console.error('[ai-generator]', error);
        this.#showError('Could not add your design to the cart. Please try again.');
      } finally {
        this.#setAddingState(false);
      }
    }

    /** Prefills optional consult wall fields from the calculator when empty. */
    #syncConsultMeasureFields() {
      this.root.querySelectorAll('[data-ai-consult-width]').forEach((input) => {
        if (input.value !== '' || !this.widthInput?.value) return;
        input.value = this.widthInput.value;
      });
      this.root.querySelectorAll('[data-ai-consult-height]').forEach((input) => {
        if (input.value !== '' || !this.heightInput?.value) return;
        input.value = this.heightInput.value;
      });
    }

    #onConsultFileChange(input) {
      const form = input.closest('form');
      const status = form?.querySelector('[data-ai-consult-file-status]');
      const file = input.files?.[0];
      if (!status) return;
      if (!file) {
        status.hidden = true;
        status.textContent = '';
        return;
      }
      status.hidden = false;
      status.textContent = `Selected: ${file.name}`;
    }

    /**
     * @param {File} file
     * @returns {Promise<{ asset_id?: string }>}
     */
    async #uploadConsultFile(file) {
      if (!isAllowedUploadFile(file)) {
        throw new Error('Please upload a PNG, JPG or WEBP image.');
      }
      if (file.size > this.maxUploadBytes) {
        const mb = Math.round(this.maxUploadBytes / (1024 * 1024));
        throw new Error(`That file is too large. Maximum size is ${mb}MB.`);
      }
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(this.#apiEndpointUrl(this.uploadEndpoint), {
        method: 'POST',
        headers: this.#apiHeaders(),
        body: formData,
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status})`);
      const data = await response.json();
      if (!data?.asset_id) throw new Error('Malformed upload response.');
      return data;
    }

    /** Copies calculator / design context into one consultant form. */
    #fillConsultContext(formEl) {
      const form = formEl;
      if (!form) return;

      const state = this.calculate();
      const lu = this.lengthUnit;
      const wallHidden = form.querySelector('[data-ai-consult-wall]');
      const widthInput = form.querySelector('[data-ai-consult-width]');
      const heightInput = form.querySelector('[data-ai-consult-height]');

      let wallSummary = '';
      const cw = toNum(widthInput?.value, 0);
      const ch = toNum(heightInput?.value, 0);
      if (cw > 0 && ch > 0) {
        wallSummary = `Customer entered: ${this.#roundDisp(cw)} × ${this.#roundDisp(ch)} ${lu}`;
      } else if (state.widthDisp > 0 && state.heightDisp > 0) {
        const pw = this.#roundDisp(this.#fromCm(state.printWidthCm));
        const ph = this.#roundDisp(this.#fromCm(state.printHeightCm));
        wallSummary = `From calculator: ${state.widthDisp} × ${state.heightDisp} ${lu} (print ${pw} × ${ph} ${lu})`;
      } else {
        wallSummary = 'Not provided';
      }
      if (wallHidden) wallHidden.value = wallSummary;

      const shapeHidden = form.querySelector('[data-ai-consult-shape]');
      const promptHidden = form.querySelector('[data-ai-consult-prompt]');
      const designHidden = form.querySelector('[data-ai-consult-design]');
      if (shapeHidden) shapeHidden.value = this.aspectSelect?.value || '';
      if (promptHidden) promptHidden.value = this.promptInput?.value || '';
      if (designHidden) {
        designHidden.value = this.currentAsset
          ? `${this.currentAsset.source}:${this.currentAsset.aiImageId}`
          : 'None yet';
      }
    }

    async #handleConsultSubmit(event) {
      const form = /** @type {HTMLFormElement} */ (event.target);
      this.#fillConsultContext(form);

      const fileInput = form.querySelector('[data-ai-consult-file]');
      const file = fileInput?.files?.[0];
      const uploadRef = form.querySelector('[data-ai-consult-upload-ref]');
      const status = form.querySelector('[data-ai-consult-file-status]');

      if (!file) return;

      event.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      if (status) {
        status.hidden = false;
        status.textContent = 'Uploading image…';
      }

      try {
        const data = await this.#uploadConsultFile(file);
        if (uploadRef) uploadRef.value = data.asset_id || file.name;
        if (status) status.textContent = `Attached: ${file.name}`;
        form.requestSubmit();
      } catch (error) {
        console.error('[ai-generator] consult upload', error);
        if (uploadRef) uploadRef.value = `File selected: ${file.name} (upload could not complete — we'll follow up)`;
        if (status) {
          status.textContent = `${file.name} noted — send request anyway and we'll contact you for the file if needed.`;
        }
        form.requestSubmit();
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    }

    openConsultant() {
      if (!this.consultDialog) return;
      this.#syncConsultMeasureFields();
      this.consultForms.forEach((form) => this.#fillConsultContext(form));
      if (typeof this.consultDialog.showModal === 'function') {
        this.consultDialog.showModal();
      } else {
        this.consultDialog.setAttribute('open', '');
      }
    }
  }

  const isStaleAiGeneratorMarkup = (root) => {
    if (!(root instanceof HTMLElement)) return false;
    const head = root.querySelector('.ai-generator__tool-panel-head');
    const toolbar = root.querySelector('[data-ai-design-toolbar]');
    if (!toolbar) return false;

    const hasNewLabel = !!head?.querySelector(
      '.ai-generator__design-tools-label, .ai-generator__design-tools-head'
    );
    const actionsRow = toolbar.querySelector('.ai-generator__toolbar-row--actions');
    if (actionsRow) {
      const shareInChip = !!actionsRow.querySelector('.ai-generator__toolbar-chip [data-ai-share-open]');
      const saveInRow = !!actionsRow.querySelector('[data-ai-save-design="list"]');
      if (!shareInChip || !saveInRow) return true;
      if (hasNewLabel) return false;
    }

    if (hasNewLabel) return false;

    return (
      !!head?.querySelector('.ai-generator__layout-toggle-head-wrap') ||
      !root.querySelector('[data-ai-design-tools-tray]') ||
      !toolbar.classList.contains('ai-generator__design-toolbar--single-row')
    );
  };

  /** Full-page HTML can lag behind the Section Rendering API — swap stale markup when needed. */
  const refreshStaleGeneratorSections = async () => {
      const staleRoots = Array.from(document.querySelectorAll(ROOT_SELECTOR)).filter((root) => {
        if (!(root instanceof HTMLElement) || root.dataset[INIT_FLAG]) return false;
        return isStaleAiGeneratorMarkup(root);
      });

    if (!staleRoots.length) return;

    await Promise.all(
      staleRoots.map(async (root) => {
        const shopifySection = root.closest('.shopify-section');
        const sectionId = shopifySection?.id?.replace(/^shopify-section-/, '');
        if (!shopifySection || !sectionId) return;

        try {
          const sectionUrl = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`;
          const response = await fetch(sectionUrl, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) return;

          const payload = await response.json();
          const html = payload?.[sectionId];
          if (typeof html !== 'string' || !html.includes('ai-generator__design-tools-label')) return;

          shopifySection.outerHTML = html;
        } catch (error) {
          console.warn('[ai-generator] stale section refresh failed', error);
        }
      })
    );
  };

  const bootGenerators = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset[INIT_FLAG]) return;
      root.dataset[INIT_FLAG] = 'true';
      new AIImageGenerator(/** @type {HTMLElement} */ (root));
    });
  };

  const init = async () => {
    await refreshStaleGeneratorSections();
    bootGenerators();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void init();
    });
  } else {
    void init();
  }

  document.addEventListener('shopify:section:load', bootGenerators);
})();
